import React, { useState, useEffect, useCallback, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate, useSearchParams } from 'react-router-dom';
import api from '../../utils/api';
import toast from 'react-hot-toast';
import clsx from 'clsx';
import { format, parseISO } from 'date-fns';
import { useConfirm } from '../../components/ConfirmDialog';
import { SupportItemPicker } from '../_shared/finance-ui';
import { getBranding } from '../../utils/exportBranding';
import { generateStatementPdf } from '../../utils/pmStatement';
import { INV_STATUS, statusLabel } from './pmStatus';
import {
  SectionCard, EmptyState, TableSkeleton, StatusBadge, BudgetCard, Currency,
  DetailDrawer, BlockReasonList, StatusStepper, Tooltip as HoverTip, Timeline,
  KpiTile, ErrorState, money as fmtMoney,
} from './ui';
import MailboxView from './Mailbox';
import Queues from './Queues';           // spec 5.4 intake + spec 13 notifications
import Reports from './Reports';         // spec 15
import Registers from './Registers';     // spec 10/11/12/14
import InvoiceEntry from './InvoiceEntry'; // fast keyboard-driven invoice entry
import AddressAutocomplete from '../../components/AddressAutocomplete';
import { ResponsiveContainer, AreaChart, Area, XAxis, YAxis, Tooltip } from 'recharts';
import NumberFlow from '@number-flow/react';
import {
  Receipt, Plus, Loader2, X, Check, Trash2, FileText, AlertTriangle, Wallet, User, Download, Landmark, RefreshCw, Users, Building2, ChevronRight, ChevronDown, Database, Upload,
  LayoutDashboard, Banknote, Scale, ShieldAlert, Eye, Percent, SlidersHorizontal, Pencil, Mail, Sparkles, CheckCircle2,
  Inbox, BarChart3, ClipboardList, Copy, Search, Maximize2, ChevronsLeft, ChevronsRight,
  Paperclip, Calculator, Clock, Hourglass, Sliders,
} from 'lucide-react';

const money = (n) => `$${Number(n || 0).toLocaleString('en-AU', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const fmtD = (d) => { if (!d) return '—'; try { return format(parseISO(String(d).slice(0, 10)), 'd MMM yyyy'); } catch { return String(d); } };
// For TIMESTAMPTZ values (paid dates). fmtD reads the first ten characters of the ISO string, which
// is the UTC day — a payment made at 8:44am in Sydney would show as the day before. This formats in
// the viewer's own timezone.
const fmtWhen = (d) => { if (!d) return null; try { return format(new Date(d), 'd MMM yyyy'); } catch { return null; } };
// A service period as one phrase: "3 Nov 2026" for a single day, "1 – 14 Nov 2026" inside a month,
// "28 Oct – 10 Nov 2026" across months. The To date is half of the claim, so it is never dropped.
const fmtRange = (from, to) => {
  const a = from ? String(from).slice(0, 10) : ''; const b = to ? String(to).slice(0, 10) : '';
  if (!a && !b) return '—';
  if (!a || !b || a === b) return fmtD(a || b);
  try {
    const x = parseISO(a); const y = parseISO(b);
    if (a.slice(0, 7) === b.slice(0, 7)) return `${format(x, 'd')} – ${format(y, 'd MMM yyyy')}`;
    if (a.slice(0, 4) === b.slice(0, 4)) return `${format(x, 'd MMM')} – ${format(y, 'd MMM yyyy')}`;
    return `${fmtD(a)} – ${fmtD(b)}`;
  } catch { return `${fmtD(a)} – ${fmtD(b)}`; }
};
// What the hover says on a paid row. Two hops, two dates.
const paidTip = (v) => {
  if (v.status === 'provider_paid') {
    const pp = fmtWhen(v.provider_paid_at); const np = fmtWhen(v.ndia_paid_at);
    return pp ? `Date paid: ${pp}${np ? ` · NDIA paid ${np}` : ''}` : null;
  }
  if (v.status === 'paid') { const np = fmtWhen(v.ndia_paid_at); return np ? `NDIA paid: ${np}` : null; }
  return null;
};
// snake_case enum → readable label. Used wherever a raw column value is shown to an operator
// (service_type, client_type), so "plan_managed" never reaches the screen as-is.
const titleCase = (s) => String(s || '').replace(/[_-]+/g, ' ').replace(/\b\w/g, (m) => m.toUpperCase());
// Browser download. Module level so every screen's "Report" button uses the same one.
const dl = (data, name, type) => { const url = URL.createObjectURL(new Blob([data], { type })); const a = document.createElement('a'); a.href = url; a.download = name; document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url); };

// A claim over 5,000 rows or 10 MB is split into several files, and the server then answers with a
// JSON manifest instead of a CSV. Both download paths asked for a blob and saved whatever came back
// under a .csv name — so a split claim was saved as a file of JSON, named .csv, and uploaded to the
// NDIA as if it were the claim. Read the manifest and pull each part down as its own real file.
// Returns how many files were saved, so the caller can say so.
const downloadClaimFiles = async (claimId, fallbackName) => {
  const first = await api.get(`/plan-management/claims/${claimId}/csv`, { responseType: 'blob' });
  if (!(first.data?.type || '').includes('json')) { dl(first.data, fallbackName, 'text/csv'); return 1; }
  let manifest = null;
  try { manifest = JSON.parse(await first.data.text()); } catch { /* fall through to the throw */ }
  if (!manifest?.multipart || !Array.isArray(manifest.files) || !manifest.files.length) {
    throw new Error(manifest?.message || 'The server did not return a claim file.');
  }
  // Sequentially, not in parallel: browsers drop all but the first of a burst of downloads.
  for (const f of manifest.files) {
    const part = await api.get(`/plan-management/claims/${claimId}/csv`, { params: { part: f.part_no }, responseType: 'blob' });
    dl(part.data, f.file_name, 'text/csv');
  }
  return manifest.files.length;
};
const num = (v) => (v === '' || v == null ? 0 : Number(v));
// One audit-log row as a sentence for the activity timeline. Unknown actions still read (their
// key with the underscores taken out) rather than disappearing.
const AUDIT_VERB = {
  created: 'created the invoice', updated: 'edited the invoice', line_added: 'added a line', line_updated: 'edited a line',
  line_deleted: 'removed a line', document_attached: 'attached the invoice document', approval_requested: 'asked the participant to approve',
  approval_approved: 'recorded the participant’s approval', approval_declined: 'recorded that the participant declined',
  claim_generated: 'generated the NDIA claim', payment_batched: 'added it to a payment run', remittance_sent: 'emailed the remittance advice',
  provider_paid_aba: 'paid the provider by ABA',
};
const auditItem = (a) => {
  const d = a.detail || {};
  const action = a.action === 'status_changed' && d.to ? `moved it to ${statusLabel(d.to)}` : (AUDIT_VERB[a.action] || String(a.action || '').replace(/_/g, ' '));
  const note = [
    d.line_total != null ? money(d.line_total) : null,
    d.via ? `via ${String(d.via).replace(/_/g, ' ')}` : null,
    d.batch_ref || null,
    d.ref ? `ref ${d.ref}` : null,
    d.to && a.action === 'remittance_sent' ? d.to : null,
    d.reason || null,
  ].filter(Boolean).join(' · ');
  const key = `${a.action} ${d.to || ''}`;
  const tone = /declin|cancel|error/.test(key) ? 'rose' : /hold/.test(key) ? 'amber'
    : /paid|approv|remittance/.test(key) ? 'emerald' : /claim/.test(key) ? 'primary' : 'slate';
  return { id: a.id, actor: a.actor_name || 'System', action, at: a.created_at, note: note || null, tone };
};
// Register columns that are useful but not decisive. Tailwind breakpoints measure the VIEWPORT, and
// these tables get the viewport minus the app sidebar, so at a 1440px laptop "every column from lg"
// meant the deciding columns (status, bank, totals) were the ones pushed off the right edge.
const WIDE_ONLY = 'hidden 2xl:table-cell';
const cap = (s) => String(s || '').replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
const isoToday = () => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`; };
// Days since the invoice arrived (received_date, else created_at) — the number a plan manager
// triages by. Amber past 7 days, red past 14.
const ageDays = (v) => { const d = v?.received_date || v?.created_at; if (!d) return 0; const n = Math.floor((Date.now() - new Date(d).getTime()) / 86400000); return Number.isFinite(n) && n > 0 ? n : 0; };
const AGE_DONE = ['provider_paid', 'paid', 'cancelled'];
// Whole days between a date and today. `ageDays` above counts from when the invoice ARRIVED, which
// is what the list triages by; this one counts from a given date — the expanded row measures age
// from the INVOICE DATE, because that is the clock the provider and the NDIA claim window run on.
const daysSince = (d) => { if (!d) return null; const n = Math.floor((Date.now() - new Date(String(d).slice(0, 10)).getTime()) / 86400000); return Number.isFinite(n) ? Math.max(0, n) : null; };
// Server body limit is 15 MB and base64 inflates ~33%, so a 9 MB file is the ceiling — the same
// ceiling InvoiceEntry's attach control uses, because both write the same `document_url`.
const MAX_DOC_BYTES = 9 * 1024 * 1024;
function AgeChip({ inv }) {
  if (AGE_DONE.includes(inv.status)) return <span className="text-slate-300">—</span>;
  const n = ageDays(inv);
  return <span className={clsx('tabular-nums font-medium', n > 14 ? 'text-red-600' : n > 7 ? 'text-amber-600' : 'text-slate-400')}>{n}d</span>;
}
// Clipboard with a visible failure. The async API is unavailable on plain http:// and inside some
// embedded browsers, and a silent no-op reads as "the button is broken" — so say so, and show the
// value so it can still be selected by hand.
const copyText = async (value, what = 'Value') => {
  const s = String(value == null ? '' : value);
  if (!s) return;
  try {
    if (!navigator.clipboard || !navigator.clipboard.writeText) throw new Error('unavailable');
    await navigator.clipboard.writeText(s);
    toast.success(`${what} copied`);
  } catch { toast.error(`Could not copy — ${s}`); }
};

const APPROVAL = { pending: 'badge-gray', approved: 'badge-green', declined: 'badge-red' };
// The Invoices register's own status chips — same keys, same words, same order (ServiceEntryTable
// TABS) — so a shortcut on the dashboard lands on the chip it names. 'created' is left out: the
// "Awaiting approval" stage of Money in motion already opens the register on it.
const DASH_STATUS_LINKS = [
  ['extract_generated', 'AI draft'], ['approved', 'Approved'], ['claimed', 'Claimed'], ['claim_error', 'Claim error'],
  ['paid', 'Reconciled'], ['provider_paid', 'Provider paid'], ['on_hold', 'On hold'],
];
// The page header each screen carries: where it sits in the sidebar (group), its name, and one line
// on what the screen is FOR. Every screen used to open with the same "Plan Management" banner and
// then title itself again inside its own card — centred on some, beside an icon on others, as a bare
// heading on a third kind — so the one thing the header should say, "you are here", it never did.
// Titles match the sidebar labels exactly, so the word you clicked is the word you land on.
// The dashboard is the module's front page, so it has no group: no breadcrumb, just its name.
const PM_VIEW_META = {
  dashboard: { title: 'Dashboard', desc: 'What needs you today and where the money is.' },
  'service-entry': { group: 'Workspace', title: 'Invoices', desc: 'Every provider invoice from receipt to provider paid. Click a row to open it in place.' },
  entry: { group: 'Workspace', title: 'New Invoice', desc: 'Provider, participant and contract first, then the lines, checked against the budget as you type.' },
  mail: { group: 'Workspace', title: 'Mailbox', desc: 'Provider invoices and replies, read and answered without leaving the ledger.' },
  queues: { group: 'Workspace', title: 'Review Queues', desc: 'Emails that could not be processed on their own, and notifications waiting on someone.' },
  participants: { group: 'Workspace', title: 'Client List', desc: 'Every plan-managed participant, their portal access and what has been invoiced.' },
  claims: { group: 'Claiming', title: 'NDIA Claims', desc: 'Bundle approved invoices into a claim, lodge it with the NDIA, then upload the results file.' },
  payments: { group: 'Claiming', title: 'Pay Providers', desc: 'Build the bank ABA file for reconciled invoices. Generating it marks them provider paid.' },
  reconcile: { group: 'Claiming', title: 'Reconcile', desc: 'Match the NDIA payment file to your claimed invoices. Anything that does not match is flagged, never skipped.' },
  downloads: { group: 'Claiming', title: 'Downloads', desc: 'Every claim and ABA file generated, who took it, and how many times.' },
  alerts: { group: 'Insights', title: 'Alerts', desc: 'What needs attention before the next claim or payment run. Open a row to go where it is fixed.' },
  reports: { group: 'Insights', title: 'Audit & Reports', desc: 'Throughput, turnaround, exceptions and anomalies across the whole pipeline.' },
  'pm-reports': { group: 'Insights', title: 'Reports', desc: 'Operational reports measured from the audit trail, each exportable as CSV.' },
  registers: { group: 'Insights', title: 'Registers', desc: 'Fees, statements, payment batches and roles: the records an auditor asks for.' },
  powerview: { group: 'Insights', title: 'Powerview', desc: 'Every report in one place, plus a custom column builder for your own exports.' },
  providers: { group: 'Setup', title: 'Service Providers', desc: 'Who you pay. ABN and bank verification decide who is payable today.' },
  statements: { group: 'Setup', title: 'Statements', desc: 'A branded PDF of a participant’s plan budget and invoices. Monthly statements also email on the 1st.' },
  settings: { group: 'Setup', title: 'Settings', desc: 'Fees, bank details, mailbox, safety controls and automation.' },
};

// Money that changes in place rolls to its new value instead of jumping, so a figure that moved
// after an approval or a refresh is noticed. Same en-AU currency format as `money()`; NumberFlow
// honours prefers-reduced-motion on its own.
const MONEY_FORMAT = { style: 'currency', currency: 'AUD', currencyDisplay: 'narrowSymbol', minimumFractionDigits: 2, maximumFractionDigits: 2 };
const MoneyFlow = ({ value }) => <NumberFlow value={Number(value) || 0} locales="en-AU" format={MONEY_FORMAT} />;

// Per-invoice status stepper now comes from the shared kit (ui.jsx StatusStepper): it drives off the
// same PIPELINE, and renders side states (on hold / claim error / declined / cancelled) OFF the path
// rather than pretending they are a stage.
// CareView "Service Entry" — full-width invoice table: filters → multi-select → batch action bar
// with a running total. Reuses the single-invoice endpoints (so all status gates still apply).
// Sentinel — standing compliance/exception alerts queue (CareView).
// Where each alert is fixed. An alert that names a problem but cannot take you to it leaves the
// operator hunting through the sidebar; every row now opens the screen that resolves it.
const ALERT_GO = {
  over_budget: { to: '?view=participants', label: 'Client list', icon: Wallet },
  expiring_plan: { to: '?view=participants', label: 'Client list', icon: Clock },
  rejected_claim: { to: '?view=claims', label: 'NDIA claims', icon: Landmark },
  recon_exception: { to: '?view=reconcile', label: 'Reconcile', icon: Scale },
  duplicate: { to: '?view=service-entry', label: 'Invoices', icon: Copy },
  coi: { to: '?view=service-entry', label: 'Invoices', icon: ShieldAlert },
  missing_doc: { to: '?view=pm-reports&report=missing-evidence', label: 'Missing evidence', icon: Paperclip },
  bank_unverified: { to: '?view=providers', label: 'Providers', icon: Banknote },
  ingest_failed: { to: '?view=queues', label: 'Review queues', icon: Inbox },
  no_agreement: { to: '?view=participants', label: 'Client list', icon: FileText },
};
function SentinelModal({ data, onClose, onRefresh, asPage, onGo }) {
  // Severity reads from the icon well and the group heading, not from tinting whole rows red and
  // amber — a wall of coloured blocks makes every alert shout at the same volume.
  const SEV = {
    high: { label: 'Needs action now', dot: 'bg-neg', text: 'text-rose-700', chip: 'bg-rose-50 text-rose-700 border-rose-100', well: 'bg-rose-50 text-rose-600' },
    medium: { label: 'Worth a look', dot: 'bg-warn', text: 'text-amber-700', chip: 'bg-amber-50 text-amber-800 border-amber-100', well: 'bg-amber-50 text-amber-600' },
    low: { label: 'For your information', dot: 'bg-slate-400', text: 'text-slate-600', chip: 'bg-slate-50 text-slate-600 border-slate-200', well: 'bg-slate-100 text-slate-500' },
  };
  const alerts = data.alerts || [];
  const bySev = { high: [], medium: [], low: [] };
  alerts.forEach((a) => { (bySev[a.severity] || bySev.low).push(a); });
  const counts = { high: bySev.high.length, medium: bySev.medium.length, low: bySev.low.length };
  return (
    <Modal page={asPage} title="Alerts" subtitle="Things that need your attention before claiming or paying" icon={AlertTriangle} wide onClose={onClose}
      footer={<><button onClick={onRefresh} className="btn-secondary text-sm flex items-center gap-1.5"><RefreshCw size={14} /> Refresh</button>{!asPage && <button onClick={onClose} className="btn-secondary text-sm">Close</button>}</>}>
      {alerts.length === 0 ? (
        <EmptyState icon={CheckCircle2} title="All clear" hint="No open alerts. Nothing needs your attention before the next claim or payment run." />
      ) : (
        <div className="space-y-6">
          <div className="flex flex-wrap gap-2">
            {['high', 'medium', 'low'].map((sev) => counts[sev] > 0 && (
              <span key={sev} className={clsx('inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-[12px] font-semibold', SEV[sev].chip)}>
                <span className={clsx('w-1.5 h-1.5 rounded-full', SEV[sev].dot)} aria-hidden="true" /> {counts[sev]} {SEV[sev].label.toLowerCase()}
              </span>
            ))}
          </div>
          {['high', 'medium', 'low'].map((sev) => bySev[sev].length > 0 && (
            <section key={sev} aria-label={SEV[sev].label}>
              <h3 className={clsx('text-[13px] font-semibold mb-2 flex items-center gap-2', SEV[sev].text)}>
                <span className={clsx('w-1.5 h-1.5 rounded-full', SEV[sev].dot)} aria-hidden="true" /> {SEV[sev].label}
                <span className="text-muted font-normal tabular-nums">{bySev[sev].length}</span>
              </h3>
              <ul className="rounded-xl border border-line divide-y divide-line overflow-hidden">
                {bySev[sev].map((a) => {
                  const go = ALERT_GO[a.type];
                  const Icon = (go && go.icon) || AlertTriangle;
                  const body = (
                    <>
                      <span className={clsx('w-9 h-9 rounded-xl flex items-center justify-center shrink-0', SEV[sev].well)}><Icon size={16} aria-hidden="true" /></span>
                      <span className="min-w-0 flex-1">
                        <span className="block text-[13.5px] font-semibold text-ink">{a.title}</span>
                        <span className="block text-[12.5px] text-muted mt-0.5" style={{ textWrap: 'pretty' }}>{a.detail}</span>
                      </span>
                      <span className="font-display text-[20px] font-bold tabular-nums text-ink shrink-0 min-w-[2ch] text-right">{a.count}</span>
                      {go && onGo && (
                        <span className="hidden sm:inline-flex items-center gap-0.5 text-[12px] font-semibold text-primary-600 whitespace-nowrap shrink-0 w-[128px] justify-end">
                          {go.label} <ChevronRight size={14} aria-hidden="true" />
                        </span>
                      )}
                    </>
                  );
                  return (
                    <li key={a.type}>
                      {go && onGo ? (
                        <button type="button" onClick={() => onGo(go.to)}
                          className="w-full flex items-center gap-3.5 px-4 py-3 text-left hover:bg-paper/60 transition-colors duration-150 focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-primary-400">
                          {body}
                        </button>
                      ) : <div className="flex items-center gap-3.5 px-4 py-3">{body}</div>}
                    </li>
                  );
                })}
              </ul>
            </section>
          ))}
        </div>
      )}
    </Modal>
  );
}

// Bulk Invoice File Import — AI-vision OCR provider PDFs → Extract Generated drafts.
function BulkImportModal({ clients, onClose, onDone, onOpenInvoice }) {
  const [files, setFiles] = useState([]);
  const [clientId, setClientId] = useState('');
  const [busy, setBusy] = useState(false);
  const [results, setResults] = useState([]);
  const [drag, setDrag] = useState(false);
  const toB64 = (file) => new Promise((resolve, reject) => { const r = new FileReader(); r.onload = () => resolve(String(r.result).split(',')[1]); r.onerror = reject; r.readAsDataURL(file); });
  const run = async () => {
    if (!files.length) return toast.error('Choose at least one file');
    setBusy(true); const out = [];
    for (const file of files) {
      try {
        const b64 = await toB64(file);
        const r = await api.post('/plan-management/invoices/extract', { file: b64, media_type: file.type, filename: file.name, client_id: clientId || undefined });
        const m = r.data.match || {};
        const newId = r.data?.id || r.data?.invoice_id || null;
        const okMatched = (m.matched || 0) + (m.by_name || 0);
        const bits = [`${m.total ?? (r.data.extracted?.lines || []).length} line(s)`];
        if (m.total) bits.push(`${okMatched}/${m.total} matched`);
        if (m.over_cap) bits.push(`${m.over_cap} over cap`);
        if (m.no_match) bits.push(`${m.no_match} to review`);
        if (r.data.provider_created) bits.push('new provider');
        out.push({ name: file.name, ok: true, id: newId, warn: (m.over_cap || 0) + (m.no_match || 0) > 0, msg: bits.join(' · ') });
      } catch (e) {
        out.push({ name: file.name, ok: false, msg: e.response?.status === 422 ? 'No participant matched — pick one above' : (e.response?.data?.message || 'Failed') });
      }
    }
    setResults(out); setBusy(false);
    const ok = out.filter((o) => o.ok).length;
    if (ok) {
      toast.success(`${ok} invoice(s) imported as Extract Generated`);
      onDone && onDone();
      // Take them to what they just imported. Staying on the import dialog, with the new
      // drafts somewhere behind it, means the next step is a hunt through the list.
      const firstId = out.find((r) => r.ok && r.id)?.id;
      if (firstId && onOpenInvoice) onOpenInvoice(firstId);
      else if (onClose) onClose();
    }
  };
  return (
    <Modal title="Upload provider invoices" subtitle="AI reads each PDF, matches every line to the NDIS price book, and flags anything over the cap" icon={Upload} wide onClose={onClose}
      footer={<><button onClick={onClose} className="btn-secondary text-sm">Close</button><button onClick={run} disabled={busy || !files.length} className="btn-primary text-sm min-w-[130px] flex items-center justify-center gap-2 disabled:opacity-50">{busy ? <Loader2 size={15} className="animate-spin" /> : <Upload size={15} />}{busy ? 'Importing…' : `Import ${files.length || ''}`}</button></>}>
      <div className="space-y-3">
        <div>
          <label className="text-xs font-semibold text-slate-500">Participant</label>
          <select value={clientId} onChange={(e) => setClientId(e.target.value)} className="input-field w-full text-sm mt-1"><option value="">Auto-match by NDIS number</option>{(clients || []).map((c) => <option key={c.id} value={c.id}>{c.first_name} {c.last_name}</option>)}</select>
        </div>
        {/* Provider invoices arrive as a folder of PDFs, so dropping is what people reach
            for first. The click-to-choose path stays — dragging is an addition, not a
            replacement, and it has to keep working for keyboards and screen readers. */}
        <label
          onDragOver={(e) => { e.preventDefault(); setDrag(true); }}
          onDragEnter={(e) => { e.preventDefault(); setDrag(true); }}
          onDragLeave={(e) => { e.preventDefault(); setDrag(false); }}
          onDrop={(e) => {
            e.preventDefault();
            setDrag(false);
            const dropped = Array.from(e.dataTransfer?.files || [])
              // Only what the importer can actually read. Silently accepting a spreadsheet
              // and failing during the import is worse than declining it here.
              .filter((f) => f.type === 'application/pdf' || f.type.startsWith('image/')
                          || /\.(pdf|png|jpe?g|webp|heic)$/i.test(f.name));
            const rejected = (e.dataTransfer?.files?.length || 0) - dropped.length;
            if (rejected > 0) toast.error(`${rejected} file(s) skipped — only PDFs and images can be read`);
            if (dropped.length) setFiles((prev) => [...prev, ...dropped]);
          }}
          className={clsx(
            'block border-2 border-dashed rounded-xl p-8 text-center cursor-pointer transition',
            drag ? 'border-primary-400 bg-primary-50/60' : 'border-slate-200 hover:border-primary-300')}
        >
          <Upload size={26} className={clsx('mx-auto mb-2', drag ? 'text-primary-500' : 'text-slate-300')} />
          <span className="block text-sm font-medium text-slate-600">
            {drag ? 'Drop them here'
                  : files.length ? `${files.length} file(s) ready to import`
                  : 'Drag provider invoices here'}
          </span>
          <span className="block text-xs text-slate-400 mt-1">
            {files.length ? 'Drop more to add them, or click to choose' : 'or click to choose — PDFs and images, several at once'}
          </span>
          <input type="file" accept=".pdf,image/*" multiple
            onChange={(e) => setFiles((prev) => [...prev, ...Array.from(e.target.files || [])])}
            className="sr-only" />
        </label>

        {/* Once files are staged, they have to be removable — dropping the wrong folder
            otherwise means closing the dialog and starting again. */}
        {files.length > 0 && (
          <div className="space-y-1 max-h-40 overflow-y-auto">
            {files.map((f, i) => (
              <div key={f.name + i} className="flex items-center gap-2 text-xs bg-slate-50 rounded-lg px-3 py-1.5">
                <FileText size={13} className="text-slate-400 shrink-0" />
                <span className="flex-1 truncate text-slate-600">{f.name}</span>
                <span className="text-slate-400 shrink-0">{(f.size / 1024).toFixed(0)} KB</span>
                <button onClick={() => setFiles((prev) => prev.filter((_, j) => j !== i))}
                  className="text-slate-400 hover:text-red-500 shrink-0" title="Remove">
                  <X size={13} />
                </button>
              </div>
            ))}
            <button onClick={() => setFiles([])} className="text-[11px] text-slate-400 hover:text-slate-600">Clear all</button>
          </div>
        )}
        {results.length > 0 && (
          <div className="space-y-1 max-h-48 overflow-y-auto">
            {results.map((r, i) => (
              <div key={i} className="flex items-center gap-2 text-xs">
                {!r.ok ? <AlertTriangle size={14} className="text-red-500 shrink-0" /> : r.warn ? <AlertTriangle size={14} className="text-amber-500 shrink-0" /> : <Check size={14} className="text-emerald-500 shrink-0" />}
                <span className="text-slate-600 truncate flex-1">{r.name}</span>
                <span className={clsx('shrink-0', !r.ok ? 'text-red-600' : r.warn ? 'text-amber-600' : 'text-slate-400')}>{r.msg}</span>
              </div>
            ))}
          </div>
        )}
        <p className="text-[11px] text-slate-400">Imported invoices land as <strong>Extract Generated</strong> drafts — review &amp; correct each, then approve. Powered by AI vision; always check the figures.</p>
      </div>
    </Modal>
  );
}

// Inline expanded service-entry row — THE invoice record. Invoice note · attachments (with a drop
// target) · the meta line an operator triages by · a one-line validation verdict that opens into the
// full panel · the line table · the workflow actions. Everything here answers "should this be paid,
// and is it going stale?" without leaving the list — which is the whole reason it expands in place.
// Heavier material (plan context, budgets, line editing, activity) sits behind `onOpenFull`.
//
// Optional props, so the row still works standalone exactly as it did before:
//   `onChanged`  — omit it and attaching simply refreshes this row instead of the list.
//   `hosted`     — the host screen already loads this record; take `detail`/`loading` from it rather
//                  than asking the server for the same invoice a second time.
//   `validation` — the gate list, for the compact verdict line.
//   `actions`    — the workflow action bar (approve / hold / cancel …), built by the host.
//   `onOpenFull` — show the full record in a drawer.
function SEExpandedRow({ id, colSpan, onChanged, hosted = false, detail: detailProp, loading = false, validation, actions, onOpenFull }) {
  const [fetched, setFetched] = useState(null);
  const [docs, setDocs] = useState([]);
  const [drag, setDrag] = useState(false);
  const [saving, setSaving] = useState(false);
  const [reload, setReload] = useState(0);
  const fileRef = useRef(null);

  useEffect(() => {
    if (hosted) return undefined;
    let alive = true;
    api.get(`/plan-management/invoices/${id}`).then((r) => { if (alive) setFetched(r.data); }).catch(() => { if (alive) setFetched(false); });
    return () => { alive = false; };
  }, [id, reload, hosted]);
  useEffect(() => {
    let alive = true;
    // The header carries one `document_url`; the evidence register is what knows the filename, the
    // version and whether the stored hash still matches — so list from there when it has anything.
    api.get(`/plan-management/invoices/${id}/documents`).then((r) => { if (alive) setDocs(r.data || []); }).catch(() => { if (alive) setDocs([]); });
    return () => { alive = false; };
  }, [id, reload]);
  // A hosted record that has not arrived yet (or that belongs to another row mid-switch) reads as
  // "loading", never as another invoice's numbers.
  const d = hosted ? ((detailProp && detailProp.id === id) ? detailProp : null) : fetched;
  const failed = hosted ? false : fetched === false;

  // Attaching is a real PUT of a data: URL — the server hashes and versions it into the evidence
  // register. It REFUSES to swap a document that is already recorded (record retention), and locks
  // claimed/paid invoices outright, so the drop target only appears when a file can actually land.
  const canAttach = !!d && !d.document_url && !['claimed', 'paid'].includes(d.status);
  const take = (file) => {
    if (!file || saving) return;
    if (file.size > MAX_DOC_BYTES) { toast.error(`${file.name} is ${(file.size / 1048576).toFixed(1)} MB — the limit is 9 MB.`); return; }
    const r = new FileReader();
    r.onerror = () => toast.error('Could not read that file');
    r.onload = async () => {
      setSaving(true);
      try {
        await api.put(`/plan-management/invoices/${id}`, { document_url: String(r.result) });
        toast.success(`${file.name} attached`);
        setReload((n) => n + 1);
        onChanged && onChanged();
      } catch (e) { toast.error(e.response?.data?.message || 'Could not attach that file'); } finally { setSaving(false); }
    };
    r.readAsDataURL(file);
  };

  // Age in days from the INVOICE DATE — the number that says an invoice is going stale, and the same
  // measure the aged-work report uses. Colour is the only thing carrying state here.
  const age = d ? daysSince(d.invoice_date) : null;
  const ndiaBits = d ? [d.ndia_status, d.claim_error_code, d.claim_error_reason].filter(Boolean) : [];
  const lines = (d && d.lines) || [];
  const linesTotal = lines.reduce((s, l) => s + Number(l.line_total || 0), 0);
  const files = docs.length ? docs
    : (d && d.document_url ? [{ id: 'header', document_url: d.document_url, filename: 'Invoice document' }] : []);
  const remaining = d && d.plan_remaining ? Number(d.plan_remaining.remaining) : null;
  const paidOut = d ? fmtWhen(d.provider_paid_at) : null;
  const ndiaPaid = d ? fmtWhen(d.ndia_paid_at) : null;

  return (
    <tr className="bg-paper/70"><td colSpan={colSpan} className="px-3 sm:px-4 py-3">
      {!d ? (
        <div className="rounded-2xl border border-line bg-white px-5 py-4 text-[12px] text-muted">{failed ? 'Could not load this invoice.' : 'Loading…'}</div>
      ) : (
        <div className={clsx('rounded-2xl border border-line bg-white shadow-pmcard overflow-hidden transition-opacity duration-150', loading && 'opacity-60')}>
          {/* Where it is, and what it is worth. The lifecycle leads because "what happens next" is
              the question an expanded row is opened to answer; the total sits opposite it. */}
          <div className="px-5 pt-4 pb-3.5 flex flex-wrap items-start justify-between gap-x-6 gap-y-3 border-b border-line">
            <div className="min-w-0 space-y-2">
              <StatusStepper status={d.status} anchorStatus={d.anchorStatus} compact />
              <ValidationSummary gates={validation || []} blocks={d.block_reasons || []} />
            </div>
            <div className="text-right shrink-0">
              <div className="text-[12px] text-muted">Invoice total</div>
              <div className="font-display text-[26px] font-bold tabular-nums text-ink leading-none tracking-[-0.02em] mt-1">{money(lines.length ? linesTotal : d.total_amount)}</div>
              {d.paid_amount != null && ['paid', 'provider_paid'].includes(d.status) && (
                <div className="text-[12px] text-pos font-medium tabular-nums mt-1">NDIA paid {money(d.paid_amount)}</div>
              )}
            </div>
          </div>

          {/* Four facts an operator checks before deciding anything: who, from whom, when, and what
              it does to the plan. Each is a short definition list, so the eye reads label → value. */}
          <div className="grid sm:grid-cols-2 xl:grid-cols-4 divide-y sm:divide-y-0 sm:divide-x divide-line border-b border-line">
            <XGroup icon={User} title="Participant">
              <XFact label="Name"><span className="font-semibold">{`${d.first_name || ''} ${d.last_name || ''}`.trim() || '—'}</span></XFact>
              <XFact label="NDIS number"><span className="tabular-nums">{d.ndis_number || '—'}</span></XFact>
              <XFact label="Approval"><ApprovalChip required={d.pm_approval_required} status={d.approval_status} /></XFact>
            </XGroup>
            <XGroup icon={Building2} title="Provider">
              <XFact label="Name"><span title={d.provider_display || ''}>{d.provider_display || '—'}</span></XFact>
              <XFact label="ABN"><span className="tabular-nums">{d.abn || d.provider_abn || '—'}</span></XFact>
              <XFact label="Their invoice">
                {d.provider_invoice_number ? (
                  <button type="button" onClick={() => copyText(d.provider_invoice_number, 'Invoice number')} title="Copy the provider's invoice number"
                    className="inline-flex items-center gap-1 rounded px-1 -mx-1 tabular-nums hover:bg-slate-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-400">
                    <span className="truncate">{d.provider_invoice_number}</span><Copy size={11} className="shrink-0 text-slate-300" aria-hidden="true" />
                  </button>
                ) : '—'}
              </XFact>
            </XGroup>
            <XGroup icon={Clock} title="Dates">
              <XFact label="Invoice date"><span className="tabular-nums">{fmtD(d.invoice_date)}</span></XFact>
              <XFact label="Service period"><span className="tabular-nums">{fmtRange(d.service_date_from, d.service_date_to)}</span></XFact>
              <XFact label="Age">
                {age == null ? '—' : (
                  <span className={clsx('tabular-nums', age > 14 ? 'text-neg' : age > 7 ? 'text-warn' : 'text-ink')}>{age} day{age === 1 ? '' : 's'}</span>
                )}
              </XFact>
            </XGroup>
            <XGroup icon={Wallet} title="Plan and NDIA">
              <XFact label="Plan remaining">
                {remaining == null ? <span className="text-muted">No plan linked</span>
                  : <span className={clsx('tabular-nums', remaining < 0 ? 'text-neg' : 'text-ink')}>{money(remaining)}</span>}
              </XFact>
              <XFact label="NDIA response"><span className={ndiaBits.length ? 'text-ink' : 'text-muted'} title={ndiaBits.join(' · ')}>{ndiaBits.length ? ndiaBits.join(' · ') : 'None yet'}</span></XFact>
              <XFact label={paidOut ? 'Provider paid' : 'NDIA paid'}>
                <span className={clsx('tabular-nums', (paidOut || ndiaPaid) ? 'text-pos' : 'text-muted')}>{paidOut || ndiaPaid || 'Not yet'}</span>
              </XFact>
            </XGroup>
          </div>

          <div className="px-5 py-4 space-y-4">
            <div className="grid gap-4 md:grid-cols-2">
              <div>
                <div className="text-[12px] font-semibold text-ink-soft mb-1.5">Invoice note</div>
                <div className="rounded-xl border border-line bg-paper/50 px-3 py-2.5 text-[12.5px] text-ink-soft whitespace-pre-wrap min-h-[42px]" style={{ textWrap: 'pretty' }}>
                  {d.notes || <span className="text-muted">No note on this invoice.</span>}
                </div>
                {d.on_hold_reason && <p className="mt-1.5 text-[12px] text-warn">On hold: {d.on_hold_reason}</p>}
                {d.assigned_to && <p className="mt-1.5 text-[12px] text-muted">Assigned to <span className="text-ink-soft font-medium">{d.assigned_to}</span></p>}
              </div>
              <div>
                <div className="text-[12px] font-semibold text-ink-soft mb-1.5">Attachments</div>
                {files.length > 0 && (
                  <ul className="space-y-1 mb-2">
                    {files.map((doc) => (
                      <li key={doc.id}>
                        <a href={doc.document_url} target="_blank" rel="noreferrer"
                          className="flex items-center gap-2 rounded-xl border border-line px-3 py-2 text-[12.5px] hover:border-primary-200 hover:bg-primary-50/40 transition-colors duration-150 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-400">
                          <Paperclip size={13} className="shrink-0 text-muted" aria-hidden="true" />
                          <span className="text-primary-700 font-medium truncate">{doc.filename || 'Invoice document'}</span>
                          {doc.version ? <span className="text-muted shrink-0 tabular-nums">v{doc.version}</span> : null}
                          {doc.verified === false && <span className="text-neg shrink-0" title="The stored hash no longer matches this file">hash mismatch</span>}
                          <span className="ml-auto text-[11.5px] text-muted shrink-0">Open</span>
                        </a>
                      </li>
                    ))}
                  </ul>
                )}
                {canAttach ? (
                  <div onDragOver={(e) => { e.preventDefault(); setDrag(true); }} onDragLeave={() => setDrag(false)}
                    onDrop={(e) => { e.preventDefault(); setDrag(false); take(e.dataTransfer.files && e.dataTransfer.files[0]); }}>
                    <input ref={fileRef} type="file" accept=".pdf,image/*" className="sr-only"
                      onChange={(e) => { take(e.target.files && e.target.files[0]); e.target.value = ''; }} />
                    <button type="button" onClick={() => fileRef.current && fileRef.current.click()} disabled={saving}
                      className={clsx('w-full flex items-center gap-2 rounded-xl border border-dashed px-3 py-2.5 text-[12.5px] font-medium transition-colors duration-150 motion-reduce:transition-none focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-400 disabled:opacity-60',
                        drag ? 'border-primary-400 bg-primary-50 text-primary-700' : 'border-slate-300 text-slate-500 hover:border-primary-300 hover:text-primary-700')}>
                      {saving ? <Loader2 size={14} className="animate-spin shrink-0" /> : <Upload size={14} className="shrink-0" />}
                      <span className="truncate">{saving ? 'Attaching…' : 'Attach the invoice document: drop a file here, or browse'}</span>
                    </button>
                  </div>
                ) : files.length > 0 ? (
                  <p className="text-[11.5px] text-muted">The original is locked for record retention. Use <strong className="font-semibold text-ink-soft">Replace invoice</strong> if the provider reissued it.</p>
                ) : (
                  <p className="text-[11.5px] text-muted">No attachment, and this invoice is locked, so nothing can be attached now.</p>
                )}
              </div>
            </div>

            {/* Line table. Quantity and Hours are separate columns on purpose: the NDIA claim file
                treats them as mutually exclusive, so seeing which one a line carries is the check. */}
            <div>
              <div className="flex items-baseline justify-between gap-3 mb-1.5">
                <div className="text-[12px] font-semibold text-ink-soft">Line items <span className="text-muted font-normal tabular-nums">{lines.length}</span></div>
              </div>
              <div className="overflow-x-auto rounded-xl border border-line">
                <table className="w-full text-[12px]">
                  <thead className="bg-paper/70">
                    <tr className="text-muted text-left text-[10.5px] uppercase tracking-wide">
                      <th className="py-2 pl-3 pr-2 font-semibold w-8">#</th>
                      <th className="py-2 pr-3 font-semibold min-w-[180px]">Service</th>
                      <th className="py-2 pr-3 font-semibold whitespace-nowrap">Service period</th>
                      <th className="py-2 pr-3 font-semibold whitespace-nowrap">Claim type</th>
                      <th className="py-2 pr-3 font-semibold whitespace-nowrap">GST</th>
                      <th className="py-2 pr-3 font-semibold text-right whitespace-nowrap">Quantity</th>
                      <th className="py-2 pr-3 font-semibold text-right whitespace-nowrap">Hours</th>
                      <th className="py-2 pr-3 font-semibold text-right whitespace-nowrap">Rate</th>
                      <th className="py-2 pr-3 font-semibold text-right whitespace-nowrap">Total</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-line">
                    {lines.length === 0 && <tr><td colSpan={9} className="py-3 px-3 text-muted">No lines on this invoice yet.</td></tr>}
                    {lines.map((l, i) => (
                      <tr key={l.id} className="align-top hover:bg-paper/40 transition-colors duration-100">
                        <td className="py-2 pl-3 pr-2 text-muted tabular-nums">{i + 1}</td>
                        <td className="py-2 pr-3">
                          <div className="text-ink font-medium">{l.support_item_name || l.description || l.support_item_code || '—'}</div>
                          {(() => {
                            const sub = [l.support_item_name && l.support_item_code, l.claim_reference && `Ref ${l.claim_reference}`].filter(Boolean).join(' · ');
                            return sub ? <div className="text-[11px] text-muted tabular-nums mt-0.5">{sub}</div> : null;
                          })()}
                        </td>
                        <td className="py-2 pr-3 text-ink-soft whitespace-nowrap tabular-nums">{fmtRange(l.service_date_from || l.item_date, l.service_date_to || l.service_date_from || l.item_date)}</td>
                        {/* A blank claim type IS the standard claim — the file only names the exceptions. */}
                        <td className="py-2 pr-3 text-ink-soft whitespace-nowrap">{l.claim_type || 'Standard'}</td>
                        <td className="py-2 pr-3 text-ink-soft whitespace-nowrap">{l.gst_code || '—'}</td>
                        <td className="py-2 pr-3 text-right tabular-nums text-ink-soft">{l.hours ? <span className="text-slate-300">—</span> : num(l.qty)}</td>
                        <td className="py-2 pr-3 text-right tabular-nums text-ink-soft">{l.hours || <span className="text-slate-300">—</span>}</td>
                        <td className="py-2 pr-3 text-right tabular-nums text-ink-soft">{money(l.unit_price)}</td>
                        <td className="py-2 pr-3 text-right tabular-nums font-semibold text-ink">{money(l.line_total)}</td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr className="border-t border-line bg-paper/50">
                      <td colSpan={8} className="py-2 pr-3 text-right text-[12px] font-semibold text-ink-soft">Invoice total</td>
                      <td className="py-2 pr-3 text-right tabular-nums font-bold text-ink">{money(lines.length ? linesTotal : d.total_amount)}</td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            </div>
          </div>

          {/* The everyday decisions, on the row. Same handlers the full record uses — this is the
              one place an operator needs for approve / hold / claim / cancel. "Open full record"
              is the explicit door to the heavy material: plan context, category budgets, line
              editing and the audit trail. */}
          {(actions || onOpenFull) && (
            <div className="px-5 py-3 border-t border-line bg-paper/50 flex flex-wrap items-center gap-2">
              {actions}
              {onOpenFull && (
                <button type="button" onClick={() => onOpenFull(id)}
                  title="Plan context, category budgets, line editing and the full activity trail"
                  className="btn-secondary text-sm inline-flex items-center gap-1.5">
                  <Maximize2 size={14} /> Open full record
                </button>
              )}
            </div>
          )}
        </div>
      )}
    </td></tr>
  );
}

// Parts of the expanded invoice row. Module level, not inside the row's render, so React keeps
// their identity between renders.
function XGroup({ icon: Icon, title, children }) {
  return (
    <div className="px-5 py-3.5 min-w-0">
      <div className="flex items-center gap-1.5 text-[12px] font-semibold text-ink-soft mb-2">
        {Icon && <Icon size={13} className="text-muted" aria-hidden="true" />}{title}
      </div>
      <dl className="space-y-1.5">{children}</dl>
    </div>
  );
}
function XFact({ label, children }) {
  return (
    <div className="flex items-baseline justify-between gap-3 text-[12.5px] min-w-0">
      <dt className="text-muted shrink-0">{label}</dt>
      <dd className="text-ink text-right truncate min-w-0">{children}</dd>
    </div>
  );
}
// Whether the participant has to sign off, and whether they have. "Not required" is a real answer
// (the participant opted out), not a missing one, so it reads neutral rather than empty.
function ApprovalChip({ required, status }) {
  if (required === false) return <span className="text-muted">Not required</span>;
  const s = status || 'pending';
  const tone = s === 'approved' ? 'text-pos' : s === 'declined' ? 'text-neg' : 'text-warn';
  const label = s === 'approved' ? 'Approved' : s === 'declined' ? 'Declined' : 'Waiting on participant';
  return <span className={clsx('inline-flex items-center gap-1.5 font-medium', tone)}><span className="w-1.5 h-1.5 rounded-full bg-current" aria-hidden="true" />{label}</span>;
}

// Plain-English names for the gates. The server publishes these only for gates that FAIL (as
// block_reasons); the passing ones arrive as raw keys, and "price_book" is not what a plan manager
// calls it. Keep in step with GATE_META in backend/routes/planManagement.js.
const GATE_LABEL = {
  participant: 'Participant', price_book: 'Support item', price_cap: 'Price above limit',
  budget: 'Funding available', date: 'Service dates', claim_window: 'Claim time limit',
  claim_expired: 'Claim period expired',
  duplicate: 'Duplicate', attachment: 'Attachment present', provider: 'Provider complete',
  abn: 'ABN', totals: 'Totals', quantity_hours: 'Quantity or hours', gst: 'GST',
  claim_type: 'Claim type', pm_recorded: 'Plan manager recorded',
};
// A gate can PASS and still be worth reading: claim_window is only emitted at all once a line is 90+
// days old, so "passed" there means "old, but not yet too old to claim". Because the server ships
// next steps only for failures, the advisory wording lives here.
const ADVISORY_GATES = {
  claim_window: { nextStep: 'Lodge this claim now — the NDIA queries claims made long after the service date.' },
};

// Validation — every gate runs, but only the ones needing a decision deserve the eye. The panel
// answers "can this invoice move forward, and if not why" in its first line, lists every failure and
// warning expanded with its next step, and folds the passing checks into one quiet, expandable row.
// Failures already arrive from the server shaped for BlockReasonList (label + nextStep + severity +
// re-check date), so this reuses that renderer rather than laying the same facts out a second time.
const gateLabel = (g) => GATE_LABEL[g] || cap(String(g || '').replace(/_/g, ' '));
// One reading of the gates, shared by the full panel and the compact summary line, so the two can
// never disagree about how many checks are outstanding or what they are called.
function summariseGates(gates = [], blocks = []) {
  const byCode = new Map((blocks || []).map((b) => [b.code, b]));
  // Prefer the server's reason (it carries the real next step); a failed gate with no matching
  // reason still gets a line rather than vanishing.
  const issues = (gates || []).filter((g) => !g.passed)
    .map((g) => byCode.get(g.gate) || { code: g.gate, label: gateLabel(g.gate), detail: g.detail, severity: 'block' });
  // The two lists are fetched separately, so a reason with no gate row still shows.
  (blocks || []).forEach((b) => { if (!issues.some((i) => i.code === b.code)) issues.push(b); });
  const advisories = (gates || []).filter((g) => g.passed && ADVISORY_GATES[g.gate])
    .map((g) => ({ code: g.gate, label: gateLabel(g.gate), detail: g.detail, severity: 'warn', nextStep: ADVISORY_GATES[g.gate].nextStep }));
  const passed = (gates || []).filter((g) => g.passed && !ADVISORY_GATES[g.gate]);
  const list = issues.concat(advisories);
  const blocking = list.filter((r) => r.severity === 'block').length;
  const noting = list.length - blocking;
  const allClear = list.length === 0;
  const title = blocking
    ? `Cannot move forward — ${blocking} ${blocking === 1 ? 'check' : 'checks'} to fix${noting ? `, ${noting} to note` : ''}`
    : `Clear to move forward — ${noting} to note`;
  return { list, passed, blocking, noting, allClear, title };
}

// The compact verdict for the expanded row: one line — "2 checks to fix · Attachment, Provider" —
// that opens into the panel above rather than laying the same facts out a second time.
function ValidationSummary({ gates = [], blocks = [], className }) {
  const [open, setOpen] = useState(false);
  const { list, passed, blocking, noting, allClear } = summariseGates(gates, blocks);
  if (!list.length && !passed.length) return null;

  const named = list.slice(0, 3).map((r) => r.label).filter(Boolean);
  const extra = list.length - named.length;
  const counts = allClear
    ? `All ${passed.length} ${passed.length === 1 ? 'check' : 'checks'} passed`
    : [blocking ? `${blocking} ${blocking === 1 ? 'check' : 'checks'} to fix` : '', noting ? `${noting} to note` : ''].filter(Boolean).join(' · ');
  // Literal class strings only — Tailwind cannot see a class name that is built at runtime.
  const tone = blocking ? 'border-rose-200 bg-rose-50/70 text-rose-700 hover:bg-rose-50'
    : noting ? 'border-amber-200 bg-amber-50/70 text-amber-700 hover:bg-amber-50'
      : 'border-emerald-200 bg-emerald-50/70 text-emerald-700 hover:bg-emerald-50';

  return (
    <div className={className}>
      <button type="button" onClick={() => setOpen((v) => !v)} aria-expanded={open}
        className={clsx('w-full flex items-center gap-2 rounded-lg border px-3 py-1.5 text-left text-[11.5px] font-medium transition-colors motion-reduce:transition-none focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-400', tone)}>
        {allClear ? <Check size={13} className="shrink-0" aria-hidden="true" /> : <AlertTriangle size={13} className="shrink-0" aria-hidden="true" />}
        <span className="truncate">
          {counts}{named.length ? ` · ${named.join(', ')}` : ''}{extra > 0 ? ` +${extra}` : ''}
        </span>
        <ChevronDown size={13} className={clsx('ml-auto shrink-0 opacity-60 transition motion-reduce:transition-none', open && 'rotate-180')} />
      </button>
      {open && <div className="mt-2"><ValidationPanel gates={gates} blocks={blocks} /></div>}
    </div>
  );
}

function ValidationPanel({ gates = [], blocks = [] }) {
  const [showPassed, setShowPassed] = useState(false);
  if (!gates.length && !blocks.length) return null;
  const { list, passed, allClear, title } = summariseGates(gates, blocks);

  return (
    <div className="space-y-2">
      {!allClear && <BlockReasonList reasons={list} title={title} />}
      {passed.length > 0 && (
        <div className={clsx('rounded-xl border bg-white', allClear ? 'border-emerald-200' : 'border-slate-200')}>
          <button type="button" onClick={() => setShowPassed((v) => !v)} aria-expanded={showPassed}
            className="w-full flex items-center gap-2 px-4 py-2 text-left rounded-xl focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-primary-400">
            <Check size={13} className={clsx('shrink-0', allClear ? 'text-emerald-600' : 'text-emerald-500')} />
            <span className={clsx('text-[12.5px]', allClear ? 'font-semibold text-emerald-700' : 'text-slate-500')}>
              {allClear
                ? `Clear to move forward — all ${passed.length} checks passed`
                : `${passed.length} ${passed.length === 1 ? 'check' : 'checks'} passed`}
            </span>
            <ChevronDown size={13} className={clsx('ml-auto shrink-0 text-slate-300 transition motion-reduce:transition-none', showPassed && 'rotate-180')} />
          </button>
          {showPassed && (
            <ul className="px-4 pb-3 pt-0.5 grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-1">
              {passed.map((g) => (
                <li key={g.gate} className="flex items-start gap-1.5 text-[11.5px]">
                  <Check size={12} className="text-emerald-400 mt-0.5 shrink-0" aria-hidden="true" />
                  <span className="min-w-0"><span className="font-medium text-slate-600">{gateLabel(g.gate)}</span><span className="text-slate-400"> · {g.detail}</span></span>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}

// "Exclude Provider Paid Invoices" is a MODE, not a filter: an operator turns it on once and expects
// it still on tomorrow. It persists here rather than resetting with the rest of the row.
const SE_EXCLUDE_PAID_KEY = 'pm.serviceEntry.excludeProviderPaid';
const readExcludePaid = () => { try { const v = localStorage.getItem(SE_EXCLUDE_PAID_KEY); return v === null ? true : v === '1'; } catch { return true; } };

// One filter control with its name ABOVE it. Module level on purpose: a component declared inside
// a render body gets a fresh identity every pass, so React would unmount and remount the input it
// wraps — and the client / invoice-number fields would lose focus after every single keystroke.
// `label` takes a node, not just a string, so the period field can hang its date-field selector there.
function FilterField({ label, children }) {
  return (
    <div className="flex flex-col gap-1">
      <span className="text-[10px] uppercase tracking-wide font-semibold text-slate-400 leading-none">{label}</span>
      {children}
    </div>
  );
}

// ─── Powerview ────────────────────────────────────────────────────────────────
// The reference presents reporting as a grid of titled cards with a View report button, split
// Financial / Participant, plus a two-pane custom column builder.
//
// The tiles launch the reports this product ACTUALLY has rather than the reference's own catalogue.
// A card that opens nothing is worse than an absent card: it advertises a report, costs a click,
// and teaches the operator not to trust the grid.
const PV_TILES = [
  { group: 'Financial', key: 'turnaround', title: 'Turnaround', blurb: 'Business days per stage against the provider commitment', icon: Clock, to: '?view=pm-reports&report=turnaround' },
  { group: 'Financial', key: 'aged', title: 'Aged Work', blurb: 'Approved but unclaimed, and claims with no NDIA result', icon: Hourglass, to: '?view=pm-reports&report=aged' },
  { group: 'Financial', key: 'claim_errors', title: 'Claim Errors', blurb: 'Grouped by reason and provider to expose a systemic fault', icon: AlertTriangle, to: '?view=pm-reports&report=claim-errors' },
  { group: 'Financial', key: 'provider_payments', title: 'Provider Payments', blurb: 'Batches, remittance, and money held by unverified details', icon: Banknote, to: '?view=pm-reports&report=provider-payments' },
  { group: 'Financial', key: 'budget_reconciliation', title: 'Budget Reconciliation', blurb: 'Plans whose available balance moved, and who is overcommitted', icon: Scale, to: '?view=pm-reports&report=budget-reconciliation' },
  { group: 'Financial', key: 'missing_evidence', title: 'Missing Evidence', blurb: 'Claims with no invoice document behind them', icon: FileText, to: '?view=pm-reports&report=missing-evidence' },
  { group: 'Financial', title: 'Audit Export', blurb: 'The trail produced for an NDIA or Commission request', icon: Eye, to: '?view=pm-reports&report=audit-export' },
  { group: 'Financial', key: 'fees', title: 'Fee Register', blurb: 'Plan-management fees by participant and month, with exceptions', icon: Calculator, to: '?view=registers&reg=fees' },
  { group: 'Financial', key: 'batches', title: 'Payment Batches', blurb: 'Every ABA file generated, released and settled', icon: Landmark, to: '?view=registers&reg=batches' },
  { group: 'Financial', title: 'Oversight', blurb: 'Throughput, straight-through rate, exceptions and anomalies', icon: BarChart3, to: '?view=reports' },
  { group: 'Participant', key: 'participants', title: 'Client List', blurb: 'Every participant with access, region and invoiced total', icon: Users, to: '?view=participants' },
  { group: 'Participant', key: 'statements', title: 'Statements', blurb: 'Monthly participant statements — generated, due and printed', icon: FileText, to: '?view=registers&reg=statements' },
  { group: 'Participant', key: 'queues', title: 'Review Queues', blurb: 'Held intake and the notifications waiting on somebody', icon: Inbox, to: '?view=queues' },
  { group: 'Participant', key: 'providers', title: 'Service Providers', blurb: 'ABN and bank verification — who is payable today', icon: Building2, to: '?view=providers' },
];

// The two-pane column picker. Available → Add → Selected, with order controls, because the order
// of Selected Columns IS the order of the exported file.
function PowerviewBuilder() {
  const [catalogue, setCatalogue] = useState(null);
  const [dataset, setDataset] = useState('participants');
  const [selected, setSelected] = useState([]);
  const [avail, setAvail] = useState([]);   // highlighted keys in the left pane
  const [chosen, setChosen] = useState([]); // highlighted keys in the right pane
  const [range, setRange] = useState({ from: '', to: '' });
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);
  // Saved reports. A column picker you must rebuild from scratch every time is a demo, not a tool —
  // the monthly export a plan manager actually runs is the same twenty columns each month.
  const [saved, setSaved] = useState([]);
  const loadSaved = useCallback(() => { api.get('/plan-management/reports/saved').then((r) => setSaved(Array.isArray(r.data) ? r.data : [])).catch(() => setSaved([])); }, []);
  useEffect(() => { loadSaved(); }, [loadSaved]);

  useEffect(() => { api.get('/plan-management/reports/custom/catalogue').then((r) => setCatalogue(r.data || [])).catch(() => setCatalogue([])); }, []);
  useEffect(() => { setSelected([]); setAvail([]); setChosen([]); setResult(null); }, [dataset]);

  const ds = (catalogue || []).find((d) => d.key === dataset);
  const cols = ds?.columns || [];
  const label = (k) => (cols.find((c) => c.key === k) || {}).label || k;
  const remaining = cols.filter((c) => !selected.includes(c.key));

  const add = () => { setSelected((s) => [...s, ...avail.filter((k) => !s.includes(k))]); setAvail([]); };
  const removeSel = () => { setSelected((s) => s.filter((k) => !chosen.includes(k))); setChosen([]); };
  const move = (dir) => {
    setSelected((s) => {
      const out = [...s];
      const idx = out.map((k, i) => (chosen.includes(k) ? i : -1)).filter((i) => i >= 0);
      const order = dir < 0 ? idx : [...idx].reverse();
      order.forEach((i) => {
        const j = i + dir;
        if (j < 0 || j >= out.length || chosen.includes(out[j])) return;
        [out[i], out[j]] = [out[j], out[i]];
      });
      return out;
    });
  };
  const reset = () => { setSelected([]); setAvail([]); setChosen([]); setResult(null); setRange({ from: '', to: '' }); };
  const run = async () => {
    if (!selected.length) return toast.error('Add at least one column');
    setBusy(true);
    try {
      const r = await api.post('/plan-management/reports/custom', { dataset, columns: selected, from: range.from || undefined, to: range.to || undefined });
      setResult(r.data);
      if (r.data?.truncated) toast(`Showing the first ${r.data.limit} rows — narrow the dates for the full set`, { icon: 'ℹ️' });
    } catch (e) { toast.error(e.response?.data?.message || 'Could not build that report'); } finally { setBusy(false); }
  };
  const exportCsv = () => {
    if (!result) return;
    const esc = (s) => { s = String(s == null ? '' : s); return /[",\n]/.test(s) ? '"' + s.replace(/"/g, '""') + '"' : s; };
    const out = [result.columns.map((c) => esc(c.label)).join(',')].concat(result.rows.map((r) => r.map(esc).join(',')));
    dl(out.join('\r\n'), `${dataset}-report-${new Date().toISOString().slice(0, 10)}.csv`, 'text/csv');
  };

  const saveReport = async () => {
    if (!selected.length) return toast.error('Add at least one column first');
    const name = window.prompt('Save this report as:', '');
    if (!name || !name.trim()) return;
    try {
      await api.post('/plan-management/reports/saved', { name: name.trim(), dataset, columns: selected, date_from: range.from || null, date_to: range.to || null });
      toast.success('Report saved'); loadSaved();
    } catch (e) { toast.error(e.response?.data?.message || 'Could not save'); }
  };
  const openSaved = (r) => {
    setDataset(r.dataset);
    // setDataset clears the selection on the next tick, so the columns are applied after it.
    setTimeout(() => {
      setSelected(Array.isArray(r.columns) ? r.columns : []);
      setRange({ from: (r.date_from || '').slice(0, 10), to: (r.date_to || '').slice(0, 10) });
      setResult(null);
    }, 0);
  };
  const deleteSaved = async (r) => {
    if (!window.confirm(`Delete the saved report "${r.name}"?`)) return;
    try { await api.delete(`/plan-management/reports/saved/${r.id}`); toast.success('Deleted'); loadSaved(); } catch (e) { toast.error(e.response?.data?.message || 'Could not delete that saved report'); }
  };

  const paneBtn = (active) => clsx('w-full text-left px-2.5 py-1.5 text-[12.5px] rounded transition-colors motion-reduce:transition-none',
    active ? 'bg-primary-600 text-white' : 'text-slate-600 hover:bg-slate-100');
  const toggle = (list, setList) => (k, e) => {
    // Ctrl/Cmd or shift keeps the existing picks — a 33-column list is painful one at a time.
    if (e.ctrlKey || e.metaKey || e.shiftKey) setList((s) => (s.includes(k) ? s.filter((x) => x !== k) : [...s, k]));
    else setList((s) => (s.length === 1 && s[0] === k ? [] : [k]));
  };

  return (
    <div className="card p-0 overflow-hidden">
      <div className="px-4 py-3 border-b border-slate-100 flex items-center gap-3 flex-wrap">
        <h3 className="text-sm font-bold text-slate-700 flex items-center gap-1.5"><Sliders size={15} className="text-primary-600" /> Custom report</h3>
        <span className="text-[11px] text-slate-400">Pick the columns you want, in the order you want them.</span>
        <span className="flex-1" />
        <button onClick={reset} className="btn-secondary !text-[11px] !px-2.5 !py-1.5">Reset</button>
        <button onClick={saveReport} disabled={!selected.length} className="btn-secondary !text-[11px] !px-2.5 !py-1.5 disabled:opacity-40 flex items-center gap-1"><Check size={12} /> Save</button>
        <button onClick={run} disabled={busy || !selected.length} className="btn-primary !text-xs !px-3 !py-1.5 flex items-center gap-1.5 disabled:opacity-40">
          {busy ? <Loader2 size={13} className="animate-spin" /> : <ChevronRight size={13} />} Continue
        </button>
      </div>

      {saved.length > 0 && (
        <div className="px-4 py-2.5 border-b border-slate-100 flex flex-wrap items-center gap-2">
          <span className="text-[10px] uppercase tracking-wide font-semibold text-slate-400">Saved</span>
          {saved.map((r) => (
            <span key={r.id} className="inline-flex items-center rounded-lg border border-slate-200 bg-white overflow-hidden">
              <button onClick={() => openSaved(r)} title={`${r.dataset} · ${(r.columns || []).length} columns${r.created_by_name ? ` · saved by ${r.created_by_name}` : ''}`}
                className="px-2.5 py-1 text-[11.5px] text-slate-600 hover:bg-slate-50">{r.name}</button>
              <button onClick={() => deleteSaved(r)} aria-label={`Delete ${r.name}`} title="Delete this saved report"
                className="px-1.5 py-1 text-slate-300 hover:text-rose-600 hover:bg-rose-50 border-l border-slate-100"><X size={11} /></button>
            </span>
          ))}
        </div>
      )}

      <div className="px-4 py-3 border-b border-slate-100 bg-slate-50/60 flex flex-wrap items-end gap-2">
        <FilterField label="Report on">
          <select value={dataset} onChange={(e) => setDataset(e.target.value)} aria-label="Dataset"
            className="border border-slate-200 rounded-lg px-2.5 py-1.5 text-xs bg-white w-48">
            {(catalogue || []).map((d) => <option key={d.key} value={d.key}>{d.label}</option>)}
          </select>
        </FilterField>
        <FilterField label="From">
          <input type="date" value={range.from} onChange={(e) => setRange((r) => ({ ...r, from: e.target.value }))} aria-label="From"
            className="border border-slate-200 rounded-lg px-2.5 py-1.5 text-xs bg-white tabular-nums w-[132px]" />
        </FilterField>
        <FilterField label="To">
          <input type="date" value={range.to} onChange={(e) => setRange((r) => ({ ...r, to: e.target.value }))} aria-label="To"
            className="border border-slate-200 rounded-lg px-2.5 py-1.5 text-xs bg-white tabular-nums w-[132px]" />
        </FilterField>
        <span className="text-[11px] text-slate-400 pb-1.5">Dates filter on when the record was created. Leave blank for everything.</span>
      </div>

      {catalogue === null ? <div className="py-10 flex justify-center"><Loader2 className="animate-spin text-slate-300" /></div> : (
        <div className="p-4 grid grid-cols-1 md:grid-cols-[1fr_auto_1fr] gap-3 items-stretch">
          <div className="min-w-0">
            <p className="text-[10px] uppercase tracking-wide font-semibold text-slate-400 mb-1.5">Available Columns ({remaining.length})</p>
            <div className="border border-slate-200 rounded-xl bg-white h-64 overflow-y-auto p-1">
              {remaining.length === 0 ? <p className="text-[12px] text-slate-300 text-center py-8">All columns selected</p>
                : remaining.map((c) => (
                  <button key={c.key} type="button" onClick={(e) => toggle(avail, setAvail)(c.key, e)} className={paneBtn(avail.includes(c.key))}>{c.label}</button>
                ))}
            </div>
          </div>

          <div className="flex md:flex-col items-center justify-center gap-2">
            <button onClick={add} disabled={!avail.length} title="Add to the report"
              className="btn-secondary !text-[11px] !px-3 !py-1.5 disabled:opacity-40 flex items-center gap-1"><ChevronRight size={12} /> Add</button>
            <button onClick={removeSel} disabled={!chosen.length} title="Remove from the report"
              className="btn-secondary !text-[11px] !px-3 !py-1.5 disabled:opacity-40 flex items-center gap-1"><ChevronRight size={12} className="rotate-180" /> Remove</button>
            <span className="hidden md:block h-2" />
            <button onClick={() => move(-1)} disabled={!chosen.length} title="Move up"
              className="btn-secondary !text-[11px] !px-3 !py-1.5 disabled:opacity-40"><ChevronDown size={12} className="rotate-180" /></button>
            <button onClick={() => move(1)} disabled={!chosen.length} title="Move down"
              className="btn-secondary !text-[11px] !px-3 !py-1.5 disabled:opacity-40"><ChevronDown size={12} /></button>
          </div>

          <div className="min-w-0">
            <p className="text-[10px] uppercase tracking-wide font-semibold text-slate-400 mb-1.5">Selected Columns ({selected.length}) — this is the column order</p>
            <div className="border border-slate-200 rounded-xl bg-white h-64 overflow-y-auto p-1">
              {selected.length === 0 ? <p className="text-[12px] text-slate-300 text-center py-8">Nothing selected yet — pick from the left and press Add</p>
                : selected.map((k, i) => (
                  <button key={k} type="button" onClick={(e) => toggle(chosen, setChosen)(k, e)} className={paneBtn(chosen.includes(k))}>
                    <span className="tabular-nums text-[10px] opacity-50 mr-1.5">{i + 1}</span>{label(k)}
                  </button>
                ))}
            </div>
          </div>
        </div>
      )}

      {result && (
        <div className="border-t border-slate-100">
          <div className="px-4 py-2 flex items-center gap-3 flex-wrap bg-slate-50/60">
            <span className="text-[11.5px] text-slate-500">
              <span className="font-bold tabular-nums text-slate-800">{result.rows.length}</span> row{result.rows.length === 1 ? '' : 's'}
              {result.truncated && <span className="text-amber-600"> · capped at {result.limit}, narrow the dates for the rest</span>}
            </span>
            <span className="flex-1" />
            <button onClick={exportCsv} disabled={!result.rows.length} className="btn-primary !text-xs !px-3 !py-1.5 flex items-center gap-1.5 disabled:opacity-40"><Download size={13} /> Export CSV</button>
          </div>
          {result.rows.length === 0 ? <p className="text-sm text-slate-400 py-8 text-center">No rows matched.</p> : (
            <div className="overflow-x-auto max-h-[50vh]">
              <table className="w-full text-[12.5px]">
                <thead className="bg-slate-700 text-[10px] uppercase tracking-wide text-slate-200 sticky top-0">
                  <tr>{result.columns.map((c) => <th key={c.key} className="px-2.5 py-2 font-semibold text-left whitespace-nowrap">{c.label}</th>)}</tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {result.rows.slice(0, 200).map((r, i) => (
                    <tr key={i} className="hover:bg-slate-50">
                      {r.map((v, j) => <td key={j} className="px-2.5 py-1.5 text-slate-600"><span className="block max-w-[240px] truncate" title={v == null ? '' : String(v)}>{v == null || v === '' ? <span className="text-slate-300">—</span> : String(v)}</span></td>)}
                    </tr>
                  ))}
                </tbody>
              </table>
              {result.rows.length > 200 && <p className="text-[11px] text-slate-400 px-4 py-2">Previewing the first 200 rows. Export CSV for all {result.rows.length}.</p>}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// Counts that represent WORK WAITING read amber when non-zero; the rest are plain inventory.
// A participant count of 39 is a fact, four invoices with no evidence behind them is a queue.
const PV_ALERT = new Set(['aged', 'claim_errors', 'provider_payments', 'missing_evidence', 'budget_reconciliation', 'fees', 'batches', 'statements', 'queues']);

function PowerviewView({ onGo }) {
  const groups = ['Financial', 'Participant'];
  // One round trip for the whole grid. Fourteen report calls to paint a landing screen would cost
  // more than the reports themselves.
  const [summary, setSummary] = useState(null);
  useEffect(() => { api.get('/plan-management/reports/summary').then((r) => setSummary(r.data || {})).catch(() => setSummary({})); }, []);
  return (
    <div className="space-y-4">
      <div className="card p-0 overflow-hidden">
        <div className="p-4 space-y-5">
          {groups.map((g) => (
            <div key={g}>
              <p className="text-[10px] uppercase tracking-wide font-semibold text-slate-400 mb-2">{g}</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
                {PV_TILES.filter((t) => t.group === g).map((t) => {
                  const Ic = t.icon;
                  return (
                    <div key={t.title} className="rounded-xl border border-slate-200 bg-white p-3.5 flex flex-col gap-2 hover:shadow-sm transition-shadow motion-reduce:transition-none">
                      <div className="flex items-start gap-2.5 min-w-0">
                        <span className="w-8 h-8 rounded-lg bg-primary-50 text-primary-600 flex items-center justify-center shrink-0"><Ic size={15} /></span>
                        <div className="min-w-0 flex-1">
                          <div className="flex items-baseline gap-2">
                            <span className="text-[13px] font-semibold text-slate-800 leading-tight">{t.title}</span>
                            {summary && t.key && summary[t.key] != null && (
                              <span className={clsx('text-[15px] font-extrabold tabular-nums leading-none ml-auto',
                                PV_ALERT.has(t.key) && Number(summary[t.key]) > 0 ? 'text-amber-600' : 'text-slate-700')}>
                                {Number(summary[t.key]).toLocaleString()}
                              </span>
                            )}
                          </div>
                          <div className="text-[11px] text-slate-400 leading-snug mt-0.5">{t.blurb}</div>
                        </div>
                      </div>
                      <button onClick={() => onGo(t.to)} className="btn-secondary !text-[11px] !px-2.5 !py-1.5 self-start mt-auto">View report</button>
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      </div>
      <PowerviewBuilder />
    </div>
  );
}

// ─── Service Providers ────────────────────────────────────────────────────────
// The reference puts the ABN's verification tick right in the list, which is the point of the
// screen: before an ABA run you want to see at a glance who is actually payable. We carry two
// separate checks and they mean different things, so both get a column — a verified ABN says the
// entity is real, a verified bank account says the money will reach it.
//
// "Provider Code" on the reference is their own PRV- sequence. Ours is the NDIS registration
// number, which is the code that actually matters on a claim, so that is what the column holds.
function ProviderListTable({ rows, loading, onOpen, onNew, onRefresh }) {
  const [q, setQ] = useState('');
  const [f, setF] = useState({ registration: '', bank: '', status: 'active' });
  const [relatedOnly, setRelatedOnly] = useState(false);
  const [payableOnly, setPayableOnly] = useState(false);
  const [sort, setSort] = useState({ key: 'name', dir: 'asc' });
  const [pageSize, setPageSize] = useState(25);
  const [page, setPage] = useState(1);
  const setFf = (k) => (e) => { setF((s) => ({ ...s, [k]: e.target.value })); setPage(1); };
  const all = rows || [];

  const norm = (s) => String(s || '').toLowerCase();
  const abnOk = (p) => p.abn_status === 'verified' || !!p.abn_verified_at;
  const bankOn = (p) => !!(p.bank_bsb && p.bank_account);
  // Payable means every gate the ABA writer applies: bank present, bank verified, not excluded.
  const payable = (p) => bankOn(p) && p.bank_verified === true && !p.exclude_from_aba;
  const filtered = all.filter((p) => {
    if (q) {
      const hay = [p.name, p.trading_name, p.abn, p.contact_person, p.email, p.phone, p.ndis_reg_number].map(norm).join(' ');
      if (!hay.includes(norm(q))) return false;
    }
    if (f.registration === 'registered' && !p.ndis_reg_number) return false;
    if (f.registration === 'unregistered' && p.ndis_reg_number) return false;
    if (f.bank === 'verified' && p.bank_verified !== true) return false;
    if (f.bank === 'unverified' && (p.bank_verified === true || !bankOn(p))) return false;
    if (f.bank === 'none' && bankOn(p)) return false;
    if (f.status === 'active' && p.is_active === false) return false;
    if (f.status === 'inactive' && p.is_active !== false) return false;
    if (relatedOnly && !p.related_party) return false;
    if (payableOnly && !payable(p)) return false;
    return true;
  });
  const cmp = (a, b) => {
    const get = {
      name: (p) => norm(p.name), abn: (p) => norm(p.abn), contact: (p) => norm(p.contact_person),
      email: (p) => norm(p.email), reg: (p) => norm(p.ndis_reg_number),
      invoices: (p) => Number(p.invoices) || 0, total: (p) => Number(p.total) || 0, clients: (p) => Number(p.clients) || 0,
    }[sort.key] || ((p) => norm(p.name));
    const x = get(a); const y = get(b);
    const d = typeof x === 'number' ? x - y : String(x).localeCompare(String(y));
    return sort.dir === 'asc' ? d : -d;
  };
  const sorted = [...filtered].sort(cmp);
  const pageCount = Math.max(1, Math.ceil(sorted.length / pageSize));
  const curPage = Math.min(page, pageCount);
  const pageRows = sorted.slice((curPage - 1) * pageSize, curPage * pageSize);
  const activeCount = all.filter((p) => p.is_active !== false).length;
  const payableCount = all.filter((p) => p.is_active !== false && payable(p)).length;
  const anyFilter = !!(q || f.registration || f.bank || relatedOnly || payableOnly || f.status !== 'active');
  const clearF = () => { setQ(''); setF({ registration: '', bank: '', status: 'active' }); setRelatedOnly(false); setPayableOnly(false); setPage(1); };
  const toggleSort = (k) => { setSort((s) => ({ key: k, dir: s.key === k && s.dir === 'asc' ? 'desc' : 'asc' })); setPage(1); };

  const downloadReport = () => {
    const esc = (s) => { s = String(s == null ? '' : s); return /[",\n]/.test(s) ? '"' + s.replace(/"/g, '""') + '"' : s; };
    const hdr = ['Name', 'ABN', 'ABN Verified', 'Billing Contact', 'Phone', 'Email', 'BSB', 'Account Number', 'Account Name', 'NDIS Registration', 'Bank Verified', 'Clients', 'Invoices', 'Total'];
    const out = [hdr.join(',')].concat(sorted.map((p) => [p.name, p.abn, abnOk(p) ? 'Yes' : 'No', p.contact_person, p.phone, p.email, p.bank_bsb, p.bank_account, p.bank_account_name, p.ndis_reg_number, p.bank_verified === true ? 'Yes' : 'No', p.clients, p.invoices, p.total].map(esc).join(',')));
    dl(out.join('\r\n'), `service-providers-${new Date().toISOString().slice(0, 10)}.csv`, 'text/csv');
  };

  const I = 'border border-slate-200 rounded-lg px-2.5 py-1.5 text-xs bg-white';
  const pageNums = (() => {
    if (pageCount <= 7) return Array.from({ length: pageCount }, (_, i) => i + 1);
    const out = [1];
    const lo = Math.max(2, curPage - 1), hi = Math.min(pageCount - 1, curPage + 1);
    if (lo > 2) out.push('…');
    for (let n = lo; n <= hi; n++) out.push(n);
    if (hi < pageCount - 1) out.push('…');
    out.push(pageCount);
    return out;
  })();
  const SortHead = ({ k, label, align, className }) => (
    <th className={clsx('px-2 py-1.5 font-semibold whitespace-nowrap select-none', align === 'right' ? 'text-right' : 'text-left', className)}
      aria-sort={sort.key === k ? (sort.dir === 'asc' ? 'ascending' : 'descending') : 'none'}>
      <button type="button" onClick={() => toggleSort(k)}
        className={clsx('inline-flex items-center gap-1 uppercase tracking-wide hover:text-white rounded px-0.5 -mx-0.5 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-300', align === 'right' && 'flex-row-reverse')}>
        {label}{sort.key === k && <ChevronDown size={11} className={clsx('transition motion-reduce:transition-none', sort.dir === 'asc' && 'rotate-180')} />}
      </button>
    </th>
  );
  const Totals = ({ edge }) => (
    <div className={clsx('px-3 py-1.5 flex flex-wrap items-center gap-x-5 gap-y-1 text-[11.5px] text-slate-500', edge)}>
      <label className="flex items-center gap-1.5">
        <span>Page Size</span>
        <select value={pageSize} onChange={(e) => { setPageSize(Number(e.target.value)); setPage(1); }} aria-label="Page size"
          className="border border-slate-200 rounded-lg px-2 py-1 text-[11px] bg-white focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-400">
          {[25, 50, 100, 250].map((n) => <option key={n} value={n}>{n}</option>)}
        </select>
      </label>
      <span>Total Items <span className="font-bold tabular-nums text-slate-800">({sorted.length})</span></span>
      <span className="flex-1" />
      <nav className="flex items-center gap-1" aria-label="Pagination">
        {pageCount > 1 && (
          <button onClick={() => setPage(Math.max(1, curPage - 1))} disabled={curPage <= 1} aria-label="Previous page"
            className="w-6 h-6 rounded border border-slate-200 bg-white text-slate-500 hover:bg-slate-50 disabled:opacity-40 flex items-center justify-center focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-400"><ChevronRight size={12} className="rotate-180" /></button>
        )}
        {pageNums.map((n, i) => (n === '…'
          ? <span key={`gap${i}`} className="px-1 text-slate-300" aria-hidden="true">…</span>
          : (
            <button key={n} onClick={() => setPage(n)} aria-label={`Page ${n}`} aria-current={n === curPage ? 'page' : undefined}
              className={clsx('min-w-[24px] h-6 px-1.5 rounded border text-[11px] font-medium tabular-nums focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-400',
                n === curPage ? 'bg-primary-600 border-primary-600 text-white' : 'border-slate-200 bg-white text-slate-500 hover:bg-slate-50')}>{n}</button>
          )))}
        {pageCount > 1 && (
          <button onClick={() => setPage(Math.min(pageCount, curPage + 1))} disabled={curPage >= pageCount} aria-label="Next page"
            className="w-6 h-6 rounded border border-slate-200 bg-white text-slate-500 hover:bg-slate-50 disabled:opacity-40 flex items-center justify-center focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-400"><ChevronRight size={12} /></button>
        )}
      </nav>
    </div>
  );

  return (
    <div className="card p-0 overflow-hidden">
      <div className="px-3 py-2.5 border-b border-slate-100 grid grid-cols-[1fr_auto_1fr] items-center gap-2">
        <div className="flex items-center gap-2 min-w-0">
          <button onClick={onNew} className="btn-primary !text-xs !px-3 !py-1.5 flex items-center gap-1.5"><Plus size={14} /> Create New</button>
        </div>
        {/* Both counts, because they answer different questions: how many providers we deal with,
            and how many of them a pay run would actually pay today. They sit where the screen's
            name used to be repeated — the page header names the screen now. */}
        <div className="text-[12.5px] text-muted flex flex-wrap justify-center gap-x-4 gap-y-1 px-2">
          <span><span className="font-semibold tabular-nums text-ink">{activeCount}</span> active providers</span>
          <span title="Bank details present, verified, and not excluded from ABA"><span className={clsx('font-semibold tabular-nums', payableCount < activeCount ? 'text-warn' : 'text-ink')}>{payableCount}</span> payable today</span>
        </div>
        <div className="flex items-center gap-2 justify-end min-w-0">
          <button onClick={downloadReport} title="Download this filtered list as CSV" className="btn-secondary !text-xs !px-3 !py-1.5 flex items-center gap-1.5"><Download size={14} /> Report</button>
          <button onClick={onRefresh} title="Reload" className="btn-secondary !text-xs !px-3 !py-1.5 flex items-center gap-1.5"><RefreshCw size={14} /> Refresh</button>
        </div>
      </div>

      <div className="px-3 py-2.5 border-b border-slate-100 bg-slate-50/60 space-y-2">
        <div className="flex flex-wrap items-end gap-2">
          <FilterField label="Search">
            <div className="relative">
              <Search size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-300" aria-hidden="true" />
              <input value={q} onChange={(e) => { setQ(e.target.value); setPage(1); }} aria-label="Search providers"
                title="Name, trading name, ABN, contact, email, phone or NDIS registration" className={`${I} pl-8 w-56`} />
            </div>
          </FilterField>
          <FilterField label="Registration">
            <select value={f.registration} onChange={setFf('registration')} aria-label="Registration" className={`${I} w-40`}>
              <option value="">(All)</option>
              <option value="registered">NDIS registered</option>
              <option value="unregistered">Unregistered</option>
            </select>
          </FilterField>
          <FilterField label="Bank">
            <select value={f.bank} onChange={setFf('bank')} aria-label="Bank status" className={`${I} w-40`}>
              <option value="">(Any)</option>
              <option value="verified">Verified</option>
              <option value="unverified">Not verified</option>
              <option value="none">No bank details</option>
            </select>
          </FilterField>
          <FilterField label="Status">
            <select value={f.status} onChange={setFf('status')} aria-label="Status" className={`${I} w-32`}>
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
              <option value="">(Any)</option>
            </select>
          </FilterField>
          <span className="flex-1" />
          <button onClick={clearF} disabled={!anyFilter}
            className="btn-secondary !text-[11px] !px-2.5 !py-1.5 flex items-center gap-1 disabled:opacity-40"><X size={11} /> Clear Filters</button>
        </div>
        <div className="flex flex-wrap items-center gap-x-5 gap-y-1">
          <label className="text-[11px] text-slate-500 flex items-center gap-1.5" title="Bank details present, verified, and not excluded from ABA">
            <input type="checkbox" checked={payableOnly} onChange={(e) => { setPayableOnly(e.target.checked); setPage(1); }} className="focus-visible:ring-2 focus-visible:ring-primary-400" />
            Show Payable Providers Only
          </label>
          <label className="text-[11px] text-slate-500 flex items-center gap-1.5" title="Providers flagged as a related party — a conflict of interest that must be disclosed">
            <input type="checkbox" checked={relatedOnly} onChange={(e) => { setRelatedOnly(e.target.checked); setPage(1); }} className="focus-visible:ring-2 focus-visible:ring-primary-400" />
            Show Related Parties Only
          </label>
        </div>
      </div>

      {!loading && <Totals edge="border-b border-slate-100" />}

      {loading ? <TableSkeleton rows={10} cols={7} />
        : sorted.length === 0 ? <EmptyState icon={Building2} title={anyFilter ? 'No providers match' : 'No service providers yet'} hint={anyFilter ? 'Try a different filter or clear them.' : 'Add a provider to start recording their invoices.'} action={<button onClick={onNew} className="btn-primary text-sm flex items-center gap-1.5"><Plus size={15} /> New provider</button>} />
          : (
            <div className="overflow-x-auto">
              <table className="w-full text-[13px]">
                <thead className="bg-slate-700 text-[10px] uppercase tracking-wide text-slate-200 sticky top-0 z-10">
                  <tr>
                    <th className="pl-3 pr-2 py-1.5 font-semibold text-right w-10">#</th>
                    <SortHead k="name" label="Name" />
                    <SortHead k="abn" label="ABN" />
                    <SortHead k="contact" label="Billing Contact" className={WIDE_ONLY} />
                    <th className={clsx('px-2 py-1.5 font-semibold text-left whitespace-nowrap', WIDE_ONLY)}>Phone</th>
                    <SortHead k="email" label="Email" />
                    <th className="px-2 py-1.5 font-semibold text-left whitespace-nowrap">BSB</th>
                    <th className="px-2 py-1.5 font-semibold text-left whitespace-nowrap">Account Number</th>
                    <th className={clsx('px-2 py-1.5 font-semibold text-left whitespace-nowrap', WIDE_ONLY)}>Account Name</th>
                    <SortHead k="reg" label="NDIS Registration" />
                    <th className="px-2 py-1.5 font-semibold text-center whitespace-nowrap" title="Bank details verified — required before an ABA pay run">Bank</th>
                    <SortHead k="clients" label="Clients" align="right" className={WIDE_ONLY} />
                    <SortHead k="invoices" label="Invoices" align="right" />
                    <SortHead k="total" label="Total" align="right" />
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {pageRows.map((p, i) => (
                    <tr key={p.id} onClick={() => onOpen(p.id)} className="group cursor-pointer hover:bg-slate-50 transition-colors motion-reduce:transition-none">
                      <td className="pl-3 pr-2 py-1.5 text-[11.5px] text-right tabular-nums text-slate-300">{(curPage - 1) * pageSize + i + 1}</td>
                      <td className="px-2 py-1.5">
                        <span className="flex items-center gap-2 min-w-0">
                          <span className="w-6 h-6 rounded-lg bg-slate-100 text-slate-400 flex items-center justify-center shrink-0"><Building2 size={12} /></span>
                          <span className="min-w-0">
                            <span className="block font-semibold text-slate-800 truncate max-w-[190px]">{p.name}</span>
                            {p.trading_name && p.trading_name !== p.name && <span className="block text-[10.5px] text-slate-400 truncate max-w-[190px]">t/a {p.trading_name}</span>}
                          </span>
                          {p.related_party && <span className="text-[9.5px] font-semibold px-1.5 py-0.5 rounded bg-amber-50 text-amber-700 shrink-0" title="Related party — conflict of interest">Related</span>}
                          {p.exclude_from_aba && <span className="text-[9.5px] font-semibold px-1.5 py-0.5 rounded bg-slate-100 text-slate-500 shrink-0" title="Excluded from ABA pay runs — settled another way">No ABA</span>}
                        </span>
                      </td>
                      {/* The tick the reference puts here. It reports the ABN check specifically —
                          not "we have an ABN on file", which is what a bare value would imply. */}
                      <td className="px-2 py-1.5 whitespace-nowrap">
                        {p.abn ? (
                          <span className="inline-flex items-center gap-1.5 text-[12.5px] tabular-nums text-slate-600">
                            {p.abn}
                            {abnOk(p)
                              ? <Check size={13} className="text-emerald-600 shrink-0" aria-label="ABN verified" />
                              : <span className="w-[13px] shrink-0 text-slate-300 text-center" title="ABN not verified" aria-label="ABN not verified">·</span>}
                          </span>
                        ) : p.abn_exemption
                          ? <span className="text-[11px] font-medium text-slate-500" title="Exempt from quoting an ABN">{p.abn_exemption}</span>
                          : <span className="text-rose-400 text-[11.5px]" title="No ABN and no exemption — this provider cannot be claimed">No ABN</span>}
                      </td>
                      <td className={clsx('px-2 py-1.5 text-[12.5px] text-slate-500', WIDE_ONLY)}><span className="block truncate max-w-[130px]">{p.contact_person || <span className="text-slate-300">—</span>}</span></td>
                      <td className={clsx('px-2 py-1.5 text-[12.5px] text-slate-500 whitespace-nowrap tabular-nums', WIDE_ONLY)}>{p.phone || <span className="text-slate-300">—</span>}</td>
                      <td className="px-2 py-1.5 text-[12.5px] text-slate-500"><span className="block truncate max-w-[180px]" title={p.email || ''}>{p.email || <span className="text-slate-300">—</span>}</span></td>
                      <td className="px-2 py-1.5 text-[12.5px] text-slate-500 whitespace-nowrap tabular-nums">{p.bank_bsb || <span className="text-slate-300">—</span>}</td>
                      <td className="px-2 py-1.5 text-[12.5px] text-slate-500 whitespace-nowrap tabular-nums">{p.bank_account || <span className="text-slate-300">—</span>}</td>
                      <td className={clsx('px-2 py-1.5 text-[12.5px] text-slate-500', WIDE_ONLY)}><span className="block truncate max-w-[150px]">{p.bank_account_name || <span className="text-slate-300">—</span>}</span></td>
                      <td className="px-2 py-1.5 text-[12.5px] text-slate-500 whitespace-nowrap tabular-nums">{p.ndis_reg_number || <span className="text-slate-300">Unregistered</span>}</td>
                      <td className="px-2 py-1.5 text-center whitespace-nowrap">
                        {!bankOn(p)
                          ? <span className="text-[10px] font-medium text-slate-400" title="No bank details — cannot be paid by ABA">None</span>
                          : p.bank_verified === true
                            ? <Check size={14} className="inline text-emerald-600" aria-label="Bank verified" />
                            : <span className="text-[10px] font-semibold text-amber-600" title="Bank details not verified — an ABA run will skip this provider">Unverified</span>}
                      </td>
                      <td className={clsx('px-2 py-1.5 text-[12.5px] text-right tabular-nums text-slate-500', WIDE_ONLY)}>{p.clients || <span className="text-slate-300">0</span>}</td>
                      <td className="px-2 py-1.5 text-[12.5px] text-right tabular-nums text-slate-500">{p.invoices || <span className="text-slate-300">0</span>}</td>
                      <td className="px-2 py-1.5 text-right tabular-nums font-semibold text-slate-800 whitespace-nowrap">{money(p.total)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

      {!loading && <Totals edge="border-t border-slate-100" />}
    </div>
  );
}

// ─── Client List ──────────────────────────────────────────────────────────────
// The participant register, built to the same chrome as Service Entry: labelled filters above the
// controls, a dark header band, and the summary bar repeated top and bottom.
//
// Three columns on the reference have no equivalent in our data, so rather than ship empty columns
// they carry the nearest field we actually hold, named for what it is:
//   Middle Name  → Preferred Name  (clients has no middle_name; preferred_name is the useful one)
//   Assigned To  → Team            (client_teams; we assign clients to teams, not individuals)
//   Advantage    → Portal          (pm_portal_accounts — participant app access, 'active' only)
function ClientListTable({ rows, loading, onOpen, onNew, onRefresh }) {
  const [q, setQ] = useState('');
  const [f, setF] = useState({ type: '', service: '', region: '', status: 'active', team: '' });
  const [paceOnly, setPaceOnly] = useState(false);
  const [portalOnly, setPortalOnly] = useState(false);
  const [sort, setSort] = useState({ key: 'last', dir: 'asc' });
  const [pageSize, setPageSize] = useState(25);
  const [page, setPage] = useState(1);
  const setFf = (k) => (e) => { setF((s) => ({ ...s, [k]: e.target.value })); setPage(1); };
  const all = rows || [];

  // Every option list is built from the DATA, not a hardcoded enum: an org that uses regions we
  // never anticipated still gets a working filter, and one that uses none gets no dead dropdown.
  const uniq = (pick) => Array.from(new Set(all.map(pick).filter(Boolean))).sort();
  const types = uniq((c) => c.client_type);
  const services = uniq((c) => c.service_type);
  const regions = uniq((c) => c.service_region);
  const teams = uniq((c) => c.assigned_to);

  const norm = (s) => String(s || '').toLowerCase();
  const filtered = all.filter((c) => {
    if (q) {
      const hay = [c.first_name, c.last_name, c.preferred_name, c.email, c.phone, c.mobile, c.ndis_number].map(norm).join(' ');
      if (!hay.includes(norm(q))) return false;
    }
    if (f.type && c.client_type !== f.type) return false;
    if (f.service && c.service_type !== f.service) return false;
    if (f.region && c.service_region !== f.region) return false;
    if (f.team && c.assigned_to !== f.team) return false;
    if (f.status === 'active' && c.is_active === false) return false;
    if (f.status === 'inactive' && c.is_active !== false) return false;
    if (paceOnly && !c.pace_participant) return false;
    if (portalOnly && c.portal_status !== 'active') return false;
    return true;
  });
  const cmp = (a, b) => {
    const k = sort.key;
    const get = {
      first: (c) => norm(c.first_name), last: (c) => norm(c.last_name), preferred: (c) => norm(c.preferred_name),
      team: (c) => norm(c.assigned_to), ndis: (c) => norm(c.ndis_number), dob: (c) => c.date_of_birth || '',
      service: (c) => norm(c.service_type), region: (c) => norm(c.service_region), email: (c) => norm(c.email),
      invoiced: (c) => Number(c.invoiced) || 0, status: (c) => (c.is_active === false ? 1 : 0),
    }[k] || ((c) => norm(c.last_name));
    const x = get(a); const y = get(b);
    const d = typeof x === 'number' ? x - y : String(x).localeCompare(String(y));
    return sort.dir === 'asc' ? d : -d;
  };
  const sorted = [...filtered].sort(cmp);
  const pageCount = Math.max(1, Math.ceil(sorted.length / pageSize));
  const curPage = Math.min(page, pageCount);
  const pageRows = sorted.slice((curPage - 1) * pageSize, curPage * pageSize);
  const activeCount = all.filter((c) => c.is_active !== false).length;
  const anyFilter = !!(q || f.type || f.service || f.region || f.team || paceOnly || portalOnly || f.status !== 'active');
  const clearF = () => { setQ(''); setF({ type: '', service: '', region: '', status: 'active', team: '' }); setPaceOnly(false); setPortalOnly(false); setPage(1); };
  const toggleSort = (k) => { setSort((s) => ({ key: k, dir: s.key === k && s.dir === 'asc' ? 'desc' : 'asc' })); setPage(1); };

  const downloadReport = () => {
    const esc = (s) => { s = String(s == null ? '' : s); return /[",\n]/.test(s) ? '"' + s.replace(/"/g, '""') + '"' : s; };
    const hdr = ['First Name', 'Preferred Name', 'Last Name', 'Team', 'NDIS Number', 'DOB', 'Service Type', 'Service Region', 'Email', 'Status', 'PACE', 'Portal', 'Invoiced'];
    const out = [hdr.join(',')].concat(sorted.map((c) => [c.first_name, c.preferred_name, c.last_name, c.assigned_to, c.ndis_number, c.date_of_birth ? fmtD(c.date_of_birth) : '', c.service_type, c.service_region, c.email, c.is_active === false ? 'Inactive' : 'Active', c.pace_participant ? 'Yes' : 'No', c.portal_status === 'active' ? 'Yes' : 'No', c.invoiced].map(esc).join(',')));
    dl(out.join('\r\n'), `client-list-${new Date().toISOString().slice(0, 10)}.csv`, 'text/csv');
  };

  const I = 'border border-slate-200 rounded-lg px-2.5 py-1.5 text-xs bg-white';
  const pageNums = (() => {
    if (pageCount <= 7) return Array.from({ length: pageCount }, (_, i) => i + 1);
    const out = [1];
    const lo = Math.max(2, curPage - 1), hi = Math.min(pageCount - 1, curPage + 1);
    if (lo > 2) out.push('…');
    for (let n = lo; n <= hi; n++) out.push(n);
    if (hi < pageCount - 1) out.push('…');
    out.push(pageCount);
    return out;
  })();
  const SortHead = ({ k, label, align, className }) => (
    <th className={clsx('px-2 py-1.5 font-semibold whitespace-nowrap select-none', align === 'right' ? 'text-right' : 'text-left', className)}
      aria-sort={sort.key === k ? (sort.dir === 'asc' ? 'ascending' : 'descending') : 'none'}>
      <button type="button" onClick={() => toggleSort(k)}
        className={clsx('inline-flex items-center gap-1 uppercase tracking-wide hover:text-white rounded px-0.5 -mx-0.5 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-300', align === 'right' && 'flex-row-reverse')}>
        {label}{sort.key === k && <ChevronDown size={11} className={clsx('transition motion-reduce:transition-none', sort.dir === 'asc' && 'rotate-180')} />}
      </button>
    </th>
  );
  const Totals = ({ edge }) => (
    <div className={clsx('px-3 py-1.5 flex flex-wrap items-center gap-x-5 gap-y-1 text-[11.5px] text-slate-500', edge)}>
      <label className="flex items-center gap-1.5">
        <span>Page Size</span>
        <select value={pageSize} onChange={(e) => { setPageSize(Number(e.target.value)); setPage(1); }} aria-label="Page size"
          className="border border-slate-200 rounded-lg px-2 py-1 text-[11px] bg-white focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-400">
          {[25, 50, 100, 250].map((n) => <option key={n} value={n}>{n}</option>)}
        </select>
      </label>
      <span>Total Items <span className="font-bold tabular-nums text-slate-800">({sorted.length})</span></span>
      <span className="flex-1" />
      <nav className="flex items-center gap-1" aria-label="Pagination">
        {pageCount > 1 && (
          <button onClick={() => setPage(Math.max(1, curPage - 1))} disabled={curPage <= 1} aria-label="Previous page"
            className="w-6 h-6 rounded border border-slate-200 bg-white text-slate-500 hover:bg-slate-50 disabled:opacity-40 flex items-center justify-center focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-400"><ChevronRight size={12} className="rotate-180" /></button>
        )}
        {pageNums.map((n, i) => (n === '…'
          ? <span key={`gap${i}`} className="px-1 text-slate-300" aria-hidden="true">…</span>
          : (
            <button key={n} onClick={() => setPage(n)} aria-label={`Page ${n}`} aria-current={n === curPage ? 'page' : undefined}
              className={clsx('min-w-[24px] h-6 px-1.5 rounded border text-[11px] font-medium tabular-nums focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-400',
                n === curPage ? 'bg-primary-600 border-primary-600 text-white' : 'border-slate-200 bg-white text-slate-500 hover:bg-slate-50')}>{n}</button>
          )))}
        {pageCount > 1 && (
          <button onClick={() => setPage(Math.min(pageCount, curPage + 1))} disabled={curPage >= pageCount} aria-label="Next page"
            className="w-6 h-6 rounded border border-slate-200 bg-white text-slate-500 hover:bg-slate-50 disabled:opacity-40 flex items-center justify-center focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-400"><ChevronRight size={12} /></button>
        )}
      </nav>
    </div>
  );

  return (
    <div className="card p-0 overflow-hidden">
      <div className="px-3 py-2.5 border-b border-slate-100 grid grid-cols-[1fr_auto_1fr] items-center gap-2">
        <div className="flex items-center gap-2 min-w-0">
          <button onClick={onNew} className="btn-primary !text-xs !px-3 !py-1.5 flex items-center gap-1.5"><Plus size={14} /> Create New</button>
        </div>
        {/* It counts ACTIVE profiles, which is the number an operator is actually accountable for —
            not the row count, which moves with whatever filter is on. In the toolbar's centre,
            where the screen's name used to be repeated under the page header. */}
        <div className="text-[12.5px] text-muted text-center px-2">
          <span className="font-semibold tabular-nums text-ink">{activeCount}</span> active client profile{activeCount === 1 ? '' : 's'}
        </div>
        <div className="flex items-center gap-2 justify-end min-w-0">
          <button onClick={downloadReport} title="Download this filtered list as CSV" className="btn-secondary !text-xs !px-3 !py-1.5 flex items-center gap-1.5"><Download size={14} /> Report</button>
          <button onClick={onRefresh} title="Reload" className="btn-secondary !text-xs !px-3 !py-1.5 flex items-center gap-1.5"><RefreshCw size={14} /> Refresh</button>
        </div>
      </div>

      <div className="px-3 py-2.5 border-b border-slate-100 bg-slate-50/60 space-y-2">
        <div className="flex flex-wrap items-end gap-2">
          <FilterField label="Search">
            <div className="relative">
              <Search size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-300" aria-hidden="true" />
              <input value={q} onChange={(e) => { setQ(e.target.value); setPage(1); }} aria-label="Search clients"
                title="Name, preferred name, email, phone, mobile or NDIS number" className={`${I} pl-8 w-52`} />
            </div>
          </FilterField>
          <FilterField label="Type">
            <select value={f.type} onChange={setFf('type')} aria-label="Client type" className={`${I} w-36`}>
              <option value="">(Any)</option>
              {types.map((t) => <option key={t} value={t}>{titleCase(t)}</option>)}
            </select>
          </FilterField>
          <FilterField label="Service Type">
            <select value={f.service} onChange={setFf('service')} aria-label="Service type" className={`${I} w-40`}>
              <option value="">(All)</option>
              {services.map((t) => <option key={t} value={t}>{titleCase(t)}</option>)}
            </select>
          </FilterField>
          <FilterField label="Region">
            <select value={f.region} onChange={setFf('region')} aria-label="Service region" className={`${I} w-36`}>
              <option value="">(All)</option>
              {regions.map((t) => <option key={t} value={t}>{t}</option>)}
            </select>
          </FilterField>
          <FilterField label="Status">
            <select value={f.status} onChange={setFf('status')} aria-label="Status" className={`${I} w-32`}>
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
              <option value="">(Any)</option>
            </select>
          </FilterField>
          <FilterField label="Assigned To">
            <select value={f.team} onChange={setFf('team')} aria-label="Assigned team" className={`${I} w-40`}>
              <option value="">(All)</option>
              {teams.map((t) => <option key={t} value={t}>{t}</option>)}
            </select>
          </FilterField>
          <span className="flex-1" />
          <button onClick={clearF} disabled={!anyFilter}
            className="btn-secondary !text-[11px] !px-2.5 !py-1.5 flex items-center gap-1 disabled:opacity-40"><X size={11} /> Clear Filters</button>
        </div>
        {/* These three are MODES, not filters — they change what the list IS rather than what it
            matches — so they sit on their own line, labelled, instead of reading as three more
            filter fields stacked under the real ones. */}
        <div className="flex flex-wrap items-center gap-x-5 gap-y-1 pt-1.5 border-t border-slate-200/70">
          <span className="text-[10px] uppercase tracking-wide font-semibold text-slate-400">View</span>
          <label className="text-[11px] text-slate-500 flex items-center gap-1.5">
            <input type="checkbox" checked={paceOnly} onChange={(e) => { setPaceOnly(e.target.checked); setPage(1); }} className="focus-visible:ring-2 focus-visible:ring-primary-400" />
            Show PACE Clients Only
          </label>
          {/* The reference's second box is "PACE API Clients Only". We have no PACE API (spec 9 is
              blocked on DPO/cyber clearance), so the slot carries the access we DO grant. */}
          <label className="text-[11px] text-slate-500 flex items-center gap-1.5" title="Only participants with an active portal login">
            <input type="checkbox" checked={portalOnly} onChange={(e) => { setPortalOnly(e.target.checked); setPage(1); }} className="focus-visible:ring-2 focus-visible:ring-primary-400" />
            Show Portal Clients Only
          </label>
        </div>
      </div>

      {!loading && <Totals edge="border-b border-slate-100" />}

      {loading ? <TableSkeleton rows={10} cols={7} />
        : sorted.length === 0 ? <EmptyState icon={Users} title={anyFilter ? 'No clients match' : 'No participants yet'} hint={anyFilter ? 'Try a different filter or clear them.' : 'Add a plan-managed participant to get started.'} action={<button onClick={onNew} className="btn-primary text-sm flex items-center gap-1.5"><Plus size={15} /> New participant</button>} />
          : (
            <div className="overflow-x-auto">
              <table className="w-full text-[13px]">
                <thead className="bg-slate-700 text-[10px] uppercase tracking-wide text-slate-200 sticky top-0 z-10">
                  <tr>
                    <th className="pl-3 pr-2 py-1.5 font-semibold text-right w-10">#</th>
                    <SortHead k="first" label="First Name" />
                    <SortHead k="preferred" label="Preferred Name" className={WIDE_ONLY} />
                    <SortHead k="last" label="Last Name" />
                    <SortHead k="team" label="Assigned To" />
                    <SortHead k="ndis" label="NDIS Number" />
                    <SortHead k="dob" label="DOB" />
                    <SortHead k="service" label="Service Type" className={WIDE_ONLY} />
                    <SortHead k="region" label="Service Region" className={WIDE_ONLY} />
                    <SortHead k="email" label="Email" />
                    <SortHead k="invoiced" label="Invoiced" align="right" />
                    <SortHead k="status" label="Status" />
                    <th className="px-2 py-1.5 font-semibold text-center whitespace-nowrap" title="NDIS PACE participant">PACE</th>
                    <th className="px-2 py-1.5 font-semibold text-center whitespace-nowrap" title="Participant portal access">Portal</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {pageRows.map((c, i) => (
                    <tr key={c.id} onClick={() => onOpen(c.id)} className="group cursor-pointer hover:bg-slate-50 transition-colors motion-reduce:transition-none">
                      <td className="pl-3 pr-2 py-1.5 text-[11.5px] text-right tabular-nums text-slate-300">{(curPage - 1) * pageSize + i + 1}</td>
                      <td className="px-2 py-1.5">
                        <span className="flex items-center gap-2 min-w-0">
                          {c.profile_photo
                            ? <img src={c.profile_photo} alt="" className="w-6 h-6 rounded-lg object-cover shrink-0" />
                            : <span className="w-6 h-6 rounded-lg bg-slate-100 text-slate-400 flex items-center justify-center shrink-0"><User size={12} /></span>}
                          <span className="font-semibold text-slate-800 truncate max-w-[140px]">{c.first_name}</span>
                        </span>
                      </td>
                      <td className={clsx('px-2 py-1.5 text-[12.5px] text-slate-500', WIDE_ONLY)}><span className="block truncate max-w-[120px]">{c.preferred_name || <span className="text-slate-300">—</span>}</span></td>
                      <td className="px-2 py-1.5"><span className="block font-semibold text-slate-800 truncate max-w-[140px]">{c.last_name}</span></td>
                      <td className="px-2 py-1.5 text-[12.5px] text-slate-500"><span className="block truncate max-w-[130px]">{c.assigned_to || <span className="text-slate-300">Unassigned</span>}</span></td>
                      <td className="px-2 py-1.5 text-[12.5px] text-slate-500 whitespace-nowrap tabular-nums">{c.ndis_number || <span className="text-slate-300">—</span>}</td>
                      <td className="px-2 py-1.5 text-[12.5px] text-slate-500 whitespace-nowrap tabular-nums">{c.date_of_birth ? fmtD(c.date_of_birth) : <span className="text-slate-300">—</span>}</td>
                      <td className={clsx('px-2 py-1.5 text-[12.5px] text-slate-500 whitespace-nowrap', WIDE_ONLY)}>{c.service_type ? titleCase(c.service_type) : <span className="text-slate-300">—</span>}</td>
                      <td className={clsx('px-2 py-1.5 text-[12.5px] text-slate-500', WIDE_ONLY)}><span className="block truncate max-w-[120px]">{c.service_region || <span className="text-slate-300">—</span>}</span></td>
                      <td className="px-2 py-1.5 text-[12.5px] text-slate-500"><span className="block truncate max-w-[180px]" title={c.email || ''}>{c.email || <span className="text-slate-300">—</span>}</span></td>
                      <td className="px-2 py-1.5 text-right tabular-nums font-semibold text-slate-800 whitespace-nowrap">{money(c.invoiced)}</td>
                      <td className="px-2 py-1.5 whitespace-nowrap">
                        <span className={clsx('inline-flex items-center gap-1.5 rounded-full border px-2 py-0.5 text-[11px] font-medium',
                          c.is_active === false ? 'border-slate-200 bg-slate-50 text-slate-400' : 'border-emerald-200 bg-emerald-50 text-emerald-700')}>
                          <span className={clsx('w-1.5 h-1.5 rounded-full shrink-0', c.is_active === false ? 'bg-slate-400' : 'bg-emerald-500')} aria-hidden="true" />
                          {c.is_active === false ? 'Inactive' : 'Active'}
                        </span>
                      </td>
                      <td className="px-2 py-1.5 text-center">
                        {c.pace_participant
                          ? <Check size={14} className="inline text-emerald-600" aria-label="PACE participant" />
                          : <span className="text-slate-200" aria-label="Not a PACE participant">—</span>}
                      </td>
                      <td className="px-2 py-1.5 text-center">
                        {c.portal_status === 'active'
                          ? <Check size={14} className="inline text-emerald-600" aria-label="Portal access active" />
                          : c.portal_status
                            ? <span className="text-[10px] font-medium text-amber-600" title={`Portal account ${c.portal_status}`}>Invited</span>
                            : <span className="text-slate-200" aria-label="No portal access">—</span>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

      {!loading && <Totals edge="border-t border-slate-100" />}
    </div>
  );
}

// Full-width invoice ledger. Selecting a row EXPANDS it in place (SEExpandedRow is the record) —
// there is no side pane, which is what lets every column fit.
//
// `onClone`, `onBulkImport` and `onOpenReconcile` are optional: leave them off and the table behaves
// exactly as it did before — no clone column, and the Data Loader jobs that need a host screen sit
// disabled with a tooltip saying so, rather than offering a dead link. `detail`/`loadingDetail`/
// `validation`/`recordActions`/`onOpenFull` are the host's record plumbing: supply them and the
// expanded row shows the verdict line and the workflow actions instead of fetching its own copy.
function ServiceEntryTable({ invoices, loading, selectedId, onSelect, onRefresh, onNew, providers, statusFilter, setStatusFilter, onClone, onBulkImport, onOpenReconcile, detail, loadingDetail, validation, recordActions, onOpenFull }) {
  const confirm = useConfirm();
  const [sel, setSel] = useState({});
  // `dateField` decides what From/To measures. Plan managers reconcile by SERVICE period, so that is
  // the default; invoice date stays selectable because that is what this range used to mean.
  // `excludePaid` defaults ON so the screen opens as a WORKLIST and not an archive — it hides only
  // `provider_paid` (genuinely finished). It deliberately no longer hides `paid`: the NDIA having
  // reimbursed us is not the end of the job, the provider still has to be paid out of it.
  const BLANK_F = { client: '', invoiceNo: '', provider: '', from: '', to: '', dateField: 'service', excludePaid: true, onHoldOnly: false, viewByBatch: false };
  const [f, setF] = useState(() => ({ ...BLANK_F, excludePaid: readExcludePaid() }));
  const [busy, setBusy] = useState('');
  // Selecting a row IS expanding it — one record open at a time, and a deep link that sets
  // `selectedId` opens that invoice expanded. `ownExpanded` is the standalone fallback for a host
  // that passes no `onSelect`.
  const [ownExpanded, setOwnExpanded] = useState(null);
  const expandedId = onSelect ? (selectedId || null) : ownExpanded;
  const toggleExpand = (rowId) => {
    const next = expandedId === rowId ? null : rowId;
    if (onSelect) onSelect(next); else setOwnExpanded(next);
  };
  const [pageSize, setPageSize] = useState(25);
  const [page, setPage] = useState(1);
  // Newest first, by when the record was created — the order the server already returns.
  const [sort, setSort] = useState({ key: 'created', dir: 'desc' });
  const [showAllTabs, setShowAllTabs] = useState(false);
  const setFf = (k) => (e) => { const val = e.target.type === 'checkbox' ? e.target.checked : e.target.value; setF((p) => ({ ...p, [k]: val })); setPage(1); };
  // Persisting in the handler rather than an effect keeps this a plain state update — nothing to
  // synchronise, and the write happens exactly when the operator makes the choice.
  const setExcludePaid = (on) => { setF((p) => ({ ...p, excludePaid: on })); setPage(1); try { localStorage.setItem(SE_EXCLUDE_PAID_KEY, on ? '1' : '0'); } catch { /* private mode: the filter still works, it just won't be remembered */ } };
  // Clear Filters clears the FIELDS, including the status the chips share — but keeps the worklist
  // mode, which is a preference the operator set deliberately.
  const clearF = () => { setF((p) => ({ ...BLANK_F, excludePaid: p.excludePaid })); setStatusFilter(''); setPage(1); };
  const dateDirty = !!(f.from || f.to);
  const anyFilter = !!(f.client || f.invoiceNo || f.provider || f.onHoldOnly || f.viewByBatch || dateDirty || statusFilter);

  // Status tabs (counts from the full set) — one click, no refetch.
  const TABS = [['', 'All'], ['extract_generated', 'AI draft'], ['created', 'Awaiting approval'], ['approved', 'Approved'], ['claimed', 'Claimed'], ['claim_error', 'Claim error'], ['paid', 'Reconciled'], ['provider_paid', 'Provider paid'], ['on_hold', 'On hold']];
  const statusCounts = (invoices || []).reduce((m, v) => { m[v.status] = (m[v.status] || 0) + 1; return m; }, {});
  // A chip reading "Claimed 0 · Claim error 0 · On hold 0" is a row of nothing, and it was pushing
  // the chips onto a second line. Empty statuses fold behind "+N empty" — never the selected one,
  // because clearing the filter would then need a chip that is not on screen.
  const visibleTabs = showAllTabs ? TABS : TABS.filter(([key]) => key === '' || key === (statusFilter || '') || (statusCounts[key] || 0) > 0);
  const hiddenTabs = TABS.length - visibleTabs.length;

  // The list endpoint (backend/routes/planManagement.js GET /invoices) accepts only client_id and
  // status — there is no date parameter to send — so the period filter runs client-side over the
  // set already loaded. An invoice matches when its SERVICE window OVERLAPS the range, so a
  // fortnight's work is not dropped for having started the day before the period.
  const inPeriod = (v) => {
    if (!f.from && !f.to) return true;
    if (f.dateField === 'invoice') {
      const d = String(v.invoice_date || '').slice(0, 10);
      if (!d) return false;
      return (!f.from || d >= f.from) && (!f.to || d <= f.to);
    }
    const s = String(v.service_date_from || v.invoice_date || '').slice(0, 10);
    const e = String(v.service_date_to || v.service_date_from || v.invoice_date || '').slice(0, 10);
    if (!s && !e) return false;
    return (!f.to || (s || e) <= f.to) && (!f.from || (e || s) >= f.from);
  };

  const rows0 = (invoices || []).filter((v) => {
    if (statusFilter && v.status !== statusFilter) return false;
    if (f.onHoldOnly && v.status !== 'on_hold') return false;
    // Two searches, not one: two providers can both send "INV-001", so the participant and the
    // invoice number are separate questions and the operator gets to ask them separately.
    if (f.client) { const q = f.client.toLowerCase(); if (!`${v.first_name || ''} ${v.last_name || ''} ${v.ndis_number || ''}`.toLowerCase().includes(q)) return false; }
    if (f.invoiceNo) { const q = f.invoiceNo.toLowerCase(); if (!`${v.provider_invoice_number || ''} ${v.reference || ''}`.toLowerCase().includes(q)) return false; }
    if (f.provider && v.provider_id !== f.provider) return false;
    if (!inPeriod(v)) return false;
    if (f.excludePaid && v.status === 'provider_paid') return false;
    if (f.viewByBatch && !v.claim_id) return false;
    return true;
  });
  const dir = sort.dir === 'asc' ? 1 : -1;
  const SORTERS = {
    reference: (a, b) => String(a.reference || '').localeCompare(String(b.reference || '')),
    created_by: (a, b) => String(a.created_by_name || '').localeCompare(String(b.created_by_name || '')),
    created: (a, b) => String(a.created_at || a.received_date || '').localeCompare(String(b.created_at || b.received_date || '')),
    provider: (a, b) => String(a.provider_display || '').localeCompare(String(b.provider_display || '')),
    client: (a, b) => `${a.first_name} ${a.last_name}`.localeCompare(`${b.first_name} ${b.last_name}`),
    lines: (a, b) => (a.line_count || 0) - (b.line_count || 0),
    period: (a, b) => String(a.service_date_from || a.invoice_date || '').localeCompare(String(b.service_date_from || b.invoice_date || '')),
    amount: (a, b) => Number(a.total_amount || 0) - Number(b.total_amount || 0),
    age: (a, b) => ageDays(a) - ageDays(b),
  };
  const rows = [...rows0].sort((a, b) => (SORTERS[sort.key] || SORTERS.created)(a, b) * dir);
  const toggleSort = (key) => setSort((s) => ({ key, dir: s.key === key && s.dir === 'desc' ? 'asc' : 'desc' }));
  const selRows = rows.filter((v) => sel[v.id]);
  const selTotal = selRows.reduce((s, v) => s + Number(v.total_amount || 0), 0);
  const filteredTotal = rows.reduce((s, v) => s + Number(v.total_amount || 0), 0);
  const pageCount = Math.max(1, Math.ceil(rows.length / pageSize));
  const curPage = Math.min(page, pageCount);
  const pageRows = rows.slice((curPage - 1) * pageSize, curPage * pageSize);
  // An invoice opened from somewhere else (a deep link, the dashboard, the mailbox) has to be ON
  // SCREEN now that the record lives in the row — so the list turns to its page. A plain index, not
  // the row array, so this settles in one pass.
  const expandedIdx = expandedId ? rows.findIndex((r) => r.id === expandedId) : -1;
  useEffect(() => { if (expandedIdx >= 0) setPage(Math.floor(expandedIdx / pageSize) + 1); }, [expandedIdx, pageSize]);
  // …and when the filters hide it outright, say so instead of opening nothing. "Show it" relaxes the
  // filters for this look only — it deliberately does not rewrite the operator's saved worklist mode.
  const hiddenRow = expandedId && expandedIdx < 0 ? (invoices || []).find((v) => v.id === expandedId) : null;
  const hiddenByPaid = !!hiddenRow && f.excludePaid && hiddenRow.status === 'provider_paid';
  const revealExpanded = () => { setF((p) => ({ ...BLANK_F, excludePaid: hiddenByPaid ? false : p.excludePaid })); setStatusFilter(''); setPage(1); };
  const allOn = pageRows.length > 0 && pageRows.every((r) => sel[r.id]);
  const toggleAll = () => { const v = !allOn; const s = { ...sel }; pageRows.forEach((r) => { s[r.id] = v; }); setSel(s); };
  const ACTIONS = {
    approve: { label: 'Approve', run: (id) => api.patch(`/plan-management/invoices/${id}/status`, { status: 'approved' }) },
    claimed: { label: 'Mark claimed', run: (id) => api.patch(`/plan-management/invoices/${id}/status`, { status: 'claimed' }) },
    paid: { label: 'Reconciled', run: (id) => api.patch(`/plan-management/invoices/${id}/status`, { status: 'paid' }) },
    provider_paid: { label: 'Provider paid', run: (id) => api.patch(`/plan-management/invoices/${id}/status`, { status: 'provider_paid' }) },
    remittance: { label: 'Remittance', run: (id) => api.post(`/plan-management/invoices/${id}/remittance`, {}) },
    hold: { label: 'Hold', run: (id) => api.patch(`/plan-management/invoices/${id}/status`, { status: 'on_hold' }) },
    release: { label: 'Release', run: (id) => api.patch(`/plan-management/invoices/${id}/status`, { status: 'created' }) },
    cancel: { label: 'Cancel', run: (id) => api.patch(`/plan-management/invoices/${id}/status`, { status: 'cancelled' }) },
    undo: { label: 'Undo cancel', run: (id) => api.patch(`/plan-management/invoices/${id}/status`, { status: 'created' }) },
  };
  const runBatch = async (key) => {
    const ids = selRows.map((v) => v.id);
    if (!ids.length) return toast.error('Select at least one invoice');
    const act = ACTIONS[key]; let ok = 0; let fail = 0;
    setBusy(key);
    for (const id of ids) { try { await act.run(id); ok++; } catch { fail++; } }
    setBusy('');
    toast.success(`${act.label}: ${ok} done${fail ? `, ${fail} skipped/blocked` : ''}`);
    setSel({}); onRefresh && onRefresh();
  };
  const downloadXero = async () => {
    const ids = selRows.map((v) => v.id);
    if (!ids.length) return toast.error('Select at least one invoice');
    setBusy('xero');
    try {
      const r = await api.post('/plan-management/xero-export', { invoice_ids: ids }, { responseType: 'blob' });
      dl(r.data, `xero-bills-${new Date().toISOString().slice(0, 10)}.csv`, 'text/csv');
      toast.success('Xero file downloaded');
    } catch (e) {
      // The server says why (no lines, nothing selected); a blanket "Failed" read as "you can only
      // download it once". It can be downloaded any number of times — it changes nothing.
      toast.error(e.response?.data?.message || 'Failed to export Xero file');
    } finally { setBusy(''); }
  };
  const downloadNdisClaim = async () => {
    const ids = selRows.map((v) => v.id);
    if (!ids.length) return toast.error('Select at least one invoice');
    setBusy('claim');
    try {
      const c = await api.post('/plan-management/claims', { invoice_ids: ids });
      const n = await downloadClaimFiles(c.data.id, `ndis-claim-${c.data.batch_ref || c.data.id.slice(0, 8)}.csv`);
      toast.success(n > 1 ? `NDIS claim created — ${n} files downloaded, upload each one to the portal` : 'NDIS claim created + downloaded');
      setSel({}); onRefresh && onRefresh();
    } catch (e) { toast.error(e.response?.data?.message || e.message || 'Failed to create claim'); } finally { setBusy(''); }
  };
  const generateAba = async () => {
    const ids = selRows.map((v) => v.id);
    if (!ids.length) return toast.error('Select at least one invoice');
    setBusy('aba');
    try {
      const r = await api.post('/plan-management/provider-payment/aba', { invoice_ids: ids });
      // Already-paid invoices come back as the ORIGINAL file(s), byte for byte — a re-download,
      // never a second payment.
      if (r.data.redownload?.length) {
        r.data.redownload.forEach((b) => dl(b.aba, `${b.batch_ref}.aba`, 'text/plain'));
        toast.success(r.data.message);
        setSel({}); onRefresh && onRefresh();
        return;
      }
      if (r.data.aba) { dl(r.data.aba, `${r.data.batch?.batch_ref || `provider-payment-${new Date().toISOString().slice(0, 10)}`}.aba`, 'text/plain'); toast.success(`ABA generated — ${r.data.paid} payment(s), ${money(r.data.total)}. Marked provider paid.`); }
      else toast('No external payments to make', { icon: 'ℹ️' });
      if (r.data.self) toast(`${r.data.self} settled internally`, { icon: 'ℹ️' });
      if (r.data.missing?.length) toast(`${r.data.missing.length} skipped (no bank): ${r.data.missing.slice(0, 3).join(', ')}`, { icon: '⚠️' });
      if (r.data.unverified?.length) toast(`${r.data.unverified.length} on hold — bank change not verified: ${r.data.unverified.slice(0, 3).join(', ')}`, { icon: '⚠️' });
      if (r.data.remitted) toast.success(`Remittance advice emailed to ${r.data.remitted} provider(s)`);
      setSel({}); onRefresh && onRefresh();
    } catch (e) { toast.error(e.response?.data?.message || 'Failed'); } finally { setBusy(''); }
  };
  // "Report" — the filtered list, exactly as it is on screen, as a CSV. Same column set and same
  // names as the table, so the file and the screen are never two different stories.
  const downloadReport = () => {
    const esc = (s) => { s = String(s == null ? '' : s); return /[",\n]/.test(s) ? '"' + s.replace(/"/g, '""') + '"' : s; };
    const hdr = ['Reference', 'Created By', 'Date Created', 'Service Provider', 'Invoice No.', 'Client', 'NDIS Number', 'Inv Lines', 'Total', 'Status'];
    const out = [hdr.join(',')].concat(rows.map((v) => [v.reference, v.created_by_name, fmtD(v.created_at || v.received_date), v.provider_display, v.provider_invoice_number, `${v.first_name} ${v.last_name}`, v.ndis_number, v.line_count, v.total_amount, statusLabel(v.status)].map(esc).join(',')));
    dl(out.join('\r\n'), `service-entry-${new Date().toISOString().slice(0, 10)}.csv`, 'text/csv');
  };
  // The monthly plan-management fee run. Same endpoint the Settings tab drives, defaulted to the
  // current month; it is a ledger-wide write, so it asks first.
  const feeMonth = isoToday().slice(0, 7);
  const generateFees = async () => {
    if (!await confirm(`Create the plan-management fee invoices for ${feeMonth}? Participants already invoiced for that month are skipped.`, { title: 'Create Monthly PM Invoices' })) return;
    setBusy('fees');
    try {
      const r = await api.post('/plan-management/fees/generate', { month: feeMonth });
      toast.success(`${r.data.created} fee invoice(s) generated${r.data.skipped ? `, ${r.data.skipped} skipped` : ''}`);
      if (r.data.no_agreement) toast(`${r.data.no_agreement} setup fee(s) held — no signed service agreement on the participant yet`, { icon: '⚠️' });
      onRefresh && onRefresh();
    } catch (e) { toast.error(e.response?.data?.message || 'Failed to generate fee invoices'); } finally { setBusy(''); }
  };
  // Data Loader — the three bulk jobs that FEED the ledger, grouped away from the per-row work.
  // Each routes to the implementation that already exists; an item with no host screen is disabled
  // with the reason on it rather than silently doing nothing.
  const dataLoaderItems = [
    { label: 'Create Monthly PM Invoices', icon: Calculator, onClick: generateFees },
    { label: 'Bulk Invoice File Import', icon: Upload, onClick: onBulkImport, disabled: !onBulkImport, title: onBulkImport ? 'AI reads each provider PDF and files it as a draft' : 'Not available here — open Invoices from the Plan Management rail to import invoice files.' },
    { label: 'Reconcile Payments', icon: Scale, onClick: onOpenReconcile, disabled: !onOpenReconcile, title: onOpenReconcile ? 'Upload the NDIA payment-results file' : 'Not available here — open Reconcile from the Plan Management rail.' },
  ];
  // The single bulk menu over the selected rows. Order follows the reference. "Approve Declined
  // Invoices" is deliberately absent: declining is an APPROVAL state (pm_invoices.approval_status)
  // set by the participant, and nothing in this file batch-clears it — a stub that quietly set the
  // invoice status instead would be a lie about what happened.
  const actionItems = [
    { label: 'Download NDIS Claim', icon: Landmark, onClick: downloadNdisClaim },
    { label: 'Download Xero File', icon: Download, onClick: downloadXero },
    { label: 'Send Remittance Advice', icon: FileText, onClick: () => runBatch('remittance') },
    { label: 'Mark as Paid', icon: Check, onClick: () => runBatch('paid') },
    { label: 'Generate ABA File', icon: Landmark, onClick: generateAba },
    { divider: true },
    { label: 'Approve Pending Invoices', icon: Check, onClick: () => runBatch('approve') },
    { label: 'Release On Hold Invoices', icon: RefreshCw, onClick: () => runBatch('release') },
    { divider: true },
    { label: 'Cancel', icon: X, onClick: () => runBatch('cancel') },
    { label: 'Undo Cancel', icon: RefreshCw, onClick: () => runBatch('undo') },
  ];
  const I = 'border border-slate-200 rounded-lg px-2.5 py-1.5 text-xs bg-white';
  // Left status stripe + pill dot colour per status — instant visual scanning of the ledger.
  const STRIPE = { extract_generated: 'bg-slate-300', created: 'bg-amber-400', approved: 'bg-indigo-400', claimed: 'bg-cyan-400', paid: 'bg-green-400', provider_paid: 'bg-emerald-500', on_hold: 'bg-yellow-400', declined: 'bg-rose-400', cancelled: 'bg-slate-200' };
  const DOT = { extract_generated: 'bg-slate-400', created: 'bg-amber-500', approved: 'bg-indigo-500', claimed: 'bg-cyan-500', paid: 'bg-green-500', provider_paid: 'bg-emerald-500', on_hold: 'bg-yellow-500', declined: 'bg-rose-500', cancelled: 'bg-slate-400' };
  // Pill skin per status. Written out in full because Tailwind only sees LITERAL class strings —
  // a name assembled at runtime is never generated into the stylesheet.
  const PILL = {
    extract_generated: 'border-slate-200 bg-slate-50 text-slate-600',
    created: 'border-amber-200 bg-amber-50 text-amber-700',
    pending_participant: 'border-amber-200 bg-amber-50 text-amber-700',
    approved: 'border-indigo-200 bg-indigo-50 text-indigo-700',
    claimed: 'border-cyan-200 bg-cyan-50 text-cyan-700',
    claim_error: 'border-rose-200 bg-rose-50 text-rose-700',
    paid: 'border-green-200 bg-green-50 text-green-700',
    provider_paid: 'border-emerald-200 bg-emerald-50 text-emerald-700',
    on_hold: 'border-yellow-200 bg-yellow-50 text-yellow-800',
    declined: 'border-rose-200 bg-rose-50 text-rose-700',
    cancelled: 'border-slate-200 bg-slate-50 text-slate-400',
  };
  const SortHead = ({ k, label, align, className, title }) => (
    <th className={clsx('px-2 py-1.5 font-semibold whitespace-nowrap select-none', align === 'right' ? 'text-right' : 'text-left', className)}
      title={title}
      aria-sort={sort.key === k ? (sort.dir === 'asc' ? 'ascending' : 'descending') : 'none'}>
      <button type="button" onClick={() => toggleSort(k)}
        className={clsx('inline-flex items-center gap-1 uppercase tracking-wide hover:text-white rounded px-0.5 -mx-0.5 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-300', align === 'right' && 'flex-row-reverse')}>
        {label}{sort.key === k && <ChevronDown size={11} className={clsx('transition motion-reduce:transition-none', sort.dir === 'asc' && 'rotate-180')} />}
      </button>
    </th>
  );
  // Column visibility, relaxed now the table owns the full width: every column is present from `lg`
  // up, which is where a plan manager actually works. Below that the lower-value columns still fold
  // so the money and the status are never the ones cut. The table also sits in an `overflow-x-auto`
  // container as a last resort, so a column can never be silently clipped. Literal class strings —
  // Tailwind only sees classes it can read in the source.
  //
  // Breakpoints are VIEWPORT widths, but this table gets the viewport minus the app sidebar — so at
  // a 1440px laptop every column used to be "on" and Status (the column a page is scanned for) was
  // pushed off the right edge behind a scrollbar nobody saw. Low-value columns now wait for 2xl, the
  // NDIS number rides under the participant's name instead of taking a column, and the SERVICE
  // PERIOD — both ends of it — gets the column instead: it is what the claim is made against.
  const C_CREATED_BY = 'hidden 2xl:table-cell';
  const C_PERIOD = 'hidden lg:table-cell';
  const C_INVOICE_NO = 'hidden md:table-cell';
  const C_LINES = 'hidden 2xl:table-cell';
  const C_AGE = 'hidden md:table-cell';
  const C_DATE = 'hidden 2xl:table-cell';
  const C_PROVIDER = '';
  // Number of body columns — the expanded row has to span them, and the clone column is optional.
  // Columns hidden by CSS still count, which is harmless: an over-long colSpan is clamped.
  const colCount = 13 + (onClone ? 1 : 0);
  // Windowed page numbers: always first and last, the current page and its neighbours, ellipses for
  // the rest. Keeps the bar one line whether the ledger is 2 pages or 200.
  const pageNums = (() => {
    if (pageCount <= 7) return Array.from({ length: pageCount }, (_, i) => i + 1);
    const out = [1];
    const lo = Math.max(2, curPage - 1), hi = Math.min(pageCount - 1, curPage + 1);
    if (lo > 2) out.push('…');
    for (let n = lo; n <= hi; n++) out.push(n);
    if (hi < pageCount - 1) out.push('…');
    out.push(pageCount);
    return out;
  })();
  // Page Size · Total Amount · Total Items · pager — one bar, rendered IDENTICALLY above and below
  // the table so an operator never has to scroll to either end to change the page size, read the
  // money total, or step pages. Both read the CURRENT filter, never the unfiltered set.
  const Totals = ({ edge }) => (
    <div className={clsx('px-3 py-1.5 flex flex-wrap items-center gap-x-5 gap-y-1 text-[11.5px] text-slate-500', edge)}>
      <label className="flex items-center gap-1.5">
        <span>Page Size</span>
        <select value={pageSize} onChange={(e) => { setPageSize(Number(e.target.value)); setPage(1); }} aria-label="Page size"
          className="border border-slate-200 rounded-lg px-2 py-1 text-[11px] bg-white focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-400">
          {[25, 50, 100, 250].map((n) => <option key={n} value={n}>{n}</option>)}
        </select>
      </label>
      <span>Total Amount <span className="font-bold tabular-nums text-slate-800">({money(filteredTotal)})</span></span>
      <span>Total Items <span className="font-bold tabular-nums text-slate-800">({rows.length})</span></span>
      <span className="flex-1" />
      {/* Numbered pages, as on the reference. Windowed to 7 so a 40-page ledger cannot wrap the bar. */}
      <nav className="flex items-center gap-1" aria-label="Pagination">
        {pageCount > 1 && (
          <button onClick={() => setPage(Math.max(1, curPage - 1))} disabled={curPage <= 1}
            aria-label="Previous page"
            className="w-6 h-6 rounded border border-slate-200 bg-white text-slate-500 hover:bg-slate-50 disabled:opacity-40 flex items-center justify-center focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-400">
            <ChevronRight size={12} className="rotate-180" />
          </button>
        )}
        {pageNums.map((n, i) => (n === '…'
          ? <span key={`gap${i}`} className="px-1 text-slate-300" aria-hidden="true">…</span>
          : (
            <button key={n} onClick={() => setPage(n)} aria-label={`Page ${n}`} aria-current={n === curPage ? 'page' : undefined}
              className={clsx('min-w-[24px] h-6 px-1.5 rounded border text-[11px] font-medium tabular-nums focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-400',
                n === curPage ? 'bg-primary-600 border-primary-600 text-white' : 'border-slate-200 bg-white text-slate-500 hover:bg-slate-50')}>
              {n}
            </button>
          )))}
        {pageCount > 1 && (
          <button onClick={() => setPage(Math.min(pageCount, curPage + 1))} disabled={curPage >= pageCount}
            aria-label="Next page"
            className="w-6 h-6 rounded border border-slate-200 bg-white text-slate-500 hover:bg-slate-50 disabled:opacity-40 flex items-center justify-center focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-400">
            <ChevronRight size={12} />
          </button>
        )}
      </nav>
    </div>
  );
  return (
    <div className="card p-0 overflow-hidden">
      {/* Toolbar — Create New · Data Loader … title … Report · Action. Creation on the left, the
          batch menu on the right, the screen's name in the middle: the arrangement a plan manager
          coming from another product already has in their hands. */}
      {/* Title leads, actions follow. A centred title between two clusters of buttons splits the
          toolbar into three things of equal weight and nothing says where to start; read
          left-to-right it is now "what this is, then what you can do with it". Adding is
          separated from the rest, because creating an invoice is the one thing done far more
          often than everything else here. */}
      <div className="px-3 py-2.5 border-b border-slate-100 flex flex-wrap items-center gap-x-3 gap-y-2">
        <span className="text-[12.5px] font-medium text-ink-soft tabular-nums whitespace-nowrap">
          {rows.length === (invoices || []).length
            ? `${rows.length} invoice${rows.length === 1 ? '' : 's'}`
            : `${rows.length} of ${(invoices || []).length} shown`}
        </span>
        <div className="ml-auto flex items-center gap-2">
          <button onClick={onNew} className="btn-primary !text-xs !px-3 !py-1.5 flex items-center gap-1.5"><Plus size={14} /> Create New</button>
          <span className="w-px h-5 bg-slate-200" aria-hidden="true" />
          <Dropdown small label="Data Loader" icon={Database} items={dataLoaderItems} />
          <button onClick={downloadReport} title="Download this filtered list as CSV" className="btn-secondary !text-xs !px-3 !py-1.5 flex items-center gap-1.5"><Download size={14} /> Report</button>
          <Dropdown small label={busy ? '…' : 'Action'} icon={Check} items={actionItems} />
        </div>
      </div>

      {/* Status chips (counts, empties folded). These and the Status select below drive the SAME
          state, so they cannot disagree: the chips are the fast path and carry the counts, the
          select is the labelled control that belongs in the filter row. */}
      <div className="px-3 py-1.5 border-b border-slate-100 flex flex-wrap items-center gap-1">
        {visibleTabs.map(([key, label]) => {
          const n = key === '' ? (invoices || []).length : (statusCounts[key] || 0);
          const active = (statusFilter || '') === key;
          return (
            <button key={key || 'all'} onClick={() => { setStatusFilter(key); setPage(1); }} aria-pressed={active}
              className={clsx('px-2.5 py-1 rounded-lg text-[11.5px] font-medium whitespace-nowrap transition motion-reduce:transition-none flex items-center gap-1.5 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-400', active ? 'bg-primary-600 text-white shadow-sm' : 'text-slate-500 hover:bg-slate-100')}>
              {label}<span className={clsx('text-[10px] font-bold tabular-nums px-1.5 py-0.5 rounded-full', active ? 'bg-white/25' : 'bg-slate-100 text-slate-500')}>{n}</span>
            </button>
          );
        })}
        {hiddenTabs > 0 && (
          <button onClick={() => setShowAllTabs(true)} className="px-2 py-1 rounded-lg text-[11px] font-medium text-slate-400 hover:bg-slate-100 hover:text-slate-600 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-400">+{hiddenTabs} empty</button>
        )}
        {showAllTabs && (
          <button onClick={() => setShowAllTabs(false)} className="px-2 py-1 rounded-lg text-[11px] font-medium text-slate-400 hover:bg-slate-100 hover:text-slate-600 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-400">Show fewer</button>
        )}
      </div>

      {/* Filters — open, never collapsed. A filter you cannot see is a filter you forget is on, and
          on a money screen that is how an operator ends up reading the wrong total. */}
      <div className="px-3 py-2.5 border-b border-slate-100 bg-slate-50/60 space-y-2">
        {/* Each control carries its NAME above it, not inside it. A placeholder disappears the
            moment someone types, so a filled filter row stops saying what any of it filters on —
            on a money screen that is how an operator ends up reading the wrong total. */}
        <div className="flex flex-wrap items-end gap-2">
          <FilterField label="Client">
            <div className="relative">
              <Search size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-300" aria-hidden="true" />
              <input value={f.client} onChange={setFf('client')} aria-label="Client" className={`${I} pl-8 w-40`} />
            </div>
          </FilterField>
          <FilterField label="Invoice Number">
            <div className="relative">
              <Search size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-300" aria-hidden="true" />
              <input value={f.invoiceNo} onChange={setFf('invoiceNo')} aria-label="Invoice number" className={`${I} pl-8 w-40`} />
            </div>
          </FilterField>
          <FilterField label="Service Provider">
            <select value={f.provider} onChange={setFf('provider')} aria-label="Service provider" className={`${I} w-44`}>
              <option value="">(All)</option>
              {(providers || []).map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
            </select>
          </FilterField>
          {/* From / To are two labelled fields, as on the reference, kept on the same two-row
              rhythm as everything beside them. "Date" is ours and says WHICH date the period
              measures — plan managers reconcile by service period, but a bare range used to mean
              the invoice date without ever saying so. */}
          <FilterField label="Date">
            <select value={f.dateField} onChange={setFf('dateField')} aria-label="Which date the period filters on"
              className={clsx(I, 'w-32', dateDirty && 'border-primary-300 bg-primary-50/60')}>
              <option value="service">Service date</option>
              <option value="invoice">Invoice date</option>
            </select>
          </FilterField>
          <FilterField label="From">
            <input type="date" value={f.from} onChange={setFf('from')} aria-label="From"
              className={clsx(I, 'tabular-nums w-[132px]', dateDirty && 'border-primary-300 bg-primary-50/60')} />
          </FilterField>
          <FilterField label="To">
            <input type="date" value={f.to} onChange={setFf('to')} aria-label="To"
              className={clsx(I, 'tabular-nums w-[132px]', dateDirty && 'border-primary-300 bg-primary-50/60')} />
          </FilterField>
          {/* Pushed to its own edge so it can never be mistaken for one more filter. Always
              present, so its position is predictable; inert when there is nothing to clear. */}
          <span className="flex-1" />
          <button onClick={clearF} disabled={!anyFilter}
            className="btn-secondary !text-[11px] !px-2.5 !py-1.5 flex items-center gap-1 disabled:opacity-40"><X size={11} /> Clear Filters</button>
        </div>
        <div className="flex flex-wrap items-center gap-x-5 gap-y-1">
          <label className="text-[11px] text-slate-500 flex items-center gap-1.5">
            <input type="checkbox" checked={f.excludePaid} onChange={(e) => setExcludePaid(e.target.checked)}
              className="focus-visible:ring-2 focus-visible:ring-primary-400" />
            Exclude Provider Paid Invoices
          </label>
          {/* A batch here is a lodged NDIA claim (pm_invoices.claim_id) — the concept already exists,
              so the reference's wording maps onto it rather than needing a new one. */}
          <label className="text-[11px] text-slate-500 flex items-center gap-1.5" title="Only invoices attached to a lodged NDIA claim batch">
            <input type="checkbox" checked={f.viewByBatch} onChange={setFf('viewByBatch')} className="focus-visible:ring-2 focus-visible:ring-primary-400" />
            View By Batch
          </label>
          <label className="text-[11px] text-slate-500 flex items-center gap-1.5">
            <input type="checkbox" checked={f.onHoldOnly} onChange={setFf('onHoldOnly')} className="focus-visible:ring-2 focus-visible:ring-primary-400" />
            Show On Hold Only
          </label>
        </div>
      </div>

      {!loading && <Totals edge="border-b border-slate-100" />}

      {hiddenRow && (
        <div className="px-3 py-2 border-b border-amber-100 bg-amber-50/70 flex flex-wrap items-center gap-2 text-[11.5px] text-amber-800">
          <AlertTriangle size={13} className="shrink-0" aria-hidden="true" />
          <span>
            The invoice you opened ({hiddenRow.reference || `${hiddenRow.first_name || ''} ${hiddenRow.last_name || ''}`.trim() || 'this record'}) is not in the list —
            {hiddenByPaid ? ' it is provider paid and “Exclude Provider Paid Invoices” is on.' : ' the current filters hide it.'}
          </span>
          <button type="button" onClick={revealExpanded} className="btn-secondary !text-[11px] !px-2 !py-1">Show it</button>
          <button type="button" onClick={() => toggleExpand(hiddenRow.id)} className="underline hover:no-underline">Dismiss</button>
        </div>
      )}

      {loading ? <TableSkeleton rows={12} cols={6} />
        : rows.length === 0 ? <EmptyState icon={Receipt} title={statusFilter || anyFilter ? 'No invoices match' : 'No invoices yet'} hint={statusFilter || anyFilter ? 'Try a different tab, widen the period, or clear your filters.' : 'Add a provider invoice to get started.'} action={<button onClick={onNew} className="btn-primary text-sm flex items-center gap-1.5"><Plus size={15} /> New invoice</button>} />
          : (
            <div className="overflow-x-auto">
              <table className="w-full text-[13px]">
                {/* Dark header band, as on the reference: it separates the column names from the
                    money below hard enough that a long ledger never reads as one continuous block. */}
                <thead className="bg-slate-700 text-[10px] uppercase tracking-wide text-slate-200 sticky top-0 z-10">
                  <tr>
                    <th className="pl-2 pr-0 py-1.5 w-7"><span className="sr-only">Expand</span></th>
                    <th className="px-1 py-1.5 w-7"><input type="checkbox" checked={allOn} onChange={toggleAll} aria-label="Select all invoices on this page" className="align-middle" /></th>
                    <SortHead k="reference" label="Reference" title="Our system key for this invoice" />
                    <SortHead k="created_by" label="Created By" className={C_CREATED_BY} />
                    <SortHead k="created" label="Date Created" className={C_DATE} />
                    <SortHead k="age" label="Age" className={C_AGE} title="Days since the invoice arrived" />
                    <SortHead k="provider" label="Service Provider" className={C_PROVIDER} />
                    <th className={clsx('px-2 py-1.5 font-semibold text-left whitespace-nowrap', C_INVOICE_NO)} title="The provider's own invoice number">Invoice No.</th>
                    <SortHead k="client" label="Client" />
                    <SortHead k="period" label="Service Period" className={C_PERIOD} title="The dates the support was delivered, from and to" />
                    <SortHead k="lines" label="Inv Lines" align="right" className={C_LINES} />
                    <SortHead k="amount" label="Total" align="right" />
                    <th className="px-2 py-1.5 font-semibold text-left whitespace-nowrap">Status</th>
                    {onClone && <th className="px-1 py-1.5 w-8"><span className="sr-only">Row actions</span></th>}
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {pageRows.map((v) => {
                    // Reference and Invoice No. are two different keys and both get their own column:
                    // ours is unique across the ledger, theirs is only unique to that provider — two
                    // providers can each send "INV-001", which is exactly why both columns exist.
                    const ref = v.reference || '';
                    const theirRef = v.provider_invoice_number || '';
                    return (
                    <React.Fragment key={v.id}>
                      <tr onClick={() => toggleExpand(v.id)} className={clsx('group cursor-pointer transition-colors motion-reduce:transition-none', expandedId === v.id ? 'bg-primary-50/70' : 'hover:bg-slate-50')}>
                        <td className="pl-0 pr-0 py-0 relative w-7">
                          <span className={clsx('absolute left-0 top-0 bottom-0 w-1', STRIPE[v.status] || 'bg-slate-200')} aria-hidden="true" />
                          <button onClick={(e) => { e.stopPropagation(); toggleExpand(v.id); }}
                            aria-expanded={expandedId === v.id} aria-label={expandedId === v.id ? 'Close this invoice' : 'Open this invoice'}
                            className="pl-2.5 pr-1 py-2 flex items-center rounded focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-400"><ChevronRight size={14} className={clsx('text-slate-300 group-hover:text-slate-400 transition motion-reduce:transition-none', expandedId === v.id && 'rotate-90 text-slate-500')} /></button>
                        </td>
                        <td className="px-1 py-1.5 w-7" onClick={(e) => e.stopPropagation()}><input type="checkbox" checked={!!sel[v.id]} onChange={(e) => setSel((s) => ({ ...s, [v.id]: e.target.checked }))} aria-label={`Select invoice for ${v.first_name} ${v.last_name}`} className="align-middle" /></td>
                        <td className="px-2 py-1.5 whitespace-nowrap">
                          {ref ? (
                            <button type="button" onClick={(e) => { e.stopPropagation(); copyText(ref, 'Reference'); }}
                              title={`Copy reference ${ref}`}
                              className="inline-flex items-center gap-1 max-w-[130px] rounded px-1 -mx-1 py-0.5 text-[12.5px] tabular-nums text-slate-600 hover:bg-slate-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-400">
                              <span className="truncate">{ref}</span>
                              <Copy size={11} className="shrink-0 text-slate-300 group-hover:text-slate-400" aria-hidden="true" />
                            </button>
                          ) : <span className="text-slate-300">—</span>}
                        </td>
                        <td className={clsx('px-2 py-1.5 text-[12.5px] text-slate-500', C_CREATED_BY)}>
                          <span className="block truncate max-w-[130px]">{v.created_by_name || '—'}</span>
                        </td>
                        <td className={clsx('px-2 py-1.5 text-[12.5px] text-slate-500 whitespace-nowrap tabular-nums', C_DATE)}>{fmtD(v.created_at || v.received_date)}</td>
                        <td className={clsx('px-2 py-1.5 text-[12.5px] whitespace-nowrap', C_AGE)}><AgeChip inv={v} /></td>
                        <td className={clsx('px-2 py-1.5 text-[12.5px] text-slate-600', C_PROVIDER)}>
                          {/* Hover a paid row's provider (or its status) for the date the money moved. */}
                          <HoverTip label={paidTip(v)}>
                            <span className="block truncate max-w-[170px]">{v.provider_display || <span className="text-slate-300">No provider</span>}</span>
                          </HoverTip>
                        </td>
                        <td className={clsx('px-2 py-1.5 whitespace-nowrap', C_INVOICE_NO)}>
                          {theirRef ? (
                            <button type="button" onClick={(e) => { e.stopPropagation(); copyText(theirRef, 'Invoice number'); }}
                              title={`Copy the provider's invoice number ${theirRef}`}
                              className="inline-flex items-center gap-1 max-w-[130px] rounded px-1 -mx-1 py-0.5 text-[12.5px] tabular-nums text-slate-600 hover:bg-slate-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-400">
                              <span className="truncate">{theirRef}</span>
                              <Copy size={11} className="shrink-0 text-slate-300 group-hover:text-slate-400" aria-hidden="true" />
                            </button>
                          ) : <span className="text-slate-300">—</span>}
                        </td>
                        <td className="px-2 py-1.5 min-w-[140px]">
                          <span className="block font-semibold text-slate-800 truncate max-w-[180px] leading-tight">{v.first_name} {v.last_name}</span>
                          <span className="block text-[11px] text-slate-400 tabular-nums leading-tight mt-0.5">{v.ndis_number ? `NDIS ${v.ndis_number}` : 'No NDIS number'}</span>
                        </td>
                        <td className={clsx('px-2 py-1.5 text-[12.5px] text-slate-600 whitespace-nowrap tabular-nums', C_PERIOD)}>
                          {(v.service_date_from || v.service_date_to || !v.fee_month)
                            ? fmtRange(v.service_date_from, v.service_date_to)
                            : <span className="text-slate-500" title="Plan-management fee for this month">{(() => { try { return format(parseISO(`${v.fee_month}-01`), 'MMM yyyy'); } catch { return v.fee_month; } })()}</span>}
                        </td>
                        <td className={clsx('px-2 py-1.5 text-[12.5px] text-right tabular-nums text-slate-500', C_LINES)}>{v.line_count ?? '—'}</td>
                        <td className="px-2 py-1.5 text-right tabular-nums font-semibold text-slate-800 whitespace-nowrap">{money(v.total_amount)}</td>
                        <td className="px-2 py-1.5 whitespace-nowrap">
                          {/* A pill, not bare text: on the reference the state is the one thing an
                              operator scans a whole page for, so it has to read without being aimed at. */}
                          <HoverTip label={paidTip(v)}>
                          <span className={clsx('inline-flex items-center gap-1.5 rounded-full border px-2 py-0.5 text-[11px] font-medium', PILL[v.status] || 'border-slate-200 bg-slate-50 text-slate-600')}>
                            <span className={clsx('w-1.5 h-1.5 rounded-full shrink-0', DOT[v.status] || 'bg-slate-400')} aria-hidden="true" />{statusLabel(v.status)}
                            {(v.duplicate_flag || v.coi_flag) && <AlertTriangle size={12} className="text-amber-500 shrink-0" aria-label={v.coi_flag ? 'Conflict of interest' : 'Possible duplicate'} />}
                          </span>
                          </HoverTip>
                        </td>
                        {onClone && (
                          <td className="px-1 py-1.5 w-8 text-right" onClick={(e) => e.stopPropagation()}>
                            <button type="button" onClick={() => onClone(v)}
                              title="Clone into a new draft invoice"
                              aria-label={`Clone invoice ${ref || ''} for ${v.first_name} ${v.last_name}`}
                              className="p-1 rounded text-slate-300 opacity-0 group-hover:opacity-100 focus-visible:opacity-100 hover:text-primary-600 hover:bg-primary-50 transition motion-reduce:transition-none focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-400">
                              <Copy size={13} />
                            </button>
                          </td>
                        )}
                      </tr>
                      {expandedId === v.id && (
                        <SEExpandedRow
                          id={v.id} colSpan={colCount} onChanged={onRefresh}
                          hosted={detail !== undefined} detail={detail} loading={!!loadingDetail}
                          validation={detail && detail.id === v.id ? validation : null}
                          actions={detail && detail.id === v.id ? recordActions : null}
                          onOpenFull={onOpenFull}
                        />
                      )}
                    </React.Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}

      {/* Sticky selection action bar */}
      {selRows.length > 0 && (
        <div className="sticky bottom-0 z-20 px-4 py-2.5 bg-primary-600 text-white flex flex-wrap items-center gap-3 shadow-[0_-4px_12px_rgba(0,0,0,0.08)]">
          <span className="font-semibold text-sm">{selRows.length} selected</span>
          <span className="text-primary-100 text-sm tabular-nums">{money(selTotal)}</span>
          <span className="flex-1" />
          <button onClick={() => runBatch('approve')} disabled={!!busy} className="bg-white/15 hover:bg-white/25 text-white text-xs font-semibold rounded-lg px-3 py-1.5 transition">Approve</button>
          <button onClick={downloadNdisClaim} disabled={!!busy} className="bg-white/15 hover:bg-white/25 text-white text-xs font-semibold rounded-lg px-3 py-1.5 transition">Claim (NDIS)</button>
          <button onClick={generateAba} disabled={!!busy} className="bg-white/15 hover:bg-white/25 text-white text-xs font-semibold rounded-lg px-3 py-1.5 transition">Pay (ABA)</button>
          <Dropdown label={busy ? '…' : 'More'} icon={ChevronDown} items={actionItems} />
          <button onClick={() => setSel({})} className="text-primary-100 hover:text-white text-xs">Clear</button>
        </div>
      )}

      {/* The same two figures again, under the table — a long list should never make an operator
          scroll back up to find out what they are looking at is worth. */}
      {!loading && <Totals edge="border-t border-slate-100" />}
    </div>
  );
}

// The workflow actions for one invoice — ONE clear next step (driven by status), participant
// approval while it still matters, and everything else tucked into a "More" menu. Rendered on the
// expanded row (where the everyday work now happens) AND inside the full record, off exactly the
// same handlers, so the two can never offer different work.
function WorkflowActions({
  detail, className, setStatus, setApproval, sendApproval, setApprovalPolicy,
  downloadClaimCsv, downloadAccountingCsv, onOpenPeriods, revertExtract,
}) {
  if (!detail) return null;
  const s = detail.status;
  const lines = detail.lines || [];
  const preClaim = !['claimed', 'provider_paid', 'paid', 'cancelled'].includes(s);
  const NEXT = {
    extract_generated: { label: 'Accept AI draft', run: () => setStatus('created'), hint: 'Confirms the AI-read details look right and moves it to awaiting approval.' },
    created:       { label: 'Approve invoice',    run: () => setStatus('approved'),      hint: 'Confirms the invoice is correct and ready to claim.' },
    approved:      { label: 'Mark as claimed',    run: () => setStatus('claimed'),       hint: 'Records that you’ve submitted this claim to the NDIA.' },
    claimed:       { label: 'Mark provider paid', run: () => setStatus('provider_paid'), hint: 'Records that you’ve paid the provider.' },
    provider_paid: { label: 'NDIA paid you',      run: () => setStatus('paid'),          hint: 'Closes the invoice — fully reconciled.' },
    declined:      { label: 'Approve anyway',     run: () => setStatus('approved') },
    cancelled:     { label: 'Undo cancel',        run: () => setStatus('created') },
  };
  const next = NEXT[s];
  const more = [];
  if (detail.approval_status === 'pending' && preClaim) more.push({ label: 'Send for approval (email)', icon: FileText, onClick: () => sendApproval('email') });
  if (detail.approval_status === 'pending' && preClaim) more.push({ label: 'Send for approval (SMS)', icon: FileText, onClick: () => sendApproval('sms') });
  // Once approved/declined, allow resetting back to "awaiting approval" so it shows in the
  // participant app again (and can be re-sent for approval).
  if (preClaim && detail.approval_status !== 'pending') more.push({ label: 'Reset to “awaiting approval”', icon: RefreshCw, onClick: () => setApproval('pending') });
  // You could mark a participant DECLINED but never APPROVED, so an approval given by phone, in
  // person or on a signed form could not be recorded — and the claim gate then refused the invoice
  // with "Participant approval is required" forever. The backend route always accepted 'approved';
  // only this menu entry was missing.
  if (preClaim && detail.approval_status !== 'approved') more.push({ label: 'Mark participant approved', icon: CheckCircle2, onClick: () => setApproval('approved') });
  if (preClaim && detail.approval_status !== 'declined')  more.push({ label: 'Mark participant declined', icon: X, onClick: () => setApproval('declined') });
  if (preClaim) more.push({ label: detail.pm_approval_required ? 'Turn off “participant must approve”' : 'Turn on “participant must approve”', icon: Check, onClick: () => setApprovalPolicy(!detail.pm_approval_required) });
  if (more.length) more.push({ divider: true });
  // Not offered once the invoice is in a batch: this route would write a SECOND file for the same
  // lines, the backend refuses it, and the only thing the operator learns is that the menu lied.
  // The batch file is downloaded from NDIA Claims instead.
  if (lines.length > 0 && s !== 'cancelled' && detail.approval_status !== 'declined' && !detail.claim_id) more.push({ label: 'Download NDIS claim (CSV)', icon: FileText, onClick: downloadClaimCsv });
  if (lines.length > 0 && s !== 'cancelled') more.push({ label: 'Download accounting bill (Xero/MYOB)', icon: Download, onClick: downloadAccountingCsv });
  if (detail.plan_id && onOpenPeriods) more.push({ label: 'Funding periods & budgets', icon: Wallet, onClick: onOpenPeriods });
  if (detail.extract_json && preClaim) more.push({ label: 'Revert to AI draft', icon: RefreshCw, onClick: revertExtract });
  if (s !== 'paid' && s !== 'cancelled') { more.push({ divider: true }); more.push({ label: 'Cancel invoice', icon: X, onClick: () => setStatus('cancelled') }); }

  return (
    // Two groups rather than a spacer. A flex-1 spacer only pushes More right while
    // everything fits on one line — the moment the row wraps it takes the leftover space on
    // line one and More drops to the left of line two, which is where it kept ending up.
    // Grouping keeps More on the right at every width.
    <div className={clsx('flex flex-wrap items-center justify-between gap-2', className)}>
      <div className="flex flex-wrap items-center gap-2 min-w-0">
        {preClaim && detail.approval_status !== 'approved' && (
          <button onClick={() => setApproval('approved')} className="btn text-sm bg-white border border-emerald-200 text-emerald-700 hover:bg-emerald-50 active:scale-[.98]"><Check size={14} /> Participant approve</button>
        )}
        {next && <button onClick={next.run} className="btn-primary text-sm">{next.label}</button>}
        {/* The hint explains the primary action, so it is the first thing to give up room
            when the row is tight rather than something that forces the wrap. */}
        {next?.hint && <span className="text-xs text-slate-400 hidden lg:inline truncate max-w-[22rem]">{next.hint}</span>}
      </div>
      {more.length > 0 && (
        <div className="ml-auto shrink-0">
          <Dropdown label="More" items={more} align="right" />
        </div>
      )}
    </div>
  );
}

// CareView-style toolbar dropdown (Data Loader / Create New).
// `align` and per-item `disabled`/`title` are optional additions: omit them and every existing menu
// renders exactly as before. A disabled item stays VISIBLE and says why in its tooltip — hiding it
// would leave an operator hunting for a job they were told this menu holds.
function Dropdown({ label, icon: Icon, items, primary, align, small }) {
  const [open, setOpen] = useState(false);
  // Where the menu sits, measured from the trigger when it opens. The menu is portalled to <body>
  // and fixed to the viewport: rendered inside the page it was clipped by whatever scrolled or
  // hid its overflow — the invoice row's card, the ledger's horizontal scroller — so "More" on an
  // expanded invoice opened BEHIND the sections around it and its items could not be reached.
  // It still flips upward when there is more room above (the bulk bar sits at the bottom).
  const [pos, setPos] = useState(null);
  const ref = React.useRef(null);
  const menuRef = React.useRef(null);
  const MENU_W = 256;
  useEffect(() => {
    if (!open) return undefined;
    const inside = (t) => (ref.current && ref.current.contains(t)) || (menuRef.current && menuRef.current.contains(t));
    const onDown = (e) => { if (!inside(e.target)) setOpen(false); };
    const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };
    // Fixed to the viewport, so if the page moves under it, close rather than float away from
    // its button. Scrolling the menu's own list is not a page move.
    const onMove = (e) => { if (!(menuRef.current && e && e.target && menuRef.current.contains(e.target))) setOpen(false); };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    window.addEventListener('scroll', onMove, true);
    window.addEventListener('resize', onMove);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
      window.removeEventListener('scroll', onMove, true);
      window.removeEventListener('resize', onMove);
    };
  }, [open]);
  const toggle = () => {
    if (open || !ref.current) { setOpen(false); return; }
    const r = ref.current.getBoundingClientRect();
    const vw = window.innerWidth; const vh = window.innerHeight;
    // Roughly what the menu needs: item height x count, capped, plus padding.
    const needed = Math.min(items.length * 38 + 12, 320);
    const below = vh - r.bottom;
    // Only flip when there is genuinely more room above, so a menu near the top of a short
    // window does not flip into the same problem upside down.
    const up = below < needed && r.top > below;
    const left = align === 'left'
      ? Math.max(8, Math.min(r.left, vw - MENU_W - 8))
      : Math.max(8, Math.min(r.right - MENU_W, vw - MENU_W - 8));
    setPos({
      up, left,
      top: up ? undefined : r.bottom + 6,
      bottom: up ? vh - r.top + 6 : undefined,
      maxHeight: Math.max(160, Math.min(vh * 0.7, (up ? r.top : below) - 16)),
    });
    setOpen(true);
  };
  return (
    <div className="relative" ref={ref}>
      <button onClick={toggle} aria-haspopup="menu" aria-expanded={open}
        className={clsx('flex items-center gap-1.5', small ? '!text-xs !px-3 !py-1.5' : 'text-sm', primary ? 'btn-primary' : 'btn-secondary')}>
        {Icon && <Icon size={15} />} {label} <ChevronDown size={14} className={clsx('transition-transform duration-150', open && 'rotate-180')} />
      </button>
      {open && pos && createPortal(
        <div ref={menuRef} role="menu"
          style={{ position: 'fixed', left: pos.left, top: pos.top, bottom: pos.bottom, width: MENU_W, maxHeight: pos.maxHeight }}
          className={clsx('z-[80] font-pm bg-white rounded-xl shadow-pmpop border border-line py-1.5 overflow-y-auto animate-scale-in',
            pos.up ? (align === 'left' ? 'origin-bottom-left' : 'origin-bottom-right') : (align === 'left' ? 'origin-top-left' : 'origin-top-right'))}>
          {items.map((it, i) => it.divider
            ? <div key={i} className="my-1 border-t border-line" />
            : (
              <button key={i} type="button" role="menuitem" disabled={it.disabled} title={it.title}
                onClick={() => { if (it.disabled || !it.onClick) return; setOpen(false); it.onClick(); }}
                className="w-full text-left px-3.5 py-2 text-[13px] text-slate-700 hover:bg-slate-50 flex items-center gap-2.5 transition-colors duration-100 disabled:opacity-40 disabled:hover:bg-transparent disabled:cursor-not-allowed focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-primary-400">
                {it.icon && <it.icon size={15} className="text-slate-400 shrink-0" />} <span className="min-w-0">{it.label}</span>
              </button>
            ))}
        </div>,
        document.body,
      )}
    </div>
  );
}
const Detail = ({ label, value }) => <div><span className="text-xs text-slate-400 block">{label}</span><span className="text-slate-700 break-words">{value}</span></div>;

// Accessible dialog shell shared by every Plan Management modal (~20 of them). Beyond the visual
// chrome it implements the WAI-ARIA dialog pattern: labelled by its title, initial focus moves
// inside, Tab/Shift+Tab are trapped, Escape and backdrop-click close, and focus is restored to the
// element that opened it. Fixing this once makes every dialog in the module keyboard-/SR-accessible.
const FOCUSABLE = 'a[href],button:not([disabled]),textarea:not([disabled]),input:not([disabled]),select:not([disabled]),[tabindex]:not([tabindex="-1"])';
function Modal({ title, subtitle, icon: Icon, onClose, children, footer, wide, page }) {
  const panelRef = useRef(null);
  const titleId = React.useId();

  useEffect(() => {
    if (page) return undefined;                     // page mode: in-flow section, no dialog semantics
    const opener = document.activeElement;          // remember what to restore focus to
    const panel = panelRef.current;
    const list = () => Array.from(panel?.querySelectorAll(FOCUSABLE) || []);
    // Move focus into the dialog (first field, else the panel itself).
    (list()[0] || panel)?.focus?.();

    const onKey = (e) => {
      if (e.key === 'Escape') { onClose(); return; }
      if (e.key !== 'Tab') return;
      const items = list();
      if (!items.length) { e.preventDefault(); panel?.focus?.(); return; }
      const idx = items.indexOf(document.activeElement);
      if (e.shiftKey && idx <= 0)               { e.preventDefault(); items[items.length - 1].focus(); }
      else if (!e.shiftKey && idx === items.length - 1) { e.preventDefault(); items[0].focus(); }
    };
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('keydown', onKey);
      if (opener && typeof opener.focus === 'function') opener.focus();  // restore focus on close
    };
  }, [onClose, page]);

  // Page mode — the same surface rendered as a ROUTED VIEW in the main column (deep-linkable,
  // back-button-friendly, no overlay/focus-trap). The rail drives which one is visible.
  // No title bar of its own: the page header above already names the screen and says what it is
  // for, and a second icon + title + subtitle inside the card said it all again. `aria-label`
  // keeps the section named for assistive tech. Same surface as every other PM panel.
  if (page) {
    return (
      <section aria-label={title} className="bg-white border border-line rounded-2xl shadow-pmcard overflow-hidden">
        <div className="px-5 py-5">{children}</div>
        {footer && <div className="px-5 py-3.5 border-t border-line flex justify-end gap-2 bg-paper/50">{footer}</div>}
      </section>
    );
  }

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center bg-slate-900/50 backdrop-blur-sm overflow-y-auto py-8 px-4" onMouseDown={onClose}>
      <div ref={panelRef} role="dialog" aria-modal="true" aria-labelledby={titleId} tabIndex={-1}
        className={clsx('bg-white rounded-2xl shadow-2xl w-full outline-none', wide ? 'max-w-3xl' : 'max-w-xl')}
        onMouseDown={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100">
          <div className="flex items-center gap-2.5">{Icon && <span className="w-9 h-9 rounded-xl bg-primary-50 text-primary-600 flex items-center justify-center"><Icon size={18} /></span>}<div><h3 id={titleId} className="font-bold text-slate-800 text-base leading-tight">{title}</h3>{subtitle && <p className="text-xs text-slate-400">{subtitle}</p>}</div></div>
          <button onClick={onClose} aria-label="Close dialog" className="text-slate-400 hover:text-slate-600 rounded-lg p-0.5 focus:outline-none focus:ring-2 focus:ring-primary-400"><X size={20} /></button>
        </div>
        <div className="px-6 py-5 max-h-[70vh] overflow-y-auto">{children}</div>
        {footer && <div className="px-6 py-4 border-t border-slate-100 flex justify-end gap-2 bg-slate-50/60 rounded-b-2xl">{footer}</div>}
      </div>
    </div>
  );
}
const Field = ({ label, children, hint }) => (<label className="block"><span className="text-xs font-semibold text-slate-500 mb-1 block">{label}</span>{children}{hint && <span className="text-[11px] text-slate-400 mt-1 block">{hint}</span>}</label>);

// Service from / to, checked here as well as on the server.
//
// The server is the authority (see resolveServiceDates in routes/planManagement.js); this exists so
// the operator is told at the field rather than after a round trip, and so the wording is the same
// in both places. Every invoice form calls it: create, upload and edit used to disagree about
// whether these dates mattered at all -- the edit form did not even show them.
export function serviceDateError(from, to) {
  const a = (from || '').slice(0, 10);
  const b = (to || '').slice(0, 10);
  if (!a || !b) return 'Service from and Service to are both required \u2014 they are the dates the NDIA claims against.';
  if (b < a) return 'Service to cannot be before Service from.';
  return null;
}

// The claim file refused an invoice: say WHICH line and WHY.
//
// The server already answers that precisely - buildClaimRows returns every rejected line with a
// plain-English list of what is wrong with it - and the screen was throwing all of it away to show
// a toast that said only "1 line(s) cannot be claimed yet". The operator was told there was a
// problem and given no way to learn what it was.
function ClaimFileErrors({ data, onClose }) {
  if (!data) return null;
  const errors = Array.isArray(data.errors) ? data.errors : [];
  // The same fault is usually on every line (a missing Organisation ID, a participant with no NDIS
  // number). Listing it once per line buries the ones that differ, so group by the problem text.
  const byProblem = new Map();
  for (const e of errors) {
    for (const pr of (e.problems || [])) {
      if (!byProblem.has(pr)) byProblem.set(pr, []);
      byProblem.get(pr).push(e);
    }
  }
  const groups = [...byProblem.entries()].sort((a, b) => b[1].length - a[1].length);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-2xl max-h-[85vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
        <div className="px-5 py-4 border-b border-slate-100 flex items-start justify-between gap-4">
          <div className="min-w-0">
            <h3 className="text-base font-semibold text-slate-900">This claim file cannot be produced yet</h3>
            <p className="text-[13px] text-slate-500 mt-0.5">
              {errors.length} line{errors.length === 1 ? '' : 's'} would be rejected by the NDIA.
              {data.valid_rows ? ` ${data.valid_rows} other line${data.valid_rows === 1 ? '' : 's'} are fine.` : ''}
              {' '}Fix these and download again.
            </p>
          </div>
          <button onClick={onClose} aria-label="Close" className="text-slate-400 hover:text-slate-600 shrink-0"><X size={18} /></button>
        </div>

        <div className="px-5 py-4 overflow-y-auto space-y-3">
          {groups.map(([problem, lines], i) => (
            <div key={i} className="rounded-xl border border-rose-200 bg-rose-50/60 px-4 py-3">
              <p className="text-[13px] text-rose-900 leading-snug">{problem}</p>
              <p className="text-[12px] text-rose-700/80 mt-1">
                {lines.length === errors.length && errors.length > 1
                  ? `On all ${errors.length} lines.`
                  : `On ${lines.length} line${lines.length === 1 ? '' : 's'}.`}
              </p>
            </div>
          ))}
          {!groups.length && <p className="text-sm text-slate-500">{data.message}</p>}
        </div>

        <div className="px-5 py-3 border-t border-slate-100 flex justify-end">
          <button onClick={onClose} className="px-4 py-2 rounded-xl bg-slate-900 text-white text-sm font-medium hover:bg-slate-800">Close</button>
        </div>
      </div>
    </div>
  );
}

export default function PlanManagement() {
  const confirm = useConfirm();
  const navigate = useNavigate();
  const [invoices, setInvoices] = useState([]);
  const [clients, setClients] = useState([]);
  const [plans, setPlans] = useState([]);
  const [providers, setProviders] = useState([]);
  const [statusFilter, setStatusFilter] = useState('');
  // Dashboard data: null while the first load is in flight, false if it failed. "You're all caught
  // up" and $0.00 are answers, so they are never shown for a load that has not answered.
  const [pipeline, setPipeline] = useState(null);
  const loadPipeline = useCallback(() => { api.get('/plan-management/pipeline').then((r) => setPipeline(r.data || [])).catch(() => setPipeline(false)); }, []);
  const [editReq, setEditReq] = useState(0); // bumped by the "Edit invoice" header button → opens the details editor
  const [dash, setDash] = useState(null);
  const loadDash = useCallback(() => { api.get('/plan-management/dashboard').then((r) => setDash(r.data || {})).catch(() => setDash(false)); }, []);
  useEffect(() => { loadDash(); }, [loadDash]);
  const [selectedId, setSelectedId] = useState(null);
  const [detail, setDetail] = useState(null);
  const [audit, setAudit] = useState([]);
  const [validation, setValidation] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [newModal, setNewModal] = useState(false);
  const [participantModal, setParticipantModal] = useState(false);
  const [importModal, setImportModal] = useState(false);
  const [sentinel, setSentinel] = useState({ alerts: [], total: 0 });
  const loadSentinel = useCallback(() => { api.get('/plan-management/sentinel').then((r) => setSentinel(r.data || { alerts: [], total: 0 })).catch(() => {}); }, []);
  useEffect(() => { loadSentinel(); }, [loadSentinel]);
  // Mailbox unread badge (routes/pmMail.js).
  const [mailUnread, setMailUnread] = useState(0);
  useEffect(() => { api.get('/plan-management/mail/unread-count').then((r) => setMailUnread(r.data?.unread || 0)).catch(() => {}); }, []);
  // Downloads queue badge (new files generated since last viewed).
  const [dlUnread, setDlUnread] = useState(0);
  const loadDlUnread = useCallback(() => { api.get('/plan-management/downloads/unread-count').then((r) => setDlUnread(r.data?.unread || 0)).catch(() => {}); }, []);
  useEffect(() => { loadDlUnread(); }, [loadDlUnread]);
  // The Client List renders this same array, so it needs to know the difference between "still
  // loading" and "genuinely nobody" — an empty register and an unfetched one look identical.
  const [clientsLoading, setClientsLoading] = useState(true);
  // Providers come from the PM route, not /service-providers: the Service Providers screen needs
  // the ABN check, bank verification and invoice counts, and the same rows still carry id+name for
  // the invoice filter's dropdown.
  const [providersLoading, setProvidersLoading] = useState(true);
  const reloadProviders = useCallback(() => {
    setProvidersLoading(true);
    api.get('/plan-management/providers').then((r) => setProviders(Array.isArray(r.data) ? r.data : [])).catch(() => {}).finally(() => setProvidersLoading(false));
  }, []);
  const reloadPickers = useCallback(() => {
    setClientsLoading(true);
    api.get('/plan-management/participants').then((r) => setClients(Array.isArray(r.data) ? r.data : [])).catch(() => {}).finally(() => setClientsLoading(false)); // PM participants ONLY — keep PM data separate from general clients
    api.get('/plan-management/plans').then((r) => setPlans(r.data || [])).catch(() => {});
  }, []);
  const [periodsModal, setPeriodsModal] = useState(null); // { planId }

  useEffect(() => {
    api.get('/plan-management/participants').then((r) => setClients(Array.isArray(r.data) ? r.data : [])).catch(() => {}).finally(() => setClientsLoading(false)); // PM participants ONLY — keep PM data separate from general clients
    api.get('/plan-management/plans').then((r) => setPlans(r.data || [])).catch(() => {});
    reloadProviders();
  }, []);

  // Fetch ALL invoices once; the Invoices table filters by status client-side (instant tabs + counts).
  const loadList = useCallback(() => {
    setLoading(true);
    api.get('/plan-management/invoices')
      .then((r) => setInvoices(r.data || [])).catch(() => toast.error('Failed to load invoices')).finally(() => setLoading(false));
  }, []);
  const loadDetail = useCallback((id) => {
    if (!id) { setDetail(null); setAudit([]); setValidation([]); return; }
    setLoadingDetail(true);
    api.get(`/plan-management/invoices/${id}`).then((r) => setDetail(r.data)).catch(() => toast.error('Failed')).finally(() => setLoadingDetail(false));
    api.get(`/plan-management/invoices/${id}/audit`).then((r) => setAudit(r.data || [])).catch(() => setAudit([]));
    api.get(`/plan-management/invoices/${id}/validation`).then((r) => setValidation(r.data || [])).catch(() => setValidation([]));
  }, []);
  useEffect(() => { loadList(); }, [loadList]);
  useEffect(() => { loadPipeline(); }, [loadPipeline]);
  // Deep-link: /plan-management?openInvoice=<id> (from a participant/provider profile page).
  const [searchParams, setSearchParams] = useSearchParams();
  const view = searchParams.get('view') || 'dashboard';
  const goView = (v) => { const n = new URLSearchParams(searchParams); v === 'dashboard' ? n.delete('view') : n.set('view', v); setSearchParams(n); };
  useEffect(() => {
    const open = searchParams.get('openInvoice');
    if (open) { setSelectedId(open); const next = new URLSearchParams(searchParams); next.delete('openInvoice'); next.set('view', 'service-entry'); setSearchParams(next, { replace: true }); }
    // Mailbox OAuth callback lands back here with a result — toast it and clean the URL.
    const mc = searchParams.get('mail_connected'); const me = searchParams.get('mail_error');
    if (mc || me) {
      if (mc) toast.success(`${mc === 'google' ? 'Gmail' : 'Microsoft 365'} connected${searchParams.get('account') ? ` (${searchParams.get('account')})` : ''}`);
      if (me) toast.error(me);
      const next = new URLSearchParams(searchParams); ['mail_connected', 'mail_error', 'account'].forEach((k) => next.delete(k)); next.set('view', 'settings'); setSearchParams(next, { replace: true });
    }
  }, [searchParams, setSearchParams]);
  // Participants and Providers are modals, but the sidebar addresses every section the same way
  // (?view=…) so it can highlight one. Open the modal and leave the dashboard behind it; closing
  // clears the param, which is what makes the sidebar highlight let go at the same moment.
  // Selecting an invoice now EXPANDS its row rather than filling a side pane, so closing the row
  // (selectedId → null) also has to drop the record: nothing is left holding a stale one.
  const [fullRecord, setFullRecord] = useState(false);
  // The claim file's 422 carries the exact per-line reasons; keep them so they can be shown.
  const [claimErrors, setClaimErrors] = useState(null);
  useEffect(() => { loadDetail(selectedId); if (!selectedId) setFullRecord(false); }, [selectedId, loadDetail]);
  // The funding-periods dialog is the module's own `Modal` — a z-50 overlay rendered in the page,
  // not a portal — so it would land UNDER the record drawer. Stand the drawer down as it opens; the
  // row underneath is still open, so closing the dialog returns the operator where they were.
  const openPeriods = (planId) => { setFullRecord(false); setPeriodsModal({ planId }); };
  const refresh = () => { loadList(); loadPipeline(); loadDash(); if (selectedId) loadDetail(selectedId); };

  // Clone a row into a fresh draft built from the same lines. InvoiceEntry's own Clone is a drawer
  // that only opens once a participant has been chosen and it exposes no clone-source prop, so the
  // list has nothing to hand it; this drives the same two public routes the entry screen uses to
  // build an invoice (POST /invoices, then POST /invoices/:id/lines) and opens the result in the
  // detail pane, which is already a full line editor. Deliberately NOT copied: the provider's
  // invoice number (provider + number is the duplicate key, and copying it is exactly the mistake
  // the 409 exists to catch) and the attachment — a clone is a new invoice, not a re-send.
  const cloneInvoice = async (v) => {
    const who = `${v.first_name || ''} ${v.last_name || ''}`.trim();
    if (!await confirm(`Create a new draft invoice from this one${who ? ` for ${who}` : ''}? Every line is copied; the provider's invoice number is not.`, { title: 'Clone invoice' })) return;
    const t = toast.loading('Cloning…');
    try {
      const { data: src } = await api.get(`/plan-management/invoices/${v.id}`);
      const { data: created } = await api.post('/plan-management/invoices', {
        client_id: src.client_id, plan_id: src.plan_id || null,
        provider_id: src.provider_id || null, provider_name: src.provider_name || null,
        invoice_date: isoToday(),
        service_date_from: src.service_date_from ? String(src.service_date_from).slice(0, 10) : null,
        service_date_to: src.service_date_to ? String(src.service_date_to).slice(0, 10) : null,
        notes: src.notes || null,
      });
      const d = (x) => (x ? String(x).slice(0, 10) : null);
      let copied = 0; let skipped = 0;
      for (const l of (src.lines || [])) {
        try {
          // One line at a time, because the server re-runs the budget / funding-period / date gates
          // per line — a line the plan can no longer fund must be reported, not silently dropped.
          await api.post(`/plan-management/invoices/${created.id}/lines`, {
            support_item_code: l.support_item_code, support_item_name: l.support_item_name,
            support_category: l.support_category, description: l.description,
            item_date: d(l.item_date), service_date_from: d(l.service_date_from), service_date_to: d(l.service_date_to),
            qty: l.qty, unit: l.unit, unit_of_measure: l.unit_of_measure, unit_price: l.unit_price,
            claim_type: l.claim_type, cancellation_reason: l.cancellation_reason,
            gst_code: l.gst_code, hours: l.hours,
          });
          copied++;
        } catch { skipped++; }
      }
      toast.success(`Cloned — ${copied} line(s) copied${skipped ? `, ${skipped} rejected by a budget or date gate` : ''}. Confirm every service date and add the provider's invoice number.`, { id: t, duration: 7000 });
      setSelectedId(created.id);
      goView('service-entry');
      refresh();
    } catch (e) { toast.error(e.response?.data?.message || 'Could not clone this invoice', { id: t }); }
  };

  const setStatus = async (status, extra = {}) => {
    if (status === 'cancelled' && !await confirm('Cancel this invoice?', { title: 'Cancel', danger: true })) return;
    try { await api.patch(`/plan-management/invoices/${selectedId}/status`, { status, ...extra }); toast.success(`Marked ${cap(status)}`); refresh(); }
    catch (e) {
      // Validation gates (duplicate / conflict-of-interest): offer an explicit override-and-retry.
      const code = e.response?.status === 409 ? e.response?.data?.code : null;
      const ovr = { DUPLICATE: 'overrideDuplicate', COI: 'overrideCoi', PRICE_CAP: 'overrideCap' }[code];
      const titles = { DUPLICATE: 'Suspected duplicate', COI: 'Conflict of interest', PRICE_CAP: 'Over price cap' };
      if (ovr) {
        if (await confirm(e.response.data.message + ' Approve anyway? An override is recorded in the audit trail.', { title: titles[code], danger: true })) {
          return setStatus(status, { ...extra, [ovr]: true });
        }
        return;
      }
      toast.error(e.response?.data?.message || 'Failed');
    }
  };
  const setApproval = async (approval_status) => {
    if (!selectedId) { toast.error('Open an invoice first'); return; }
    try {
      await api.patch(`/plan-management/invoices/${selectedId}/approval`, { approval_status });
      toast.success(`Participant ${approval_status}`);
      // Refresh the row as well as the record. Updating only the detail left the list
      // behind it showing the old status, so the screen looked unchanged and the action
      // looked like it had failed.
      loadDetail(selectedId);
      loadList();
    }
    catch (e) {
      const d = e.response?.data;
      // A refusal by PM role is a permissions answer, not a failure — say which.
      toast.error(d?.code === 'PM_ROLE_DENIED' ? d.message : (d?.message || 'Could not record the participant decision'));
    }
  };
  const sendApproval = async (channel = 'email') => {
    try {
      const r = await api.post(`/plan-management/invoices/${selectedId}/send-approval`, { channel });
      toast.success(r.data?.smsed ? 'Approval link texted to participant' : r.data?.emailed ? 'Approval request emailed to participant' : 'Approval request recorded');
      loadDetail(selectedId);
    } catch (e) { toast.error(e.response?.data?.message || 'Failed'); }
  };
  const setApprovalPolicy = async (required) => {
    if (!detail?.client_id) return;
    try { await api.patch(`/plan-management/participants/${detail.client_id}/approval-policy`, { required }); toast.success(required ? 'Participant now requires approval' : 'Approval no longer required'); loadDetail(selectedId); }
    catch (e) { toast.error(e.response?.data?.message || 'Failed'); }
  };
  const delLine = async (l) => {
    try { await api.delete(`/plan-management/lines/${l.id}`); loadDetail(selectedId); loadList(); }
    catch (e) { toast.error(e.response?.data?.message || 'Failed'); }
  };
  const seedDemo = async () => {
    if (!await confirm('This DELETES all Plan Management data (plan-managed participants, their plans/budgets, and every PM invoice/claim) for this organisation, then loads a fresh demo set. Service-delivery clients, Support Coordination and the price book are NOT touched. Continue?', { title: 'Reset & load demo data', danger: true })) return;
    const t = toast.loading('Resetting & seeding Plan Management…');
    try {
      const r = await api.post('/plan-management/seed-demo', {});
      toast.success(`Demo loaded — ${r.data.participants} participants, ${r.data.providers} providers, ${r.data.invoices} invoices`, { id: t });
      setSelectedId(null); refresh(); loadSentinel(); reloadPickers();
    } catch (e) { toast.error(e.response?.data?.message || 'Seed failed', { id: t }); }
  };
  const revertExtract = async () => {
    if (!await confirm('Discard your edits and restore the original AI extraction?', { title: 'Revert to Extract Generated' })) return;
    try { await api.post(`/plan-management/invoices/${selectedId}/revert-extract`, {}); toast.success('Reverted to the original extraction'); refresh(); }
    catch (e) { toast.error(e.response?.data?.message || 'Failed'); }
  };
  const downloadClaimCsv = async () => {
    try {
      const r = await api.get(`/plan-management/invoices/${selectedId}/claim-csv`, { responseType: 'blob' });
      const url = URL.createObjectURL(r.data); const a = document.createElement('a'); a.href = url; a.download = `ndis-claim-${selectedId.slice(0, 8)}.csv`; a.click(); URL.revokeObjectURL(url);
      refresh();
    } catch (e) {
      const d = e.response?.data;
      // api.js already unwraps a JSON error that arrived as a Blob, so the per-line reasons are
      // here. Show them rather than the count: the count is the one thing that cannot be acted on.
      if (d?.code === 'CLAIM_FILE_INVALID') { setClaimErrors(d); return; }
      toast.error(d?.message || 'Download failed');
    }
  };
  const downloadAccountingCsv = async () => {
    try {
      const r = await api.get(`/plan-management/invoices/${selectedId}/accounting-csv`, { responseType: 'blob' });
      const url = URL.createObjectURL(r.data); const a = document.createElement('a'); a.href = url; a.download = `accounting-bill-${selectedId.slice(0, 8)}.csv`; a.click(); URL.revokeObjectURL(url);
    } catch (e) { toast.error(e.response?.data?.message || 'Download failed'); }
  };

  const locked = detail && ['claimed', 'paid'].includes(detail.status);

  return (
    // py- only: Layout already pads the content area horizontally, and a second p-6 here was
    // spending 48px a side on nothing — width the ledger's columns actually need.
    <div className="pm-root font-pm text-ink py-2 w-full">
      {/* Warm paper canvas — fills the content area behind the whole section */}
      <div aria-hidden className="fixed inset-0 -z-10 bg-paper" />
      {/* One header per screen: breadcrumb (which sidebar group), the screen's own name, and what it
          is for. The dashboard is the front page, so it carries no breadcrumb and no greeting — just
          its name and what it is for, like every other screen. */}
      <header className="flex flex-wrap items-end justify-between gap-x-6 gap-y-3 mb-5">
        <div className="min-w-0">
          {PM_VIEW_META[view]?.group && (
            <nav aria-label="Breadcrumb" className="flex items-center gap-1 text-[12px] text-muted">
              <button type="button" onClick={() => goView('dashboard')}
                className="rounded hover:text-ink transition-colors duration-150 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-400">Plan Management</button>
              <ChevronRight size={12} className="text-muted/60" aria-hidden="true" />
              <span aria-current="page">{PM_VIEW_META[view].group}</span>
            </nav>
          )}
          <h1 className={clsx('font-display text-[26px] font-bold text-ink leading-tight tracking-[-0.02em]', PM_VIEW_META[view]?.group && 'mt-1')} style={{ textWrap: 'balance' }}>
            {PM_VIEW_META[view]?.title || 'Plan Management'}
          </h1>
          {PM_VIEW_META[view]?.desc && (
            <p className="text-[13.5px] text-muted mt-1 max-w-[80ch]" style={{ textWrap: 'pretty' }}>
              {PM_VIEW_META[view].desc}
            </p>
          )}
        </div>
        <div className="flex items-center gap-2 shrink-0">
          {view === 'dashboard' && (
            <button onClick={() => goView('service-entry')} className="btn-secondary text-sm flex items-center gap-1.5"><FileText size={15} className="text-muted" /> All invoices</button>
          )}
          {/* The entry screen IS "new invoice"; offering "New" on top of it is a second way into
              the page you are already on. */}
          {view !== 'entry' && <Dropdown primary label="New" icon={Plus} items={[
            { label: 'New invoice', icon: Receipt, onClick: () => setNewModal(true) },
            { label: 'Upload invoice (AI reads the PDF)', icon: Upload, onClick: () => setImportModal(true) },
            { divider: true },
            { label: 'New participant', icon: User, onClick: () => setParticipantModal(true) },
            { label: 'New provider', icon: Building2, onClick: () => navigate('/plan-management/providers/new') },
          ]} />}
        </div>
      </header>

      {/* Full width. The section nav that used to sit here now lives in the main
          application sidebar, under Plan Management — one navigation, not two, and
          the ledger gets the ~230px back. */}
      <div className="min-w-0">
        {/* Main column */}
        <div className="min-w-0">

      {/* DASHBOARD — the work first. What needs a decision today (full width), then where the money
          sits in the pipeline, then the three headline figures, then the operation's health, the
          latest invoices and the trend. Money in motion is the one pipeline view: the stage tiles and
          the status donut that repeated it are gone, and their drill-throughs into the register live
          on in its "Invoices by status" row. */}
      {view === 'dashboard' && (
      <div className="space-y-4">
        {/* Needs your attention — each row names the one action that clears it */}
        <div className="rounded-2xl bg-white border border-line shadow-pmcard overflow-hidden">
          <div className="px-5 py-3.5 border-b border-line flex items-center gap-2">
            <AlertTriangle size={15} className="text-warn" aria-hidden="true" />
            <h2 className="font-display text-[15px] font-bold text-ink">Needs your attention</h2>
          </div>
          {(() => {
            if (dash === null || pipeline === null) return (
              <div className="divide-y divide-line/70" aria-busy="true" aria-label="Loading what needs your attention">
                {[0, 1, 2].map((i) => (
                  <div key={i} className="flex items-center gap-3 px-5 py-3">
                    <span className="skeleton w-8 h-8 shrink-0" />
                    <span className="skeleton h-4 flex-1 max-w-sm" />
                  </div>
                ))}
              </div>
            );
            if (dash === false || pipeline === false) {
              return <ErrorState message="We couldn't load what needs your attention." onRetry={() => { setDash(null); setPipeline(null); loadDash(); loadPipeline(); loadSentinel(); }} />;
            }
            const cnt = (k) => Number(((Array.isArray(pipeline) ? pipeline : []).find((p) => p.status === k) || {}).count || 0);
            const items = [];
            if (cnt('created') > 0) items.push({ icon: Receipt, tone: 'bg-warn/10 text-warn', label: `${cnt('created')} invoice${cnt('created') === 1 ? '' : 's'} awaiting approval`, action: 'Review', onClick: () => { setStatusFilter('created'); goView('service-entry'); } });
            if (cnt('approved') > 0) items.push({ icon: Landmark, tone: 'bg-primary-50 text-primary-600', label: `${cnt('approved')} approved invoice${cnt('approved') === 1 ? '' : 's'} ready to claim`, action: 'Claim', onClick: () => goView('claims') });
            const over14 = (dash || {}).aging?.over14 || (invoices || []).filter((v) => ['created', 'extract_generated', 'approved', 'on_hold'].includes(v.status) && ageDays(v) > 14).length;
            if (over14 > 0) items.push({ icon: AlertTriangle, tone: 'bg-warn/10 text-warn', label: `${over14} invoice${over14 === 1 ? '' : 's'} older than 14 days — chase before they go stale`, action: 'Review', onClick: () => { setStatusFilter(''); goView('service-entry'); } });
            if ((dash || {}).exceptions?.rejectedClaims > 0) items.push({ icon: Landmark, tone: 'bg-neg/10 text-neg', label: `${dash.exceptions.rejectedClaims} claim batch${dash.exceptions.rejectedClaims === 1 ? '' : 'es'} rejected or part-paid`, action: 'Review', onClick: () => goView('claims') });
            if ((dash || {}).exceptions?.recon > 0) items.push({ icon: Scale, tone: 'bg-warn/10 text-warn', label: `${dash.exceptions.recon} reconciliation exception${dash.exceptions.recon === 1 ? '' : 's'} to resolve`, action: 'Resolve', onClick: () => goView('reconcile') });
            if ((sentinel.total || 0) > 0) items.push({ icon: ShieldAlert, tone: 'bg-neg/10 text-neg', label: `${sentinel.total} alert${sentinel.total === 1 ? '' : 's'} to review`, action: 'Open', onClick: () => goView('alerts') });
            if (items.length === 0) return <div className="px-5 py-10 text-center text-sm text-muted flex items-center justify-center gap-2"><Check size={15} className="text-pos" aria-hidden="true" /> You’re all caught up.</div>;
            return <div className="divide-y divide-line/70">{items.map((it, i) => { const Icon = it.icon; return (
              <button key={i} type="button" onClick={it.onClick} className="w-full flex items-center gap-3 px-5 py-3 hover:bg-paper/60 text-left transition-colors duration-150 focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-primary-400">
                <span className={clsx('w-8 h-8 rounded-lg flex items-center justify-center shrink-0', it.tone)}><Icon size={15} aria-hidden="true" /></span>
                <span className="flex-1 text-[13px] text-ink-soft font-medium">{it.label}</span>
                <span className="text-[12px] font-semibold text-primary-600 flex items-center gap-0.5">{it.action} <ChevronRight size={13} aria-hidden="true" /></span>
              </button>
            ); })}</div>;
          })()}
        </div>

        {/* Money in motion — cash by stage, in stage order; each stage opens the queue that moves it
            on, and its badge is the number of invoices behind that amount (the same /dashboard count
            the amount is summed from). The row underneath opens the Invoices register on a status:
            the drill-throughs the old "Pipeline by stage" tiles gave, plus the side states. */}
        {(() => {
          const d = dash || {};
          const amt = (x) => Number((x || {}).amount || 0);
          const cnt = (x) => Number((x || {}).count || 0);
          const stages = [
            { label: 'Awaiting approval', v: d.awaiting, tone: 'bg-warn', onClick: () => { setStatusFilter('created'); goView('service-entry'); } },
            { label: 'Ready to claim', v: d.readyToClaim, tone: 'bg-primary-500', onClick: () => goView('claims') },
            { label: 'In flight with NDIA', v: d.inFlight, tone: 'bg-primary-400', onClick: () => goView('reconcile') },
            { label: 'Outstanding to providers', v: d.outstandingToProviders, tone: 'bg-ink', onClick: () => goView('payments') },
            { label: 'Provider paid', v: d.providerPaid, tone: 'bg-pos', onClick: () => goView('payments') },
          ];
          const maxAmt = Math.max(1, ...stages.map((s) => amt(s.v)));
          // Counts per status from /pipeline — the source the stage tiles used. "All" is every invoice,
          // as that tile counted it; empty statuses are left out, as the register's own chips do.
          const pipe = Array.isArray(pipeline) ? pipeline : null;
          const byStatus = (k) => Number((((pipe || []).find((p) => p.status === k)) || {}).count || 0);
          const links = pipe ? [
            ['', 'All', pipe.reduce((s, p) => s + (p.count || 0), 0)],
            ...DASH_STATUS_LINKS.map(([k, label]) => [k, label, byStatus(k)]).filter(([, , n]) => n > 0),
          ] : [];
          return (
            <div className="rounded-2xl bg-white border border-line shadow-pmcard overflow-hidden">
              <div className="px-5 pt-4 pb-1 flex items-center justify-between">
                <h2 className="font-display text-[15px] font-bold text-ink">Money in motion</h2>
                <span className="text-[12px] text-muted hidden sm:block">Received, approved, claimed, paid</span>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-5 divide-x divide-line/70">
                {stages.map((s, i) => (
                  <button key={i} type="button" onClick={s.onClick} className="text-left px-5 py-4 hover:bg-paper/60 transition-colors duration-150 focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-primary-400">
                    <div className="flex items-center justify-between gap-2">
                      <span className="text-[12px] font-medium text-ink-soft truncate">{s.label}</span>
                      {dash && (
                        <span className="shrink-0 rounded-full bg-slate-100 px-1.5 py-px text-[11px] font-semibold tabular-nums text-slate-600">
                          {cnt(s.v)}<span className="sr-only"> invoice{cnt(s.v) === 1 ? '' : 's'}</span>
                        </span>
                      )}
                    </div>
                    {/* Mounted only once the figures have answered, so the roll plays when a figure
                        CHANGES (after an approval or a refresh) and never as a count-up on arrival. */}
                    <div className="font-display text-[22px] font-bold tabular-nums text-ink leading-none tracking-[-0.01em] mt-1.5">
                      {dash === null ? <span className="skeleton inline-block h-[22px] w-28 align-top" aria-label="Loading" />
                        : dash === false ? '—' : <MoneyFlow value={amt(s.v)} />}
                    </div>
                    <div className="mt-2 h-1 rounded-full bg-line overflow-hidden">
                      {dash && <div className={clsx('h-full rounded-full', s.tone)} style={{ width: `${Math.max(4, Math.round((amt(s.v) / maxAmt) * 100))}%` }} />}
                    </div>
                  </button>
                ))}
              </div>
              {links.length > 0 && (
                <div className="flex flex-wrap items-center gap-1 border-t border-line px-3.5 py-1.5">
                  <span className="px-1.5 mr-1 text-[12px] text-muted">Invoices by status</span>
                  {links.map(([k, label, n]) => (
                    <button key={k || 'all'} type="button" onClick={() => { setStatusFilter(k); goView('service-entry'); }}
                      className="inline-flex min-h-9 items-center gap-1.5 rounded-lg px-2.5 text-[12px] font-medium text-ink-soft hover:bg-paper hover:text-ink transition-colors duration-150 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-400">
                      {label}
                      <span className="rounded-full bg-slate-100 px-1.5 py-px text-[11px] font-semibold tabular-nums text-slate-600">{n}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>
          );
        })()}

        {/* The three headline figures as equal tiles — each from the same /dashboard field, with the
            same caption, as before. Invoiced this month still opens the register. */}
        {(() => {
          const d = dash || {};
          const mom = (d.thisMonth?.amount || 0) - (d.lastMonth?.amount || 0);
          const momPct = d.lastMonth?.amount ? Math.round((mom / d.lastMonth.amount) * 100) : null;
          const fig = (v) => (dash === null ? <span className="skeleton inline-block h-[26px] w-36 align-top" aria-label="Loading" /> : dash === false ? '—' : fmtMoney(v));
          return (
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <KpiTile label="Funds under management" accent="slate" icon={Wallet} value={fig(d.fundsUnderMgmt || 0)}
                sub={!!dash && <><span className="tabular-nums">{fmtMoney(d.remaining || 0)}</span> remaining across {d.participants ?? 0} participant{d.participants === 1 ? '' : 's'}</>} />
              <button type="button" onClick={() => goView('service-entry')}
                className="grid text-left rounded-xl transition-shadow duration-150 hover:shadow-pmpop focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-400">
                <KpiTile label="Invoiced this month" accent="slate" icon={Receipt} value={fig(d.thisMonth?.amount || 0)}
                  sub={!!dash && (momPct == null
                    ? `${d.thisMonth?.count || 0} invoices`
                    : <><span className={clsx('font-semibold', momPct >= 0 ? 'text-emerald-700' : 'text-rose-600')}><span aria-hidden="true">{momPct >= 0 ? '▲' : '▼'}</span><span className="sr-only">{momPct >= 0 ? 'Up' : 'Down'}</span> {Math.abs(momPct)}%</span> vs last month</>)} />
              </button>
              <KpiTile label="Your fee revenue this month" accent="slate" icon={Percent} value={fig(d.feeRevenue?.month || 0)}
                sub={!!dash && `${fmtMoney(d.feeRevenue?.ytd || 0)} year to date`} />
            </div>
          );
        })()}

        {/* Efficiency & health — how smoothly the operation runs. A dash until /dashboard answers,
            never a 0 that reads as a result. */}
        {(() => {
          const d = dash || {};
          const stat = (label, value, tone, hint) => (
            <div className="min-w-[104px]">
              <div className="text-[11px] text-muted">{label}</div>
              <div className={clsx('font-display text-[20px] font-bold tabular-nums leading-tight mt-0.5', (dash && tone) || 'text-ink')}>{dash ? value : '—'}</div>
              {hint && <div className="text-[10px] text-muted mt-0.5">{hint}</div>}
            </div>
          );
          return (
            <div className="rounded-2xl bg-white border border-line px-5 py-4 shadow-pmcard flex flex-wrap gap-x-8 gap-y-4">
              {stat('Avg turnaround', d.turnaroundDays != null ? `${d.turnaroundDays}d` : '—', null, 'received → paid')}
              {stat('Straight-through', d.straightThroughPct != null ? `${d.straightThroughPct}%` : '—', 'text-pos', 'no manual holds')}
              {stat('Aging > 7 days', d.aging?.over7 ?? 0, (d.aging?.over7 ? 'text-warn' : 'text-ink'), 'open invoices')}
              {stat('Aging > 14 days', d.aging?.over14 ?? 0, (d.aging?.over14 ? 'text-neg' : 'text-ink'), 'chase these')}
              {stat('Rejected claims', d.exceptions?.rejectedClaims ?? 0, (d.exceptions?.rejectedClaims ? 'text-neg' : 'text-ink'))}
              {stat('Recon exceptions', d.exceptions?.recon ?? 0, (d.exceptions?.recon ? 'text-warn' : 'text-ink'))}
              {stat('Participants', d.participants ?? 0)}
              {stat('Providers', d.providers ?? 0)}
            </div>
          );
        })()}

        {/* The latest invoices, then the trend — charts come last */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
          <div className="rounded-2xl bg-white border border-line shadow-pmcard overflow-hidden">
            <div className="px-5 py-3.5 border-b border-line flex items-center justify-between">
              <div className="flex items-center gap-2"><FileText size={15} className="text-muted" aria-hidden="true" /><h2 className="font-display text-[15px] font-bold text-ink">Recent invoices</h2></div>
              <button type="button" onClick={() => goView('service-entry')} className="text-[12px] font-semibold text-primary-600 hover:underline">View all</button>
            </div>
            {(() => {
              const recent = [...(invoices || [])].sort((a, b) => String(b.invoice_date || '').localeCompare(String(a.invoice_date || ''))).slice(0, 6);
              if (recent.length === 0 && loading) return <TableSkeleton rows={4} cols={3} />;
              if (recent.length === 0) return <div className="px-5 py-10 text-center text-sm text-muted">No invoices yet.</div>;
              return <div className="divide-y divide-line/70">{recent.map((v) => (
                <button key={v.id} type="button" onClick={() => { setStatusFilter(''); goView('service-entry'); setSelectedId(v.id); }} className="w-full flex items-center gap-3 px-5 py-2.5 hover:bg-paper/60 text-left transition-colors duration-150 focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-primary-400">
                  <div className="min-w-0 flex-1">
                    <div className="text-[13px] font-medium text-ink truncate">{v.first_name} {v.last_name}</div>
                    <div className="text-[11px] text-muted truncate">{v.provider_display || '—'} · {fmtD(v.invoice_date)}</div>
                  </div>
                  <span className="text-[13px] font-semibold tabular-nums text-ink shrink-0">{money(v.total_amount)}</span>
                  <span className={clsx('badge !text-[10px] shrink-0', INV_STATUS[v.status] || 'badge-gray')}>{statusLabel(v.status)}</span>
                </button>
              ))}</div>;
            })()}
          </div>

          <div className="rounded-2xl bg-white border border-line p-5 shadow-pmcard flex flex-col">
            <h2 className="font-display text-[15px] font-bold text-ink mb-3">Invoices over time</h2>
            {(() => {
              const m = {};
              (invoices || []).filter((v) => v.status !== 'cancelled').forEach((v) => { const k = String(v.invoice_date || '').slice(0, 7); if (k) m[k] = (m[k] || 0) + Number(v.total_amount || 0); });
              const data = Object.keys(m).sort().slice(-6).map((k) => ({ label: (() => { try { return new Date(k + '-01').toLocaleDateString('en-AU', { month: 'short' }); } catch { return k; } })(), total: Math.round(m[k]) }));
              if (data.length === 0 && loading) return <div className="skeleton h-[220px]" aria-busy="true" aria-label="Loading" />;
              if (data.length === 0) return <p className="py-12 text-center text-sm text-muted">No invoices yet.</p>;
              // Fills the card so it lines up with Recent invoices beside it, never shorter than 220px.
              // The chart sits in an absolute box so its own height can't feed back into the card's.
              // Flat fill and no draw-in animation: no gradients, no entrance motion on the dashboard.
              return (
                <div className="relative flex-1 min-h-[220px]">
                  <div className="absolute inset-0">
                    <ResponsiveContainer width="100%" height="100%">
                      <AreaChart data={data} margin={{ top: 5, right: 8, left: 0, bottom: 0 }}>
                        <XAxis dataKey="label" tick={{ fontSize: 11, fill: '#64626d' }} axisLine={false} tickLine={false} />
                        <YAxis tick={{ fontSize: 11, fill: '#64626d' }} axisLine={false} tickLine={false} width={44} tickFormatter={(v) => `$${(v / 1000).toFixed(0)}k`} />
                        <Tooltip formatter={(v) => money(v)} contentStyle={{ borderRadius: 12, border: '1px solid #eae8e3', fontSize: 12 }} />
                        <Area type="monotone" dataKey="total" stroke="#4f46e5" strokeWidth={2.2} fill="#4f46e5" fillOpacity={0.08} isAnimationActive={false} />
                      </AreaChart>
                    </ResponsiveContainer>
                  </div>
                </div>
              );
            })()}
          </div>
        </div>
      </div>
      )}

      {/* CLIENT LIST — the participant register. A full screen now, not a picker modal: it is where
          an operator answers "who do we manage, and is their access set up", which a dialog cannot
          hold. Opening a row goes to the participant's own profile page. */}
      {view === 'participants' && (
        <ClientListTable
          rows={clients} loading={clientsLoading}
          onOpen={(id) => navigate(`/plan-management/participants/${id}`)}
          onNew={() => setParticipantModal(true)}
          onRefresh={reloadPickers}
        />
      )}

      {/* POWERVIEW — the reporting launcher plus the custom column builder. */}
      {view === 'powerview' && (
        <PowerviewView onGo={(to) => { const n = new URLSearchParams(to.replace(/^\?/, '')); setSearchParams(n); }} />
      )}

      {/* SERVICE PROVIDERS — the payability register. A full screen, not a picker: before a pay run
          you need to scan who is actually payable, which is a table, not a dialog. */}
      {view === 'providers' && (
        <ProviderListTable
          rows={providers} loading={providersLoading}
          onOpen={(id) => navigate(`/plan-management/providers/${id}`)}
          onNew={() => navigate('/plan-management/providers/new')}
          onRefresh={reloadProviders}
        />
      )}

      {view === 'service-entry' && (
      /* Full-width ledger. There is no side pane: clicking an invoice expands it IN PLACE — note,
         attachments, the verdict line, the line items and the workflow actions — which is what lets
         every column fit instead of the table being squeezed into half a screen. The heavy material
         (plan context, category budgets, line editing, activity) is one click further, behind
         "Open full record", in the drawer below. */
      <>
        <ServiceEntryTable
          invoices={invoices} loading={loading} selectedId={selectedId} onSelect={setSelectedId}
          onRefresh={refresh} onNew={() => setNewModal(true)} providers={providers}
          statusFilter={statusFilter} setStatusFilter={setStatusFilter} onClone={cloneInvoice}
          onBulkImport={() => setImportModal(true)} onOpenReconcile={() => goView('reconcile')}
          detail={detail} loadingDetail={loadingDetail} validation={validation}
          onOpenFull={() => setFullRecord(true)}
          recordActions={(
            <WorkflowActions
              detail={detail} className="flex-1 min-w-0"
              setStatus={setStatus} setApproval={setApproval} sendApproval={sendApproval}
              setApprovalPolicy={setApprovalPolicy} downloadClaimCsv={downloadClaimCsv}
              downloadAccountingCsv={downloadAccountingCsv} revertExtract={revertExtract}
              onOpenPeriods={detail?.plan_id ? () => openPeriods(detail.plan_id) : undefined}
            />
          )}
        />

        {/* The full record — everything the old detail pane held, now opened deliberately rather
            than sitting half-empty beside the list. */}
        <DetailDrawer
          open={!!(fullRecord && selectedId)} onClose={() => setFullRecord(false)} width="xl"
          icon={Receipt}
          title={detail?.provider_display || 'Invoice record'}
          subtitle={detail ? `${detail.first_name || ''} ${detail.last_name || ''}`.trim() || detail.reference : ''}
          bodyClassName="bg-paper/40">
        {(loadingDetail || !detail ? (
          <div className="card"><TableSkeleton rows={6} cols={4} /></div>
        ) : (
          <div className="space-y-5">
            <div className="rounded-2xl bg-white border border-line p-5 shadow-pmcard">
              <div className="flex items-start justify-between gap-4 flex-wrap">
                <div>
                  <div className="flex items-center gap-2 flex-wrap">
                    <h2 className="font-display text-[19px] font-bold text-ink">{detail.provider_display || 'Provider'}</h2>
                    <span className={clsx('badge', INV_STATUS[detail.status] || 'badge-gray')}>{statusLabel(detail.status)}</span>
                    <span className={clsx('badge', APPROVAL[detail.approval_status] || 'badge-gray')}>Participant: {cap(detail.approval_status)}</span>
                    {detail.duplicate_flag && <span className="badge bg-amber-50 text-amber-700 inline-flex items-center gap-1" title="Possible duplicate of another invoice — review before approving"><AlertTriangle size={11} /> Possible duplicate</span>}
                    {detail.coi_flag && <span className="badge bg-orange-50 text-orange-700 inline-flex items-center gap-1" title={detail.coi_reason || 'Conflict of interest'}><AlertTriangle size={11} /> Conflict of interest</span>}
                  </div>
                  <div className="flex flex-wrap gap-x-4 gap-y-1 mt-2 text-xs text-muted">
                    <span className="inline-flex items-center gap-1"><User size={13} className="text-muted/60" /> {detail.first_name} {detail.last_name}</span>
                    {detail.provider_invoice_number && <span className="inline-flex items-center gap-1"><FileText size={13} className="text-slate-300" /> #{detail.provider_invoice_number}</span>}
                    <span className="inline-flex items-center gap-1">Invoice {fmtD(detail.invoice_date)}</span>
                    {(detail.service_date_from || detail.service_date_to) && <span>Service {fmtD(detail.service_date_from)} – {fmtD(detail.service_date_to)}</span>}
                    {detail.document_url && <a href={detail.document_url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-primary-600 hover:underline"><FileText size={13} /> Original (locked)</a>}
                    {detail.retain_until && <span className="text-slate-400">Retain until {fmtD(detail.retain_until)}</span>}
                  </div>
                  {/* CareView context card — participant + provider at a glance */}
                  <div className="flex flex-wrap gap-x-4 gap-y-1 mt-2 text-[11px] text-muted">
                    {detail.ndis_number && <span>NDIS {detail.ndis_number}</span>}
                    {detail.date_of_birth && <span>Age {Math.floor((Date.now() - new Date(detail.date_of_birth)) / 3.15576e10)}</span>}
                    {detail.service_region && <span>Region {detail.service_region}</span>}
                    {detail.client_email && <span>{detail.client_email}</span>}
                    {detail.client_address_full && <span>{detail.client_address_full}</span>}
                    {(detail.abn || detail.provider_abn) && <span>ABN {detail.abn || detail.provider_abn}</span>}
                    {detail.assigned_to && <span>Assigned: {detail.assigned_to}</span>}
                  </div>
                </div>
                <div className="text-right shrink-0">
                  <p className="font-display text-[26px] font-bold text-ink tabular-nums leading-none">{money(detail.total_amount)}</p>
                  {detail.plan_remaining && <p className={clsx('text-xs font-medium mt-1', detail.plan_remaining.remaining < 0 ? 'text-neg' : 'text-muted')}><Wallet size={11} className="inline" /> {money(detail.plan_remaining.remaining)} plan remaining</p>}
                  {!locked
                    ? <button onClick={() => setEditReq((n) => n + 1)} className="btn-secondary !text-xs !px-3 !py-1.5 mt-2.5 inline-flex items-center gap-1.5"><Pencil size={13} /> Edit invoice</button>
                    : <span className="text-[11px] text-slate-400 mt-2.5 inline-flex items-center gap-1" title="Claimed/paid invoices are locked to protect the submitted claim.">🔒 Locked ({statusLabel(detail.status)})</span>}
                </div>
              </div>

              {/* CareView status stepper — anchorStatus is the furthest stage the invoice actually
                  reached, so on hold / claim error / declined / cancelled render off the path. */}
              <div className="mt-4 pt-4 border-t border-line">
                <StatusStepper status={detail.status} anchorStatus={detail.anchorStatus} />
              </div>

              {/* Workflow actions — the same component the expanded row uses, off the same handlers. */}
              <WorkflowActions
                detail={detail} className="mt-4"
                setStatus={setStatus} setApproval={setApproval} sendApproval={sendApproval}
                setApprovalPolicy={setApprovalPolicy} downloadClaimCsv={downloadClaimCsv}
                downloadAccountingCsv={downloadAccountingCsv} revertExtract={revertExtract}
                onOpenPeriods={() => openPeriods(detail.plan_id)}
              />
              {detail.plan_remaining && detail.plan_remaining.remaining < 0 && (
                <div className="mt-3 flex items-center gap-2 text-sm bg-red-50 text-red-700 border border-red-100 rounded-xl px-4 py-2.5"><AlertTriangle size={16} /> This invoice exceeds the plan's remaining budget.</div>
              )}
            </div>

            {/* Spec 6.2 — every block reason at once, each with its own next step and its
                "will re-check on …" date, so the operator never fixes one and hits the next. Now
                folded into the validation panel so the verdict and the reasons for it are one
                thing, read before the line items rather than buried under them. */}
            <ValidationPanel gates={validation} blocks={detail.block_reasons || []} />

            {/* Editable invoice details (CareView Edit Invoice header) */}
            {!locked && <InvoiceDetailsEditor detail={detail} editReq={editReq} onSaved={() => { loadDetail(selectedId); loadList(); }} />}

            {/* category budgets */}
            {detail.category_budgets && detail.category_budgets.length > 0 && (
              <SectionCard title="Category budgets" icon={Wallet} actions={<span className="badge-gray !text-[10px]">{detail.category_budgets.length}</span>} bodyClass="p-5 grid grid-cols-1 sm:grid-cols-2 gap-4">
                {detail.category_budgets.map((c) => (
                  <BudgetCard key={c.category} label={c.label} budget={c.budget} spent={(c.spent || 0) + (c.committed || 0)} remaining={c.remaining} />
                ))}
              </SectionCard>
            )}

            {/* lines */}
            <div className="card p-0 overflow-hidden">
              <div className="px-5 py-3.5 border-b border-slate-100 flex items-center justify-between"><div className="flex items-center gap-2"><FileText size={16} className="text-slate-400" /><span className="font-semibold text-slate-700 text-sm">Line items</span><span className="badge-gray !text-[10px]">{detail.lines.length}</span></div></div>
              {!locked && <LineForm invId={detail.id} onAdded={() => { loadDetail(selectedId); loadList(); }} />}
              {detail.lines.length === 0 ? <div className="py-10 text-center text-sm text-slate-400">No lines yet — add the invoice's services above.</div> : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-slate-50 text-[11px] uppercase tracking-wide text-slate-400"><tr><th className="px-4 py-2.5 text-left font-semibold">Item</th><th className="px-2 py-2.5 text-left font-semibold">Category</th><th className="px-2 py-2.5 text-left font-semibold">Service from</th><th className="px-2 py-2.5 text-left font-semibold">Service to</th><th className="px-2 py-2.5 text-left font-semibold">Time</th><th className="px-2 py-2.5 text-left font-semibold">Hours</th><th className="px-2 py-2.5 text-right font-semibold">Qty</th><th className="px-2 py-2.5 text-right font-semibold">Unit</th><th className="px-2 py-2.5 text-left font-semibold">GST</th><th className="px-4 py-2.5 text-right font-semibold">Total</th><th className="px-2 py-2.5 text-left font-semibold">Claim</th><th></th></tr></thead>
                    <tbody className="divide-y divide-slate-50">
                      {detail.lines.map((l) => (
                        <LineRow key={l.id} line={l} locked={locked} onSaved={() => { loadDetail(selectedId); loadList(); }} onDelete={delLine} />
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              {/* Validation gates moved up: they answer "can this move forward", which is a
                  question you ask before you read the lines, not after. See ValidationPanel. */}

              {/* The activity trail moved out of the line-items card into its own section below. */}
            </div>

            {/* Audit trail — append-only "why was this paid" history. A timeline in plain words
                ("Demo Admin moved it to Approved"), newest last, each step with who and when. */}
            <SectionCard title="Activity" icon={Clock} actions={<span className="badge-gray !text-[10px] tabular-nums">{audit.length}</span>} bodyClass="px-5 py-4">
              <Timeline dense items={audit.map(auditItem)} />
            </SectionCard>
          </div>
        ))}
        </DetailDrawer>
      </>
      )}

      {/* Routed work surfaces — pages, not dialogs: deep-linkable (?view=…), back-button friendly,
          and they sit beside the rail instead of covering the user's context. */}
      {view === 'mail' && <MailboxView onUnreadChange={setMailUnread} onOpenInvoice={(id) => { goView('service-entry'); setSelectedId(id); }} />}

      {/* Review queues, reports and registers. Each keeps its sub-selection in the URL so a
          queue or report can be linked to directly — an operator sending "look at this" should
          not have to say which tab to click. */}
      {/* Fast invoice entry — provider → participant → contract, then a keyboard-driven line grid
          with live budget validation. This is where a plan manager spends the day, so it is a
          screen of its own rather than a modal on the invoice list. */}
      {view === 'entry' && (
        <InvoiceEntry
          onOpenInvoice={(id) => { goView('service-entry'); setSelectedId(id); }}
          onExit={() => goView('service-entry')}
        />
      )}
      {view === 'queues' && (
        <Queues
          tab={searchParams.get('queue') === 'notifications' ? 'notifications' : 'intake'}
          onTabChange={(t) => { const n = new URLSearchParams(searchParams); n.set('view', 'queues'); t === 'intake' ? n.delete('queue') : n.set('queue', t); setSearchParams(n); }}
        />
      )}
      {view === 'pm-reports' && (
        <Reports
          report={searchParams.get('report') || 'turnaround'}
          onReportChange={(id) => { const n = new URLSearchParams(searchParams); n.set('view', 'pm-reports'); n.set('report', id); setSearchParams(n, { replace: true }); }}
        />
      )}
      {view === 'registers' && (
        <Registers
          tab={searchParams.get('reg') || 'fees'}
          onTabChange={(k) => { const n = new URLSearchParams(searchParams); n.set('view', 'registers'); n.set('reg', k); setSearchParams(n, { replace: true }); }}
        />
      )}
      {view === 'downloads' && <DownloadsView onSeen={loadDlUnread} />}
      {view === 'claims' && <ClaimsModal asPage invoices={invoices} onClose={() => goView('dashboard')} onChanged={() => { refresh(); loadSentinel(); }} />}
      {view === 'payments' && <AbaModal asPage onClose={() => goView('dashboard')} onChanged={() => { refresh(); loadSentinel(); }} onGo={goView} />}
      {view === 'reconcile' && <ReconcileModal asPage onClose={() => goView('dashboard')} onDone={() => { refresh(); loadSentinel(); }} />}
      {view === 'alerts' && <SentinelModal asPage data={sentinel} onClose={() => goView('dashboard')} onRefresh={loadSentinel}
        onGo={(to) => setSearchParams(new URLSearchParams(to.replace(/^\?/, '')))} />}
      {view === 'reports' && <OversightModal asPage onClose={() => goView('dashboard')} onOpenInvoice={(id) => { goView('service-entry'); setSelectedId(id); }} />}
      {view === 'statements' && <StatementModal asPage clients={clients} onClose={() => goView('dashboard')} />}
      {view === 'settings' && <SetupModal asPage onClose={() => goView('dashboard')} onChanged={refresh} onReset={seedDemo} onOpenMail={() => goView('mail')} />}
        </div>{/* /main column */}
      </div>{/* /rail grid */}

      <ClaimFileErrors data={claimErrors} onClose={() => setClaimErrors(null)} />
      {newModal && <NewInvoiceEditor clients={clients} plans={plans} providers={providers}
        onClose={() => setNewModal(false)} onSaved={(id) => { setNewModal(false); loadList(); if (id) { goView('service-entry'); setSelectedId(id); } }} />}
      {importModal && <BulkImportModal clients={clients} onClose={() => setImportModal(false)} onDone={() => { refresh(); }}
        onOpenInvoice={(id) => { setImportModal(false); goView('service-entry'); setSelectedId(id); }} />}
      {participantModal && <ParticipantModal onClose={() => setParticipantModal(false)} onSaved={(planId) => { setParticipantModal(false); reloadPickers(); if (planId) setPeriodsModal({ planId }); }} />}
      {periodsModal && <FundingPeriodsModal planId={periodsModal.planId} onClose={() => setPeriodsModal(null)} onChanged={() => { refresh(); }} />}
    </div>
  );
}

// Funding periods (PACE release schedule). Generate even N-month periods, edit released amounts,
// toggle plan flexibility. When a plan is NOT flexible, line-adds/approvals are capped per period.
function FundingPeriodsModal({ planId, onClose, onChanged }) {
  const confirm = useConfirm();
  const [plan, setPlan] = useState(null);
  const [siblingPlans, setSiblingPlans] = useState([]);
  const [supersedeId, setSupersedeId] = useState('');
  const [periods, setPeriods] = useState([]);
  const [loading, setLoading] = useState(true);
  const [months, setMonths] = useState(3);
  const [busy, setBusy] = useState(false);
  const [cats, setCats] = useState([]);          // [{key,label}]
  const [catVals, setCatVals] = useState({});    // { categoryKey: amount }
  const [savingCats, setSavingCats] = useState(false);
  const [provBudgets, setProvBudgets] = useState({ budgets: [], providers: [], categories: [] });
  const [pbForm, setPbForm] = useState({ provider_id: '', support_category: '', cap_amount: '' });
  const [tab, setTab] = useState('periods');   // periods | categories | providers | supersede
  const load = useCallback(() => {
    setLoading(true);
    Promise.all([
      api.get('/plan-management/plans'),
      api.get(`/plan-management/plans/${planId}/funding-periods`),
      api.get(`/plan-management/plans/${planId}/category-budgets`),
      api.get(`/plan-management/plans/${planId}/provider-budgets`).catch(() => ({ data: { budgets: [], providers: [], categories: [] } })),
    ]).then(([pr, fr, cr, pb]) => {
      setProvBudgets(pb.data || { budgets: [], providers: [], categories: [] });
      const all = pr.data || [];
      const me = all.find((p) => p.id === planId) || null;
      setPlan(me);
      setSiblingPlans(me ? all.filter((p) => p.client_id === me.client_id && p.id !== planId) : []);
      setPeriods(fr.data || []);
      setCats(cr.data?.categories || []);
      const vals = {}; (cr.data?.budgets || []).forEach((b) => { vals[b.support_category] = Number(b.fund_amount) || 0; });
      setCatVals(vals);
    }).catch(() => toast.error('Failed to load')).finally(() => setLoading(false));
  }, [planId]);
  useEffect(() => { load(); }, [load]);

  const supersede = async () => {
    if (!supersedeId) return toast.error('Pick the replacement plan');
    if (!await confirm('Mark this plan superseded? It will be deactivated and linked to the newer plan.', { title: 'Supersede plan', danger: true })) return;
    try { await api.patch(`/plan-management/plans/${planId}/supersede`, { new_plan_id: supersedeId }); toast.success('Plan superseded'); load(); onChanged && onChanged(); }
    catch (e) { toast.error(e.response?.data?.message || 'Failed'); }
  };

  const toggleFlex = async () => {
    try { await api.patch(`/plan-management/plans/${planId}/pace`, { funding_period_flexibility: !plan?.funding_period_flexibility }); load(); onChanged && onChanged(); }
    catch (e) { toast.error(e.response?.data?.message || 'Failed'); }
  };
  const generate = async () => {
    if (periods.length && !await confirm('Regenerate periods? This replaces the current schedule.', { title: 'Regenerate', danger: true })) return;
    setBusy(true);
    try { await api.post(`/plan-management/plans/${planId}/funding-periods/generate`, { period_months: months }); toast.success('Funding periods generated'); load(); onChanged && onChanged(); }
    catch (e) { toast.error(e.response?.data?.message || 'Failed'); } finally { setBusy(false); }
  };
  const saveReleased = async (p, val) => {
    const amt = Number(val);
    if (!Number.isFinite(amt) || amt === Number(p.released_amount)) return;
    try { await api.put(`/plan-management/funding-periods/${p.id}`, { released_amount: amt }); load(); onChanged && onChanged(); }
    catch (e) { toast.error(e.response?.data?.message || 'Failed'); }
  };
  const saveCats = async () => {
    setSavingCats(true);
    try {
      const budgets = cats.map((c) => ({ support_category: c.key, fund_amount: Number(catVals[c.key]) || 0 }));
      await api.put(`/plan-management/plans/${planId}/category-budgets`, { budgets });
      toast.success('Category budgets saved'); load(); onChanged && onChanged();
    } catch (e) { toast.error(e.response?.data?.message || 'Failed'); } finally { setSavingCats(false); }
  };
  const savePb = async () => {
    if (!pbForm.provider_id || !pbForm.support_category) return toast.error('Pick a provider and category');
    try { await api.put(`/plan-management/plans/${planId}/provider-budgets`, pbForm); toast.success('Provider cap saved'); setPbForm({ provider_id: '', support_category: '', cap_amount: '' }); load(); }
    catch (e) { toast.error(e.response?.data?.message || 'Failed'); }
  };
  const deletePb = async (id) => {
    try { await api.put(`/plan-management/plans/${planId}/provider-budgets`, { delete: id }); load(); }
    catch (e) { toast.error(e.response?.data?.message || 'Failed'); }
  };
  const totalReleased = periods.reduce((a, p) => a + (Number(p.released_amount) || 0), 0);
  const catTotal = cats.reduce((a, c) => a + (Number(catVals[c.key]) || 0), 0);
  const flexible = !!plan?.funding_period_flexibility;

  return (
    <Modal title="Funding periods & budgets" subtitle="Release schedule · category budgets · provider caps · replace plan" icon={Wallet} wide onClose={onClose}
      footer={<button onClick={onClose} className="btn-secondary text-sm">Close</button>}>
      {loading ? <div className="py-10 flex justify-center"><Loader2 className="animate-spin text-slate-300" /></div> : (
        <div className="space-y-4">
          <div className="flex gap-1 border-b border-slate-100">
            {[['periods', 'Release schedule'], ['categories', 'Category budgets'], ['providers', 'Provider caps'], ['supersede', 'Replace plan']].map(([k, l]) => (
              <button key={k} onClick={() => setTab(k)} className={clsx('px-3 py-2 text-sm font-medium border-b-2 -mb-px transition-colors', tab === k ? 'border-primary-500 text-primary-700' : 'border-transparent text-slate-500 hover:text-slate-700')}>{l}</button>
            ))}
          </div>
          {tab === 'periods' && (<div className="space-y-4">
          <div className={clsx('rounded-xl border px-4 py-3 flex items-center justify-between gap-3', flexible ? 'bg-amber-50 border-amber-100' : 'bg-slate-50 border-slate-100')}>
            <div>
              <p className="text-sm font-semibold text-slate-700">Period flexibility {flexible ? 'ON' : 'OFF'}</p>
              <p className="text-xs text-slate-500">{flexible ? 'Spend can move across periods — only the plan total is enforced.' : 'Spend is capped to each period\'s released amount.'}</p>
            </div>
            <button onClick={toggleFlex} className={clsx('text-sm px-3 py-1.5 rounded-lg font-medium border', flexible ? 'border-slate-200 text-slate-600 hover:bg-white' : 'border-amber-200 text-amber-700 hover:bg-amber-50')}>{flexible ? 'Turn off' : 'Turn on'}</button>
          </div>

          <div className="flex items-end gap-2 flex-wrap">
            <Field label="Period length"><select value={months} onChange={(e) => setMonths(Number(e.target.value))} className="input-field !w-auto"><option value={1}>Monthly</option><option value={3}>Quarterly</option><option value={6}>Half-yearly</option><option value={12}>Annual</option></select></Field>
            <button onClick={generate} disabled={busy} className="btn-primary text-sm flex items-center gap-1.5">{busy ? <Loader2 size={15} className="animate-spin" /> : <Plus size={15} />} {periods.length ? 'Regenerate' : 'Generate'}</button>
            {plan && <span className="text-xs text-slate-400 ml-auto">Plan budget {money(plan.budget)} · released {money(totalReleased)}</span>}
          </div>

          {periods.length === 0 ? (
            <p className="py-8 text-center text-sm text-slate-400">No funding periods yet. Generate a release schedule from the plan dates.</p>
          ) : (
            <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-[11px] uppercase tracking-wide text-slate-400"><tr><th className="px-3 py-2 text-left font-semibold">#</th><th className="px-3 py-2 text-left font-semibold">Period</th><th className="px-3 py-2 text-right font-semibold">Released</th><th className="px-3 py-2 text-right font-semibold">Committed</th><th className="px-3 py-2 text-right font-semibold">Remaining</th></tr></thead>
              <tbody className="divide-y divide-slate-50">
                {periods.map((p) => {
                  const rem = (Number(p.released_amount) || 0) - (Number(p.committed) || 0);
                  return (
                    <tr key={p.id}>
                      <td className="px-3 py-2 text-slate-500">{p.period_no}</td>
                      <td className="px-3 py-2 text-slate-600 whitespace-nowrap">{fmtD(p.start_date)} – {fmtD(p.end_date)}</td>
                      <td className="px-3 py-2 text-right"><input type="number" step="0.01" defaultValue={Number(p.released_amount) || 0} onBlur={(e) => saveReleased(p, e.target.value)} className="input-field !w-28 text-right tabular-nums" /></td>
                      <td className="px-3 py-2 text-right tabular-nums text-slate-500">{money(p.committed)}</td>
                      <td className={clsx('px-3 py-2 text-right tabular-nums font-semibold', rem < 0 ? 'text-red-600' : 'text-emerald-600')}>{money(rem)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            </div>
          )}
          <p className="text-[11px] text-slate-400">Editing a released amount saves on blur. Caps apply to invoice lines by their service date when flexibility is off.</p>
          </div>)}

          {tab === 'categories' && (
          <div>
            <div className="flex items-center justify-between mb-2">
              <p className="text-xs font-bold uppercase tracking-wide text-slate-400">Category budgets — which services the funding covers</p>
              <span className="text-xs text-slate-400">Total {money(catTotal)}{plan ? ` of ${money(plan.budget)} plan` : ''}</span>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              {cats.map((c) => (
                <div key={c.key} className="flex items-center gap-2">
                  <span className="text-sm text-slate-600 flex-1 truncate" title={c.label}>{c.label}</span>
                  <input type="number" step="0.01" min="0" value={catVals[c.key] ?? ''} onChange={(e) => setCatVals((p) => ({ ...p, [c.key]: e.target.value }))} placeholder="0.00" className="input-field !w-28 text-right tabular-nums" />
                </div>
              ))}
            </div>
            <div className="flex items-center justify-between mt-2">
              <p className="text-[11px] text-slate-400">Set $0 for categories this plan doesn't fund. Invoices are validated against these — a line in an unbudgeted category is blocked.</p>
              <button onClick={saveCats} disabled={savingCats} className="btn-secondary text-sm">{savingCats ? '…' : 'Save budgets'}</button>
            </div>
            {catTotal > Number(plan?.budget || 0) + 0.005 && <p className="text-[11px] text-amber-600 mt-1">Category total exceeds the plan budget.</p>}
          </div>
          )}

          {tab === 'providers' && (
          <div>
            <p className="text-xs font-bold uppercase tracking-wide text-slate-400 mb-2">Service-provider category budgets</p>
            {provBudgets.budgets.length > 0 && (
              <div className="overflow-x-auto">
              <table className="w-full text-sm mb-2">
                <thead className="bg-slate-50 text-[11px] uppercase tracking-wide text-slate-400"><tr><th className="px-3 py-1.5 text-left font-semibold">Provider</th><th className="px-3 py-1.5 text-left font-semibold">Category</th><th className="px-3 py-1.5 text-right font-semibold">Cap</th><th className="px-3 py-1.5 text-right font-semibold">Used</th><th></th></tr></thead>
                <tbody className="divide-y divide-slate-50">
                  {provBudgets.budgets.map((b) => (
                    <tr key={b.id}>
                      <td className="px-3 py-1.5 text-slate-700">{b.provider_name || '—'}</td>
                      <td className="px-3 py-1.5 text-slate-500">{(provBudgets.categories.find((c) => c.key === b.support_category) || {}).label || b.support_category}</td>
                      <td className="px-3 py-1.5 text-right tabular-nums">{money(b.cap_amount)}</td>
                      <td className={clsx('px-3 py-1.5 text-right tabular-nums', Number(b.committed) > Number(b.cap_amount) ? 'text-red-600' : 'text-slate-500')}>{money(b.committed)}</td>
                      <td className="px-3 py-1.5 text-right"><button onClick={() => deletePb(b.id)} className="text-slate-400 hover:text-red-600 p-1"><Trash2 size={13} /></button></td>
                    </tr>
                  ))}
                </tbody>
              </table>
              </div>
            )}
            <div className="flex items-end gap-2 flex-wrap">
              <select value={pbForm.provider_id} onChange={(e) => setPbForm((p) => ({ ...p, provider_id: e.target.value }))} className="input-field !w-auto text-sm"><option value="">Provider…</option>{provBudgets.providers.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}</select>
              <select value={pbForm.support_category} onChange={(e) => setPbForm((p) => ({ ...p, support_category: e.target.value }))} className="input-field !w-auto text-sm"><option value="">Category…</option>{provBudgets.categories.map((c) => <option key={c.key} value={c.key}>{c.label}</option>)}</select>
              <input type="number" step="0.01" min="0" value={pbForm.cap_amount} onChange={(e) => setPbForm((p) => ({ ...p, cap_amount: e.target.value }))} placeholder="Cap $" className="input-field !w-28 text-right tabular-nums" />
              <button onClick={savePb} className="btn-secondary text-sm">Add cap</button>
            </div>
            <p className="text-[11px] text-slate-400 mt-1">Caps how much of a category a given provider can bill on this plan — enforced when the invoice is approved. Useful when you are also a provider on the plan.</p>
          </div>
          )}

          {tab === 'supersede' && (
          <div>
            <p className="text-xs font-bold uppercase tracking-wide text-slate-400 mb-2">Plan supersession</p>
            {plan?.superseded_by ? (
              <p className="text-sm text-slate-500">This plan has been superseded and is inactive.</p>
            ) : siblingPlans.length === 0 ? (
              <p className="text-sm text-slate-400">No other plan exists for this participant to supersede with. Create the new plan first.</p>
            ) : (
              <div className="flex items-end gap-2 flex-wrap">
                <Field label="Replaced by plan">
                  <select value={supersedeId} onChange={(e) => setSupersedeId(e.target.value)} className="input-field !w-auto">
                    <option value="">Select newer plan…</option>
                    {siblingPlans.map((p) => <option key={p.id} value={p.id}>{money(p.budget)} · {fmtD(p.start_date)}–{fmtD(p.end_date)}</option>)}
                  </select>
                </Field>
                <button onClick={supersede} className="text-sm px-3 py-2 rounded-lg font-medium border border-amber-200 text-amber-700 hover:bg-amber-50">Mark superseded</button>
              </div>
            )}
          </div>
          )}
        </div>
      )}
    </Modal>
  );
}

// Reconciliation: load claimed invoices, upload a PRODA payment-summary CSV, auto-match by
// amount, confirm → mark matched invoices paid. Reuses PATCH /invoices/:id/status.
// The NDIA payment-results file, spelled out on the upload screen. This is the single detail worth
// copying from the reference: nearly every reconcile support ticket is a wrong or missing column,
// and the fix is showing the expected header set and a real row BEFORE somebody uploads.
//
// The starred columns are the ones NDIA requires. The two we actually MATCH on are called out
// separately, because "required by NDIA" and "required by us to match a payment" are different
// claims and running them together is how a guide starts lying.
// Laid out as the NDIA actually exports it (a real file: "MELISSA 07091309.CSV"): spaced names
// like "Payment Request Number", DD/MM/YYYY dates, amounts to three decimals. The example used to
// show a hyphenated registration number and a hyphenated claim reference, both of which the NDIA
// rejects — a guide showing an invalid row.
const RESULTS_REQUIRED = [
  'RegistrationNumber', 'NDISNumber', 'SupportsDeliveredFrom', 'SupportsDeliveredTo', 'SupportNumber',
  'ClaimReference', 'Quantity', 'UnitPrice', 'GSTCode', 'PaidTotalAmount', 'Payment Request Number',
];
const RESULTS_OPTIONAL = ['Participant Name', 'Capped Price', 'Payment Request Status', 'Error Message', 'ClaimType', 'CancellationReason', 'ABN of Support Provider'];
const RESULTS_EXAMPLE = [
  '4050149119', '430012653', '07/09/2026', '13/09/2026', '01_821_0138_1_1',
  'INV0136L1', '1.000', '6387.000', 'P2', '6387.000', '847780953',
];
function ResultsFileSpec() {
  const [open, setOpen] = useState(false);
  // A template beats a description: the fastest way to get the header row right is to start from
  // one that already is. Example row included so the date and GST formats are unambiguous.
  const downloadTemplate = () => {
    const hdr = [...RESULTS_REQUIRED, ...RESULTS_OPTIONAL].join(',');
    const row = [...RESULTS_EXAMPLE, 'JANE CITIZEN', 'No', 'SUCCESSFUL', '', '', '', '25673100984'].join(',');
    dl(`${hdr}\r\n${row}\r\n`, 'ndia-payment-results-template.csv', 'text/csv');
  };
  return (
    <div className="mt-2.5 rounded-lg border border-slate-200 bg-slate-50/60 overflow-hidden">
      <div className="flex items-center gap-2 px-3 py-2">
        <button type="button" onClick={() => setOpen((o) => !o)} aria-expanded={open}
          className="flex items-center gap-1.5 text-[11.5px] font-semibold text-slate-600 hover:text-slate-800 rounded focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-400">
          <ChevronDown size={13} className={clsx('transition motion-reduce:transition-none', !open && '-rotate-90')} />
          Expected file format
        </button>
        <span className="flex-1" />
        <button type="button" onClick={downloadTemplate} className="btn-secondary !text-[11px] !px-2 !py-1 flex items-center gap-1"><Download size={11} /> Template CSV</button>
      </div>
      {open && (
        <div className="px-3 pb-3 space-y-2.5">
          <div>
            <p className="text-[10px] uppercase tracking-wide font-semibold text-slate-400 mb-1">Columns</p>
            <div className="flex flex-wrap gap-1">
              {RESULTS_REQUIRED.map((c) => (
                <span key={c} className="font-mono text-[10.5px] bg-white border border-slate-200 rounded px-1.5 py-0.5 text-slate-600">
                  <span className="text-rose-500" aria-hidden="true">*</span>{c}
                </span>
              ))}
              {RESULTS_OPTIONAL.map((c) => (
                <span key={c} className="font-mono text-[10.5px] bg-white border border-dashed border-slate-200 rounded px-1.5 py-0.5 text-slate-400">{c}</span>
              ))}
            </div>
            <p className="text-[11px] text-slate-400 mt-1.5">
              <span className="text-rose-500">*</span> required by NDIA. {RESULTS_OPTIONAL.join(', ')} are optional.
            </p>
          </div>
          <div>
            <p className="text-[10px] uppercase tracking-wide font-semibold text-slate-400 mb-1">Example row</p>
            <div className="overflow-x-auto rounded border border-slate-200 bg-white">
              <table className="text-[10.5px] font-mono whitespace-nowrap">
                <thead className="bg-slate-700 text-slate-200">
                  <tr>{RESULTS_REQUIRED.map((c) => <th key={c} className="px-2 py-1 text-left font-semibold">{c}</th>)}</tr>
                </thead>
                <tbody>
                  <tr>{RESULTS_EXAMPLE.map((v, i) => <td key={RESULTS_REQUIRED[i]} className="px-2 py-1 text-slate-600 border-t border-slate-100">{v}</td>)}</tr>
                </tbody>
              </table>
            </div>
          </div>
          <p className="text-[11px] text-slate-500 bg-white border border-slate-200 rounded-lg px-2.5 py-2">
            We match on <span className="font-mono text-slate-700">ClaimReference</span> first, falling back to{' '}
            <span className="font-mono text-slate-700">PaidTotalAmount</span> against a claimed invoice's total. Those two are what
            reconciliation needs — the rest are required by NDIA, not by us. Anything that cannot be matched is recorded as an
            exception rather than silently skipped.
          </p>
        </div>
      )}
    </div>
  );
}

function ReconcileModal({ onClose, onDone, asPage }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [paidAmounts, setPaidAmounts] = useState(null); // Set of amounts parsed from CSV
  const [parsedLines, setParsedLines] = useState([]);   // [{amount, ref}] incl ClaimReference
  const [ticked, setTicked] = useState({});
  const [busy, setBusy] = useState(false);
  const [exceptions, setExceptions] = useState([]);
  // The invoice(s) a payment is being reconciled against by hand. Null = closed.
  const [manualFor, setManualFor] = useState(null);
  const loadExc = () => api.get('/plan-management/reconciliation/items').then((r) => setExceptions((r.data || []).filter((x) => !x.resolved && x.status === 'exception'))).catch(() => {});
  const resolveExc = async (id) => { try { await api.patch(`/plan-management/reconciliation/items/${id}/resolve`, {}); toast.success('Exception resolved'); loadExc(); } catch (e) { toast.error('Failed'); } };
  // Reconcile by hand: the file could not be read, the reference was missing, or the NDIA
  // paid an amount that does not match. Marking an exception "handled" only hid it — the
  // claim stayed unpaid and the books stayed wrong.
  const reconcileManually = async ({ amount, ref, reason, invoiceIds, exceptionId, paidAt }) => {
    try {
      const r = await api.post('/plan-management/reconciliation/manual', {
        amount, ref: ref || null, reason, invoice_ids: invoiceIds, exception_id: exceptionId || null, paid_at: paidAt || null,
      });
      toast.success(`Reconciled — ${r.data.invoices_paid} invoice(s) marked paid`);
      setManualFor(null);
      loadExc();
      const inv = await api.get('/plan-management/invoices', { params: { status: 'claimed' } });
      setRows(inv.data || []);
    } catch (e) {
      toast.error(e.response?.data?.message || 'Could not reconcile');
    }
  };
  useEffect(() => {
    api.get('/plan-management/invoices', { params: { status: 'claimed' } })
      .then((r) => setRows(r.data || [])).catch(() => toast.error('Failed')).finally(() => setLoading(false));
    loadExc();
  }, []);
  const onFile = (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      const text = String(reader.result || '').replace(/^﻿/, '');
      const lines = text.split(/\r?\n/).filter((l) => l.trim());
      // A real CSV split. The old one split on , ; AND tab regardless of quotes, so an NDIA
      // "Error Message" containing a comma shifted every column after it by one.
      const splitRow = (l) => {
        const out = []; let cur = ''; let q = false;
        for (let i = 0; i < l.length; i++) {
          const ch = l[i];
          if (q) { if (ch === '"' && l[i + 1] === '"') { cur += '"'; i++; } else if (ch === '"') q = false; else cur += ch; }
          else if (ch === '"') q = true;
          else if (ch === ',') { out.push(cur.trim()); cur = ''; }
          else cur += ch;
        }
        out.push(cur.trim());
        return out;
      };
      // The NDIA writes amounts to THREE decimals — "6387.000". This accepted at most two, so
      // every line of a genuine NDIA payment file was discarded and the screen said "read 0
      // payment line(s)". Any number of decimals now, rounded to cents.
      const toAmt = (c) => { const m = String(c ?? '').replace(/[$,"\s]/g, ''); return /^-?\d+(\.\d+)?$/.test(m) ? Math.round(parseFloat(m) * 100) / 100 : null; };
      const amts = new Set(); const parsed = [];
      const header = lines.length ? splitRow(lines[0]) : [];
      // Exact NDIA names first, fuzzy only as a fallback. The fuzzy amount pattern matches
      // "Payment Request Number" on the word "Payment", and findIndex takes the FIRST hit — with a
      // numeric request number that silently reconciles against a dollar figure that is really a
      // reference. Hence the exact pass, and the /request/ guard on the fallback.
      const norm = (h) => String(h).replace(/[\s_-]/g, '').toLowerCase();
      const exact = (names) => header.findIndex((h) => names.includes(norm(h)));
      let amtCol = exact(['paidtotalamount', 'paidamount', 'totalpaid', 'amountpaid']);
      if (amtCol < 0) amtCol = header.findIndex((h) => /amount|paid|payment|total|claimed/i.test(h) && !/request|reference|number|status/i.test(h));
      let refCol = exact(['claimreference', 'claimref']);
      if (refCol < 0) refCol = header.findIndex((h) => /claim.?reference|claimref|reference/i.test(h));
      // The columns that say WHAT happened to each line. A payment file lists refused lines too;
      // without the status a rejected line with a matching amount was reconciled as paid.
      const statusCol = exact(['paymentrequeststatus', 'status']);
      const errCol = exact(['errormessage', 'error']);
      const prnCol = exact(['paymentrequestnumber']);
      const fromCol = exact(['supportsdeliveredfrom']);
      const toCol = exact(['supportsdeliveredto']);
      const itemCol = exact(['supportnumber']);
      const ndisCol = exact(['ndisnumber']);
      if (amtCol >= 0) {
        lines.slice(1).forEach((l) => {
          const cols = splitRow(l); const v = toAmt(cols[amtCol]);
          if (v == null) return;
          amts.add(v);
          const at = (k) => (k >= 0 ? (cols[k] || null) : null);
          parsed.push({
            amount: v, ref: at(refCol), status: at(statusCol), error: at(errCol),
            payment_request_number: at(prnCol), from: at(fromCol), to: at(toCol),
            support_number: at(itemCol), ndis_number: at(ndisCol),
          });
        });
      } else {
        // No header match → tighter heuristic: only cents-bearing values or amounts ≥ $50.
        lines.forEach((l) => splitRow(l).forEach((c) => { const v = toAmt(c); if (v != null && (/\.\d+$/.test(String(c).replace(/[$,"\s]/g, '')) || v >= 50)) { amts.add(v); parsed.push({ amount: v, ref: null }); } }));
      }
      setPaidAmounts(amts); setParsedLines(parsed);
      setParseMeta({
        file: file.name,
        amtHeader: amtCol >= 0 ? header[amtCol] : null,
        refHeader: refCol >= 0 ? header[refCol] : null,
        rows: parsed.length,
        refused: parsed.filter((p) => p.status && !/^(successful|success|paid|accepted)$/i.test(p.status)).length,
      });
    };
    reader.readAsText(file);
  };
  const [parseMeta, setParseMeta] = useState(null);
  // Recompute matches whenever the parsed amounts OR the loaded invoices change (avoids a stale
  // closure if the CSV is read before the invoice list finishes loading).
  useEffect(() => {
    if (!paidAmounts) return;
    const tk = {}; rows.forEach((v) => { if (paidAmounts.has(Math.round(Number(v.total_amount) * 100) / 100)) tk[v.id] = true; });
    setTicked(tk);
  }, [paidAmounts, rows]);
  const apply = async () => {
    if (!paidAmounts || paidAmounts.size === 0) return toast.error('Upload a remittance CSV first');
    setBusy(true);
    try {
      const lines = parsedLines.length ? parsedLines : [...paidAmounts].map((a) => ({ amount: a }));
      const r = await api.post('/plan-management/reconciliation', { lines });
      toast.success(`${r.data.matched} matched · ${r.data.exceptions} exception(s) recorded`);
      loadExc(); onDone();
    } catch (e) { toast.error(e.response?.data?.message || 'Failed'); } finally { setBusy(false); }
  };
  const cents = (n) => Math.round(Number(n) * 100) / 100;
  const amtCount = {};
  rows.forEach((v) => { const a = cents(v.total_amount); amtCount[a] = (amtCount[a] || 0) + 1; });
  const matchedCount = paidAmounts ? rows.filter((v) => paidAmounts.has(cents(v.total_amount))).length : 0;
  return (
    <Modal page={asPage} title="Reconcile payments" subtitle="Match the NDIA's payment file to your claimed invoices — anything that doesn't match is flagged, never silently skipped" icon={Check} wide onClose={onClose}
      footer={<>{!asPage && <button onClick={onClose} className="btn-secondary text-sm">Cancel</button>}<button onClick={apply} disabled={busy || !paidAmounts} className="btn-primary text-sm min-w-[120px] flex items-center justify-center gap-2">{busy ? <Loader2 size={15} className="animate-spin" /> : null}Reconcile</button></>}>
      {/* Step 1 — upload, with a plain-English format guide */}
      <div className="mb-3 rounded-xl border border-slate-200 p-3.5">
        <div className="flex items-center justify-between gap-3">
          <div className="flex items-center gap-2 text-sm font-semibold text-slate-700"><span className="w-5 h-5 rounded-full bg-primary-600 text-white text-[10px] flex items-center justify-center">1</span> Upload the NDIA payment file</div>
          <label className="btn-secondary text-sm cursor-pointer">Choose CSV<input type="file" accept=".csv,text/csv" onChange={onFile} className="hidden" /></label>
        </div>
        <p className="text-[11px] text-slate-400 mt-2">Export the payment results from NDIA myplace. We find the columns by NAME, so the order below does not have to be exact — but every required column has to be present and spelled as NDIA spells it.</p>
        <ResultsFileSpec />
        {/* Show-your-work preview: exactly what we read from the file, before anything matches */}
        {parseMeta && (
          <div className="mt-3 rounded-lg border border-slate-100 bg-slate-50/60 p-3">
            <div className="text-[11px] text-slate-500 mb-1.5">
              <strong className="text-slate-700">{parseMeta.file}</strong> — read <strong>{parseMeta.rows}</strong> payment line(s)
              {parseMeta.amtHeader ? <> from column <span className="font-mono">"{parseMeta.amtHeader}"</span></> : <> using the no-header heuristic (dollar-like values)</>}
              {parseMeta.refHeader && <>, references from <span className="font-mono">"{parseMeta.refHeader}"</span></>}.
              {!parseMeta.amtHeader && <span className="text-amber-600"> Check the sample below looks like real payment amounts before reconciling.</span>}
              {parseMeta.refused > 0 && <span className="text-rose-700"> {parseMeta.refused} line(s) were NOT paid by the NDIA — they will be recorded as exceptions, never as paid.</span>}
            </div>
            {parsedLines.length > 0 && (
              <div className="flex flex-wrap gap-1.5">
                {parsedLines.slice(0, 8).map((p, i) => {
                  const refused = p.status && !/^(successful|success|paid|accepted)$/i.test(p.status);
                  return (
                    <span key={i} title={p.error || p.status || ''} className={clsx('text-[10px] border rounded-md px-1.5 py-0.5 tabular-nums',
                      refused ? 'bg-rose-50 border-rose-200 text-rose-700' : 'bg-white border-slate-200 text-slate-600')}>
                      {p.ref ? `${p.ref} · ` : ''}{money(p.amount)}{p.status ? ` · ${p.status}` : ''}
                    </span>
                  );
                })}
                {parsedLines.length > 8 && <span className="text-[10px] text-slate-400 py-0.5">+{parsedLines.length - 8} more</span>}
              </div>
            )}
          </div>
        )}
      </div>
      {/* Step 2 — review */}
      {paidAmounts && (
        <div className="mb-3 flex items-center gap-2 rounded-xl border border-primary-100 bg-primary-50/60 px-4 py-2.5 text-sm text-primary-800">
          <span className="w-5 h-5 rounded-full bg-primary-600 text-white text-[10px] flex items-center justify-center shrink-0">2</span>
          <span>Matched <strong>{matchedCount}</strong> of {rows.length} claimed invoice{rows.length === 1 ? '' : 's'} by amount. Tick or untick below, then <strong>Reconcile</strong>.</span>
        </div>
      )}
      <div className="rounded-xl border border-slate-200 overflow-hidden">
        <div className="px-4 py-2.5 bg-slate-50/70 border-b border-slate-100 flex items-center gap-2.5">
          {/* Step 2 (the match summary) only exists once a file is read, so this step is numbered
              after it only then — "1, 3" with nothing between read like a missing step. */}
          <span className="w-5 h-5 rounded-full bg-primary-600 text-white text-[10px] flex items-center justify-center shrink-0">{paidAmounts ? 3 : 2}</span>
          <span className="text-sm font-semibold text-slate-700">Claimed invoices awaiting payment</span>
          <span className="text-[11px] text-slate-400">{rows.length} invoice{rows.length === 1 ? '' : 's'}</span>
        </div>
      {loading ? <div className="py-10 flex justify-center"><Loader2 className="animate-spin text-slate-300" /></div>
        : rows.length === 0 ? <div className="py-10 text-center text-sm text-slate-400">Nothing awaiting payment. Once you submit a claim in <strong>NDIA Claims</strong> and the NDIA pays it, the claimed invoices appear here to reconcile.</div>
          : (
            <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-[11px] uppercase tracking-wide text-slate-400"><tr><th className="px-3 py-2"></th><th className="px-3 py-2 text-left font-semibold">Provider</th><th className="px-3 py-2 text-left font-semibold">Participant</th><th className="px-3 py-2 text-left font-semibold">Invoice #</th><th className="px-3 py-2 text-left font-semibold">Invoice date</th><th className="px-3 py-2 text-left font-semibold">Service dates</th><th className="px-3 py-2 text-right font-semibold">Amount</th><th className="px-3 py-2 text-left font-semibold">Match</th></tr></thead>
              <tbody className="divide-y divide-slate-50">
                {rows.map((v) => {
                  const matched = paidAmounts && paidAmounts.has(cents(v.total_amount));
                  const ambiguous = matched && amtCount[cents(v.total_amount)] > 1;
                  return (
                    <tr key={v.id} className="hover:bg-slate-50">
                      <td className="px-3 py-2"><input type="checkbox" checked={!!ticked[v.id]} onChange={(e) => setTicked((p) => ({ ...p, [v.id]: e.target.checked }))} className="w-4 h-4 rounded" aria-label="Include in reconciliation" /></td>
                      <td className="px-3 py-2 text-slate-700">{v.provider_display}</td>
                      <td className="px-3 py-2 text-slate-500">{v.first_name} {v.last_name}</td>
                      <td className="px-3 py-2 text-slate-500 tabular-nums">{v.provider_invoice_number || <span className="text-slate-300">—</span>}</td>
                      <td className="px-3 py-2 text-slate-500 tabular-nums whitespace-nowrap">{fmtD(v.invoice_date)}</td>
                      <td className="px-3 py-2 text-slate-500 tabular-nums whitespace-nowrap">{(v.service_date_from || v.service_date_to) ? `${fmtD(v.service_date_from)} – ${fmtD(v.service_date_to)}` : <span className="text-slate-300">—</span>}</td>
                      <td className="px-3 py-2 text-right tabular-nums font-medium text-slate-700">{money(v.total_amount)}</td>
                      <td className="px-3 py-2">{!paidAmounts ? <span className="text-slate-300">—</span>
                        : ambiguous ? <span className="badge-yellow !text-[10px]" title="More than one claimed invoice has this exact amount — confirm this is the right one before reconciling">Check — same amount</span>
                        : matched ? <span className="badge-green !text-[10px]">Matched</span>
                        : <span className="badge-gray !text-[10px]">No match</span>}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            </div>
          )}
      </div>
      {/* Reconciling by hand has to be reachable without an exception to click: the file may
          never have imported, or the payment may have arrived with no remittance at all. */}
      <div className="mt-4 flex items-center justify-between gap-3 rounded-xl border border-slate-200 bg-slate-50/60 px-4 py-3">
        <p className="text-xs text-slate-500">
          Remittance file not working, or a payment arrived without one? Record it by hand and
          mark the invoices paid.
        </p>
        <button onClick={() => setManualFor({})}
          className="btn-secondary !text-xs !px-3 !py-1.5 inline-flex items-center gap-1.5 shrink-0">
          <Wallet size={13} /> Reconcile manually
        </button>
      </div>

      {manualFor && (
        <ManualReconcileModal
          preset={manualFor}
          claimed={rows}
          onClose={() => setManualFor(null)}
          onSubmit={reconcileManually}
        />
      )}

      {/* Exceptions queue — unmatched / ambiguous remittance lines that need a human */}
      {exceptions.length > 0 && (
        <div className="mt-5">
          <h3 className="text-xs uppercase tracking-wide text-amber-600 font-semibold mb-2 flex items-center gap-1.5"><AlertTriangle size={13} /> Exceptions ({exceptions.length})</h3>
          <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead className="bg-amber-50/60 text-[10px] uppercase tracking-wide text-amber-700"><tr><th className="px-3 py-1.5 text-left font-semibold">Type</th><th className="px-3 py-1.5 text-left font-semibold">Reference</th><th className="px-3 py-1.5 text-right font-semibold">Amount</th><th></th></tr></thead>
            <tbody className="divide-y divide-amber-50">
              {exceptions.map((x) => (
                <tr key={x.id}>
                  <td className="px-3 py-1.5"><span className="badge bg-amber-50 text-amber-700 !text-[10px]">{cap(x.exception_type || 'exception')}</span></td>
                  <td className="px-3 py-1.5 text-slate-500"><span className="font-mono">{x.remittance_ref || '—'}</span>{x.detail && <span className="block text-[10px] text-slate-400 font-sans">{x.detail}</span>}</td>
                  <td className="px-3 py-1.5 text-right tabular-nums">{money(x.remittance_amount)}</td>
                  <td className="px-3 py-1.5 text-right whitespace-nowrap">
                    <button onClick={() => setManualFor({ amount: x.remittance_amount, ref: x.remittance_ref, exceptionId: x.id })}
                      title="Say which invoices this payment covers and mark them paid"
                      className="text-[11px] font-semibold text-primary-600 hover:underline">Reconcile manually</button>
                    <span className="text-slate-200 mx-1.5">|</span>
                    <button onClick={() => resolveExc(x.id)}
                      title="Dismiss without recording a payment — use only if this line is not ours"
                      className="text-[11px] text-slate-400 hover:underline">Dismiss</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          </div>
        </div>
      )}
    </Modal>
  );
}

// NDIA Claims — batch approved invoices → submit via the (mock-first) NdiaGateway → poll Paid/Rejected.
const CLAIM_STATUS = { draft: 'badge-gray', submitted: 'bg-blue-50 text-blue-700', paid: 'bg-emerald-50 text-emerald-700', part_paid: 'bg-amber-50 text-amber-700', rejected: 'bg-red-50 text-red-700' };
// Split a CSV line respecting quoted cells (for the NDIA payment-results upload).
const splitCsv = (line) => { const out = []; let cur = ''; let q = false; for (let i = 0; i < line.length; i++) { const ch = line[i]; if (q) { if (ch === '"') { if (line[i + 1] === '"') { cur += '"'; i++; } else q = false; } else cur += ch; } else if (ch === '"') q = true; else if (ch === ',') { out.push(cur); cur = ''; } else cur += ch; } out.push(cur); return out; };
function ClaimsModal({ invoices, onClose, onChanged, asPage }) {
  const [claims, setClaims] = useState([]);
  const [gw, setGw] = useState(null);
  const [busy, setBusy] = useState('');
  const fileRef = useRef(null);
  const [resultsFor, setResultsFor] = useState(null);
  const load = () => { api.get('/plan-management/claims').then((r) => setClaims(r.data || [])).catch(() => setClaims([])); };
  useEffect(() => { load(); api.get('/plan-management/claims/gateway').then((r) => setGw(r.data)).catch(() => {}); }, []);
  // NDIA payment-results upload: NDIA pays/rejects individual LINES, so the results file is what
  // turns a claim into paid / part-paid / rejected — and releases rejected invoices for re-claim.
  const uploadResults = async (c, file) => {
    setBusy('r' + c.id);
    try {
      const text = await file.text();
      const rowsRaw = text.split(/\r?\n/).filter((l) => l.trim());
      if (rowsRaw.length < 2) { toast.error('That file has no result rows'); return; }
      const hdr = splitCsv(rowsRaw[0]).map((h) => h.trim().toLowerCase());
      const iRef = hdr.findIndex((h) => /claim.?ref/.test(h));
      const iPaid = hdr.findIndex((h) => /paid.?total|paid.?amount|^paid$/.test(h));
      const iErr = hdr.findIndex((h) => /error|comment|reason/.test(h));
      if (iRef < 0 || iPaid < 0) { toast.error('Could not find the ClaimReference and PaidTotalAmount columns — is this the NDIA payment-results file?'); return; }
      const lines = rowsRaw.slice(1).map(splitCsv)
        .map((cells) => ({ ref: (cells[iRef] || '').trim(), paid: Number(String(cells[iPaid] || '').replace(/[^0-9.-]/g, '')), error: iErr >= 0 ? (cells[iErr] || '').trim() : '' }))
        .filter((r) => r.ref);
      if (!lines.length) { toast.error('No rows with a ClaimReference found'); return; }
      const r = await api.post(`/plan-management/claims/${c.id}/results`, { lines });
      const d = r.data;
      toast.success(`Results recorded — ${d.paid} paid · ${d.short_paid} short-paid · ${d.rejected} rejected${d.unmatched ? ` · ${d.unmatched} unmatched` : ''}`);
      if (d.rejected) toast(`Rejected lines released their invoices back to Approved — fix and re-claim them.`, { icon: 'ℹ️' });
      load(); onChanged && onChanged();
    } catch (e) { toast.error(e.response?.data?.message || 'Could not process the results file'); }
    finally { setBusy(''); }
  };
  const eligible = (invoices || []).filter((v) => v.status === 'approved' && !v.claim_id);
  const createClaim = async () => {
    if (!eligible.length) return toast.error('No approved invoices available to claim');
    setBusy('new');
    try { await api.post('/plan-management/claims', { invoice_ids: eligible.map((v) => v.id) }); toast.success('Claim batch created'); load(); onChanged && onChanged(); }
    catch (e) { toast.error(e.response?.data?.message || 'Failed'); } finally { setBusy(''); }
  };
  const submit = async (c) => {
    setBusy('s' + c.id);
    try { await api.post(`/plan-management/claims/${c.id}/submit`); toast.success('Submitted to NDIA'); load(); }
    catch (e) { toast.error(e.response?.data?.message || 'Failed'); } finally { setBusy(''); }
  };
  const poll = async (c) => {
    setBusy('p' + c.id);
    try {
      const r = await api.post(`/plan-management/claims/${c.id}/poll`, {});
      const st = (r.data?.status || '').toLowerCase();
      if (st === 'paid') toast.success('Claim paid — member invoices marked paid'); else if (st === 'rejected') toast.error(`Rejected: ${r.data.rejection_reason || 'see code'}`); else toast('Still processing…');
      load(); onChanged && onChanged();
    } catch (e) { toast.error(e.response?.data?.message || 'Failed'); } finally { setBusy(''); }
  };
  const downloadCsv = async (c) => {
    try {
      const n = await downloadClaimFiles(c.id, `ndis-claim-${c.ndia_ref || c.batch_ref || c.id.slice(0, 8)}.csv`);
      if (n > 1) toast.success(`${n} files downloaded — upload each one to the portal`);
    } catch (e) { toast.error(e.response?.data?.message || e.message || 'Failed to download claim CSV'); }
  };
  return (
    <Modal page={asPage} title="NDIA Claims" subtitle="Batch approved invoices → submit → upload the NDIA results file" icon={Landmark} wide onClose={onClose}
      footer={asPage ? null : <button onClick={onClose} className="btn-secondary text-sm">Close</button>}>
      <input ref={fileRef} type="file" accept=".csv,text/csv,text/plain" className="hidden"
        onChange={async (e) => { const f = e.target.files?.[0]; e.target.value = ''; if (f && resultsFor) await uploadResults(resultsFor, f); }} />
      {gw && (
        <div className="mb-4 flex items-center gap-2 text-xs">
          <span className={clsx('inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 font-semibold', gw.live ? 'bg-emerald-50 text-emerald-700 border border-emerald-200' : 'bg-slate-100 text-slate-600')}>
            <span className={clsx('w-1.5 h-1.5 rounded-full', gw.live ? 'bg-emerald-500' : 'bg-slate-400')} /> NDIA gateway: {gw.live ? 'Live' : 'Mock (testing)'}
          </span>
          <span className="text-slate-400">{gw.message}</span>
        </div>
      )}

      {/* Step 1 — bundle approved invoices */}
      <div className="rounded-xl border border-slate-200 overflow-hidden mb-4">
        <div className="px-4 py-2.5 bg-slate-50/70 border-b border-slate-100 flex items-center gap-2.5">
          <span className="w-6 h-6 rounded-full bg-primary-600 text-white text-xs font-bold flex items-center justify-center">1</span>
          <span className="text-sm font-bold text-slate-700">Bundle approved invoices into a claim</span>
        </div>
        <div className="p-4 flex flex-wrap items-center justify-between gap-3">
          <div>
            <span className="text-2xl font-bold text-slate-800 tabular-nums">{eligible.length}</span>
            <span className="text-sm text-slate-500"> approved invoice{eligible.length === 1 ? '' : 's'} ready</span>
            {eligible.length > 0 && <span className="text-sm text-slate-400"> · {money(eligible.reduce((s, v) => s + Number(v.total_amount || 0), 0))}</span>}
            <p className="text-[11px] text-slate-400 mt-0.5">{eligible.length ? 'These will be grouped into one NDIS claim batch.' : 'Approve some invoices first — they’ll appear here to claim.'}</p>
          </div>
          <button onClick={createClaim} disabled={busy === 'new' || !eligible.length} className="btn-primary text-sm flex items-center gap-1.5 disabled:opacity-50">
            {busy === 'new' ? <Loader2 size={15} className="animate-spin" /> : <Plus size={15} />} Create claim
          </button>
        </div>
      </div>

      {/* Step 2 — submit, track & record results */}
      <div className="rounded-xl border border-slate-200 overflow-hidden">
        <div className="px-4 py-2.5 bg-slate-50/70 border-b border-slate-100 flex items-center gap-2.5">
          <span className="w-6 h-6 rounded-full bg-primary-600 text-white text-xs font-bold flex items-center justify-center">2</span>
          <span className="text-sm font-bold text-slate-700">Submit to NDIA, then upload the payment results</span>
          <span className="text-[11px] text-slate-400 hidden sm:inline">Download the CSV → lodge in PRODA → Submit → Check status → Results</span>
        </div>
      {claims.length === 0 ? (
        <div className="text-center py-10 text-slate-400 text-sm">No claim batches yet — create one above.</div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead><tr className="border-b border-slate-100 text-[11px] uppercase tracking-wide text-slate-400">
              <th className="text-left px-3 py-2 font-semibold">Batch</th><th className="text-left px-3 py-2 font-semibold">Invoices</th><th className="text-right px-3 py-2 font-semibold">Total</th><th className="text-left px-3 py-2 font-semibold">Status</th><th className="text-left px-3 py-2 font-semibold" title="The reference the NDIA gives this claim batch once you submit it">NDIA ref</th><th className="text-left px-3 py-2 font-semibold" title="Payment Request Number — the NDIA issues this when a claim is accepted for payment"><span className="border-b border-dotted border-slate-300">PRN</span></th><th></th>
            </tr></thead>
            <tbody className="divide-y divide-slate-50">
              {claims.map((c) => (
                <tr key={c.id} className="hover:bg-slate-50">
                  <td className="px-3 py-2.5 font-mono text-xs text-slate-600">{c.batch_ref}</td>
                  <td className="px-3 py-2.5 text-slate-600">{c.invoice_count}</td>
                  <td className="px-3 py-2.5 text-right tabular-nums font-semibold text-slate-700">{money(c.total_amount)}</td>
                  <td className="px-3 py-2.5"><span className={clsx('badge', CLAIM_STATUS[c.status] || 'badge-gray')}>{cap(c.status)}</span>{['rejected', 'part_paid'].includes(c.status) && c.rejection_reason && <div className="text-[11px] text-red-600 mt-0.5">{c.rejection_reason}</div>}</td>
                  <td className="px-3 py-2.5 font-mono text-[11px] text-slate-500">{c.ndia_ref || '—'}</td>
                  <td className="px-3 py-2.5 font-mono text-[11px] text-slate-500">{c.payment_request_number || '—'}</td>
                  <td className="px-3 py-2.5 text-right whitespace-nowrap space-x-1">
                    <button onClick={() => downloadCsv(c)} className="text-xs px-2.5 py-1.5 rounded-lg font-medium border border-slate-200 text-slate-600 hover:bg-slate-50 inline-flex items-center gap-1"><Download size={12} /> NDIS Claim</button>
                    {['draft', 'rejected'].includes(c.status) && <button onClick={() => submit(c)} disabled={busy === 's' + c.id} className="text-xs px-2.5 py-1.5 rounded-lg font-medium bg-primary-600 text-white hover:bg-primary-700 disabled:opacity-60 inline-flex items-center gap-1"><Landmark size={12} /> {c.status === 'rejected' ? 'Resubmit' : 'Submit'}</button>}
                    {c.status === 'submitted' && <button onClick={() => poll(c)} disabled={busy === 'p' + c.id} className="text-xs px-2.5 py-1.5 rounded-lg font-medium border border-slate-200 text-slate-600 hover:bg-slate-50 disabled:opacity-60 inline-flex items-center gap-1"><RefreshCw size={12} /> Check status</button>}
                    {['submitted', 'part_paid', 'paid', 'rejected'].includes(c.status) && (
                      <button onClick={() => { setResultsFor(c); fileRef.current?.click(); }} disabled={busy === 'r' + c.id}
                        title="Upload the NDIA payment-results CSV — records each line as paid / short-paid / rejected"
                        className="text-xs px-2.5 py-1.5 rounded-lg font-medium border border-slate-200 text-slate-600 hover:bg-slate-50 disabled:opacity-60 inline-flex items-center gap-1">
                        {busy === 'r' + c.id ? <Loader2 size={12} className="animate-spin" /> : <Upload size={12} />} Results
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      </div>
    </Modal>
  );
}

// Add a plan-managed participant (finance-only — no service delivery). Creates client + plan-managed plan.
function ParticipantModal({ onClose, onSaved }) {
  const navigate = useNavigate();
  const confirm = useConfirm();
  const [f, setF] = useState({
    title: '', first_name: '', last_name: '', date_of_birth: '', gender: '', ndis_number: '',
    service_type: 'plan_managed', service_region: '', pace_participant: false, pm_approval_required: false, pm_fee_rate: '',
    statement_opt_out: false, auto_approve_invoices: false, postal_statement: false, dcp_client: false,
    email: '', phone: '', mobile: '', additional_emails: '', address: '', suburb: '', state: '', postcode: '',
    primary_disability: '', other_disability: '', cultural_requirements: '', interests: '', atsi_status: '', consent_to_audit: false, referred_by: '',
    budget: '', start_date: '', end_date: '',
  });
  const [saving, setSaving] = useState(false);
  const [step, setStep] = useState(1);   // 1 = essentials, 2 = optional details
  const set = (k) => (e) => setF((p) => ({ ...p, [k]: e.target.type === 'checkbox' ? e.target.checked : e.target.value }));
  const save = async (force = false) => {
    if (!f.first_name || !f.last_name) { setStep(1); return toast.error('First and last name are required'); }
    setSaving(true);
    try {
      const r = await api.post('/plan-management/participants', { ...f, force });
      toast.success('Participant created');
      const cid = r.data?.client?.id;
      onClose();
      if (cid) navigate(`/plan-management/participants/${cid}`); // land on the new finance profile
      else onSaved && onSaved(r.data?.plan?.id);
    } catch (e) {
      // Duplicate guard (409): this person already exists. Default to opening the existing record
      // (avoids another duplicate); "Cancel" lets them deliberately create a separate one.
      if (e.response?.status === 409 && e.response?.data?.duplicate) {
        const dup = e.response.data.duplicate;
        setSaving(false);
        const openExisting = await confirm(`${e.response.data.message}\n\nClick OK to open the existing participant, or Cancel to create a separate new record anyway.`, { title: 'This participant already exists' });
        if (openExisting) { onClose(); navigate(`/plan-management/participants/${dup.id}`); }
        else { save(true); } // they insist it's a different person → create anyway
        return;
      }
      toast.error(e.response?.data?.message || 'Failed');
    } finally { setSaving(false); }
  };
  const I = 'w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500';
  const L = 'block text-[11px] font-medium text-slate-500 mb-1';
  const Sec = ({ title }) => <div className="col-span-2 mt-1 mb-0.5 text-[11px] font-bold uppercase tracking-wider text-slate-400 border-b border-slate-100 pb-1">{title}</div>;
  return (
    <Modal title="New participant" subtitle={step === 1 ? 'Step 1 of 2 — the essentials' : 'Step 2 of 2 — optional details'} icon={User} wide onClose={onClose}
      footer={<>
        <button onClick={onClose} className="btn-secondary text-sm">Cancel</button>
        {step === 1
          ? <button onClick={() => setStep(2)} className="btn-secondary text-sm">Add more details →</button>
          : <button onClick={() => setStep(1)} className="btn-secondary text-sm">← Back</button>}
        <button onClick={save} disabled={saving} className="btn-primary text-sm min-w-[150px] flex items-center justify-center gap-2">{saving ? <Loader2 size={15} className="animate-spin" /> : <Plus size={15} />}{saving ? 'Creating…' : 'Create participant'}</button>
      </>}>
      {/* Step indicator */}
      <div className="flex items-center gap-2 mb-4">
        {[[1, 'Essentials'], [2, 'More details']].map(([n, lbl], i) => (
          <React.Fragment key={n}>
            {i > 0 && <div className="flex-1 h-px bg-slate-200" />}
            <button type="button" onClick={() => setStep(n)} className="flex items-center gap-1.5 text-xs font-semibold">
              <span className={clsx('w-5 h-5 rounded-full flex items-center justify-center text-[10px]', step === n ? 'bg-primary-600 text-white' : step > n ? 'bg-emerald-500 text-white' : 'bg-slate-200 text-slate-500')}>{step > n ? '✓' : n}</span>
              <span className={step === n ? 'text-slate-800' : 'text-slate-400'}>{lbl}</span>
            </button>
          </React.Fragment>
        ))}
      </div>

      {step === 1 ? (
        <div className="grid grid-cols-2 gap-x-4 gap-y-3">
          <Sec title="Who is the participant?" />
          <div><label className={L}>First name *</label><input className={I} value={f.first_name} onChange={set('first_name')} autoFocus /></div>
          <div><label className={L}>Last name *</label><input className={I} value={f.last_name} onChange={set('last_name')} /></div>
          <div><label className={L}>Date of birth</label><input type="date" className={I} value={f.date_of_birth} onChange={set('date_of_birth')} /></div>
          <div><label className={L}>NDIS number</label><input className={I} value={f.ndis_number} onChange={set('ndis_number')} /></div>
          <div className="col-span-2"><label className={L}>Client type</label><select className={I} value={f.service_type} onChange={set('service_type')}><option value="plan_managed">Plan Management Client</option><option value="support_coordination">Support Coordination Client</option></select></div>

          <Sec title="First funding plan" />
          <div><label className={L}>Plan budget</label><input type="number" step="0.01" className={I} value={f.budget} onChange={set('budget')} placeholder="0.00" /></div>
          <div />
          <div><label className={L}>Plan start</label><input type="date" className={I} value={f.start_date} onChange={set('start_date')} /></div>
          <div><label className={L}>Plan end</label><input type="date" className={I} value={f.end_date} onChange={set('end_date')} /></div>
          <p className="col-span-2 text-[11px] text-slate-400 mt-1">That’s all you need to get started. You can add contact, address and background details now (“Add more details”) or later from the participant’s profile.</p>
        </div>
      ) : (
        <div className="grid grid-cols-2 gap-x-4 gap-y-3">
          <Sec title="Identity" />
          <div><label className={L}>Title</label><select className={I} value={f.title} onChange={set('title')}><option value="">—</option><option>Mr</option><option>Mrs</option><option>Ms</option><option>Mx</option><option>Dr</option><option>Master</option><option>Miss</option></select></div>
          <div><label className={L}>Gender</label><select className={I} value={f.gender} onChange={set('gender')}><option value="">—</option><option>Male</option><option>Female</option><option>Non-binary</option><option>Other</option></select></div>
          <div><label className={L}>Service region</label><select className={I} value={f.service_region} onChange={set('service_region')}><option value="">—</option><option>NSW</option><option>VIC</option><option>QLD</option><option>SA</option><option>WA</option><option>TAS</option><option>ACT</option><option>NT</option><option>Remote</option><option>Very remote</option></select></div>
          <div><label className={L}>Plan management rate</label><input type="number" step="0.01" className={I} value={f.pm_fee_rate} onChange={set('pm_fee_rate')} placeholder="0.00" /></div>

          <Sec title="Options" />
          <label className="flex items-center gap-2 text-sm text-slate-700"><input type="checkbox" checked={f.pace_participant} onChange={set('pace_participant')} /> NDIS PACE participant</label>
          <label className="flex items-center gap-2 text-sm text-slate-700"><input type="checkbox" checked={f.pm_approval_required} onChange={set('pm_approval_required')} /> Requires invoice approval</label>
          <label className="flex items-center gap-2 text-sm text-slate-700"><input type="checkbox" checked={f.auto_approve_invoices} onChange={set('auto_approve_invoices')} /> All invoices are auto-approved</label>
          <label className="flex items-center gap-2 text-sm text-slate-700"><input type="checkbox" checked={f.statement_opt_out} onChange={set('statement_opt_out')} /> Monthly statement opt-out</label>
          <label className="flex items-center gap-2 text-sm text-slate-700"><input type="checkbox" checked={f.postal_statement} onChange={set('postal_statement')} /> Send postal statement</label>
          <label className="flex items-center gap-2 text-sm text-slate-700"><input type="checkbox" checked={f.dcp_client} onChange={set('dcp_client')} /> DCP client</label>

          <Sec title="Contact & address" />
          <div><label className={L}>Email</label><input type="email" className={I} value={f.email} onChange={set('email')} /></div>
          <div><label className={L}>Phone</label><input className={I} value={f.phone} onChange={set('phone')} /></div>
          <div><label className={L}>Mobile</label><input className={I} value={f.mobile} onChange={set('mobile')} /></div>
          <div><label className={L}>Additional emails</label><input className={I} value={f.additional_emails} onChange={set('additional_emails')} placeholder="comma-separated" /></div>
          <div className="col-span-2"><label className={L}>Address</label><AddressAutocomplete className={I} value={f.address} onChange={v => setF(p => ({ ...p, address: v }))} onSelect={a => setF(p => ({ ...p, address: a.street || p.address, suburb: a.suburb || p.suburb, state: a.state || p.state, postcode: a.postcode || p.postcode, latitude: a.lat, longitude: a.lon }))} /></div>
          <div><label className={L}>Suburb</label><input className={I} value={f.suburb} onChange={set('suburb')} /></div>
          <div className="grid grid-cols-2 gap-3"><div><label className={L}>State</label><input className={I} value={f.state} onChange={set('state')} /></div><div><label className={L}>Postcode</label><input className={I} value={f.postcode} onChange={set('postcode')} /></div></div>

          <Sec title="Background" />
          <div><label className={L}>Primary disability</label><input className={I} value={f.primary_disability} onChange={set('primary_disability')} /></div>
          <div><label className={L}>Other disability</label><input className={I} value={f.other_disability} onChange={set('other_disability')} /></div>
          <div><label className={L}>Cultural requirements</label><input className={I} value={f.cultural_requirements} onChange={set('cultural_requirements')} /></div>
          <div><label className={L}>Interests</label><input className={I} value={f.interests} onChange={set('interests')} /></div>
          <div><label className={L}>ATSI status</label><select className={I} value={f.atsi_status} onChange={set('atsi_status')}><option value="">—</option><option>Aboriginal</option><option>Torres Strait Islander</option><option>Both</option><option>Neither</option></select></div>
          <div><label className={L}>Referred by</label><input className={I} value={f.referred_by} onChange={set('referred_by')} /></div>
          <label className="flex items-center gap-2 text-sm text-slate-700 self-end pb-2"><input type="checkbox" checked={f.consent_to_audit} onChange={set('consent_to_audit')} /> Consents to auditing</label>
        </div>
      )}
    </Modal>
  );
}

// Pay providers via ABA (Direct Entry) bank file — bank config + generate + mark provider-paid.
function AbaModal({ onClose, onChanged, asPage, onGo }) {
  const [bank, setBank] = useState(null);
  const [rows, setRows] = useState([]);
  const [provMap, setProvMap] = useState({});
  const [ticked, setTicked] = useState({});
  const [busy, setBusy] = useState('');
  // Two-phase pay run (spec 10.1.5): generating the file does not pay anyone. The batch sits here
  // until the uploader confirms the bank processed it — only then do invoices settle and
  // remittances go out. A provider is never told they've been paid before the money moved.
  const [batch, setBatch] = useState(null);
  const [bankRef, setBankRef] = useState('');
  useEffect(() => {
    api.get('/plan-management/bank-settings').then((r) => setBank(r.data || {})).catch(() => setBank({}));
    api.get('/service-providers').then((r) => {
      const m = {}; (r.data || []).forEach((p) => { m[p.id] = { bsb: p.bank_bsb, account: p.bank_account, verified: p.bank_verified !== false, excluded: p.exclude_from_aba === true }; });
      setProvMap(m);
    }).catch(() => {});
    api.get('/plan-management/invoices').then((r) => {
      // An invoice already carrying a payment_batch_id is in a batch that has been cut but not
      // yet marked processed — its status stays 'paid' the whole time. Listing it here, pre-ticked,
      // is how the same provider invoice ended up in a second ABA file and got paid twice.
      // Only 'paid' — meaning the NDIA has paid US for it. The list offered 'approved' and
      // 'claimed' too, pre-ticked, but POST /provider-payment/aba accepts `v.status = 'paid'`
      // alone, so those were silently dropped and the ABA came out short with no explanation.
      // The backend is right: paying a provider before the NDIA reimburses you is paying out of
      // your own float, for money that may yet be capped or refused.
      const cand = (r.data || []).filter((v) => v.status === 'paid' && v.provider_id && !v.payment_batch_id);
      setRows(cand); setTicked(Object.fromEntries(cand.map((v) => [v.id, true])));
    }).catch(() => setRows([]));
  }, []);
  // Per-invoice bank readiness — so the user sees upfront what will/won't be paid.
  const bankStatus = (v) => {
    const p = provMap[v.provider_id];
    if (!p) return { ok: false, label: 'No bank', tone: 'bg-amber-100 text-amber-700' };
    if (p.excluded) return { ok: false, label: 'Excluded', tone: 'bg-slate-100 text-slate-500' };
    if (!p.bsb || !p.account) return { ok: false, label: 'No bank details', tone: 'bg-amber-100 text-amber-700' };
    if (!p.verified) return { ok: false, label: 'Bank unverified', tone: 'bg-amber-100 text-amber-700' };
    return { ok: true, label: `BSB ${String(p.bsb).replace(/\D/g, '').slice(0, 3)}···`, tone: 'bg-emerald-100 text-emerald-700' };
  };
  const generate = async () => {
    const ids = Object.keys(ticked).filter((k) => ticked[k]);
    if (!ids.length) return toast.error('Select at least one invoice');
    setBusy('gen');
    try {
      const r = await api.post('/plan-management/provider-payment/aba', { invoice_ids: ids });
      if (r.data.redownload?.length) {
        r.data.redownload.forEach((b) => dl(b.aba, `${b.batch_ref}.aba`, 'text/plain'));
        toast.success(r.data.message);
        onChanged && onChanged();
        return;
      }
      if (r.data.aba) {
        const blob = new Blob([r.data.aba], { type: 'text/plain' });
        const url = window.URL.createObjectURL(blob); const a = document.createElement('a');
        a.href = url; a.download = `provider-payment-${new Date().toISOString().slice(0, 10)}.aba`;
        document.body.appendChild(a); a.click(); a.remove(); window.URL.revokeObjectURL(url);
        toast.success(`ABA generated — ${r.data.payments} payment(s), ${money(r.data.total)}. Invoices marked provider paid.`);
      } else {
        toast.success('No external payments to make');
      }
      if (r.data.self) toast(`${r.data.self} settled internally (you are the provider — no bank transfer)`, { icon: 'ℹ️' });
      if (r.data.missing?.length) toast(`${r.data.missing.length} skipped (no bank details): ${r.data.missing.slice(0, 3).join(', ')}`, { icon: '⚠️' });
      if (r.data.excluded?.length) toast(`${r.data.excluded.length} excluded from ABA — pay manually: ${r.data.excluded.slice(0, 3).join(', ')}`, { icon: 'ℹ️' });
      if (r.data.unverified?.length) toast(`${r.data.unverified.length} on hold — bank change not verified yet: ${r.data.unverified.slice(0, 3).join(', ')}`, { icon: '⚠️' });
      // Stay open on the confirmation step — the run isn't finished until the bank confirms.
      if (r.data.batch) setBatch(r.data.batch);
      onChanged && onChanged();
    } catch (e) { toast.error(e.response?.data?.message || 'Failed'); } finally { setBusy(''); }
  };
  // Bank has processed the file — settle the invoices and send the remittance advices.
  const markProcessed = async () => {
    if (!bankRef.trim()) return toast.error("Enter the bank's batch reference");
    setBusy('proc');
    try {
      const r = await api.post(`/plan-management/payment-batches/${batch.id}/processed`, { bank_reference: bankRef.trim() });
      // The invoices were already marked provider-paid when the file was generated, so `settled`
      // is usually 0 here; saying '0 invoice(s) marked Provider paid' read as a failure.
      toast.success(r.data.settled ? `${r.data.settled} invoice(s) marked Provider paid` : `${batch.batch_ref || 'Batch'} confirmed as processed at the bank`);
      if (r.data.remitted) toast.success(`Remittance advice emailed to ${r.data.remitted} provider(s)`);
      setBatch(null); setBankRef('');
      onChanged && onChanged(); if (!asPage) onClose();
    } catch (e) { toast.error(e.response?.data?.message || 'Failed'); } finally { setBusy(''); }
  };
  // Totals didn't match at the bank (spec 10.1.4) — stop, void, regenerate.
  const voidBatch = async () => {
    const reason = window.prompt("Why is this batch being voided? (e.g. bank total didn't match)");
    if (!reason || !reason.trim()) return;
    setBusy('void');
    try {
      await api.post(`/plan-management/payment-batches/${batch.id}/void`, { reason: reason.trim() });
      toast.success('Batch voided — the invoices are free to be paid in a new run');
      setBatch(null); setBankRef('');
      onChanged && onChanged();
    } catch (e) { toast.error(e.response?.data?.message || 'Failed'); } finally { setBusy(''); }
  };
  return (
    <Modal page={asPage} title="Pay providers — ABA file" subtitle="Generate a bank Direct Entry (ABA) file for approved / claimed invoices" icon={Landmark} wide onClose={onClose}
      footer={batch ? (
        <>
          <button onClick={voidBatch} disabled={!!busy} className="btn-secondary text-sm">Void batch</button>
          <button onClick={markProcessed} disabled={!!busy || !bankRef.trim()} className="btn-primary text-sm min-w-[190px] flex items-center justify-center gap-2">
            {busy === 'proc' ? <Loader2 size={15} className="animate-spin" /> : <CheckCircle2 size={15} />} Confirm bank processed
          </button>
        </>
      ) : (
        <>{!asPage && <button onClick={onClose} className="btn-secondary text-sm">Cancel</button>}<button onClick={generate} disabled={busy === 'gen' || !Object.values(ticked).some(Boolean)} title={Object.values(ticked).some(Boolean) ? undefined : 'Tick at least one invoice to pay'} className="btn-primary text-sm min-w-[140px] flex items-center justify-center gap-2">{busy === 'gen' ? <Loader2 size={15} className="animate-spin" /> : <Download size={15} />} Download ABA</button></>
      )}>
      {!bank ? <div className="py-8 text-center text-slate-400 text-sm">Loading…</div> : (() => {
        const selRows = rows.filter((v) => ticked[v.id]);
        const payable = selRows.filter((v) => bankStatus(v).ok);
        const payableTotal = payable.reduce((s, v) => s + Number(v.total_amount || 0), 0);
        const bankReady = bank.de_user_id && bank.trace_bsb && bank.trace_account;
        if (batch) return (
          // Step 2 — the file exists but nobody has been paid yet. The operator checks the bank's
          // confirmation screen against these figures BEFORE releasing (spec 10.1.4), then records
          // the bank's reference here to settle the run.
          <div className="space-y-4">
            <div className="flex items-start gap-2 text-sm bg-amber-50 text-amber-800 border border-amber-100 rounded-xl px-4 py-3">
              <AlertTriangle size={16} className="mt-0.5 shrink-0" />
              <span><strong>Nobody has been paid yet.</strong> Upload the file at your bank, then check the bank’s total and payment count match the figures below. If they differ, <strong>void this batch</strong> instead of releasing it.</span>
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div className="rounded-xl border border-slate-200 p-3.5"><div className="text-[11px] text-slate-400 uppercase tracking-wide">Payments</div><div className="text-2xl font-bold text-slate-800 tabular-nums">{batch.payment_count}</div><div className="text-[10px] text-slate-400">one per provider</div></div>
              <div className="rounded-xl border border-emerald-200 bg-emerald-50/50 p-3.5"><div className="text-[11px] text-emerald-600 uppercase tracking-wide">Batch total</div><div className="text-2xl font-bold text-emerald-700 tabular-nums">{money(batch.total_amount)}</div><div className="text-[10px] text-emerald-600/70">{batch.invoice_count} invoice{batch.invoice_count === 1 ? '' : 's'}</div></div>
              <div className="rounded-xl border border-slate-200 p-3.5"><div className="text-[11px] text-slate-400 uppercase tracking-wide">Batch</div><div className="text-sm font-bold text-slate-800 font-mono mt-1">{batch.batch_ref}</div><div className="text-[10px] text-slate-400 font-mono truncate" title={batch.aba_hash}>SHA {String(batch.aba_hash || '').slice(0, 12)}…</div></div>
            </div>
            <div>
              <label className="block text-[11px] font-semibold text-slate-500 uppercase tracking-wide mb-1.5">Bank batch reference</label>
              <input value={bankRef} onChange={(e) => setBankRef(e.target.value)} placeholder="Reference shown by your bank after release"
                className="input w-full text-sm" autoFocus />
              <p className="text-[11px] text-slate-400 mt-1.5">Recorded against every invoice in this run so a payment can be traced back to the bank. Remittance advices are emailed only once you confirm. Not released yet? Close this and confirm it later from Registers → Payment batches.</p>
            </div>
          </div>
        );
        return (
        <div className="space-y-4">
          {!bankReady && (
            <div className="flex items-start gap-2 text-sm bg-amber-50 text-amber-800 border border-amber-100 rounded-xl px-4 py-2.5">
              <AlertTriangle size={16} className="mt-0.5 shrink-0" />
              <span>Your bank details aren’t set up yet. Add them in <strong>Settings → Bank &amp; payments</strong> before generating the payment file.</span>
            </div>
          )}
          {/* Payable summary — the money figure leads; the two counts either side say how the
              selection breaks down. Three zeros over an empty list say nothing, so they wait
              until there is something to pay. */}
          {rows.length > 0 && <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div className="rounded-xl border border-line p-4"><div className="text-[12.5px] font-medium text-ink-soft">Selected</div><div className="font-display text-[24px] font-bold text-ink tabular-nums leading-none mt-2">{selRows.length}</div><div className="text-[12px] text-muted mt-1.5">of {rows.length} waiting to be paid</div></div>
            <div className="rounded-xl border border-emerald-200 bg-emerald-50/40 p-4"><div className="text-[12.5px] font-medium text-emerald-700">Ready to pay</div><div className="font-display text-[24px] font-bold text-emerald-700 tabular-nums leading-none mt-2"><MoneyFlow value={payableTotal} /></div><div className="text-[12px] text-emerald-700/70 mt-1.5">{payable.length} invoice{payable.length === 1 ? '' : 's'} go in the file</div></div>
            <div className="rounded-xl border border-line p-4"><div className="text-[12.5px] font-medium text-ink-soft">Skipped</div><div className={clsx('font-display text-[24px] font-bold tabular-nums leading-none mt-2', selRows.length - payable.length ? 'text-warn' : 'text-ink')}>{selRows.length - payable.length}</div><div className="text-[12px] text-muted mt-1.5">no verified bank details</div></div>
          </div>}
          <div className="rounded-xl border border-line overflow-hidden">
            <div className="px-4 py-2.5 bg-paper/60 border-b border-line text-[13px] font-semibold text-ink">Invoices to pay <span className="text-[12px] font-normal text-muted tabular-nums">{rows.length}</span></div>
            {rows.length === 0 ? (
              // Only RECONCILED invoices can be paid out: the NDIA has to have paid us first. Saying
              // that — and where to go — is the difference between an empty screen and a next step.
              <EmptyState icon={Banknote} title="Nothing to pay yet"
                hint="An invoice lands here once the NDIA has paid you for it (Reconciled) and it is linked to a registered provider. Already paid? Re-select it in Invoices and choose Pay (ABA) to download its file again."
                action={onGo ? <button onClick={() => onGo('reconcile')} className="btn-secondary text-sm flex items-center gap-1.5">Go to Reconcile <ChevronRight size={14} /></button> : null} />
            ) : (
              <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 text-[11px] uppercase tracking-wide text-slate-400"><tr><th className="px-3 py-2"></th><th className="px-3 py-2 text-left font-semibold">Provider</th><th className="px-3 py-2 text-left font-semibold">Participant</th><th className="px-3 py-2 text-left font-semibold">Bank</th><th className="px-3 py-2 text-right font-semibold">Amount</th><th className="px-3 py-2 text-left font-semibold">Status</th></tr></thead>
                <tbody className="divide-y divide-slate-50">
                  {rows.map((v) => { const bs = bankStatus(v); return (
                    <tr key={v.id} className={clsx('hover:bg-slate-50', !bs.ok && ticked[v.id] && 'bg-amber-50/30')}>
                      <td className="px-3 py-2.5"><input type="checkbox" checked={!!ticked[v.id]} onChange={(e) => setTicked((p) => ({ ...p, [v.id]: e.target.checked }))} className="w-4 h-4 rounded" /></td>
                      <td className="px-3 py-2.5 font-medium text-slate-700">{v.provider_display}</td>
                      <td className="px-3 py-2.5 text-slate-500">{v.first_name} {v.last_name}</td>
                      <td className="px-3 py-2.5"><span className={clsx('inline-flex items-center gap-1 text-[10px] font-semibold rounded-full px-2 py-0.5', bs.tone)}>{bs.ok ? <Check size={10} /> : <AlertTriangle size={10} />}{bs.label}</span></td>
                      <td className="px-3 py-2.5 text-right tabular-nums font-bold text-slate-800">{money(v.total_amount)}</td>
                      <td className="px-3 py-2.5"><span className={clsx('badge !text-[10px]', INV_STATUS[v.status] || 'badge-gray')}>{statusLabel(v.status)}</span></td>
                    </tr>
                  ); })}
                </tbody>
              </table>
              </div>
            )}
          </div>
          <p className="text-[11px] text-slate-400">Only providers with verified BSB + account details are included in the ABA file; the rest are reported as skipped. Generating marks those invoices <strong>provider-paid</strong>. Remittance advices are emailed to providers only once you confirm the bank processed the batch — on the next step, or later from Registers → Payment batches.</p>
        </div>
      ); })()}
    </Modal>
  );
}
// Automated plan-management fees — configure setup/monthly fee items + generate fee invoices.
// Setup hub — every Plan Management SETTING in one place (previously scattered inside the Pay
// Providers, Fees and Audit modals): your bank details for paying providers, your NDIS fee items,
// the safety controls, and the demo-data reset. Day-to-day actions (claim, pay, reconcile) live
// on their own screens — this is only configuration you set once and rarely change.
function SetupSection({ icon: Icon, title, desc, children, tone = 'primary' }) {
  const tones = { primary: 'bg-primary-50 text-primary-600', rose: 'bg-rose-50 text-rose-600' };
  return (
    <section className="border border-slate-200 rounded-2xl overflow-hidden">
      <div className="px-4 py-3 bg-slate-50/70 border-b border-slate-100 flex items-center gap-2.5">
        <span className={clsx('w-8 h-8 rounded-lg flex items-center justify-center shrink-0', tones[tone])}><Icon size={16} /></span>
        <div><div className="text-sm font-bold text-slate-800">{title}</div>{desc && <div className="text-[11px] text-slate-400">{desc}</div>}</div>
      </div>
      <div className="p-4">{children}</div>
    </section>
  );
}

// Full PM settings page (routed view `?view=settings`) — one tabbed surface for everything an
// org configures in Plan Management: fees, bank/ABA, email inbox + Mailbox, safety, automation.
function SetupModal({ asPage, onClose, onChanged, onReset, onOpenMail }) {
  const [tab, setTab] = useState('fees');
  const [bank, setBank] = useState(null);
  const [fees, setFees] = useState(null);
  const [gov, setGov] = useState(null);
  const [month, setMonth] = useState(() => new Date().toISOString().slice(0, 7));
  const [busy, setBusy] = useState('');
  const [ingest, setIngest] = useState(null);
  const [ingestLog, setIngestLog] = useState([]);
  const [acct, setAcct] = useState(null);
  const [ndia, setNdia] = useState(null);
  const [rejCodes, setRejCodes] = useState([]);
  const [region, setRegion] = useState(null);
  const [regionOpts, setRegionOpts] = useState([]);
  const [templates, setTemplates] = useState(null);
  const [placeholders, setPlaceholders] = useState([]);
  const setTpl = (kind, patch) => setTemplates((ts) => (ts || []).map((t) => (t.kind === kind ? { ...t, ...patch } : t)));
  const saveTpl = async (t) => { setBusy(`tpl-${t.kind}`); try { await api.put('/plan-management/email-templates', t); toast.success('Template saved'); } catch (e) { toast.error(e.response?.data?.message || 'Failed'); } finally { setBusy(''); } };
  const [oauth, setOauth] = useState(null);
  const [creds, setCreds] = useState({ ms_client_id: '', ms_client_secret: '', ms_tenant: '', google_client_id: '', google_client_secret: '' });
  const [showCreds, setShowCreds] = useState(false);
  const loadOauth = useCallback(() => {
    api.get('/plan-management/mail/oauth/status').then((r) => {
      setOauth(r.data || {});
      const c = r.data?.credentials || {};
      setCreds({ ms_client_id: c.ms_client_id || '', ms_tenant: c.ms_tenant || '', google_client_id: c.google_client_id || '', ms_client_secret: '', google_client_secret: '' });
    }).catch(() => setOauth({ configured: {}, connected: null }));
  }, []);
  const saveCreds = async () => { setBusy('creds'); try { await api.put('/plan-management/mail/oauth/credentials', creds); toast.success('App credentials saved'); loadOauth(); } catch (e) { toast.error(e.response?.data?.message || 'Failed'); } finally { setBusy(''); } };
  const copyText = async (t) => { try { await navigator.clipboard.writeText(t); toast.success('Copied'); } catch { /* ignore */ } };
  const connectMailbox = async (provider) => {
    try { const r = await api.get(`/plan-management/mail/oauth/${provider}/start`); if (r.data?.url) window.location.href = r.data.url; }
    catch (e) { toast.error(e.response?.data?.message || 'Could not start sign-in'); }
  };
  const disconnectMailbox = async () => {
    try { await api.post('/plan-management/mail/oauth/disconnect', {}); toast.success('Mailbox disconnected'); loadOauth(); }
    catch (e) { toast.error(e.response?.data?.message || 'Failed'); }
  };
  useEffect(() => {
    api.get('/plan-management/bank-settings').then((r) => setBank(r.data || {})).catch(() => setBank({}));
    api.get('/plan-management/fee-settings').then((r) => setFees(r.data || {})).catch(() => setFees({}));
    api.get('/plan-management/settings').then((r) => setGov(r.data || {})).catch(() => setGov({}));
    api.get('/plan-management/ingest-settings').then((r) => setIngest(r.data || {})).catch(() => setIngest({}));
    api.get('/plan-management/ingest/log').then((r) => setIngestLog(r.data || [])).catch(() => {});
    loadOauth();
    api.get('/plan-management/accounting-settings').then((r) => setAcct(r.data || {})).catch(() => setAcct({}));
    api.get('/plan-management/ndia-settings').then((r) => setNdia(r.data || {})).catch(() => setNdia({}));
    api.get('/plan-management/ndia/rejection-codes').then((r) => setRejCodes(r.data || [])).catch(() => {});
    api.get('/plan-management/region-settings').then((r) => { setRegion(r.data || {}); setRegionOpts(r.data?.regions || []); }).catch(() => setRegion({}));
    api.get('/plan-management/email-templates').then((r) => { setTemplates(r.data?.templates || []); setPlaceholders(r.data?.placeholders || []); }).catch(() => setTemplates([]));
  }, []);
  const saveAcct = async () => { setBusy('acct'); try { const r = await api.put('/plan-management/accounting-settings', acct); setAcct(r.data); toast.success('Accounting settings saved'); } catch (e) { toast.error(e.response?.data?.message || 'Failed'); } finally { setBusy(''); } };
  const saveNdia = async () => { setBusy('ndia'); try { const r = await api.put('/plan-management/ndia-settings', ndia); setNdia((p) => ({ ...p, ...r.data })); toast.success('NDIA settings saved'); } catch (e) { toast.error(e.response?.data?.message || 'Failed'); } finally { setBusy(''); } };
  const saveRegion = async (val) => { const prev = region; setRegion((p) => ({ ...p, service_region: val })); try { await api.put('/plan-management/region-settings', { service_region: val }); toast.success('Service region saved'); } catch (e) { toast.error(e.response?.data?.message || 'Failed'); setRegion(prev); } };
  const saveIngest = async () => {
    setBusy('ingest');
    try {
      const r = await api.put('/plan-management/ingest-settings', ingest || {});
      setIngest({ ...r.data, imap_pass: '' }); toast.success('Inbox settings saved');
    } catch (e) { toast.error(e.response?.data?.message || 'Failed'); } finally { setBusy(''); }
  };
  const testIngest = async () => {
    setBusy('ingest-test');
    try { const r = await api.post('/plan-management/ingest/test', {}); toast.success(`Connected — ${r.data.unseen} unread email(s) in ${r.data.folder}`); }
    catch (e) { toast.error(e.response?.data?.message || 'Connection failed'); } finally { setBusy(''); }
  };
  const setB = (k) => (e) => setBank((p) => ({ ...p, [k]: e.target.value }));
  const setFe = (k) => (e) => setFees((p) => ({ ...p, [k]: e.target.value }));
  // Auto-fill a fee amount + name from the price book when the item number is entered.
  const lookupFee = async (which) => {
    const code = (which === 'setup' ? fees.setup_item : fees.monthly_item) || '';
    if (!code.trim()) return;
    try {
      const r = await api.get('/plan-management/fee-item-lookup', { params: { code: code.trim() } });
      if (r.data?.found) { setFees((p) => ({ ...p, [`${which}_amount`]: r.data.rate, [`${which}_name`]: r.data.name })); toast.success(`${r.data.name} — $${r.data.rate.toFixed(2)} from price book`); }
      else { setFees((p) => ({ ...p, [`${which}_name`]: '' })); toast(`No price-book match for ${code}`, { icon: 'ℹ️' }); }
    } catch { /* ignore */ }
  };
  const saveBank = async () => { setBusy('bank'); try { const r = await api.put('/plan-management/bank-settings', bank); setBank(r.data); toast.success('Bank details saved'); } catch (e) { toast.error(e.response?.data?.message || 'Failed'); } finally { setBusy(''); } };
  const saveFees = async () => { setBusy('fees'); try { const r = await api.put('/plan-management/fee-settings', fees); setFees(r.data); toast.success('Fees saved'); } catch (e) { toast.error(e.response?.data?.message || 'Failed'); } finally { setBusy(''); } };
  const genFees = async () => { setBusy('gen'); try { const r = await api.post('/plan-management/fees/generate', { month }); toast.success(`${r.data.created} fee invoice(s) generated${r.data.skipped ? `, ${r.data.skipped} skipped` : ''}`); if (r.data.no_agreement) toast(`${r.data.no_agreement} setup fee(s) held — no signed service agreement on the participant yet`, { icon: '⚠️' }); onChanged && onChanged(); } catch (e) { toast.error(e.response?.data?.message || 'Failed'); } finally { setBusy(''); } };
  const toggleGov = async (key) => { const prev = gov; const next = { ...gov, [key]: !gov[key] }; setGov(next); try { await api.put('/plan-management/settings', next); toast.success('Saved'); } catch (e) { toast.error(e.response?.data?.message || 'Failed'); setGov(prev); } };
  const I = 'w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500';
  const loading = !bank || !fees || !gov || !ingest || !acct || !ndia || !region;
  const TABS = [
    ['fees', 'Fees & billing', Percent],
    ['bank', 'Bank & payments', Banknote],
    ['accounting', 'Accounting export', FileText],
    ['ndia', 'NDIA claiming', Landmark],
    ['region', 'Service region', Scale],
    ['email', 'Email & Mailbox', Mail],
    ['templates', 'Email templates', Pencil],
    ['safety', 'Safety controls', ShieldAlert],
    ['automation', 'Automation', RefreshCw],
    ['danger', 'Demo data', Database],
  ];
  return (
    <Modal page={asPage} title="Plan Management settings" subtitle="Fees, bank details, mailbox, safety controls and automation — all in one place" icon={SlidersHorizontal} wide onClose={onClose}
      footer={asPage ? null : <button onClick={onClose} className="btn-primary text-sm min-w-[90px]">Done</button>}>
      {loading ? <div className="py-10 text-center text-slate-400 text-sm">Loading…</div> : (
        <div className="flex flex-col sm:flex-row gap-5">
          {/* Settings tab rail */}
          <div className="sm:w-48 shrink-0 flex sm:flex-col gap-1 overflow-x-auto">
            {TABS.map(([k, l, Ic]) => (
              <button key={k} onClick={() => setTab(k)}
                className={clsx('flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium text-left whitespace-nowrap sm:w-full',
                  tab === k
                    ? (k === 'danger' ? 'bg-rose-50 text-rose-600' : 'bg-primary-50 text-primary-600')
                    : 'text-slate-500 hover:bg-slate-50')}>
                <Ic size={15} className="shrink-0" /> {l}
              </button>
            ))}
          </div>

          <div className="flex-1 min-w-0 min-h-[340px] space-y-4">
          {/* Bank details */}
          {tab === 'bank' && (
          <SetupSection icon={Banknote} title="Your bank details" desc="Used to build the bank file (ABA) that pays providers">
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
              <div><label className="block text-xs text-slate-600 mb-1">APCA / Direct-Entry user ID</label><input className={I} value={bank.de_user_id || ''} onChange={setB('de_user_id')} placeholder="123456" /></div>
              <div><label className="block text-xs text-slate-600 mb-1">Bank</label><input className={I} value={bank.bank_abbrev || ''} onChange={setB('bank_abbrev')} placeholder="WBC / CBA / NAB / ANZ" /></div>
              <div><label className="block text-xs text-slate-600 mb-1">Remitter name (your business)</label><input className={I} value={bank.remitter_name || ''} onChange={setB('remitter_name')} /></div>
              <div><label className="block text-xs text-slate-600 mb-1">Your BSB</label><input className={I} value={bank.trace_bsb || ''} onChange={setB('trace_bsb')} placeholder="032-000" /></div>
              <div><label className="block text-xs text-slate-600 mb-1">Your account number</label><input className={I} value={bank.trace_account || ''} onChange={setB('trace_account')} /></div>
              <div><label className="block text-xs text-slate-600 mb-1">Bank reference <span className="text-slate-300">(optional)</span></label><input className={I} value={bank.lodgement_ref || ''} onChange={setB('lodgement_ref')} placeholder="ask your bank if unsure" /></div>
            </div>
            <div className="flex justify-end mt-3"><button onClick={saveBank} disabled={busy === 'bank'} className="btn-secondary text-sm min-w-[130px]">{busy === 'bank' ? '…' : 'Save bank details'}</button></div>
          </SetupSection>
          )}

          {/* Accounting export (Xero / MYOB / QuickBooks) */}
          {tab === 'accounting' && (
          <SetupSection icon={FileText} title="Accounting export" desc="How provider bills export to your accounting software — the 'Download accounting file' buttons use these">
            <div className="space-y-3">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium text-slate-600 mb-1">Accounting software</label>
                  <select className={I} value={acct.accounting_package || 'xero'} onChange={(e) => setAcct((p) => ({ ...p, accounting_package: e.target.value }))}>
                    <option value="xero">Xero</option>
                    <option value="myob">MYOB</option>
                    <option value="quickbooks">QuickBooks</option>
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-medium text-slate-600 mb-1">Default GST treatment</label>
                  <select className={I} value={acct.gst_default || 'GSTFREE'} onChange={(e) => setAcct((p) => ({ ...p, gst_default: e.target.value }))}>
                    <option value="GSTFREE">GST free (most NDIS supports)</option>
                    <option value="GST">GST applies</option>
                  </select>
                </div>
                <div><label className="block text-xs font-medium text-slate-600 mb-1">Provider-bill account code</label><input className={I} placeholder="e.g. 800" value={acct.bill_account_code || ''} onChange={(e) => setAcct((p) => ({ ...p, bill_account_code: e.target.value }))} /></div>
                <div><label className="block text-xs font-medium text-slate-600 mb-1">Fee-income account code <span className="text-slate-300">(optional)</span></label><input className={I} placeholder="e.g. 200" value={acct.sales_account_code || ''} onChange={(e) => setAcct((p) => ({ ...p, sales_account_code: e.target.value }))} /></div>
                {(acct.accounting_package || 'xero') === 'xero' && (
                  <div className="sm:col-span-2"><label className="block text-xs font-medium text-slate-600 mb-1">Xero tracking category <span className="text-slate-300">(optional)</span></label><input className={I} placeholder="e.g. Plan Management" value={acct.tracking_category || ''} onChange={(e) => setAcct((p) => ({ ...p, tracking_category: e.target.value }))} /></div>
                )}
              </div>
              <p className="text-[11px] text-slate-400">The export matches your software's bill-import format. A line's own GST code wins; the default above fills lines that don't carry one.</p>
              <div className="flex justify-end"><button onClick={saveAcct} disabled={busy === 'acct'} className="btn-secondary text-sm min-w-[130px]">{busy === 'acct' ? '…' : 'Save accounting'}</button></div>
            </div>
          </SetupSection>
          )}

          {/* NDIA claiming */}
          {tab === 'ndia' && (
          <SetupSection icon={Landmark} title="NDIA claiming" desc="Your NDIS registration and how claims leave the system">
            <div className="space-y-3">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium text-slate-600 mb-1">NDIS registration number</label>
                  <input className={I} placeholder={ndia.branding_reg ? `${ndia.branding_reg} (from your org profile)` : '4050000123'} value={ndia.ndis_registration_number || ''} onChange={(e) => setNdia((p) => ({ ...p, ndis_registration_number: e.target.value }))} />
                  <span className="text-[11px] text-slate-400 mt-1 block">Printed as RegistrationNumber on every NDIS claim file. Blank = use your organisation profile.</span>
                </div>
                <div>
                  <label className="block text-xs font-medium text-slate-600 mb-1">How claims are lodged</label>
                  <select className={I} value={ndia.claim_gateway_mode || 'file'} onChange={(e) => setNdia((p) => ({ ...p, claim_gateway_mode: e.target.value }))}>
                    <option value="file">Claim file (download CSV → upload to PRODA)</option>
                    <option value="mock">Simulated gateway (testing)</option>
                    <option value="live" disabled={!ndia.gateway_live_available}>Live PRODA/PACE API{ndia.gateway_live_available ? '' : ' — not available yet'}</option>
                  </select>
                  <span className="text-[11px] text-slate-400 mt-1 block">The CSV bulk-upload path always works. Live API needs NDIA digital-partner credentials.</span>
                </div>
              </div>
              <div className="flex justify-end"><button onClick={saveNdia} disabled={busy === 'ndia'} className="btn-secondary text-sm min-w-[130px]">{busy === 'ndia' ? '…' : 'Save NDIA settings'}</button></div>
              {rejCodes.length > 0 && (
                <div className="rounded-xl border border-slate-100 overflow-hidden mt-2">
                  <div className="px-3 py-1.5 bg-slate-50 text-[10px] font-bold uppercase tracking-wide text-slate-400">NDIA rejection reasons — and how we prevent each before you claim</div>
                  <div className="divide-y divide-slate-50 max-h-64 overflow-y-auto">
                    {rejCodes.map((r) => (
                      <div key={r.code} className="px-3 py-2 text-xs">
                        <div className="font-semibold text-slate-700">{r.reason}</div>
                        <div className="text-slate-400 mt-0.5">{r.prevented_by}</div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </SetupSection>
          )}

          {/* Service region */}
          {tab === 'region' && (
          <SetupSection icon={Scale} title="Service region" desc="Applies the NDIS remote / very-remote price loading when checking lines against the price cap">
            <div className="space-y-3">
              <div className="space-y-2">
                {(regionOpts.length ? regionOpts : [{ key: 'national', label: 'National (metro/regional)', loading: 1 }]).map((o) => (
                  <label key={o.key} className={clsx('flex items-start gap-2.5 text-sm cursor-pointer border rounded-xl px-3 py-2.5', (region.service_region || 'national') === o.key ? 'border-primary-300 bg-primary-50/50' : 'border-slate-200')}>
                    <input type="radio" name="pm-region" className="mt-0.5" checked={(region.service_region || 'national') === o.key} onChange={() => saveRegion(o.key)} />
                    <span><span className="font-medium text-slate-700">{o.label}</span><span className="block text-xs text-slate-400">{o.loading > 1 ? `Price caps lifted by ${Math.round((o.loading - 1) * 100)}% for eligible supports.` : 'Standard NDIS price caps — no loading.'}</span></span>
                  </label>
                ))}
              </div>
              <p className="text-[11px] text-slate-400">This only relaxes the over-cap warning to match the NDIS limit for your area — it doesn't change the rates you enter. Saved instantly.</p>
            </div>
          </SetupSection>
          )}

          {/* Fees */}
          {tab === 'fees' && (
          <SetupSection icon={Percent} title="Plan-management fees" desc="Your NDIS fee items — the amount is pulled from the price book automatically">
            <div className="grid grid-cols-2 gap-3">
              <div><label className="block text-xs font-medium text-slate-600 mb-1">Setup fee — NDIS item #</label><input className={I} value={fees.setup_item || ''} onChange={setFe('setup_item')} onBlur={() => lookupFee('setup')} placeholder="14_034_0127_8_3" />{fees.setup_name && <span className="text-[10px] text-emerald-600 mt-0.5 block truncate">✓ {fees.setup_name}</span>}</div>
              <div><label className="block text-xs font-medium text-slate-600 mb-1">Setup fee — amount <span className="text-slate-300">(auto)</span></label><input type="number" step="0.01" className={I} value={fees.setup_amount ?? ''} onChange={setFe('setup_amount')} /></div>
              <div><label className="block text-xs font-medium text-slate-600 mb-1">Monthly fee — NDIS item #</label><input className={I} value={fees.monthly_item || ''} onChange={setFe('monthly_item')} onBlur={() => lookupFee('monthly')} placeholder="14_034_0127_8_3" />{fees.monthly_name && <span className="text-[10px] text-emerald-600 mt-0.5 block truncate">✓ {fees.monthly_name}</span>}</div>
              <div><label className="block text-xs font-medium text-slate-600 mb-1">Monthly fee — amount <span className="text-slate-300">(auto)</span></label><input type="number" step="0.01" className={I} value={fees.monthly_amount ?? ''} onChange={setFe('monthly_amount')} /></div>
            </div>
            <div className="flex items-center gap-2 mt-2">
              <button onClick={() => { lookupFee('setup'); lookupFee('monthly'); }} className="btn-secondary !text-xs !px-3 !py-1.5 flex items-center gap-1.5"><RefreshCw size={13} /> Fetch rates from price book</button>
              <p className="text-[11px] text-slate-400">Type the item number → the amount fills from the NDIS price book. Setup fee: once per plan; monthly: once per participant per month.</p>
            </div>
            <div className="flex flex-wrap items-end justify-between gap-2 mt-3 pt-3 border-t border-slate-100">
              <div className="flex items-end gap-2">
                <div><label className="block text-xs font-medium text-slate-600 mb-1">Bill monthly fees for</label><input type="month" className={I} value={month} onChange={(e) => setMonth(e.target.value)} /></div>
                <button onClick={genFees} disabled={busy === 'gen'} className="btn-secondary text-sm h-[42px] flex items-center gap-1.5">{busy === 'gen' ? <Loader2 size={15} className="animate-spin" /> : <Plus size={15} />} Bill this month</button>
              </div>
              <button onClick={saveFees} disabled={busy === 'fees'} className="btn-secondary text-sm h-[42px] min-w-[90px]">{busy === 'fees' ? '…' : 'Save fees'}</button>
            </div>
          </SetupSection>
          )}

          {/* Email inbox (IMAP) + Mailbox */}
          {tab === 'email' && (<>
          <SetupSection icon={Mail} title="Connect your mailbox" desc="Sign in with Microsoft or Google — no password, works with 2-factor. Recommended for Microsoft 365 and Gmail.">
            {oauth?.connected ? (
              <div className="flex flex-wrap items-center gap-3">
                <span className="inline-flex items-center gap-2 text-sm font-medium text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-lg px-3 py-2">
                  <Check size={15} /> Connected: {oauth.connected.account || '(account)'} <span className="text-emerald-500">· {oauth.connected.provider === 'google' ? 'Gmail' : 'Microsoft 365'}</span>
                </span>
                <button onClick={disconnectMailbox} className="btn-secondary text-sm">Disconnect</button>
                <span className="text-[11px] text-slate-400 w-full">Inbox syncs every 10 minutes; Compose/Reply send from this mailbox. You can also use the manual IMAP option below instead.</span>
              </div>
            ) : (
              <div className="space-y-2.5">
                <div className="flex flex-wrap gap-2">
                  <button onClick={() => connectMailbox('microsoft')} disabled={!oauth?.configured?.microsoft} className="btn-secondary text-sm flex items-center gap-2 disabled:opacity-50">
                    <Mail size={15} /> Connect Microsoft 365
                  </button>
                  <button onClick={() => connectMailbox('google')} disabled={!oauth?.configured?.google} className="btn-secondary text-sm flex items-center gap-2 disabled:opacity-50">
                    <Mail size={15} /> Connect Gmail
                  </button>
                  <button onClick={() => setShowCreds((v) => !v)} className="text-xs text-primary-600 hover:underline ml-auto self-center">{showCreds ? 'Hide setup keys' : 'Set up sign-in keys'}</button>
                </div>
                {oauth && !oauth.configured?.microsoft && !oauth.configured?.google && !showCreds && (
                  <p className="text-[11px] text-amber-600">To turn on one-click sign-in, add your Microsoft or Google app keys below (<button onClick={() => setShowCreds(true)} className="underline">set up sign-in keys</button>) — or use the password (IMAP) option below.</p>
                )}
                {oauth && (oauth.configured?.microsoft || oauth.configured?.google) && !showCreds && (
                  <p className="text-[11px] text-slate-400">A Microsoft/Google window opens to approve access. Nothing is stored except a secure token you can revoke here anytime.</p>
                )}
                {showCreds && (
                  <div className="rounded-xl border border-slate-200 p-3 space-y-3 bg-slate-50/40">
                    <div className="flex items-start gap-2 text-[11px] text-slate-500">
                      <span className="flex-1">Register a free app once (Microsoft Entra / Google Cloud), then paste its keys here. Use this exact <strong>redirect URI</strong> when registering:</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <code className="flex-1 text-[11px] bg-white border border-slate-200 rounded-lg px-2.5 py-1.5 truncate">{oauth?.redirect_uri || '…'}</code>
                      <button onClick={() => copyText(oauth?.redirect_uri || '')} className="btn-secondary !text-xs !px-2.5 !py-1.5">Copy</button>
                    </div>
                    <div className="pt-1 border-t border-slate-100">
                      <div className="text-xs font-bold text-slate-600 mb-1.5">Microsoft 365 {oauth?.configured?.microsoft && <span className="text-emerald-600 font-medium">· ready</span>}</div>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                        <div><label className="block text-[11px] font-medium text-slate-600 mb-1">Application (client) ID</label><input className={I} value={creds.ms_client_id} onChange={(e) => setCreds((p) => ({ ...p, ms_client_id: e.target.value }))} placeholder="00000000-0000-…" /></div>
                        <div><label className="block text-[11px] font-medium text-slate-600 mb-1">Client secret</label><input type="password" className={I} value={creds.ms_client_secret} onChange={(e) => setCreds((p) => ({ ...p, ms_client_secret: e.target.value }))} placeholder={oauth?.credentials?.ms_has_secret ? '•••••• (saved — type to replace)' : 'secret value'} /></div>
                        <div><label className="block text-[11px] font-medium text-slate-600 mb-1">Directory (tenant) ID <span className="text-slate-300">(or "common")</span></label><input className={I} value={creds.ms_tenant} onChange={(e) => setCreds((p) => ({ ...p, ms_tenant: e.target.value }))} placeholder="common" /></div>
                      </div>
                    </div>
                    <div className="pt-1 border-t border-slate-100">
                      <div className="text-xs font-bold text-slate-600 mb-1.5">Gmail / Google Workspace {oauth?.configured?.google && <span className="text-emerald-600 font-medium">· ready</span>}</div>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                        <div><label className="block text-[11px] font-medium text-slate-600 mb-1">Client ID</label><input className={I} value={creds.google_client_id} onChange={(e) => setCreds((p) => ({ ...p, google_client_id: e.target.value }))} placeholder="…apps.googleusercontent.com" /></div>
                        <div><label className="block text-[11px] font-medium text-slate-600 mb-1">Client secret</label><input type="password" className={I} value={creds.google_client_secret} onChange={(e) => setCreds((p) => ({ ...p, google_client_secret: e.target.value }))} placeholder={oauth?.credentials?.google_has_secret ? '•••••• (saved — type to replace)' : 'secret value'} /></div>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <button onClick={saveCreds} disabled={busy === 'creds'} className="btn-secondary text-sm min-w-[90px]">{busy === 'creds' ? '…' : 'Save keys'}</button>
                      <span className="text-[11px] text-slate-400">Secrets are encrypted. After saving, the Connect buttons above turn on.</span>
                    </div>
                  </div>
                )}
              </div>
            )}
          </SetupSection>

          <SetupSection icon={Upload} title="Or connect with a password (IMAP)" desc="For mailboxes that still allow app passwords — new invoice PDFs become AI drafts automatically">
            <div className="space-y-3">
              <label className="flex items-start gap-2.5 text-sm cursor-pointer">
                <input type="checkbox" className="mt-0.5 w-4 h-4 rounded" checked={!!ingest?.ingest_enabled} onChange={(e) => setIngest((p) => ({ ...p, ingest_enabled: e.target.checked }))} />
                <span><span className="font-medium text-slate-700">Watch this mailbox</span><span className="block text-xs text-slate-400">Checked every 10 minutes. Each PDF/image attachment is read by AI, matched to the participant by NDIS number, and lands as an "AI draft" invoice for review. Nothing is approved automatically.</span></span>
              </label>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div><label className="block text-xs font-medium text-slate-600 mb-1">IMAP host</label><input className={I} placeholder="imap.gmail.com / outlook.office365.com" value={ingest?.imap_host || ''} onChange={(e) => setIngest((p) => ({ ...p, imap_host: e.target.value }))} /></div>
                <div className="grid grid-cols-2 gap-3">
                  <div><label className="block text-xs font-medium text-slate-600 mb-1">Port</label><input className={I} placeholder="993" value={ingest?.imap_port || ''} onChange={(e) => setIngest((p) => ({ ...p, imap_port: e.target.value }))} /></div>
                  <div><label className="block text-xs font-medium text-slate-600 mb-1">Folder</label><input className={I} placeholder="INBOX" value={ingest?.imap_folder || ''} onChange={(e) => setIngest((p) => ({ ...p, imap_folder: e.target.value }))} /></div>
                </div>
                <div><label className="block text-xs font-medium text-slate-600 mb-1">Username (email)</label><input className={I} placeholder="invoices@yourcompany.com.au" value={ingest?.imap_user || ''} onChange={(e) => setIngest((p) => ({ ...p, imap_user: e.target.value }))} /></div>
                <div><label className="block text-xs font-medium text-slate-600 mb-1">Password / app password</label><input type="password" className={I} placeholder={ingest?.has_pass ? '••••••••  (saved — type to replace)' : 'App password'} value={ingest?.imap_pass || ''} onChange={(e) => setIngest((p) => ({ ...p, imap_pass: e.target.value }))} /></div>
              </div>
              <div className="flex items-center gap-2">
                <button onClick={saveIngest} disabled={busy === 'ingest'} className="btn-secondary text-sm min-w-[90px]">{busy === 'ingest' ? '…' : 'Save inbox'}</button>
                <button onClick={testIngest} disabled={busy === 'ingest-test'} className="btn-secondary text-sm flex items-center gap-1.5">{busy === 'ingest-test' ? <Loader2 size={14} className="animate-spin" /> : <Check size={14} />} Test connection</button>
                <span className="text-[11px] text-slate-400">Gmail/Outlook need an app password, not your normal one.</span>
              </div>
              {ingestLog.length > 0 && (
                <div className="rounded-xl border border-slate-100 overflow-hidden">
                  <div className="px-3 py-1.5 bg-slate-50 text-[10px] font-bold uppercase tracking-wide text-slate-400">Recent inbox activity</div>
                  <div className="divide-y divide-slate-50 max-h-44 overflow-y-auto">
                    {ingestLog.slice(0, 8).map((r) => (
                      <div key={r.id} className="px-3 py-1.5 flex items-center gap-2 text-xs">
                        <span className={clsx('badge !text-[9px] shrink-0', r.status === 'ok' ? 'badge-green' : r.status === 'failed' ? 'badge-red' : 'badge-gray')}>{r.status}</span>
                        <span className="text-slate-600 truncate flex-1">{r.attachment}{r.from_addr ? ` · ${r.from_addr}` : ''}</span>
                        {r.error && <span className="text-[10px] text-rose-500 truncate max-w-[220px]" title={r.error}>{r.error}</span>}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </SetupSection>

          <SetupSection icon={Mail} title="Mailbox" desc="Read, reply and send email (with attachments) without leaving Plan Management">
            <div className="space-y-3">
              <label className="flex items-start gap-2.5 text-sm cursor-pointer">
                <input type="checkbox" className="mt-0.5 w-4 h-4 rounded" checked={ingest?.mail_sync_enabled !== false} onChange={(e) => setIngest((p) => ({ ...p, mail_sync_enabled: e.target.checked }))} />
                <span><span className="font-medium text-slate-700">Show this inbox in the Mailbox</span><span className="block text-xs text-slate-400">Every message (and its attachments) from the mailbox above appears under Mailbox in the left menu — read-only against the real inbox, so nothing is moved or marked. Uses the same connection details.</span></span>
              </label>
              <div className="flex flex-wrap items-center gap-2">
                <button onClick={saveIngest} disabled={busy === 'ingest'} className="btn-secondary text-sm min-w-[90px]">{busy === 'ingest' ? '…' : 'Save'}</button>
                {onOpenMail && <button onClick={onOpenMail} className="btn-secondary text-sm flex items-center gap-1.5"><Mail size={14} /> Open Mailbox</button>}
                <span className="text-[11px] text-slate-400">{ingest?.mail_last_sync ? `Last checked ${new Date(ingest.mail_last_sync).toLocaleString('en-AU')}` : 'Not checked yet — it runs every 10 minutes, or use "Check mail" in the Mailbox.'}</span>
              </div>
              <p className="text-[11px] text-slate-400">Sending email (compose / reply / remittance / statements) uses your organisation's email server from Settings → Notifications; everything sent shows in the Mailbox's Sent folder.</p>
            </div>
          </SetupSection>

          {/* AI auto-draft from provider emails */}
          <SetupSection icon={Sparkles} title="AI drafts from provider emails" desc="When a provider emails an invoice, AI reads it and — if the participant AND provider both match — creates a draft for you to review">
            <div className="space-y-3">
              <label className="flex items-start gap-2.5 text-sm cursor-pointer">
                <input type="checkbox" className="mt-0.5 w-4 h-4 rounded" checked={!!ingest?.auto_draft_from_email} onChange={(e) => setIngest((p) => ({ ...p, auto_draft_from_email: e.target.checked }))} />
                <span><span className="font-medium text-slate-700">Auto-create draft invoices from provider emails</span><span className="block text-xs text-slate-400">AI reads each incoming invoice PDF. It matches the <strong>participant</strong> (NDIS number) and the <strong>provider</strong> (ABN / sender email / name). If <strong>both</strong> match, it creates an "AI draft" invoice — never approved, never paid. If one doesn't match, it goes to the review list below instead. The provider must already exist under Providers.</span></span>
              </label>
              <div className="flex items-center gap-2">
                <button onClick={saveIngest} disabled={busy === 'ingest'} className="btn-secondary text-sm min-w-[90px]">{busy === 'ingest' ? '…' : 'Save'}</button>
                <span className="text-[11px] text-slate-400">Runs on every mailbox check (about every 10 minutes).</span>
              </div>
              {ingestLog.length > 0 && (
                <div className="rounded-xl border border-slate-100 overflow-hidden">
                  <div className="px-3 py-1.5 bg-slate-50 text-[10px] font-bold uppercase tracking-wide text-slate-400">Recent AI drafts &amp; review items</div>
                  <div className="divide-y divide-slate-50 max-h-52 overflow-y-auto">
                    {ingestLog.slice(0, 10).map((r) => (
                      <div key={r.id} className="px-3 py-1.5 flex items-center gap-2 text-xs">
                        <span className={clsx('badge !text-[9px] shrink-0', r.status === 'ok' ? 'badge-green' : r.status === 'review' ? '!bg-amber-100 !text-amber-700' : r.status === 'failed' ? 'badge-red' : 'badge-gray')}>{r.status === 'ok' ? 'drafted' : r.status}</span>
                        <span className="text-slate-600 truncate flex-1">{r.attachment}{r.from_addr ? ` · ${r.from_addr}` : ''}</span>
                        {r.error && <span className="text-[10px] text-amber-600 truncate max-w-[240px]" title={r.error}>{r.error}</span>}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </SetupSection>
          </>)}

          {/* Email templates */}
          {tab === 'templates' && (
          <SetupSection icon={Pencil} title="Email templates" desc="The wording of the automatic emails. Use placeholders like {{provider}}, {{participant}}, {{invoice_number}}, {{total}}.">
            <div className="space-y-4">
              {(templates || []).map((t) => {
                const meta = {
                  remittance: { label: 'Remittance advice', hint: 'Sent to the provider after you pay them (with a PDF attached). Fires automatically on the ABA payment run.' },
                  invoice_received: { label: 'Invoice received acknowledgement', hint: 'Sent to the provider when a new invoice is created for them.' },
                  duplicate_invoice: { label: 'Duplicate invoice notice', hint: 'Sent to the provider when an invoice looks like a duplicate.' },
                }[t.kind] || { label: t.kind, hint: '' };
                return (
                  <div key={t.kind} className="rounded-xl border border-slate-200 p-3 space-y-2.5">
                    <div className="flex items-start justify-between gap-2">
                      <div><div className="text-sm font-bold text-slate-700">{meta.label}</div><div className="text-[11px] text-slate-400">{meta.hint}</div></div>
                      <div className="flex items-center gap-3 shrink-0">
                        <label className="flex items-center gap-1.5 text-[11px] text-slate-500 cursor-pointer"><input type="checkbox" className="rounded" checked={t.enabled !== false} onChange={(e) => setTpl(t.kind, { enabled: e.target.checked })} /> On</label>
                        <label className="flex items-center gap-1.5 text-[11px] text-slate-500 cursor-pointer"><input type="checkbox" className="rounded" checked={t.auto_send === true} onChange={(e) => setTpl(t.kind, { auto_send: e.target.checked })} /> Auto-send</label>
                      </div>
                    </div>
                    <div><label className="block text-[11px] font-medium text-slate-600 mb-1">Subject</label><input className={I} value={t.subject || ''} onChange={(e) => setTpl(t.kind, { subject: e.target.value })} /></div>
                    <div><label className="block text-[11px] font-medium text-slate-600 mb-1">Message</label><textarea rows={5} className={I} value={t.body || ''} onChange={(e) => setTpl(t.kind, { body: e.target.value })} /></div>
                    <div className="flex items-center justify-between">
                      <span className="text-[10px] text-slate-400">Placeholders: {placeholders.map((p) => `{{${p}}}`).join('  ')}</span>
                      <button onClick={() => saveTpl(t)} disabled={busy === `tpl-${t.kind}`} className="btn-secondary !text-xs !px-3 !py-1.5 min-w-[70px]">{busy === `tpl-${t.kind}` ? '…' : 'Save'}</button>
                    </div>
                  </div>
                );
              })}
              <p className="text-[11px] text-slate-400">"On" = the email can send. "Auto-send" = it fires automatically at its trigger (remittance always fires on a pay run). Sent from your connected mailbox if you have one, else your org email server.</p>
            </div>
          </SetupSection>
          )}

          {/* Controls / governance */}
          {tab === 'safety' && (
          <SetupSection icon={ShieldAlert} title="Safety controls" desc="Extra checks before any money moves">
            <div className="space-y-2.5">
              <label className="flex items-start gap-2.5 text-sm cursor-pointer">
                <input type="checkbox" className="mt-0.5 w-4 h-4 rounded" checked={!!gov.segregation_of_duties} onChange={() => toggleGov('segregation_of_duties')} />
                <span><span className="font-medium text-slate-700">Two-person rule</span><span className="block text-xs text-slate-400">The person who approved an invoice can’t also release its payment (provider-paid / NDIA-reimbursed).</span></span>
              </label>
              <label className="flex items-start gap-2.5 text-sm cursor-pointer">
                <input type="checkbox" className="mt-0.5 w-4 h-4 rounded" checked={!!gov.require_2fa_for_payment} onChange={() => toggleGov('require_2fa_for_payment')} />
                <span><span className="font-medium text-slate-700">Require 2-factor sign-in to pay</span><span className="block text-xs text-slate-400">Users must have two-factor authentication on before they can mark an invoice paid.</span></span>
              </label>
            </div>
          </SetupSection>
          )}

          {/* Automation (runs daily, 1st–5th of the month for the monthly jobs) */}
          {tab === 'automation' && (
          <SetupSection icon={RefreshCw} title="Automation" desc="Runs every morning — the monthly jobs fire on the 1st of the month">
            <div className="space-y-2.5">
              <label className="flex items-start gap-2.5 text-sm cursor-pointer">
                <input type="checkbox" className="mt-0.5 w-4 h-4 rounded" checked={!!gov.auto_statements} onChange={() => toggleGov('auto_statements')} />
                <span><span className="font-medium text-slate-700">Email monthly statements automatically</span><span className="block text-xs text-slate-400">On the 1st, every plan-managed participant gets last month's statement by email. Participants with "statement opt-out" are skipped; postal-statement participants are listed in your Monday summary instead.</span></span>
              </label>
              <label className="flex items-start gap-2.5 text-sm cursor-pointer">
                <input type="checkbox" className="mt-0.5 w-4 h-4 rounded" checked={!!gov.auto_fees} onChange={() => toggleGov('auto_fees')} />
                <span><span className="font-medium text-slate-700">Bill plan-management fees automatically</span><span className="block text-xs text-slate-400">On the 1st, the monthly fee (and any unbilled setup fee) is generated for each participant — same budget checks as the manual "Bill this month" button.</span></span>
              </label>
              <label className="flex items-start gap-2.5 text-sm cursor-pointer">
                <input type="checkbox" className="mt-0.5 w-4 h-4 rounded" checked={!!gov.auto_send_approval} onChange={() => toggleGov('auto_send_approval')} />
                <span><span className="font-medium text-slate-700">Ask the participant to approve every new invoice</span><span className="block text-xs text-slate-400">As soon as a new invoice is created, the participant is emailed a one-click approve/decline link — no need to send it manually. Skipped for participants set to auto-approve, or with no email on file (add their email in the participant’s contact details).</span></span>
              </label>
              <p className="text-[11px] text-slate-400">Plan-expiry notices (60/30/14 days) and the Monday work summary always email your admins — they don't contact participants.</p>
            </div>
          </SetupSection>
          )}

          {/* Danger zone */}
          {tab === 'danger' && (
          <SetupSection icon={Database} title="Demo data" tone="rose">
            <button onClick={() => { onClose(); onReset && onReset(); }} className="text-sm px-3 py-1.5 rounded-lg font-medium border border-rose-200 text-rose-600 hover:bg-rose-50 inline-flex items-center gap-1.5"><RefreshCw size={14} /> Reset &amp; load demo data</button>
            <p className="text-[11px] text-slate-400 mt-1.5">Deletes all Plan Management data for this organisation and loads a fresh demo set. Service delivery, Support Coordination and the price book are not touched.</p>
          </SetupSection>
          )}
          </div>
        </div>
      )}
    </Modal>
  );
}
// Downloads — every claim file, bank file, accounting export and statement this org has generated.
//
// The two columns that earn this screen are Download Count and Last Downloaded By. An ABA file
// uploaded to the bank twice pays every provider on it twice; being able to see, before you upload,
// that a colleague already took it is the cheapest defence there is. So taking a file is recorded
// as its own act, separate from generating it.
function DownloadsView({ onSeen }) {
  const confirm = useConfirm();
  const [rows, setRows] = useState(null);
  const [range, setRange] = useState({ from: '', to: '' });
  const [kind, setKind] = useState('');
  const [busy, setBusy] = useState('');
  const KIND = {
    claim_csv: { label: 'NDIS Claim', source: 'Invoice Entry', icon: Landmark, tone: 'text-sky-600 bg-sky-50' },
    aba: { label: 'ABA File', source: 'Pay Providers', icon: Banknote, tone: 'text-emerald-600 bg-emerald-50' },
    accounting: { label: 'Accounting Export', source: 'Invoice Entry', icon: FileText, tone: 'text-violet-600 bg-violet-50' },
    statement: { label: 'Statement', source: 'Statements', icon: FileText, tone: 'text-amber-600 bg-amber-50' },
    remittance: { label: 'Remittance', source: 'Pay Providers', icon: Mail, tone: 'text-slate-600 bg-slate-100' },
  };
  const load = useCallback(() => {
    const params = {};
    if (range.from) params.from = range.from;
    if (range.to) params.to = range.to;
    api.get('/plan-management/downloads', { params }).then((r) => setRows(r.data || [])).catch(() => setRows([]));
  }, [range.from, range.to]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    // Opening the view clears the "new" badge.
    api.post('/plan-management/downloads/seen', {}).then(() => onSeen && onSeen()).catch(() => {});
  }, [onSeen]);

  // Fetch, hand the file over, THEN record it. Recording first would count a download that never
  // happened when the regenerate fails, which is the wrong way round for a count people rely on.
  const take = async (d) => {
    if (!d.regen_endpoint) return;
    setBusy(d.id);
    try {
      const r = await api.get(d.regen_endpoint, { responseType: 'blob' });
      const url = URL.createObjectURL(r.data); const a = document.createElement('a');
      a.href = url; a.download = d.filename || 'download'; document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
      await api.post(`/plan-management/downloads/${d.id}/downloaded`, {}).catch(() => {});
      load();
    } catch (e) { toast.error(e.response?.data?.message || 'Could not regenerate this file'); } finally { setBusy(''); }
  };
  const remove = async (d) => {
    if (!await confirm(`Remove "${d.filename || 'this entry'}" from the downloads list? The file itself is regenerated on demand, so nothing financial is deleted.`, { title: 'Remove from list', danger: true })) return;
    try { await api.delete(`/plan-management/downloads/${d.id}`); toast.success('Removed'); load(); } catch (e) { toast.error(e.response?.data?.message || 'Failed'); }
  };

  const list = (rows || []).filter((d) => !kind || d.kind === kind);
  const kinds = Array.from(new Set((rows || []).map((d) => d.kind))).sort();
  const I = 'border border-slate-200 rounded-lg px-2.5 py-1.5 text-xs bg-white';
  const never = <span className="text-slate-300">Never</span>;

  return (
    <div className="card p-0 overflow-hidden">
      <div className="px-3 py-2.5 border-b border-slate-100 grid grid-cols-[1fr_auto_1fr] items-center gap-2">
        <div className="min-w-0 text-[12.5px] text-muted">
          <span className="font-semibold tabular-nums text-ink">{list.length}</span> file{list.length === 1 ? '' : 's'}{kind ? ' from this source' : ''}
        </div>
        <span aria-hidden="true" />
        <div className="flex items-center gap-2 justify-end min-w-0">
          <button onClick={load} className="btn-secondary !text-xs !px-3 !py-1.5 flex items-center gap-1.5"><RefreshCw size={14} /> Refresh</button>
        </div>
      </div>

      <div className="px-3 py-2.5 border-b border-slate-100 bg-slate-50/60">
        <div className="flex flex-wrap items-end gap-2">
          <FilterField label="Requested From">
            <input type="date" value={range.from} onChange={(e) => setRange((r) => ({ ...r, from: e.target.value }))} aria-label="Requested from" className={`${I} tabular-nums w-[132px]`} />
          </FilterField>
          <FilterField label="To">
            <input type="date" value={range.to} onChange={(e) => setRange((r) => ({ ...r, to: e.target.value }))} aria-label="Requested to" className={`${I} tabular-nums w-[132px]`} />
          </FilterField>
          <FilterField label="Source">
            <select value={kind} onChange={(e) => setKind(e.target.value)} aria-label="Source" className={`${I} w-44`}>
              <option value="">(All)</option>
              {kinds.map((k) => <option key={k} value={k}>{(KIND[k] || {}).label || k}</option>)}
            </select>
          </FilterField>
          <span className="flex-1" />
          <button onClick={() => { setRange({ from: '', to: '' }); setKind(''); }} disabled={!range.from && !range.to && !kind}
            className="btn-secondary !text-[11px] !px-2.5 !py-1.5 flex items-center gap-1 disabled:opacity-40"><X size={11} /> Clear Filters</button>
        </div>
      </div>

      <div className="px-3 py-1.5 border-b border-slate-100 text-[11.5px] text-slate-500">
        Total Items <span className="font-bold tabular-nums text-slate-800">({list.length})</span>
      </div>

      {!rows ? <div className="p-10 text-center text-slate-400"><Loader2 size={20} className="animate-spin mx-auto" /></div>
        : list.length === 0 ? (
          <EmptyState icon={Download} title={range.from || range.to || kind ? 'No files in this range' : 'No files yet'}
            hint={range.from || range.to || kind ? 'Widen the dates or clear the filters.' : 'Generate a claim, pay providers, or export to your accounting software and it will appear here.'} />
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-[13px]">
              <thead className="bg-slate-700 text-[10px] uppercase tracking-wide text-slate-200 sticky top-0 z-10">
                <tr>
                  <th className="pl-3 pr-2 py-1.5 font-semibold text-right w-10">#</th>
                  <th className="px-2 py-1.5 font-semibold text-left whitespace-nowrap">Source</th>
                  <th className="px-2 py-1.5 font-semibold text-left whitespace-nowrap">File</th>
                  <th className="px-2 py-1.5 font-semibold text-left whitespace-nowrap">Date Requested</th>
                  <th className="px-2 py-1.5 font-semibold text-left whitespace-nowrap">Requested By</th>
                  <th className="px-2 py-1.5 font-semibold text-left whitespace-nowrap">Status</th>
                  <th className="px-2 py-1.5 font-semibold text-center whitespace-nowrap">Download</th>
                  <th className="px-2 py-1.5 font-semibold text-left whitespace-nowrap">Last Downloaded By</th>
                  <th className="px-2 py-1.5 font-semibold text-right whitespace-nowrap" title="How many times this file has been taken">Download Count</th>
                  <th className="px-2 py-1.5 font-semibold text-center w-10"><span className="sr-only">Delete</span></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {list.map((d, i) => {
                  const k = KIND[d.kind] || { label: d.kind, source: '—', icon: FileText, tone: 'text-slate-600 bg-slate-100' };
                  const Ic = k.icon;
                  const count = Number(d.download_count) || 0;
                  // An ABA taken more than once is the case this screen exists to make visible.
                  const risky = d.kind === 'aba' && count > 1;
                  return (
                    <tr key={d.id} className="hover:bg-slate-50">
                      <td className="pl-3 pr-2 py-1.5 text-[11.5px] text-right tabular-nums text-slate-300">{i + 1}</td>
                      <td className="px-2 py-1.5 whitespace-nowrap">
                        <span className="inline-flex items-center gap-2">
                          <span className={clsx('w-6 h-6 rounded-lg flex items-center justify-center shrink-0', k.tone)}><Ic size={12} /></span>
                          <span className="text-[12.5px] text-slate-700">{k.source}</span>
                        </span>
                      </td>
                      <td className="px-2 py-1.5">
                        <span className="block font-semibold text-slate-800 truncate max-w-[260px]" title={d.filename || ''}>{d.filename || k.label}</span>
                        {d.ref && <span className="block text-[10.5px] text-slate-400 truncate max-w-[260px]">{d.ref}</span>}
                      </td>
                      <td className="px-2 py-1.5 text-[12.5px] text-slate-500 whitespace-nowrap tabular-nums">{d.created_at ? new Date(d.created_at).toLocaleString('en-AU', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '—'}</td>
                      <td className="px-2 py-1.5 text-[12.5px] text-slate-500"><span className="block truncate max-w-[140px]">{d.created_by_name || <span className="text-slate-300">—</span>}</span></td>
                      <td className="px-2 py-1.5 whitespace-nowrap">
                        <span className={clsx('inline-flex items-center gap-1.5 rounded-full border px-2 py-0.5 text-[11px] font-medium',
                          count > 0 ? 'border-emerald-200 bg-emerald-50 text-emerald-700' : 'border-slate-200 bg-slate-50 text-slate-500')}>
                          <span className={clsx('w-1.5 h-1.5 rounded-full shrink-0', count > 0 ? 'bg-emerald-500' : 'bg-slate-400')} aria-hidden="true" />
                          {count > 0 ? 'Downloaded' : 'Ready'}
                        </span>
                      </td>
                      <td className="px-2 py-1.5 text-center whitespace-nowrap">
                        {d.regen_endpoint ? (
                          <button onClick={() => take(d)} disabled={busy === d.id}
                            title={`Download ${d.filename || k.label}`}
                            className="btn-secondary !text-[11px] !px-2.5 !py-1 inline-flex items-center gap-1.5 disabled:opacity-50">
                            {busy === d.id ? <Loader2 size={12} className="animate-spin" /> : <Download size={12} />} Download
                          </button>
                        ) : <span className="text-[11px] text-slate-300" title="No file is kept for this entry — it is a record that one was sent">Record only</span>}
                      </td>
                      <td className="px-2 py-1.5 text-[12.5px] text-slate-500">
                        <span className="block truncate max-w-[150px]">{d.last_downloaded_by_name || never}</span>
                        {d.last_downloaded_at && <span className="block text-[10.5px] text-slate-400 tabular-nums">{new Date(d.last_downloaded_at).toLocaleString('en-AU', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })}</span>}
                      </td>
                      <td className="px-2 py-1.5 text-right whitespace-nowrap">
                        <span className={clsx('inline-flex items-center gap-1 tabular-nums font-semibold text-[12.5px]', risky ? 'text-amber-600' : count > 0 ? 'text-slate-700' : 'text-slate-300')}
                          title={risky ? 'This bank file has been taken more than once — check it was not uploaded to the bank twice' : undefined}>
                          {risky && <AlertTriangle size={12} className="shrink-0" />}
                          {count}
                        </span>
                      </td>
                      <td className="px-2 py-1.5 text-center">
                        <button onClick={() => remove(d)} title="Remove from this list (the file itself is regenerated on demand)"
                          aria-label={`Remove ${d.filename || 'entry'} from the list`}
                          className="p-1 rounded text-slate-300 hover:text-rose-600 hover:bg-rose-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-400"><X size={13} /></button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
    </div>
  );
}

// Oversight & fraud dashboard — throughput, turnaround, straight-through %, exceptions, anomalies.
function OversightModal({ onClose, onOpenInvoice, asPage }) {
  const [d, setD] = useState(null);
  useEffect(() => {
    api.get('/plan-management/oversight').then((r) => setD(r.data || {})).catch(() => setD({}));
  }, []);
  const Tile = ({ label, value, tone, hint }) => (
    <div className="rounded-xl border border-line p-4" title={hint || undefined}>
      <div className={clsx('text-[12.5px] font-medium text-ink-soft', hint && 'underline decoration-dotted decoration-slate-300 underline-offset-4 inline-block')}>{label}</div>
      <div className={clsx('font-display text-[24px] font-bold tabular-nums leading-none mt-2', tone || 'text-ink')}>{value}</div>
    </div>
  );
  const ex = d?.exceptions || {}; const tp = d?.throughput || {}; const stt = d?.straight_through || {};
  const exTotal = (ex.flagged || 0) + (ex.over_cap_open || 0) + (ex.rejected_claims || 0) + (ex.recon_exceptions || 0);
  return (
    <Modal page={asPage} title="Audit & reports" subtitle="Throughput, turnaround, exceptions & anomalies" icon={AlertTriangle} wide onClose={onClose}
      footer={asPage ? null : <button onClick={onClose} className="btn-secondary text-sm">Close</button>}>
      {!d ? <div className="py-10 text-center text-slate-400 text-sm">Loading…</div> : (
        <div className="space-y-5">
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
            <Tile label="Open invoices" value={tp.open_count ?? 0} hint="Invoices not yet fully paid/reconciled" />
            <Tile label="Open value" value={money(tp.open_amount || 0)} hint="Total dollars still owed on open invoices" />
            <Tile label="Paid, last 30 days" value={tp.paid_30d ?? 0} tone="text-pos" hint="Invoices fully reconciled in the last 30 days" />
            <Tile label="Straight-through" value={stt.pct == null ? '—' : `${stt.pct}%`} hint="Share of invoices that went from created to claimed with no manual hold or exception — higher is smoother" />
            <Tile label="Avg turnaround" value={d.turnaround_days == null ? '—' : `${d.turnaround_days}d`} hint="Average days from an invoice being created to being paid" />
          </div>
          <div className="flex flex-wrap gap-3">
            <div className={clsx('rounded-xl border p-4 flex-1 min-w-[140px]', exTotal ? 'bg-amber-50/60 border-amber-100' : 'border-line')}>
              <div className="text-[12.5px] font-medium text-ink-soft">Exception backlog</div>
              <div className={clsx('font-display text-[24px] font-bold tabular-nums leading-none mt-2', exTotal ? 'text-warn' : 'text-ink')}>{exTotal}</div>
              <div className="text-[12px] text-muted mt-2">{ex.flagged || 0} flagged · {ex.over_cap_open || 0} over-cap · {ex.rejected_claims || 0} rejected claims · {ex.recon_exceptions || 0} unmatched receipts</div>
            </div>
          </div>

          {d.budgets_at_risk?.length > 0 && (
            <div className="rounded-xl border border-slate-200 overflow-hidden">
              <div className="px-4 py-2.5 bg-slate-50/70 border-b border-slate-100 text-sm font-bold text-slate-700 flex items-center gap-1.5"><Wallet size={14} className="text-primary-500" /> Budgets at risk</div>
              <div className="divide-y divide-slate-50">
                {d.budgets_at_risk.map((b) => (
                  <div key={b.id} className="flex items-center gap-3 px-4 py-2.5 text-sm">
                    <span className="text-slate-700 font-medium flex-1 truncate">{b.client_name}</span>
                    <div className="w-24 h-1.5 rounded-full bg-slate-100 overflow-hidden shrink-0"><span className={clsx('block h-full', b.pct >= 100 ? 'bg-red-500' : 'bg-amber-500')} style={{ width: `${Math.min(100, b.pct)}%` }} /></div>
                    <span className={clsx('font-semibold tabular-nums shrink-0 w-14 text-right', b.pct >= 100 ? 'text-red-600' : 'text-amber-600')}>{b.pct}%</span>
                    <span className="text-[11px] text-slate-400 tabular-nums shrink-0 hidden sm:block">{money(b.committed)} / {money(b.budget)}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="rounded-xl border border-slate-200 overflow-hidden">
            <div className="px-4 py-2.5 bg-slate-50/70 border-b border-slate-100 text-sm font-bold text-slate-700 flex items-center gap-1.5"><AlertTriangle size={14} className="text-amber-500" /> Anomalies to review</div>
            {!d.anomalies?.length ? <p className="text-sm text-slate-400 py-6 text-center">No anomalies — all clear.</p> : (
              <div className="divide-y divide-slate-50">
                {d.anomalies.map((a) => (
                  <button key={a.id} onClick={() => onOpenInvoice(a.id)} className="w-full flex items-center justify-between gap-2 text-sm px-4 py-2.5 hover:bg-slate-50 text-left transition">
                    <span className="text-slate-700 truncate">{a.client_name} · <span className="text-slate-400">{a.provider || '—'} · {money(a.total_amount)}</span></span>
                    <span className="flex gap-1 shrink-0">
                      {a.duplicate_flag && <span className="badge bg-amber-50 text-amber-700 !text-[10px]">dup</span>}
                      {a.coi_flag && <span className="badge bg-orange-50 text-orange-700 !text-[10px]">COI</span>}
                      {a.over_cap && <span className="badge bg-red-50 text-red-700 !text-[10px]">over cap</span>}
                    </span>
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </Modal>
  );
}

function StatementModal({ clients, onClose, asPage }) {
  const [clientId, setClientId] = useState('');
  const [from, setFrom] = useState(() => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-01`; });
  const [to, setTo] = useState(isoToday);
  const [busy, setBusy] = useState(false);
  const who = clients.find((c) => c.id === clientId);
  // The periods a statement is actually asked for. Picking one fills From / To, which stay editable
  // for anything else. Australian financial year: 1 July.
  const presets = (() => {
    const t = new Date(); const y = t.getFullYear(); const m = t.getMonth();
    const iso = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
    return [
      { label: 'This month', from: iso(new Date(y, m, 1)), to: isoToday() },
      { label: 'Last month', from: iso(new Date(y, m - 1, 1)), to: iso(new Date(y, m, 0)) },
      { label: 'This quarter', from: iso(new Date(y, m - (m % 3), 1)), to: isoToday() },
      { label: 'Financial year to date', from: iso(new Date(m >= 6 ? y : y - 1, 6, 1)), to: isoToday() },
    ];
  })();
  const go = async (e) => {
    e.preventDefault();
    if (!clientId) return toast.error('Select a participant');
    if (from && to && from > to) return toast.error('"From" date must be on or before "To" date');
    setBusy(true);
    try {
      const [r, branding] = await Promise.all([
        api.get('/plan-management/statement', { params: { client_id: clientId, from, to } }),
        getBranding(),
      ]);
      generateStatementPdf(r.data, branding);
      const name = who ? `${who.first_name} ${who.last_name}` : 'participant';
      api.post('/plan-management/downloads', { kind: 'statement', filename: `statement-${name.replace(/\s+/g, '-').toLowerCase()}-${to}.pdf`, ref: name, detail: { from, to } }).catch(() => {}); // log to Downloads queue
      toast.success('Statement generated'); if (!asPage) onClose();
    } catch (err) { toast.error(err.response?.data?.message || 'Failed'); } finally { setBusy(false); }
  };
  return (
    <Modal page={asPage} title="Participant Statement" subtitle="Branded PDF of plan budget + invoices. Monthly statements also email automatically on the 1st (opt-out per participant)." icon={FileText} onClose={onClose}
      footer={<>{!asPage && <button onClick={onClose} className="btn-secondary text-sm">Cancel</button>}<button type="submit" form="stmt" disabled={busy || !clientId} className="btn-primary text-sm min-w-[130px] flex items-center justify-center gap-2">{busy ? <Loader2 size={15} className="animate-spin" /> : <Download size={15} />}{busy ? 'Generating…' : 'Download PDF'}</button></>}>
      <form id="stmt" onSubmit={go} className={clsx('grid gap-6', asPage && 'lg:grid-cols-[minmax(0,480px)_minmax(0,1fr)]')}>
        <div className="space-y-5">
          <Field label="Participant *"><select value={clientId} onChange={(e) => setClientId(e.target.value)} className="input-field"><option value="">Select…</option>{clients.map((c) => <option key={c.id} value={c.id}>{c.first_name} {c.last_name}</option>)}</select></Field>
          <div>
            <span className="text-xs font-semibold text-slate-500 mb-1.5 block">Period</span>
            <div className="flex flex-wrap gap-1.5 mb-3" role="group" aria-label="Quick periods">
              {presets.map((p) => {
                const on = p.from === from && p.to === to;
                return (
                  <button key={p.label} type="button" aria-pressed={on} onClick={() => { setFrom(p.from); setTo(p.to); }}
                    className={clsx('px-2.5 py-1 rounded-lg text-[12px] font-medium border transition-colors duration-150 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-400',
                      on ? 'bg-primary-600 border-primary-600 text-white' : 'bg-white border-line text-ink-soft hover:border-slate-300')}>
                    {p.label}
                  </button>
                );
              })}
            </div>
            <div className="grid grid-cols-2 gap-3">
              <Field label="From"><input type="date" value={from} onChange={(e) => setFrom(e.target.value)} className="input-field tabular-nums" /></Field>
              <Field label="To"><input type="date" value={to} onChange={(e) => setTo(e.target.value)} className="input-field tabular-nums" /></Field>
            </div>
          </div>
        </div>
        {asPage && (
          <aside className="rounded-xl border border-line bg-paper/50 p-5 self-start" aria-live="polite">
            {who ? (
              <>
                <div className="text-[12px] text-muted">Statement for</div>
                <div className="font-display text-[19px] font-bold text-ink leading-tight mt-0.5">{who.first_name} {who.last_name}</div>
                <dl className="mt-3 space-y-1.5">
                  <XFact label="NDIS number"><span className="tabular-nums">{who.ndis_number || '—'}</span></XFact>
                  <XFact label="Email"><span title={who.email || ''}>{who.email || <span className="text-muted">No email on file</span>}</span></XFact>
                  <XFact label="Invoiced to date"><span className="tabular-nums">{money(who.invoiced || 0)}</span></XFact>
                  <XFact label="Period"><span className="tabular-nums">{fmtRange(from, to)}</span></XFact>
                </dl>
                {!who.email && <p className="mt-3 text-[12px] text-warn">Without an email address the monthly statement cannot be sent automatically. Download it here instead.</p>}
              </>
            ) : (
              <>
                <div className="text-[13.5px] font-semibold text-ink">Choose a participant</div>
                <p className="text-[12.5px] text-muted mt-1" style={{ textWrap: 'pretty' }}>
                  The statement is a PDF on your letterhead showing the participant’s plan budget and the invoices in the period you pick.
                  Monthly statements also email automatically on the 1st, unless the participant has opted out.
                </p>
              </>
            )}
          </aside>
        )}
      </form>
    </Modal>
  );
}

const PM_CATS = [
  { key: 'daily_activities', label: 'Daily Life (Personal Care / SIL)' }, { key: 'social_community', label: 'Social & Community' },
  { key: 'transport', label: 'Transport' }, { key: 'consumables', label: 'Consumables' },
  { key: 'capacity_building', label: 'Capacity Building' }, { key: 'capital', label: 'Capital' }, { key: 'other', label: 'Other / General' },
];
const GST_CODES = [{ code: 'P1', label: 'P1 (GST applies)' }, { code: 'P2', label: 'P2 (GST-free)' }];
const CLAIM_TYPES = [{ code: 'STD', label: 'Standard' }, { code: 'CANC', label: 'Cancellation' }, { code: 'TRAN', label: 'Transport' }, { code: 'NF2F', label: 'Non-face-to-face' }, { code: 'THLT', label: 'Telehealth' }, { code: 'REPW', label: 'Report writing' }];

// Editable invoice header (CareView Edit Invoice): provider#, date, ABN, approved-by-client,
// auto-calculate, on-hold reason, assigned-to, note.
// CareView-style "Edit Invoice" header editor (open by default; Yes/No toggle pills).
function InvoiceDetailsEditor({ detail, onSaved, editReq }) {
  const [f, setF] = useState(detail);
  const [busy, setBusy] = useState(false);
  const [editing, setEditing] = useState(false);
  const cardRef = useRef(null);
  useEffect(() => { setF(detail); setEditing(false); }, [detail.id]);
  // The prominent "Edit invoice" button in the detail header bumps editReq → open edit mode + scroll.
  const detailRef = useRef(detail);
  detailRef.current = detail;
  useEffect(() => {
    if (editReq) { setF(detailRef.current); setEditing(true); setTimeout(() => cardRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 50); }
  }, [editReq]);
  const set = (k) => (e) => setF((p) => ({ ...p, [k]: e.target.value }));
  const save = async () => {
    const dateErr = serviceDateError(f.service_date_from, f.service_date_to);
    if (dateErr) return toast.error(dateErr);
    setBusy(true);
    try {
      await api.put(`/plan-management/invoices/${detail.id}`, {
        provider_invoice_number: f.provider_invoice_number, invoice_date: f.invoice_date, abn: f.abn,
        // The service period was missing from this form entirely, so an invoice recorded with the
        // wrong dates -- or with none, which used to be possible -- could never be corrected.
        service_date_from: (f.service_date_from || '').slice(0, 10),
        service_date_to: (f.service_date_to || '').slice(0, 10),
        approved_by_client: f.approved_by_client === true, auto_calculate: f.auto_calculate !== false,
        on_hold_reason: f.on_hold_reason, assigned_to: f.assigned_to, notes: f.notes,
      });
      toast.success('Invoice details saved'); setEditing(false); onSaved();
    } catch (e) { toast.error(e.response?.data?.message || 'Failed'); } finally { setBusy(false); }
  };
  const I = 'input-field w-full text-sm'; const L = 'text-[11px] font-medium text-slate-500';
  const yesNo = (v) => (v === true ? 'Yes' : v === false ? 'No' : '—');
  const D = ({ label, value }) => <div><span className="text-[11px] uppercase tracking-wide text-slate-400 font-semibold">{label}</span><span className="text-sm text-slate-800 break-words block mt-0.5">{value || '—'}</span></div>;
  // Green-Yes / red-No toggle pill (CareView).
  const Toggle = ({ value, onChange }) => (
    <div className="inline-flex rounded-lg border border-slate-200 overflow-hidden">
      <button type="button" onClick={() => onChange(true)} className={clsx('px-3 py-1.5 text-xs font-semibold', value === true ? 'bg-emerald-500 text-white' : 'bg-white text-slate-500')}>Yes</button>
      <button type="button" onClick={() => onChange(false)} className={clsx('px-3 py-1.5 text-xs font-semibold border-l border-slate-200', value === false ? 'bg-rose-500 text-white' : 'bg-white text-slate-500')}>No</button>
    </div>
  );
  const actions = editing
    ? (<div className="flex items-center gap-2">
        <button onClick={() => { setEditing(false); setF(detail); }} className="btn-secondary !text-xs !px-3 !py-1.5">Cancel</button>
        <button onClick={save} disabled={busy} className="btn-primary !text-xs !px-3 !py-1.5 flex items-center gap-1.5">{busy ? <Loader2 size={13} className="animate-spin" /> : <Check size={13} />} Save changes</button>
      </div>)
    : (<button onClick={() => { setF(detail); setEditing(true); }} className="btn-secondary !text-xs !px-3 !py-1.5 flex items-center gap-1.5"><Pencil size={13} /> Edit</button>);
  return (
    <div ref={cardRef}>
    <SectionCard title="Invoice details" icon={FileText} actions={actions} bodyClass="p-5 space-y-4">
      {!editing ? (
        <div className="grid grid-cols-2 lg:grid-cols-3 gap-x-6 gap-y-4">
          <D label="Provider invoice #" value={f.provider_invoice_number} />
          <D label="Invoice date" value={f.invoice_date ? fmtD(f.invoice_date) : ''} />
          <D label="Service from" value={f.service_date_from ? fmtD(f.service_date_from) : ''} />
          <D label="Service to" value={f.service_date_to ? fmtD(f.service_date_to) : ''} />
          <D label="ABN" value={f.abn} />
          <D label="Assigned to" value={f.assigned_to} />
          <D label="On-hold reason" value={f.on_hold_reason} />
          <D label="Approved by client" value={yesNo(f.approved_by_client === true ? true : f.approved_by_client === false ? false : null)} />
          <D label="Auto calculate" value={yesNo(f.auto_calculate !== false)} />
          {f.notes && <div className="col-span-2 lg:col-span-3"><span className="text-[11px] uppercase tracking-wide text-slate-400 font-semibold">Invoice note</span><span className="text-sm text-slate-800 break-words block mt-0.5">{f.notes}</span></div>}
        </div>
      ) : (
        <>
          <div className="grid grid-cols-2 lg:grid-cols-3 gap-3">
            <div><label className={L}>Provider invoice #</label><input className={I} value={f.provider_invoice_number || ''} onChange={set('provider_invoice_number')} /></div>
            <div><label className={L}>Invoice date</label><input type="date" className={I} value={(f.invoice_date || '').slice(0, 10)} onChange={set('invoice_date')} /></div>
            {/* Same two fields, same names, same rule as the create form and the line editor. */}
            <div><label className={L}>Service from *</label>
              <input type="date" required className={clsx(I, !(f.service_date_from || '').slice(0, 10) && '!border-rose-300')}
                value={(f.service_date_from || '').slice(0, 10)}
                onChange={(e) => setF((p) => ({ ...p, service_date_from: e.target.value,
                  service_date_to: (!p.service_date_to || String(p.service_date_to).slice(0, 10) < e.target.value) ? e.target.value : p.service_date_to }))} /></div>
            <div><label className={L}>Service to *</label>
              <input type="date" required min={(f.service_date_from || '').slice(0, 10) || undefined}
                className={clsx(I, !!serviceDateError(f.service_date_from, f.service_date_to) && '!border-rose-300')}
                value={(f.service_date_to || '').slice(0, 10)} onChange={set('service_date_to')} /></div>
            <div><label className={L}>ABN</label><input className={I} value={f.abn || ''} onChange={set('abn')} /></div>
            <div><label className={L}>Assigned to</label><input className={I} value={f.assigned_to || ''} onChange={set('assigned_to')} /></div>
            <div className="col-span-2 lg:col-span-1"><label className={L}>On-hold reason</label><input className={I} value={f.on_hold_reason || ''} onChange={set('on_hold_reason')} /></div>
          </div>
          <div className="flex flex-wrap gap-x-8 gap-y-3">
            <div><span className={clsx(L, 'block mb-1')}>Approved by client</span><Toggle value={f.approved_by_client === true ? true : f.approved_by_client === false ? false : null} onChange={(v) => setF((p) => ({ ...p, approved_by_client: v }))} /></div>
            <div><span className={clsx(L, 'block mb-1')}>Auto calculate</span><Toggle value={f.auto_calculate !== false} onChange={(v) => setF((p) => ({ ...p, auto_calculate: v }))} /></div>
          </div>
          <div><label className={L}>Invoice note</label><textarea rows={2} className={I} value={f.notes || ''} onChange={set('notes')} /></div>

          {/* The same actions again, at the end of the form. The card header carries them
              too, but a long form scrolls its own header out of view — which is how an
              editor ends up looking like it has no way to save. Whichever end you reach,
              the button is there. */}
          <div className="flex items-center justify-end gap-2 pt-3 border-t border-slate-100">
            <button onClick={() => { setEditing(false); setF(detail); }}
              className="btn-secondary !text-xs !px-4 !py-2">Cancel</button>
            <button onClick={save} disabled={busy}
              className="btn-primary !text-xs !px-4 !py-2 flex items-center gap-1.5">
              {busy ? <Loader2 size={13} className="animate-spin" /> : <Check size={13} />} Save changes
            </button>
          </div>
        </>
      )}
    </SectionCard>
    </div>
  );
}

// Reconcile a payment by hand.
//
// Used when the remittance file cannot carry the work: the bank sent a PDF, the reference
// is missing, the NDIA paid a rounded or partial amount, or the import simply failed. It
// asks for the same three things the automatic matcher derives — how much arrived, what it
// pays, and the reference — plus a reason, because a manual reconciliation is an assertion
// that money arrived with no file to back it up, and the reason is the only evidence there
// will be.
function ManualReconcileModal({ preset, claimed, onClose, onSubmit }) {
  const [amount, setAmount] = useState(preset?.amount ?? '');
  const [ref, setRef] = useState(preset?.ref || '');
  const [paidAt, setPaidAt] = useState(new Date().toISOString().slice(0, 10));
  const [reason, setReason] = useState('');
  const [picked, setPicked] = useState({});
  const [busy, setBusy] = useState(false);
  const [q, setQ] = useState('');

  const chosen = Object.keys(picked).filter((k) => picked[k]);
  const chosenTotal = (claimed || [])
    .filter((r) => picked[r.id])
    .reduce((n, r) => n + Number(r.total_amount || 0), 0);
  const diff = Math.round((Number(amount || 0) - chosenTotal) * 100) / 100;

  const term = q.trim().toLowerCase();
  const list = term
    ? (claimed || []).filter((r) => `${r.provider_name} ${r.client_name} ${r.provider_invoice_number}`.toLowerCase().includes(term))
    : (claimed || []);

  const submit = async () => {
    // The amount is optional (owner's call). Left blank or 0, the selected invoices are recorded as
    // paid in full — the server takes their total as the amount received, and says so in the audit.
    if (!chosen.length) return toast.error('Choose which invoices this payment covers');
    if (!reason.trim()) return toast.error('Give a reason — it is the only record of why this was done by hand');
    setBusy(true);
    await onSubmit({ amount: Number(amount) > 0 ? Number(amount) : null, ref, reason: reason.trim(), invoiceIds: chosen, exceptionId: preset?.exceptionId, paidAt });
    setBusy(false);
  };

  return (
    <Modal title="Reconcile manually" subtitle="Record a payment the remittance file could not match" icon={Wallet} wide onClose={onClose}
      footer={<>
        <button onClick={onClose} className="btn-secondary text-sm">Cancel</button>
        <button onClick={submit} disabled={busy} className="btn-primary text-sm min-w-[150px] flex items-center justify-center gap-2">
          {busy ? <Loader2 size={15} className="animate-spin" /> : <Check size={15} />} Mark paid
        </button>
      </>}>
      <div className="space-y-4">
        <div className="grid grid-cols-3 gap-3">
          <div>
            <label className="text-xs font-semibold text-slate-500">Amount received</label>
            <input type="number" step="0.01" value={amount} onChange={(e) => setAmount(e.target.value)}
              className="input-field w-full text-sm mt-1" placeholder={chosenTotal > 0 ? chosenTotal.toFixed(2) : 'Optional'} />
            <p className="text-[10.5px] text-slate-400 mt-1">Optional — leave blank to record the selected invoices as paid in full.</p>
          </div>
          <div>
            <label className="text-xs font-semibold text-slate-500">Date received</label>
            <input type="date" value={paidAt} onChange={(e) => setPaidAt(e.target.value)} className="input-field w-full text-sm mt-1" />
          </div>
          <div>
            <label className="text-xs font-semibold text-slate-500">Reference</label>
            <input value={ref} onChange={(e) => setRef(e.target.value)} className="input-field w-full text-sm mt-1" placeholder="Bank or NDIA reference" />
          </div>
        </div>

        <div>
          <div className="flex items-center justify-between mb-1.5">
            <label className="text-xs font-semibold text-slate-500">Invoices this payment covers *</label>
            {chosen.length > 0 && (
              <span className={clsx('text-xs font-semibold', Math.abs(diff) < 0.005 ? 'text-emerald-600' : 'text-amber-600')}>
                {chosen.length} selected · {money(chosenTotal)}
                {Math.abs(diff) >= 0.005 && ` · ${diff > 0 ? 'over' : 'short'} by ${money(Math.abs(diff))}`}
              </span>
            )}
          </div>
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search provider, participant or invoice number"
            className="input-field w-full text-sm mb-2" />
          <div className="border border-slate-200 rounded-xl max-h-56 overflow-y-auto divide-y divide-slate-100">
            {list.length === 0 && <p className="text-sm text-slate-400 text-center py-6">No claimed invoices waiting to be reconciled.</p>}
            {list.map((r) => (
              <label key={r.id} className="flex items-center gap-3 px-3 py-2 text-sm hover:bg-slate-50 cursor-pointer">
                <input type="checkbox" checked={!!picked[r.id]}
                  onChange={(e) => setPicked((p) => ({ ...p, [r.id]: e.target.checked }))} />
                <span className="flex-1 min-w-0">
                  <span className="block font-medium text-slate-700 truncate">{r.provider_name || 'Provider'}</span>
                  <span className="block text-xs text-slate-400 truncate">{r.client_name} · {r.provider_invoice_number || 'no invoice #'}</span>
                </span>
                <span className="tabular-nums font-semibold text-slate-700">{money(r.total_amount)}</span>
              </label>
            ))}
          </div>
          {/* A mismatch is allowed — the NDIA rounds and pays partially — but it should be a
              decision rather than something noticed later in a bank reconciliation. */}
          {chosen.length > 0 && Math.abs(diff) >= 0.005 && (
            <p className="text-[11px] text-amber-700 mt-1.5">
              The amount received does not equal the invoices selected. That is allowed — say why below.
            </p>
          )}
        </div>

        <div>
          <label className="text-xs font-semibold text-slate-500">Reason *</label>
          <textarea rows={2} value={reason} onChange={(e) => setReason(e.target.value)}
            className="input-field w-full text-sm mt-1"
            placeholder="e.g. Remittance arrived as a PDF the importer could not read; matched against the bank statement." />
          <p className="text-[11px] text-slate-400 mt-1">
            Recorded against every invoice this marks paid, with your name and the time.
          </p>
        </div>
      </div>
    </Modal>
  );
}

// A single invoice line — display + inline edit (qty/rate/date/times/category/GST/claim).
function LineRow({ line, locked, onSaved, onDelete }) {
  const [edit, setEdit] = useState(false);
  const [f, setF] = useState(line);
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setF((p) => ({ ...p, [k]: e.target.value }));
  const save = async () => {
    // Same rule as the add form, because the claim file needs both dates and rejects a range
    // that runs backwards.
    const sdFrom = (f.service_date_from || f.item_date || '').slice(0, 10);
    const sdTo = (f.service_date_to || f.service_date_from || f.item_date || '').slice(0, 10);
    if (!sdFrom || !sdTo) { toast.error('Service from and Service to are both required.'); return; }
    if (sdTo < sdFrom) { toast.error('Service to cannot be before Service from.'); return; }
    setBusy(true);
    try {
      const r = await api.put(`/plan-management/lines/${line.id}`, {
        support_item_code: f.support_item_code, support_item_name: f.support_item_name, support_category: f.support_category || null,
        // sdFrom / sdTo were computed and checked above and then NOT SENT -- the body carried
        // item_date alone, so an edit here could never change Service to. Same field set as the
        // add form and the grid: the period as its own two fields, item_date as its first day.
        item_date: sdFrom, service_date_from: sdFrom, service_date_to: sdTo,
        delivered_from: f.delivered_from || null, delivered_to: f.delivered_to || null, hours: f.hours || null,
        qty: num(f.qty), unit_price: num(f.unit_price), claim_type: f.claim_type, gst_code: f.gst_code,
      });
      if (r.data?.warning) toast(r.data.warning, { icon: '⚠️' });
      toast.success('Line updated'); setEdit(false); onSaved();
    } catch (e) { toast.error(e.response?.data?.message || 'Failed'); } finally { setBusy(false); }
  };
  if (edit) return (
    <tr className="bg-primary-50/30">
      <td className="px-4 py-2 text-xs text-slate-600">{line.support_item_name || line.support_item_code || '—'}</td>
      <td className="px-2 py-2"><select value={f.support_category || ''} onChange={set('support_category')} className="input-field !text-xs !py-1 !w-32"><option value="">Auto</option>{PM_CATS.map((c) => <option key={c.key} value={c.key}>{c.label}</option>)}</select></td>
      <td className="px-2 py-2">
        <input type="date" aria-label="Service from" value={(f.service_date_from || f.item_date || '').slice(0, 10)}
          onChange={(e) => setF((p) => ({ ...p, service_date_from: e.target.value, item_date: e.target.value,
            service_date_to: (!p.service_date_to || String(p.service_date_to).slice(0, 10) < e.target.value) ? e.target.value : p.service_date_to }))}
          className="input-field !text-xs !py-1 !w-32" /></td>
      <td className="px-2 py-2">
        <input type="date" aria-label="Service to" value={(f.service_date_to || f.service_date_from || f.item_date || '').slice(0, 10)}
          min={(f.service_date_from || f.item_date || '').slice(0, 10) || undefined} onChange={set('service_date_to')}
          className="input-field !text-xs !py-1 !w-32" /></td>
      <td className="px-2 py-2"><div className="flex gap-1"><input type="time" value={f.delivered_from || ''} onChange={set('delivered_from')} className="input-field !text-xs !py-1 !w-20" /><input type="time" value={f.delivered_to || ''} onChange={set('delivered_to')} className="input-field !text-xs !py-1 !w-20" /></div></td>
      <td className="px-2 py-2"><input value={f.hours || ''} onChange={set('hours')} className="input-field !text-xs !py-1 !w-14" /></td>
      <td className="px-2 py-2"><input type="number" step="0.01" value={f.qty} onChange={set('qty')} className="input-field !text-xs !py-1 !w-14 text-right" /></td>
      <td className="px-2 py-2"><input type="number" step="0.01" value={f.unit_price} onChange={set('unit_price')} className="input-field !text-xs !py-1 !w-20 text-right" /></td>
      <td className="px-2 py-2"><select value={f.gst_code || 'P2'} onChange={set('gst_code')} className="input-field !text-xs !py-1 !w-16">{GST_CODES.map((c) => <option key={c.code} value={c.code}>{c.code}</option>)}</select></td>
      <td className="px-4 py-2 text-right tabular-nums font-semibold">{money(num(f.qty) * num(f.unit_price))}</td>
      <td className="px-2 py-2"><select value={f.claim_type || 'STD'} onChange={set('claim_type')} className="input-field !text-xs !py-1 !w-20">{CLAIM_TYPES.map((c) => <option key={c.code} value={c.code}>{c.code}</option>)}</select></td>
      <td className="px-2 py-2 text-right whitespace-nowrap"><button onClick={save} disabled={busy} className="text-emerald-600 p-1">{busy ? '…' : <Check size={14} />}</button><button onClick={() => { setEdit(false); setF(line); }} className="text-slate-400 p-1"><X size={14} /></button></td>
    </tr>
  );
  return (
    <tr className="hover:bg-slate-50">
      <td className="px-4 py-3"><p className="text-slate-700">{line.support_item_name || line.description || '—'}</p>{line.support_item_code && <p className="text-[11px] text-slate-400 font-mono">{line.support_item_code}</p>}</td>
      <td className="px-2 py-3 text-xs text-slate-500">{(PM_CATS.find((c) => c.key === line.support_category) || {}).label || line.support_category || '—'}</td>
      <td className="px-2 py-3 text-xs text-slate-500 whitespace-nowrap">{fmtD(line.service_date_from || line.item_date)}</td>
      <td className="px-2 py-3 text-xs text-slate-500 whitespace-nowrap">{fmtD(line.service_date_to || line.service_date_from || line.item_date)}</td>
      <td className="px-2 py-3 text-xs text-slate-500">{(line.delivered_from || line.delivered_to) ? `${line.delivered_from || ''}–${line.delivered_to || ''}` : '—'}</td>
      {/* An hourly item is claimed as Hours; when none were typed the quantity IS the hours, and
          the claim file will send it that way. Show what will be claimed, not a dash. */}
      <td className="px-2 py-3 text-xs text-slate-500">{line.hours || (/^(h|hr|hour|hourly|hours)$/i.test(String(line.unit_of_measure || line.unit || '')) && Number(line.qty) > 0 ? `${Number(line.qty)} h` : '—')}</td>
      <td className="px-2 py-3 text-right tabular-nums text-slate-600">{line.qty}</td>
      <td className="px-2 py-3 text-right tabular-nums text-slate-600">{money(line.unit_price)}{line.over_cap && <span className="ml-1 text-[10px] font-semibold text-red-600" title={line.cap_rate != null ? `Exceeds NDIS cap ${money(line.cap_rate)}` : 'Exceeds cap'}>over cap</span>}</td>
      <td className="px-2 py-3 text-xs text-slate-500">{line.gst_code}</td>
      <td className="px-4 py-3 text-right tabular-nums font-semibold text-slate-700">{money(line.line_total)}</td>
      <td className="px-2 py-3 text-xs text-slate-500">{line.claim_type}</td>
      <td className="px-2 py-3 text-right whitespace-nowrap">{!locked && <><button onClick={() => { setF(line); setEdit(true); }} className="text-slate-400 hover:text-primary-600 p-1.5 text-xs font-medium">Edit</button><button onClick={() => onDelete(line)} aria-label="Delete line" className="text-slate-400 hover:text-red-600 p-1.5"><Trash2 size={14} /></button></>}</td>
    </tr>
  );
}

// inline line-entry form (price-book picker + budget-validated add + live per-line check)
function LineForm({ invId, onAdded }) {
  // service_date_from/to are the DATES the support was delivered across — Appendix A columns 3
  // and 4 of the claim file. "From"/"To" here were TIMES of day (delivered_from/to), and the
  // service dates were never captured at all: the server fell back to item_date for both, so a
  // weekly SIL invoice was claimed as a single day. Both forms now carry the range, and the two
  // forms carry the same fields as each other.
  const blank = { support_item_code: '', support_item_name: '', support_category: '', item_date: isoToday(),
    service_date_from: isoToday(), service_date_to: isoToday(),
    delivered_from: '', delivered_to: '', hours: '', qty: '1', unit_price: '', claim_type: 'STD', gst_code: 'P2' };
  const [f, setF] = useState(blank);
  const [saving, setSaving] = useState(false);
  const [chk, setChk] = useState(null);
  const set = (k) => (e) => setF((p) => ({ ...p, [k]: e.target.value }));
  const total = num(f.qty) * num(f.unit_price);
  // Live validation: contract remaining + category remaining + price cap + date window.
  useEffect(() => {
    if (!f.support_item_code && !f.support_category) { setChk(null); return; }
    const t = setTimeout(() => {
      api.get(`/plan-management/invoices/${invId}/line-check`, { params: { code: f.support_item_code, date: f.item_date, category: f.support_category } })
        .then((r) => setChk(r.data)).catch(() => setChk(null));
    }, 350);
    return () => clearTimeout(t);
  }, [invId, f.support_item_code, f.support_category, f.item_date]);
  const add = async () => {
    if (!f.support_item_code && !f.support_item_name) return toast.error('Pick a support item');
    // The claim file needs both, and rejects a range that runs backwards — say so here rather
    // than letting the batch fail days later.
    if (!f.service_date_from || !f.service_date_to) return toast.error('Service from and Service to are both required — they are what the NDIA claims against.');
    if (f.service_date_to < f.service_date_from) return toast.error('Service to cannot be before Service from.');
    setSaving(true);
    try {
      const r = await api.post(`/plan-management/invoices/${invId}/lines`, { ...f, qty: num(f.qty), unit_price: num(f.unit_price) });
      if (r.data?.warning) toast(r.data.warning, { icon: '⚠️' });
      setF({ ...blank, item_date: f.item_date }); setChk(null);
      onAdded();
    } catch (e) { toast.error(e.response?.data?.message || 'Failed to add line'); } finally { setSaving(false); }
  };
  const overCap = chk?.cap != null && num(f.unit_price) > chk.cap + 0.005;
  return (
    <div className="px-5 py-3 bg-slate-50/60 border-b border-slate-100">
      <div className="flex flex-wrap gap-2 items-end">
        <div className="flex-1 min-w-[200px]"><Field label="Support item">
          <SupportItemPicker value={f.support_item_code} valueName={f.support_item_name} rate={f.unit_price === '' ? null : f.unit_price}
            onSelect={({ code, name, rate }) => setF((p) => ({ ...p, support_item_code: code, support_item_name: name, unit_price: rate == null ? p.unit_price : rate }))} />
        </Field></div>
        <Field label="Category"><select value={f.support_category} onChange={set('support_category')} className="input-field !w-auto"><option value="">Auto</option>{PM_CATS.map((c) => <option key={c.key} value={c.key}>{c.label}</option>)}</select></Field>
        {/* Service dates first — they are what the NDIA claims against. Moving the item date
            with them keeps the budget/period check, which reads item_date, on the same day. */}
        <Field label="Service from *"><input type="date" required value={f.service_date_from}
          onChange={(e) => setF((p) => ({ ...p, service_date_from: e.target.value, item_date: e.target.value,
            service_date_to: (!p.service_date_to || p.service_date_to < e.target.value) ? e.target.value : p.service_date_to }))}
          className={clsx('input-field !w-36', !f.service_date_from && '!border-rose-300')} /></Field>
        <Field label="Service to *"><input type="date" required value={f.service_date_to} min={f.service_date_from || undefined}
          onChange={set('service_date_to')}
          className={clsx('input-field !w-36', (!f.service_date_to || (f.service_date_from && f.service_date_to < f.service_date_from)) && '!border-rose-300')} /></Field>
        <Field label="Time from"><input type="time" value={f.delivered_from} onChange={set('delivered_from')} className="input-field !w-28" /></Field>
        <Field label="Time to"><input type="time" value={f.delivered_to} onChange={set('delivered_to')} className="input-field !w-28" /></Field>
        <Field label="Hours"><input value={f.hours} onChange={set('hours')} placeholder="HH:MM" className="input-field !w-20" /></Field>
        <Field label="Qty"><input type="number" step="0.01" value={f.qty} onChange={set('qty')} className="input-field !w-20" /></Field>
        <Field label="Unit $"><input type="number" step="0.01" value={f.unit_price} onChange={set('unit_price')} className="input-field !w-24" /></Field>
        {/* PM's own lists, which are the codes the claim-file writer accepts. This form used to
            borrow Support Coordination's code table (STAN / GSTFREE ...), which holds neither STD
            nor P2 -- so with the state correctly at STD/P2 the browser showed the FIRST option,
            CANC and GSTFREE, and what was on screen was not what would be saved. Pick GSTFREE and
            the NDIA file rejects the line outright. Modules stay separate. */}
        <Field label="Claim"><select value={f.claim_type} onChange={set('claim_type')} className="input-field !w-auto">{CLAIM_TYPES.map((c) => <option key={c.code} value={c.code}>{c.code}</option>)}</select></Field>
        <Field label="GST"><select value={f.gst_code} onChange={set('gst_code')} className="input-field !w-auto">{GST_CODES.map((c) => <option key={c.code} value={c.code}>{c.code}</option>)}</select></Field>
        <button onClick={add} disabled={saving} className="btn-primary text-sm h-[42px] flex items-center gap-1">{saving ? <Loader2 size={15} className="animate-spin" /> : <Plus size={15} />} {money(total)}</button>
      </div>
      {chk && (f.support_item_code || f.support_category) && (
        <div className="flex flex-wrap items-center gap-x-4 gap-y-1 mt-2 text-[11px]">
          {chk.cap != null && <span className={clsx(overCap ? 'text-red-600 font-semibold' : 'text-slate-500')}>Price guide: {money(chk.cap)}{overCap ? ' — over cap!' : ' ✓'}</span>}
          {chk.category_remaining && <span className={clsx(chk.category_remaining.remaining < total ? 'text-amber-600' : 'text-slate-500')}>{chk.category_remaining.label}: {money(chk.category_remaining.remaining)} left</span>}
          {chk.plan_remaining != null && <span className={clsx(chk.plan_remaining < total ? 'text-red-600 font-semibold' : 'text-slate-500')}>Plan remaining: {money(chk.plan_remaining)}</span>}
          {chk.date_error && <span className="text-red-600 font-semibold">⚠ {chk.date_error}</span>}
        </div>
      )}
    </div>
  );
}

// CareView-style full-page "Create New Invoice": header + client budget context + inline line-items
// table (with price-book search, live total, per-line NDIS-cap flag) + drag-drop AI prefill.
function NewInvoiceEditor({ clients, plans, providers, onClose, onSaved }) {
  const confirm = useConfirm();
  const [h, setH] = useState({ client_id: '', plan_id: '', provider_id: '', provider_name: '', provider_invoice_number: '', invoice_date: isoToday(), service_date_from: '', service_date_to: '', note: '', abn: '', approved_by_client: false, auto_calculate: true, on_hold_reason: '' });
  const [lines, setLines] = useState([]);
  const [ctx, setCtx] = useState(null);
  const [saving, setSaving] = useState(false);
  const [ocr, setOcr] = useState(false);
  // The dropped PDF, held until the invoice exists so it can be attached to it.
  const [pendingDoc, setPendingDoc] = useState(null);
  const [drag, setDrag] = useState(false);
  const setHf = (k) => (e) => setH((p) => ({ ...p, [k]: e.target.type === 'checkbox' ? e.target.checked : e.target.value }));
  const clientPlans = plans.filter((p) => p.client_id === h.client_id);
  const client = clients.find((c) => c.id === h.client_id);
  useEffect(() => {
    if (!h.client_id) { setCtx(null); return; }
    api.get('/plan-management/plans', { params: { client_id: h.client_id } }).then((r) => setCtx(r.data || [])).catch(() => setCtx([]));
  }, [h.client_id]);
  const planRemaining = (ctx || []).find((p) => p.id === h.plan_id)?.remaining ?? (ctx || [])[0]?.remaining;

  const uid = () => Math.random().toString(36).slice(2);
  // A line gets the invoice's service period as its starting point. Typing the same two dates on
  // every line of a weekly invoice is the kind of work the header already did once.
  const addLine = () => setLines((ls) => [...ls, { key: uid(), claim_type: 'STD', gst_code: 'P2', support_item_code: '', support_item_name: '', support_category: '', description: '',
    service_date_from: h.service_date_from || '', service_date_to: h.service_date_to || h.service_date_from || '', qty: 1, unit_price: '', cap: null }]);
  const setLine = (key, patch) => setLines((ls) => ls.map((l) => (l.key === key ? { ...l, ...patch } : l)));
  const delLine = (key) => setLines((ls) => ls.filter((l) => l.key !== key));
  const rowTotal = (l) => Math.round((Number(l.qty) || 0) * (Number(l.unit_price) || 0) * 100) / 100;
  const total = lines.reduce((a, l) => a + rowTotal(l), 0);

  const readFile = async (file) => {
    if (!file) return;
    setOcr(true);
    try {
      const b64 = await new Promise((res, rej) => { const r = new FileReader(); r.onload = () => res(String(r.result).split(',')[1]); r.onerror = rej; r.readAsDataURL(file); });
      const r = await api.post('/plan-management/invoices/extract', { file: b64, media_type: file.type, preview: true, client_id: h.client_id || undefined });
      const d = r.data || {};
      // Apply the provider id as well as the name, so a provider already on file is
      // selected in the dropdown rather than left as typed-in free text next to it.
      setH((p) => ({ ...p,
        client_id: p.client_id || d.client_id || '',
        plan_id: p.plan_id || d.plan_id || '',
        provider_id: p.provider_id || d.provider_id || '',
        provider_name: p.provider_name || d.provider_name || '',
        abn: p.abn || d.abn || '',
        provider_invoice_number: p.provider_invoice_number || d.provider_invoice_number || '',
        invoice_date: d.invoice_date || p.invoice_date,
        service_date_from: d.service_date_from || p.service_date_from,
        service_date_to: d.service_date_to || p.service_date_to,
      }));
      // Keep the file so it is attached to the invoice on create — reading a PDF and then
      // not storing it is what leaves a new invoice failing the evidence check.
      setPendingDoc({ file: b64, media_type: file.type, filename: file.name });
      // Say what was matched. "Invoice read" gives no clue whether the participant and
      // provider were recognised or still need choosing.
      const got = [];
      if (d.client_id) got.push('participant');
      if (d.plan_id) got.push('plan');
      if (d.provider_id) got.push('provider');
      toast.success(got.length
        ? `Invoice read — ${got.join(', ')} matched, review the lines below`
        : 'Invoice read — choose the participant and provider, then review the lines');
      // The extractor now reads the period off the document (see utils/serviceDateRange.js), so the
    // grid shows what the invoice actually said rather than one date it had to guess at.
    // description is what the PROVIDER wrote on the line, and on most invoices it is where the
    // service period is written. The grid had no field for it at all, so the extractor read
    // "14/09/2026 to 20/09/2026", the grid dropped it, and the line was saved with nothing to
    // recover the range from. It is carried and shown now.
    setLines((d.lines || []).map((l) => ({ key: uid(), claim_type: 'STD', gst_code: 'P2', support_item_code: l.support_item_code || '', support_item_name: l.support_item_name || '', support_category: l.support_category || '',
      description: l.description || '',
      service_date_from: (l.service_date_from || l.item_date || '').slice(0, 10),
      service_date_to: (l.service_date_to || l.service_date_from || l.item_date || '').slice(0, 10),
      qty: l.qty || 1, unit_price: l.unit_price ?? '', cap: l.cap ?? null })));
    } catch (e) { toast.error(e.response?.data?.message || 'Could not read that file'); } finally { setOcr(false); }
  };

  const create = async (again, force = false) => {
    if (!h.client_id) return toast.error('Select a client');
    if (!h.provider_id && !h.provider_name) return toast.error('Select or name a provider');
    // These were accepted blank and posted as null, so an invoice could be created with no service
    // period at all -- and nothing downstream could claim it.
    const dateErr = serviceDateError(h.service_date_from, h.service_date_to);
    if (dateErr) return toast.error(dateErr);
    // Every line carries its own period and the server enforces it, so say which line is wrong here
    // rather than letting it come back as "n line(s) blocked" with no reason.
    const realLines = lines.filter((l) => l.support_item_code || l.support_item_name);
    for (let i = 0; i < realLines.length; i++) {
      const e = serviceDateError(realLines[i].service_date_from, realLines[i].service_date_to);
      if (e) return toast.error(`Line ${i + 1}: ${e}`);
    }
    setSaving(true);
    try {
      const inv = await api.post('/plan-management/invoices', { client_id: h.client_id, plan_id: h.plan_id || null, provider_id: h.provider_id || null, provider_name: h.provider_name || null, provider_invoice_number: h.provider_invoice_number || null, invoice_date: h.invoice_date || null, service_date_from: h.service_date_from || null, service_date_to: h.service_date_to || null, force });
      const id = inv.data?.id;
      await api.put(`/plan-management/invoices/${id}`, { note: h.note || null, abn: h.abn || null, approved_by_client: !!h.approved_by_client, auto_calculate: !!h.auto_calculate, on_hold_reason: h.on_hold_reason || null }).catch(() => {});

      // Now the invoice exists, attach the PDF that filled it in. Reading a document to
      // build an invoice and then not storing it leaves that invoice failing the evidence
      // check with the file sitting in the browser that created it.
      if (pendingDoc?.file) {
        try {
          // PUT /invoices/:id already records a document_url as version 1 of the evidence,
          // so the attachment goes through the same path a manual attach uses.
          await api.put(`/plan-management/invoices/${id}`, {
            document_url: `data:${pendingDoc.media_type || 'application/pdf'};base64,${pendingDoc.file}`,
          });
        } catch {
          // Not fatal: the invoice and its lines are the valuable part.
          toast.error('Invoice created, but the PDF could not be attached — attach it from the invoice.');
        }
      }
      let blocked = 0;
      for (const l of lines) {
        if (!l.support_item_code && !l.support_item_name) continue;
        try {
          await api.post(`/plan-management/invoices/${id}/lines`, {
            support_item_code: l.support_item_code || null, support_item_name: l.support_item_name || null,
            support_category: l.support_category || null,
            // Dropped entirely before this. Without it the line loses the only place most providers
            // write the service period, and nothing downstream can recover it.
            description: l.description || null,
            // item_date is the FIRST day of the period, which is what every single-date check in the
            // product means by it. The period itself travels as its own two fields.
            item_date: l.service_date_from || null,
            service_date_from: l.service_date_from || null,
            service_date_to: l.service_date_to || l.service_date_from || null,
            qty: Number(l.qty) || 0, unit_price: Number(l.unit_price) || 0,
            gst_code: l.gst_code || null, claim_type: l.claim_type || null,
          });
        } catch { blocked++; }
      }
      toast.success(`Invoice created${blocked ? ` — ${blocked} line(s) blocked by budget/cap` : ''}`);
      if (again) { setH((p) => ({ ...p, provider_invoice_number: '', note: '' })); setLines([]); }
      else onSaved(id);
    } catch (e) {
      // Duplicate-invoice guard (409): same provider invoice # for this participant already exists.
      // OK opens the existing one; Cancel force-creates a separate invoice.
      if (e.response?.status === 409 && e.response?.data?.duplicate) {
        const dup = e.response.data.duplicate;
        setSaving(false);
        const openExisting = await confirm(`${e.response.data.message}\n\nClick OK to open the existing invoice, or Cancel to create a separate one anyway.`, { title: 'Duplicate invoice' });
        if (openExisting) { onSaved(dup.id); } else { create(again, true); }
        return;
      }
      toast.error(e.response?.data?.message || 'Failed to create');
    } finally { setSaving(false); }
  };

  const Lbl = ({ children }) => <label className="block text-[11px] font-semibold text-slate-500 mb-1">{children}</label>;
  const I = 'input-field w-full text-sm';

  return (
    <div className="fixed inset-0 z-50 bg-slate-100 overflow-y-auto">
      <div className="sticky top-0 z-10 bg-white border-b border-slate-200">
        <div className="w-full px-6 py-3 flex items-center gap-3">
          <button onClick={onClose} className="btn-secondary !text-sm !px-3 !py-1.5">Cancel</button>
          <h1 className="text-base font-bold text-slate-800 flex-1 text-center">Create New Invoice</h1>
          <button onClick={() => create(true)} disabled={saving} className="btn-secondary !text-sm !px-3 !py-1.5 disabled:opacity-50">Save &amp; add new</button>
          <button onClick={() => create(false)} disabled={saving} className="btn-primary !text-sm !px-4 !py-1.5 flex items-center gap-1.5 disabled:opacity-50">{saving ? <Loader2 size={14} className="animate-spin" /> : null} Create invoice</button>
        </div>
      </div>

      <div className="w-full px-6 py-6 space-y-5">
        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-5">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <Lbl>Client *</Lbl>
              <select value={h.client_id} onChange={(e) => setH((p) => ({ ...p, client_id: e.target.value, plan_id: '' }))} className={I}><option value="">Select…</option>{clients.map((c) => <option key={c.id} value={c.id}>{c.first_name} {c.last_name}</option>)}</select>
              {client && <div className="mt-2 text-[11px] text-slate-500 bg-slate-50 rounded-lg px-3 py-2 space-y-0.5">
                {client.ndis_number && <div>NDIS {client.ndis_number}</div>}
                {planRemaining != null && <div>Plan remaining: <span className="font-semibold text-slate-700">{money(planRemaining)}</span></div>}
              </div>}
            </div>
            <div>
              <Lbl>Plan / budget</Lbl>
              <select value={h.plan_id} onChange={setHf('plan_id')} className={I} disabled={!h.client_id}><option value="">{h.client_id ? '— select —' : 'pick client first'}</option>{clientPlans.map((p) => <option key={p.id} value={p.id}>{cap(p.managed_by || 'plan')} · {money(p.remaining)} left</option>)}</select>
            </div>
            <div>
              <Lbl>Service Provider</Lbl>
              <select value={h.provider_id} onChange={setHf('provider_id')} className={I}><option value="">— or type below —</option>{providers.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}</select>
              <input value={h.provider_name} onChange={setHf('provider_name')} placeholder="…or provider name" className={clsx(I, 'mt-2')} />
            </div>
            <div><Lbl>Provider Invoice #</Lbl><input value={h.provider_invoice_number} onChange={setHf('provider_invoice_number')} className={I} /></div>
            {/* Three dates, and they are three different facts. The invoice date is the day the
                provider issued it; the service period is the days the support was actually
                delivered, and that period is what the NDIA claims against (columns 3 and 4). The
                period had no fields here at all, so it could only ever be saved empty. */}
            <div><Lbl>Provider Invoice Date</Lbl><input type="date" value={h.invoice_date} onChange={setHf('invoice_date')} className={I} /></div>
            <div><Lbl>Service from *</Lbl>
              <input type="date" value={h.service_date_from} className={clsx(I, !h.service_date_from && '!border-rose-300')}
                onChange={(e) => setH((p) => ({ ...p, service_date_from: e.target.value,
                  service_date_to: (!p.service_date_to || p.service_date_to < e.target.value) ? e.target.value : p.service_date_to }))} /></div>
            <div><Lbl>Service to *</Lbl>
              <input type="date" value={h.service_date_to} min={h.service_date_from || undefined}
                className={clsx(I, !!serviceDateError(h.service_date_from, h.service_date_to) && '!border-rose-300')}
                onChange={setHf('service_date_to')} /></div>
            <div><Lbl>ABN</Lbl><input value={h.abn} onChange={setHf('abn')} className={I} /></div>
            <div className="md:col-span-2"><Lbl>Invoice Note</Lbl><textarea value={h.note} onChange={setHf('note')} rows={3} className={I} /></div>
            <div>
              <Lbl>Attachments</Lbl>
              <label onDragOver={(e) => { e.preventDefault(); setDrag(true); }} onDragLeave={() => setDrag(false)} onDrop={(e) => { e.preventDefault(); setDrag(false); readFile(e.dataTransfer.files?.[0]); }}
                className={clsx('flex flex-col items-center justify-center gap-1 border-2 border-dashed rounded-xl py-4 px-3 cursor-pointer text-center transition', drag ? 'border-primary-400 bg-primary-50/40' : 'border-slate-200 hover:border-primary-300')}>
                {ocr ? <Loader2 size={20} className="animate-spin text-primary-500" /> : <Upload size={20} className="text-slate-300" />}
                <span className="text-[11px] text-slate-500">{ocr ? 'Reading invoice…' : 'Drag & drop a provider PDF — AI reads it & fills the lines'}</span>
                <input type="file" accept=".pdf,image/*" className="hidden" onChange={(e) => readFile(e.target.files?.[0])} />
              </label>
            </div>
            <div className="flex items-end gap-4">
              <label className="flex items-center gap-2 text-sm text-slate-700"><input type="checkbox" checked={h.approved_by_client} onChange={setHf('approved_by_client')} /> Approved by client</label>
              <label className="flex items-center gap-2 text-sm text-slate-700"><input type="checkbox" checked={h.auto_calculate} onChange={setHf('auto_calculate')} /> Auto calculate</label>
            </div>
            <div className="md:col-span-2"><Lbl>On-hold reason</Lbl><input value={h.on_hold_reason} onChange={setHf('on_hold_reason')} placeholder="Leave blank unless holding this invoice" className={I} /></div>
          </div>
        </div>

        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
          <div className="px-5 py-3 border-b border-slate-100 flex items-center justify-between">
            <span className="text-sm font-bold text-slate-700">Line items</span>
            <button onClick={addLine} className="btn-secondary !text-xs !px-3 !py-1.5 flex items-center gap-1.5"><Plus size={14} /> Add line</button>
          </div>
          {lines.length === 0 ? <p className="py-10 text-center text-sm text-slate-400">No lines yet — “Add line”, or drop a provider PDF above to auto-fill.</p> : (
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead className="bg-slate-50 text-[10px] uppercase tracking-wide text-slate-400">
                  <tr>
                    <th className="px-2 py-2 text-left font-semibold min-w-[220px]">Support item</th>
                    <th className="px-2 py-2 text-left font-semibold">Category</th>
                    <th className="px-2 py-2 text-left font-semibold">Type</th>
                    <th className="px-2 py-2 text-left font-semibold">GST</th>
                    <th className="px-2 py-2 text-left font-semibold">Service from</th>
                    <th className="px-2 py-2 text-left font-semibold">Service to</th>
                    <th className="px-2 py-2 text-right font-semibold">Qty</th>
                    <th className="px-2 py-2 text-right font-semibold">Rate</th>
                    <th className="px-2 py-2 text-right font-semibold">Amount</th>
                    <th className="px-2 py-2" />
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-50">
                  {lines.map((l) => {
                    // A price-book rate of 0 means "no published cap" for this item -- quoted supports
                    // such as SIL carry no unit limit. Treating it as a cap OF zero flagged every
                    // such line as over cap and told the operator the limit was "$0.00".
                    const over = l.cap != null && Number(l.cap) > 0 && Number(l.unit_price) > Number(l.cap) + 0.001;
                    return (
                      <tr key={l.key}>
                        <td className="px-2 py-1.5 align-top min-w-[220px]">
                          <SupportItemPicker value={l.support_item_code} valueName={l.support_item_name} rate={l.unit_price === '' ? null : l.unit_price}
                            onSelect={({ code, name, rate }) => setLine(l.key, { support_item_code: code, support_item_name: name, unit_price: rate == null ? l.unit_price : rate })} />
                          <input value={l.description || ''} onChange={(e) => setLine(l.key, { description: e.target.value })}
                            placeholder="Description as it appears on the provider's invoice"
                            aria-label="Line description"
                            className="input-field !text-[11px] !py-0.5 !px-1.5 mt-1 w-full" />
                          {over && <span className="text-[10px] text-rose-600">Over NDIS cap {money(l.cap)}</span>}
                        </td>
                        <td className="px-2 py-1.5"><select value={l.support_category} onChange={(e) => setLine(l.key, { support_category: e.target.value })} className="input-field !text-xs !py-1 !w-auto"><option value="">Auto</option>{PM_CATS.map((c) => <option key={c.key} value={c.key}>{c.label}</option>)}</select></td>
                        <td className="px-2 py-1.5"><select value={l.claim_type} onChange={(e) => setLine(l.key, { claim_type: e.target.value })} className="input-field !text-xs !py-1 !w-auto">{CLAIM_TYPES.map((c) => <option key={c.code} value={c.code}>{c.code}</option>)}</select></td>
                        <td className="px-2 py-1.5"><select value={l.gst_code} onChange={(e) => setLine(l.key, { gst_code: e.target.value })} className="input-field !text-xs !py-1 !w-auto">{GST_CODES.map((g) => <option key={g.code} value={g.code}>{g.code}</option>)}</select></td>
                        {/* Both ends of the period, per line. One "Date" box could only ever say a
                            line was delivered on a single day, so a week of support was claimed as
                            one. Moving From forward drags To with it when To would be left behind. */}
                        <td className="px-2 py-1.5">
                          <input type="date" aria-label="Service from" value={l.service_date_from || ''}
                            onChange={(e) => setLine(l.key, { service_date_from: e.target.value,
                              service_date_to: (!l.service_date_to || l.service_date_to < e.target.value) ? e.target.value : l.service_date_to })}
                            className={clsx('input-field !text-xs !py-1 !w-32', !l.service_date_from && '!border-rose-300')} /></td>
                        <td className="px-2 py-1.5">
                          <input type="date" aria-label="Service to" value={l.service_date_to || ''} min={l.service_date_from || undefined}
                            onChange={(e) => setLine(l.key, { service_date_to: e.target.value })}
                            className={clsx('input-field !text-xs !py-1 !w-32', !!serviceDateError(l.service_date_from, l.service_date_to) && '!border-rose-300')} /></td>
                        <td className="px-2 py-1.5 text-right"><input type="number" step="0.01" value={l.qty} onChange={(e) => setLine(l.key, { qty: e.target.value })} className="input-field !text-xs !py-1 !w-16 text-right" /></td>
                        <td className="px-2 py-1.5 text-right"><input type="number" step="0.01" value={l.unit_price} onChange={(e) => setLine(l.key, { unit_price: e.target.value })} className={clsx('input-field !text-xs !py-1 !w-20 text-right', over && '!border-rose-300')} /></td>
                        <td className="px-2 py-1.5 text-right tabular-nums font-semibold">{money(rowTotal(l))}</td>
                        <td className="px-2 py-1.5 text-right"><button onClick={() => delLine(l.key)} className="text-slate-300 hover:text-rose-600"><X size={13} /></button></td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
          <div className="px-5 py-3 border-t border-slate-100 flex items-center justify-end gap-3">
            <span className="text-sm font-semibold text-slate-500">Invoice total</span>
            <span className="text-lg font-bold text-slate-800 tabular-nums">{money(total)}</span>
          </div>
        </div>
      </div>
    </div>
  );
}
